import { type FormEvent, type PointerEvent as ReactPointerEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Camera, CameraOff, FlipHorizontal, Lock, Mic, Monitor, MousePointer2, PanelRightClose, Play, RotateCcw, SlidersHorizontal, Square, X, ZoomIn, ZoomOut } from 'lucide-react'
import './App.css'
import GuestCloud from './GuestCloud'
import WorkspaceUpdate from './WorkspaceUpdate'
import fallbackDemoScript from './demo/presenter-script.json'
import fallbackPresenterModuleMap from './demo/presenter-module-map.json'
import fallbackPresenterRagPack from './demo/presenter-rag-pack.json'
import { canOfferDistrictDemoPersona, DISTRICT_DEMO_PERSONA_ID } from './district/demoGate'
import { districtSnapshotFromMirrorState, isDistrictMirrorState, type DistrictMirrorState } from './district/mirrorContract'

type Stage = 'landing' | 'auth' | 'app' | 'parentAccess' | 'guestRelease' | 'tabletAccess' | 'browserGate' | 'profileDenied'
type AuthFlow = 'signup' | 'signin'
type NexusBootProfile = 'dev' | 'production-headless' | 'production' | 'recovery'
type NexusSurfaceTheme = 'dark' | 'light'
type BrowserGateCheck = {
  id: string
  label: string
  status: 'PASS' | 'FAIL' | 'UNKNOWN'
  detail?: string
}
type BrowserGateStatus = {
  status: 'PASS' | 'FAIL' | 'CHECKING' | 'UNKNOWN'
  profile: NexusBootProfile
  checks: BrowserGateCheck[]
  checkedAt?: string
}
type InternalTab = {
  id: string
  title: string
  url: string
}
const INTERNAL_TAB_ORDER_STORAGE_KEY = 'nexus-browser-internal-tab-order-v1'
type ParentSession = {
  email: string
  name: string
  role: string
  lease: string
}
type ShellPresence = {
  count: number
  label: string
  tooltip: string
}
type AppStreamCatalogApp = {
  app_id: string
  display_name: string
  bundle_id?: string
  allowed_roles?: string[]
  allowed_overlays?: string[]
  allow_remote_control?: boolean
  allow_recording?: boolean
  allow_clipboard?: boolean
  allow_file_export?: boolean
  require_bound_device?: boolean
  require_private_lan_or_secure_remote?: boolean
  installed?: boolean
  streaming?: Record<string, unknown>
  notes?: string
}
type AppStreamSession = {
  session_id: string
  app_id: string
  display_name?: string
  overlay?: string
  status?: string
  stream_status?: string
  capture_state?: string
  stream_url?: string | null
  window_only?: boolean
  window_id?: number | null
  window_owner_name?: string
  window_name?: string
  last_error?: string | null
  app_running?: boolean
  created_at?: string
  started_at?: string
  stopped_at?: string
  streaming?: Record<string, unknown>
}
type ParentAttachment = {
  id: string
  name: string
  title: string
  type: string
  url: string
  previewUrl: string
  path: string
  mime: string
}
type ParentMessage = {
  messageId: string
  senderUserId: string
  senderEmail: string
  senderName: string
  senderRole: string
  recipientGuestId: string
  relatedStudentId: string
  relatedStudentName: string
  subject: string
  body: string
  messageType: string
  eventDate: string
  attachments: ParentAttachment[]
  createdAt: string
  readAt: string
  acknowledgedAt: string
  status: string
  raw: Record<string, unknown>
}
type ParentDeliveryNotification = {
  notificationId: string
  sourceMessageId: string
  eventType: string
  deliveryStatus: string
  inboxStatus: string
  pushStatus: string
  pushProviderResponse: string
  pushDisplayStatus: string
  pushLastAttemptedAt: string
  pushLastClickAt: string
  emailStatus: string
  emailLastAttemptedAt: string
  emailLastSentAt: string
  emailLastError: string
  targetUrl: string
  createdAt: string
}
type ParentEarlyDismissal = {
  earlyDismissalId: string
  studentId: string
  studentName: string
  pickupPersonName: string
  pickupRelationship: string
  reasonCode: string
  reasonNotes: string
  status: string
  createdAt: string
  expiresAt: string
  requiresPhoto: boolean
  pickupConfirmation: {
    pickupPersonName: string
    pickupRelationship: string
    pickupReason: string
    secureAttachmentId: string
    submittedAt: string
  }
}
type GuestReleaseRequest = {
  earlyDismissalId: string
  guestLinkId: string
  studentName: string
  pickupPersonName: string
  pickupRelationship: string
  reasonCode: string
  reasonNotes: string
  status: string
  guestSessionStatus: string
  expiresAt: string
  openedAt: string
  submittedAt: string
}
type ParentDocumentViewerState = {
  message: ParentMessage
  attachment: ParentAttachment
  url: string
  isPdf: boolean
  objectUrl?: string
  sourceUrl?: string
}
type ParentPortalPlatformGuide = {
  key:
    | 'android_phone'
    | 'android_tablet'
    | 'ios_iphone'
    | 'ios_ipad'
    | 'desktop_macos'
    | 'desktop_windows'
    | 'chromeos'
    | 'desktop_other'
  label: string
  installTitle: string
  installSteps: string[]
  alertTitle: string
  alertSteps: string[]
  note: string
  installButtonLabel: string
  requiresStandaloneForPush: boolean
}
type RuntimeDeviceClass =
  | 'iphone'
  | 'ipad'
  | 'android_phone'
  | 'android_tablet'
  | 'desktop_macos'
  | 'desktop_windows'
  | 'chromeos'
  | 'desktop_other'
type RuntimeBrowserClass = 'safari' | 'chrome' | 'edge' | 'firefox' | 'other'
type RuntimeDeviceProfile = {
  deviceClass: RuntimeDeviceClass
  browserClass: RuntimeBrowserClass
  isTouch: boolean
  isTablet: boolean
  isStandalone: boolean
  label: string
}
type InstitutionalTabletPlatform = {
  isIPad: boolean
  isTablet: boolean
  isStandalone: boolean
  deviceClass: RuntimeDeviceClass
  browserClass: RuntimeBrowserClass
  label: string
  installTitle: string
  installSteps: string[]
  runtimeStatus: string
}
type InstitutionalEarlyDismissalSummary = {
  earlyDismissalId: string
  studentName: string
  pickupPersonName: string
  status: string
  liveStatus: string
  createdAt: string
}
type InstitutionalBindingState = {
  deviceId: string
  hardwareId: string
  label: string
  userId: string
  institutionId: string
  lastSeen: string
}
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>
  userChoice?: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}
type DigitalMirrorPosition = {
  left: number
  top: number
}
type DemoPersona = {
  id: string
  label: string
  email: string
  role: string
  name: string
  productMode: 'school' | 'academy'
}
type ProductProfile = 'dual_demo' | 'school' | 'academy'
declare const __NEXUS_PRODUCT_PROFILE__: ProductProfile
declare const __NEXUS_ENABLE_SCHOOL__: boolean
declare const __NEXUS_ENABLE_ACADEMY__: boolean
declare const __NEXUS_ENABLE_DEMO_TOOLS__: boolean
type AcademyGrade = '3' | '4' | '5' | '6' | '7' | '8'
type AcademyGradeContext = {
  grade: AcademyGrade
  gradeId: string
  title: string
  schoolLevel: string
  route: string
  academyTrack: string
  enrollmentScope: string
  gradePackageId: string
  curriculumScope: string
  ragNamespace: string
  curriculumManifestPath: string
  cardDeckPath: string
  moduleManifestPath: string
  progressLedgerScope: string
  reviewQueueScope: string
  certificateWorkflowScope: string
  certificatePath: string
  status: string
}
type DemoNarration = {
  business?: string
  pedagogical?: string
  technical?: string
}
type DemoStep = {
  id: string
  title: string
  module?: string
  stageAct?: string
  stageHeadline?: string
  stageSubhead?: string
  stageProof?: string[]
  stageRisk?: string
  operatorCue?: string
  role?: string
  view?: string
  action?: string
  highlightSelector?: string
  highlightLabel?: string
  visibleContext?: string
  narration?: DemoNarration
  businessMessage?: string
  pedagogicalMessage?: string
  technicalMessage?: string
  desiredTone?: string
  maxNarrationSeconds?: number
  fallbackNarration?: string
  narrationFallback?: string
  narrative?: string
  repeatNarrative?: string
  expectedEvidence?: string[]
  qaChecks?: string[]
  relatedDocs?: string[]
  questionsItCanAnswer?: string[]
  avatarEmotion?: string
  avatarGestureHint?: string
  listenAfter?: boolean
  expectedCommands?: string[]
  moduleToOpen?: string
  selectorsToHighlight?: string[]
}
type PresenterAvatarState =
  | 'idle'
  | 'speaking'
  | 'listening'
  | 'thinking'
  | 'paused'
  | 'error'
  | 'fallback_audio_only'
type PresenterAvatarProvider = 'none' | 'local_video' | 'did' | 'simli' | 'future_provider'
type PresenterAvatarLanguage = 'en' | 'es'
type PresenterAvatarAdapter = {
  provider: PresenterAvatarProvider
  initAvatarSession: () => Promise<void>
  sendAudioOrText: (payload: { text?: string; audioBase64?: string; mime?: string }) => Promise<void>
  startSpeaking: () => void
  stopSpeaking: () => void
  startListening: () => void
  stopListening: () => void
  destroySession: () => void
  fallbackToVoiceOnly: (reason?: string) => void
}
type AvatarRecordingKind = 'consent' | 'source' | 'spanish'
type PresenterAudioDuration = {
  durationMs: number
}
type AvatarRecordingScript = {
  kind: AvatarRecordingKind
  label: string
  fileName: string
  durationHint: string
  body: string
}
type DemoScript = {
  schemaVersion?: string
  title: string
  subtitle?: string
  targetDurationMinutes?: string
  runtimeModel?: string
  sourceDocs?: string[]
  voice?: {
    enabled?: boolean
    engine?: string
    lang?: string
    rate?: number
    pitch?: number
    volume?: number
    voiceHint?: string
    neural?: boolean
    neuralProvider?: string
    neuralModel?: string
    neuralVoice?: string
    neuralInstructions?: string
    preferredVoices?: string[]
    avoidVoices?: string[]
  }
  steps: DemoStep[]
}
type PresenterModule = {
  id: string
  title: string
  role?: string
  view?: string
  module?: string
  summary?: string
  business?: string
  pedagogical?: string
  technical?: string
  highlightSelector?: string
  aliases?: string[]
  expectedEvidence?: string[]
  qaChecks?: string[]
  safePresenterActions?: string[]
  relatedDocs?: string[]
  questionsItCanAnswer?: string[]
  fallbackAnswer?: string
}
type PresenterModuleMap = {
  schemaVersion?: string
  title: string
  generatedFrom?: string[]
  institutionalIndexContract?: {
    role?: string
    summary?: string
    readOnlyEndpoints?: string[]
    rule?: string
  }
  modules: PresenterModule[]
}
type PresenterRagChunk = {
  id: string
  source?: string
  title: string
  roles?: string[]
  modules?: string[]
  keywords?: string[]
  summary?: string
  answerGuidance?: string
  returnBridge?: string
}
type PresenterRagPack = {
  schemaVersion?: string
  title: string
  purpose?: string
  runtimeRules?: string[]
  voiceGuidance?: {
    style?: string
    tone?: string
    avoid?: string
  }
  chunks: PresenterRagChunk[]
}

declare global {
  interface Window {
    nexusBridge?: {
      platform?: string
      captureScreenshot?: () => Promise<string | null>
      speakPresenterText?: (payload: Record<string, unknown>) => Promise<{ ok?: boolean; engine?: string; voice?: string; error?: string }>
      playPresenterAudio?: (payload: Record<string, unknown>) => Promise<{ ok?: boolean; engine?: string; voice?: string; model?: string; error?: string }>
      stopPresenterSpeech?: () => Promise<{ ok?: boolean; error?: string }>
      savePresenterAvatarRecording?: (payload: Record<string, unknown>) => Promise<{ ok?: boolean; path?: string; file_name?: string; bytes?: number; error?: string }>
      uiUpdateVersion?: () => Promise<Record<string, unknown>>
      checkUiUpdate?: () => Promise<Record<string, unknown>>
      installUiUpdate?: (manifest?: Record<string, unknown>) => Promise<Record<string, unknown>>
      restartAfterUiUpdate?: () => Promise<Record<string, unknown>>
      requestStudentProtectedExit?: () => Promise<{
        handled?: boolean
        ok?: boolean
        enforced?: boolean
        reason?: string
        error?: string
      }>
    }
  }
}

const SESSION_KEY = 'NEXUS_SESSION_LEASE'
const LAN_CANONICAL_ORIGIN = 'http://192.168.1.251:2801'
const DEMO_CONFIRM = 'NEXUS_ACADEMY_DEMO'
const ROOT_OWNER_EMAIL = 'olaldemarsaul@gmail.com'
const DEMO_SESSION_KEY = 'NEXUS_DEMO_SESSION'
const DEMO_OWNER_KEY = 'NEXUS_DEMO_CONTROL_OWNER'
const DIGITAL_MIRROR_AUDIT_KEY = 'NEXUS_DIGITAL_MIRROR_AUDIT'
const DIGITAL_MIRROR_POSITION_KEY = 'nexusDigitalMirrorPosition'
const DIGITAL_MIRROR_DRAG_THRESHOLD = 4
const TABLET_DEVICE_TYPE = 'ipad_tablet'
const TABLET_SHELL_MODE = 'tablet_institutional'
const TABLET_OVERLAY = 'institutional_tablet'
const APPSTREAM_OVERLAY_ID = 'elders_limmerloop'
const ACADEMY_OVERLAY_ID = 'academy'
const ACADEMY_LEGACY_OVERLAY_ID = 'academy_student'
const ACTIVE_PRODUCT_MODE_KEY = 'NEXUS_ACTIVE_PRODUCT_MODE'
const ACTIVE_DEMO_PERSONA_KEY = 'NEXUS_ACTIVE_DEMO_PERSONA'

/** District always uses the public Mirror gateway; it never takes the legacy
 * localhost:8700 WebSocket shortcut used by older panels. */
function districtMirrorGatewayOrigin(): string {
  if (window.location.protocol === 'file:') return 'http://127.0.0.1:2801'
  const location = window.location
  if (location.port === '2801') return location.origin
  return `${location.protocol}//${location.hostname}:2801`
}
const normalizeProductProfile = (value: unknown): ProductProfile => {
  const normalized = String(value || '').trim().toLowerCase()
  if (normalized === 'dual_demo' || normalized === 'school' || normalized === 'academy') return normalized
  throw new Error(`[nexus-browser] Unsupported product profile: ${normalized || '(empty)'}`)
}
const NEXUS_PRODUCT_PROFILE = normalizeProductProfile(__NEXUS_PRODUCT_PROFILE__)
const SCHOOL_SURFACE_ENABLED = __NEXUS_ENABLE_SCHOOL__
const ACADEMY_SURFACE_ENABLED = __NEXUS_ENABLE_ACADEMY__
const DEMO_TOOLS_ENABLED = __NEXUS_ENABLE_DEMO_TOOLS__
const SYNTHETIC_DISTRICT_PREVIEW_ENABLED = String((import.meta as any).env?.VITE_NEXUS_DISTRICT_SYNTHETIC_PREVIEW || '') === '1'
const ACADEMY_DEFAULT_GRADE: AcademyGrade = '5'
const ACADEMY_ALLOWED_GRADES: AcademyGrade[] = ACADEMY_SURFACE_ENABLED ? ['3', '4', '5', '6', '7', '8'] : []
const APPSTREAM_EARLY_ACCESS_EMAILS = [ROOT_OWNER_EMAIL]
const APPSTREAM_ALLOWED_ROLES = ['owner', 'root', 'developer', 'admin', 'principal_admin', 'subprincipal_admin', 'elder_limmerloop']
const APPSTREAM_MJPEG_BOUNDARY = 'nexusappstream'
const GUEST_TABLET_ROLES = ['guest', 'trusted_guest', 'invited', 'external_guest', 'external_collaborator', 'parent_guest', 'parent', 'padre']
const ACADEMY_TENANT_PROFILE = {
  tenant_type: 'academy',
  delivery_model: 'academy_online',
  student_portal_only: true,
  human_teacher_access: false,
  virtual_teachers_enabled: true,
  progress_ledger_enabled: true,
  review_queue_enabled: true,
  certificate_workflow_enabled: true,
  allowed_roles: ['student'],
  overlay: ACADEMY_OVERLAY_ID,
  kiosk_policy: 'academy_student_kiosk'
} as const
const ACADEMY_CONTEXT_STORAGE_KEYS = [
  'NEXUS_TENANT_TYPE',
  'NEXUS_DELIVERY_MODEL',
  'NEXUS_STUDENT_PORTAL_ONLY',
  'NEXUS_HUMAN_TEACHER_ACCESS',
  'NEXUS_VIRTUAL_TEACHERS_ENABLED',
  'NEXUS_ALLOWED_ROLES',
  'NEXUS_OVERLAY',
  'NEXUS_ACTIVE_OVERLAY',
  'NEXUS_KIOSK_POLICY',
  'NEXUS_ACADEMY_ENROLLMENT_TICKET',
  'NEXUS_ACADEMY_PROFILE',
  'NEXUS_ACADEMY_DEMO_PROFILE',
  'NEXUS_ACADEMY_MULTIGRADE_ENABLED',
  'NEXUS_ACADEMY_ALLOWED_GRADES',
  'NEXUS_ACADEMY_ASSIGNED_GRADE',
  'NEXUS_ACADEMY_DEFAULT_GRADE',
  'NEXUS_ACADEMY_ACTIVE_GRADE',
  'NEXUS_ACADEMY_TRACK',
  'NEXUS_ACADEMY_ENROLLMENT_SCOPE',
  'NEXUS_ACADEMY_GRADE_PACKAGE',
  'NEXUS_ACADEMY_CURRICULUM_SCOPE',
  'NEXUS_ACADEMY_RAG_NAMESPACE',
  'NEXUS_ACADEMY_CURRICULUM_MANIFEST',
  'NEXUS_ACADEMY_CARD_DECK',
  'NEXUS_ACADEMY_MODULE_MANIFEST',
  'NEXUS_ACADEMY_PROGRESS_LEDGER_SCOPE',
  'NEXUS_ACADEMY_REVIEW_QUEUE_SCOPE',
  'NEXUS_ACADEMY_CERTIFICATE_WORKFLOW_SCOPE',
  'NEXUS_ACADEMY_CERTIFICATE_PATH',
  'NEXUS_ACADEMY_LAST_AUDIT'
]
const EMPTY_INSTITUTIONAL_BINDING: InstitutionalBindingState = {
  deviceId: '',
  hardwareId: '',
  label: '',
  userId: '',
  institutionId: '',
  lastSeen: ''
}

/* @nexus-profile demo-personas:start */
const DEMO_PERSONAS: DemoPersona[] = DEMO_TOOLS_ENABLED ? [
  { id: 'developer', label: 'School Developer', email: 'olaldemarsaul@gmail.com', role: 'developer', name: 'Saul Developer', productMode: 'school' },
  { id: 'admin', label: 'School Admin', email: 'admin.demo@nqai', role: 'admin', name: 'Adriana Salas', productMode: 'school' },
  { id: 'teacher', label: 'School Teacher', email: 'teacher.demo@nqai', role: 'teacher', name: 'Carlos Espinosa', productMode: 'school' },
  { id: 'student', label: 'School Student', email: 'student.demo@nqai', role: 'student', name: 'Lucía Hernández', productMode: 'school' },
  { id: 'guest', label: 'School Guest', email: 'guest.demo@nqai', role: 'guest', name: 'Guest Parent', productMode: 'school' },
  { id: 'academy', label: 'Academy Student', email: 'student.demo@nqai', role: 'student', name: 'Lucía Hernández', productMode: 'academy' },
  ...(canOfferDistrictDemoPersona({
    productProfile: NEXUS_PRODUCT_PROFILE,
    demoToolsEnabled: DEMO_TOOLS_ENABLED,
    previewEnabled: SYNTHETIC_DISTRICT_PREVIEW_ENABLED,
  }) ? [{
    id: DISTRICT_DEMO_PERSONA_ID,
    label: 'District Administrator · Demo V1',
    email: '',
    role: 'district_admin',
    name: 'District Administrator',
    productMode: 'school' as const,
  }] : [])
] : []
/* @nexus-profile demo-personas:end */
const AVAILABLE_DEMO_PERSONAS = DEMO_PERSONAS.filter((persona) => (
  DEMO_TOOLS_ENABLED && NEXUS_PRODUCT_PROFILE === 'dual_demo' && (SCHOOL_SURFACE_ENABLED || persona.productMode !== 'school') && (ACADEMY_SURFACE_ENABLED || persona.productMode !== 'academy')
))
const EMPTY_DEMO_PERSONA: DemoPersona = {
  id: 'disabled',
  label: 'Demo disabled',
  email: '',
  role: 'guest',
  name: '',
  productMode: 'school'
}
const DEFAULT_DEMO_PERSONA = (AVAILABLE_DEMO_PERSONAS[0] || EMPTY_DEMO_PERSONA) as DemoPersona

function readStoredDemoPersonaId(): string {
  try {
    const stored = String(window.localStorage.getItem(ACTIVE_DEMO_PERSONA_KEY) || '').trim()
    // A stored presentation preference cannot reopen District.
    return stored !== DISTRICT_DEMO_PERSONA_ID
      && AVAILABLE_DEMO_PERSONAS.some((persona) => persona.id === stored)
      ? stored
      : DEFAULT_DEMO_PERSONA.id
  } catch {
    return DEFAULT_DEMO_PERSONA.id
  }
}

function isDistrictDemoPersona(persona: DemoPersona): boolean {
  return persona.id === DISTRICT_DEMO_PERSONA_ID && persona.role === 'district_admin'
}
const ACADEMY_GRADE_REGISTRY: AcademyGradeContext[] = ACADEMY_ALLOWED_GRADES.map((grade) => {
  const gradeId = `grade_${grade}`
  const schoolLevel = ['3', '4', '5'].includes(grade) ? 'elementary' : 'middle_school'
  return {
    grade,
    gradeId,
    title: `Grade ${grade}`,
    schoolLevel,
    route: `/academy/grades/${grade}`,
    academyTrack: `academy_grade_${grade}`,
    enrollmentScope: `academy_enrollment_grade_${grade}`,
    gradePackageId: `academy_grade_package_${grade}`,
    curriculumScope: `academy.grade_${grade}`,
    ragNamespace: `academy.grade_${grade}`,
    curriculumManifestPath: `nexus-academy://curriculum/grades/${gradeId}/curriculum_manifest.json`,
    cardDeckPath: `nexus-academy://curriculum/grades/${gradeId}/lesson_cards.json`,
    moduleManifestPath: `nexus-academy://curriculum/grades/${gradeId}/module_manifest.json`,
    progressLedgerScope: `academy_progress_grade_${grade}`,
    reviewQueueScope: `academy_review_grade_${grade}`,
    certificateWorkflowScope: `academy_certificate_grade_${grade}`,
    certificatePath: `nexus-academy://certificates/grades/${gradeId}/certificate_manifest.json`,
    status: 'pending_academy_manifest'
  }
})
const STRICT_BOOT_PROFILES = new Set<NexusBootProfile>(['production-headless', 'production', 'recovery'])
const DEFAULT_BROWSER_GATE_STATUS = (profile: NexusBootProfile): BrowserGateStatus => ({
  status: 'UNKNOWN',
  profile,
  checks: [
    { id: 'core', label: 'Core health', status: 'UNKNOWN' },
    { id: 'gateway', label: 'Gateway', status: 'UNKNOWN' },
    { id: 'auth', label: 'Auth', status: 'UNKNOWN' },
    { id: 'device_binding', label: 'Device binding', status: 'UNKNOWN' },
    { id: 'session', label: 'Session', status: 'UNKNOWN' }
  ]
})

const FALLBACK_DEMO_SCRIPT: DemoScript = fallbackDemoScript as DemoScript
const FALLBACK_PRESENTER_MODULE_MAP: PresenterModuleMap = fallbackPresenterModuleMap as PresenterModuleMap
const FALLBACK_PRESENTER_RAG_PACK: PresenterRagPack = fallbackPresenterRagPack as PresenterRagPack

function canonicalizeLanOrigin(): boolean {
  try {
    if (window.location.protocol !== 'http:') return false
    if (window.location.port && window.location.port !== '2801') return false
    const host = window.location.hostname.trim().toLowerCase()
    const isLanAlias =
      host === 'sauls-mac-mini.local' ||
      host === 'nexusbrowser.local' ||
      host === 'nexus.qcloud.local' ||
      host === 'nexusbrowser.lan' ||
      host.endsWith('.local')

    if (!isLanAlias) return false
    const target =
      LAN_CANONICAL_ORIGIN +
      window.location.pathname +
      window.location.search +
      window.location.hash
    if (target === window.location.href) return false
    window.location.replace(target)
    return true
  } catch {
    return false
  }
}

function hasSessionLease(): boolean {
  try {
    return !!window.localStorage.getItem(SESSION_KEY)
  } catch {
    return false
  }
}

function readAppCookie(name: string): string {
  try {
    const encoded = `${encodeURIComponent(String(name || ''))}=`
    const parts = String(document.cookie || '').split(';')
    for (const part of parts) {
      const trimmed = part.trim()
      if (trimmed.startsWith(encoded)) return decodeURIComponent(trimmed.slice(encoded.length))
    }
  } catch {
    // Ignore cookie read errors.
  }
  return ''
}

function writeAppCookie(name: string, value: string, maxAgeSeconds = 60 * 60 * 24 * 7): void {
  try {
    if (!name) return
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value || '')}; Path=/; Max-Age=${Math.max(0, Number(maxAgeSeconds) || 0)}; SameSite=Lax${secure}`
  } catch {
    // Ignore cookie write errors.
  }
}

function getEntryParams(): URLSearchParams {
  try {
    return new URLSearchParams(window.location.search)
  } catch {
    return new URLSearchParams()
  }
}

function isAcademyOverlayValue(value: string): boolean {
  const normalized = String(value || '').trim().toLowerCase()
  return normalized === ACADEMY_OVERLAY_ID || normalized === ACADEMY_LEGACY_OVERLAY_ID
}

function isAcademyDemoPersona(persona: DemoPersona): boolean {
  return persona.id === ACADEMY_OVERLAY_ID
}

function hasExplicitAcademyContext(params: URLSearchParams = getEntryParams()): boolean {
  try {
    const path = (window.location.pathname || '').trim().toLowerCase().replace(/\/+$/, '') || '/'
    const flow = (params.get('flow') || '').trim().toLowerCase()
    const source = (params.get('source') || '').trim().toLowerCase()
    const tenantType = (params.get('tenant_type') || params.get('tenant') || '').trim().toLowerCase()
    const deliveryModel = (params.get('delivery_model') || '').trim().toLowerCase()
    const overlay = (params.get('overlay') || '').trim().toLowerCase()
    return (
      path === '/academy' ||
      path.startsWith('/academy/') ||
      flow === 'academy' ||
      flow === 'academy_signup' ||
      flow === 'academy_signin' ||
      source === 'academy' ||
      source === 'nexus_academy' ||
      tenantType === ACADEMY_TENANT_PROFILE.tenant_type ||
      deliveryModel === ACADEMY_TENANT_PROFILE.delivery_model ||
      isAcademyOverlayValue(overlay) ||
      params.get('academy') === '1' ||
      !!getExplicitAcademyTicket(params)
    )
  } catch {
    return false
  }
}

function hasAcademyEntry(params: URLSearchParams = getEntryParams()): boolean {
  return ACADEMY_SURFACE_ENABLED && NEXUS_PRODUCT_PROFILE !== 'school' && hasExplicitAcademyContext(params)
}

function getExplicitAcademyTicket(params: URLSearchParams = getEntryParams()): string {
  try {
    return String(
      params.get('academy_enrollment_ticket') ||
        params.get('academy_ticket') ||
        params.get('academy_token') ||
        ''
    ).trim()
  } catch {
    return ''
  }
}

function getAcademyEnrollmentTicket(params: URLSearchParams = getEntryParams()): string {
  try {
    const explicitTicket = getExplicitAcademyTicket(params)
    if (explicitTicket) return explicitTicket
    if (!hasExplicitAcademyContext(params)) return ''
    return String(
      params.get('enrollment_ticket') ||
        ''
    ).trim()
  } catch {
    return ''
  }
}

function persistAcademyRuntimeContext(params: URLSearchParams = getEntryParams()): void {
  try {
    const ticket = getAcademyEnrollmentTicket(params)
    window.localStorage.setItem(ACTIVE_PRODUCT_MODE_KEY, 'academy')
    window.localStorage.setItem('NEXUS_TENANT_TYPE', ACADEMY_TENANT_PROFILE.tenant_type)
    window.localStorage.setItem('NEXUS_DELIVERY_MODEL', ACADEMY_TENANT_PROFILE.delivery_model)
    window.localStorage.setItem('NEXUS_STUDENT_PORTAL_ONLY', ACADEMY_TENANT_PROFILE.student_portal_only ? '1' : '0')
    window.localStorage.setItem('NEXUS_HUMAN_TEACHER_ACCESS', ACADEMY_TENANT_PROFILE.human_teacher_access ? '1' : '0')
    window.localStorage.setItem('NEXUS_VIRTUAL_TEACHERS_ENABLED', ACADEMY_TENANT_PROFILE.virtual_teachers_enabled ? '1' : '0')
    window.localStorage.setItem('NEXUS_ALLOWED_ROLES', JSON.stringify(ACADEMY_TENANT_PROFILE.allowed_roles))
    window.localStorage.setItem('NEXUS_OVERLAY', ACADEMY_TENANT_PROFILE.overlay)
    window.localStorage.setItem('NEXUS_ACTIVE_OVERLAY', ACADEMY_TENANT_PROFILE.overlay)
    window.localStorage.setItem('NEXUS_KIOSK_POLICY', ACADEMY_TENANT_PROFILE.kiosk_policy)
    window.localStorage.setItem('NEXUS_SESSION_ROLE', 'student')
    window.localStorage.setItem('NEXUS_LAST_ROLE', 'student')
    window.localStorage.removeItem('NEXUS_DEMO_CONTROL')
    window.localStorage.removeItem(DEMO_OWNER_KEY)
    window.localStorage.removeItem(DEMO_SESSION_KEY)
    if (ticket) {
      window.localStorage.setItem('NEXUS_ACADEMY_ENROLLMENT_TICKET', ticket)
      window.localStorage.setItem('NEXUS_PREBOUND_INVITE_TOKEN', ticket)
      window.sessionStorage.setItem('NEXUS_PREBOUND_INVITE_TOKEN', ticket)
      writeAppCookie('NEXUS_PREBOUND_INVITE_TOKEN_COOKIE', ticket)
    }
  } catch {
    // Academy routing metadata must not block the existing auth flow.
  }
}

function clearAcademyRuntimeContext(): void {
  try {
    const academyTicket = String(window.localStorage.getItem('NEXUS_ACADEMY_ENROLLMENT_TICKET') || '').trim()
    const localInvite = String(window.localStorage.getItem('NEXUS_PREBOUND_INVITE_TOKEN') || '').trim()
    const sessionInvite = String(window.sessionStorage.getItem('NEXUS_PREBOUND_INVITE_TOKEN') || '').trim()
    const cookieInvite = String(readAppCookie('NEXUS_PREBOUND_INVITE_TOKEN_COOKIE') || '').trim()
    ACADEMY_CONTEXT_STORAGE_KEYS.forEach((keyName) => window.localStorage.removeItem(keyName))
    if (academyTicket && localInvite === academyTicket) window.localStorage.removeItem('NEXUS_PREBOUND_INVITE_TOKEN')
    if (academyTicket && sessionInvite === academyTicket) window.sessionStorage.removeItem('NEXUS_PREBOUND_INVITE_TOKEN')
    if (academyTicket && cookieInvite === academyTicket) writeAppCookie('NEXUS_PREBOUND_INVITE_TOKEN_COOKIE', '', 0)
  } catch {
    // Ignore storage restrictions.
  }
}

function clearSchoolProductionTransientContext(): void {
  if (NEXUS_PRODUCT_PROFILE !== 'school') return
  try {
    const inheritedDemo = Boolean(
      window.localStorage.getItem('NEXUS_DEMO_CONTROL') ||
      window.localStorage.getItem(DEMO_SESSION_KEY) ||
      window.localStorage.getItem(DEMO_OWNER_KEY) ||
      window.localStorage.getItem(ACTIVE_DEMO_PERSONA_KEY)
    )
    if (inheritedDemo) {
      ;[
        SESSION_KEY,
        'NEXUS_SESSION_ID',
        'NEXUS_SESSION_EMAIL',
        'NEXUS_SESSION_NAME',
        'NEXUS_SESSION_ROLE',
        'NEXUS_LAST_EMAIL',
        'NEXUS_LAST_ROLE',
        'NEXUS_EMAIL',
        'NEXUS_USER_NAME'
      ].forEach((keyName) => window.localStorage.removeItem(keyName))
    }
    ;[
      'NEXUS_DEMO_CONTROL',
      DEMO_SESSION_KEY,
      DEMO_OWNER_KEY,
      ACTIVE_DEMO_PERSONA_KEY,
      'NEXUS_ACADEMY_DEMO_PROFILE'
    ].forEach((keyName) => window.localStorage.removeItem(keyName))
  } catch {
    // A School build still denies demo/Academy state in memory if storage is unavailable.
  }
}

function readAcademyRuntimePayload(): Record<string, unknown> {
  try {
    const tenantType = String(window.localStorage.getItem('NEXUS_TENANT_TYPE') || '').trim().toLowerCase()
    const overlay = String(window.localStorage.getItem('NEXUS_OVERLAY') || window.localStorage.getItem('NEXUS_ACTIVE_OVERLAY') || '').trim().toLowerCase()
    if (tenantType !== ACADEMY_TENANT_PROFILE.tenant_type && !isAcademyOverlayValue(overlay)) return {}
    const gradeContext = buildAcademyGradeContext(readAcademyActiveGrade())
    return {
      tenant_type: ACADEMY_TENANT_PROFILE.tenant_type,
      delivery_model: ACADEMY_TENANT_PROFILE.delivery_model,
      student_portal_only: true,
      human_teacher_access: false,
      virtual_teachers_enabled: true,
      progress_ledger_enabled: true,
      review_queue_enabled: true,
      certificate_workflow_enabled: true,
      allowed_roles: [...ACADEMY_TENANT_PROFILE.allowed_roles],
      overlay: ACADEMY_TENANT_PROFILE.overlay,
      active_overlay: ACADEMY_TENANT_PROFILE.overlay,
      kiosk_policy: ACADEMY_TENANT_PROFILE.kiosk_policy,
      active_grade: gradeContext.grade,
      academy_track: gradeContext.academyTrack,
      enrollment_scope: gradeContext.enrollmentScope,
      grade_package_id: gradeContext.gradePackageId,
      curriculum_scope: gradeContext.curriculumScope,
      rag_namespace: gradeContext.ragNamespace,
      curriculum_manifest_path: gradeContext.curriculumManifestPath,
      card_deck_path: gradeContext.cardDeckPath,
      work_item_filter: { grade: gradeContext.grade, package_id: gradeContext.gradePackageId },
      progress_ledger_scope: gradeContext.progressLedgerScope,
      review_queue_scope: gradeContext.reviewQueueScope,
      certificate_workflow_scope: gradeContext.certificateWorkflowScope,
      certificate_path: gradeContext.certificatePath,
      school_class_id: null,
      teacher_dashboard_scope: null,
      academy_enrollment_ticket: String(window.localStorage.getItem('NEXUS_ACADEMY_ENROLLMENT_TICKET') || '').trim()
    }
  } catch {
    return {}
  }
}

function bootstrapTenantRuntimeContext(): void {
  try {
    window.localStorage.setItem('NEXUS_PRODUCT_PROFILE', NEXUS_PRODUCT_PROFILE)
  } catch {
    // Product profile remains available from the build-time constant.
  }
  if (NEXUS_PRODUCT_PROFILE === 'school') {
    clearAcademyRuntimeContext()
    clearSchoolProductionTransientContext()
    try {
      window.localStorage.setItem(ACTIVE_PRODUCT_MODE_KEY, 'school')
      if (window.localStorage.getItem(ACTIVE_DEMO_PERSONA_KEY) === ACADEMY_OVERLAY_ID) {
        window.localStorage.removeItem(ACTIVE_DEMO_PERSONA_KEY)
      }
    } catch {
      // A school-only build still rejects Academy in memory when storage is restricted.
    }
    return
  }
  if (hasAcademyEntry(getEntryParams())) return
  try {
    const storedPersona = String(window.localStorage.getItem(ACTIVE_DEMO_PERSONA_KEY) || '').trim()
    const storedMode = String(window.localStorage.getItem(ACTIVE_PRODUCT_MODE_KEY) || '').trim().toLowerCase()
    const restoreAcademyDemo =
      NEXUS_PRODUCT_PROFILE === 'dual_demo' &&
      storedPersona === ACADEMY_OVERLAY_ID &&
      storedMode === 'academy'
    if (restoreAcademyDemo) {
      window.localStorage.setItem(ACTIVE_DEMO_PERSONA_KEY, ACADEMY_OVERLAY_ID)
      window.localStorage.setItem(ACTIVE_PRODUCT_MODE_KEY, 'academy')
      persistAcademyDemoProfileContext()
      return
    }
    clearAcademyRuntimeContext()
    window.localStorage.setItem(ACTIVE_PRODUCT_MODE_KEY, NEXUS_PRODUCT_PROFILE === 'academy' ? 'academy' : 'school')
  } catch {
    clearAcademyRuntimeContext()
  }
}

bootstrapTenantRuntimeContext()

function hasIncomingAuthFlow(): boolean {
  const params = getEntryParams()
  const flow = (params.get('flow') || '').trim().toLowerCase()
  const hasToken = !!(
    params.get('invite_token') ||
    params.get('pre_enrollment_token') ||
    params.get('token') ||
    params.get('enrollment_token') ||
    getAcademyEnrollmentTicket(params)
  )
  return hasToken || hasAcademyEntry(params) || ['signup', 'signin', 'school_first_access', 'parent_guest', 'trusted_guest'].includes(flow)
}

function hasParentGuestEntry(): boolean {
  const params = getEntryParams()
  const flow = (params.get('flow') || '').trim().toLowerCase()
  const role = (params.get('role') || '').trim().toLowerCase()
  const source = (params.get('source') || '').trim().toLowerCase()
  const platform = (params.get('platform') || '').trim().toLowerCase()
  const shellMode = (params.get('shell_mode') || '').trim().toLowerCase()
  const overlay = (params.get('overlay') || '').trim().toLowerCase()
  const deviceType = (params.get('device_type') || '').trim().toLowerCase()
  const path = (window.location.pathname || '').trim().toLowerCase()
  const hasInviteToken = !!(
    params.get('invite_token') ||
    params.get('pre_enrollment_token') ||
    params.get('token') ||
    params.get('enrollment_token') ||
    params.get('parent_guest_token') ||
    params.get('guest_token')
  )
  const parentLikeRole = ['parent_guest', 'parent', 'padre'].includes(role)
  const explicitTabletShell =
    platform === 'ipad' ||
    path === '/ipad' ||
    shellMode === TABLET_SHELL_MODE ||
    overlay === TABLET_OVERLAY ||
    deviceType === TABLET_DEVICE_TYPE
  const tabletGuestRole = GUEST_TABLET_ROLES.includes(role || 'guest')
  if (
    explicitTabletShell &&
    tabletGuestRole &&
    !path.startsWith('/parent-portal/') &&
    !path.startsWith('/portal/')
  ) {
    return false
  }
  const parentSourceHint =
    source.includes('parent_guest') ||
    source.includes('parent_portal') ||
    source.includes('parent')
  const parentInviteContext = hasInviteToken && (
    parentLikeRole ||
    parentSourceHint ||
    flow === 'parent_guest' ||
    params.get('parent_guest') === '1' ||
    !!params.get('parent_guest_token')
  )
  const trustedGuestTabletShell = flow === 'trusted_guest' && (
    path === '/ipad' ||
    shellMode === TABLET_SHELL_MODE ||
    overlay === TABLET_OVERLAY ||
    deviceType === TABLET_DEVICE_TYPE
  )
  const iPadParentPortalEntry =
    platform === 'ipad' &&
    (
      path === '/ipad' ||
      shellMode === TABLET_SHELL_MODE ||
      overlay === TABLET_OVERLAY ||
      deviceType === TABLET_DEVICE_TYPE
    ) &&
    (
      parentLikeRole &&
      (
        hasInviteToken ||
        ['parent_guest', 'school_first_access', 'signup', 'signin'].includes(flow)
      )
    )
  return (
    flow === 'parent_guest' ||
    (flow === 'trusted_guest' && !trustedGuestTabletShell) ||
    iPadParentPortalEntry ||
    path === '/parent-portal' ||
    path === '/parents' ||
    path === '/parent' ||
    path === '/terminal' ||
    path === '/remote-terminal' ||
    path.startsWith('/parent-portal/messages/') ||
    path.startsWith('/parent-portal/documents/') ||
    path.startsWith('/parent-portal/alerts/') ||
    path.startsWith('/parent-portal/early-dismiss/') ||
    path.startsWith('/portal/messages/') ||
    path.startsWith('/portal/documents/') ||
    path.startsWith('/portal/alerts/') ||
    path.startsWith('/portal/early-dismiss/') ||
    parentInviteContext ||
    params.get('parent_guest') === '1'
  )
}

function hasGuestReleaseEntry(): boolean {
  const path = (window.location.pathname || '').trim().toLowerCase()
  return path.startsWith('/guest-release/early-dismiss/')
}

function hasInstitutionalTabletEntry(): boolean {
  const params = getEntryParams()
  const flow = (params.get('flow') || '').trim().toLowerCase()
  const role = (params.get('role') || '').trim().toLowerCase()
  const platform = (params.get('platform') || '').trim().toLowerCase()
  const shellMode = (params.get('shell_mode') || '').trim().toLowerCase()
  const overlay = (params.get('overlay') || '').trim().toLowerCase()
  const deviceType = (params.get('device_type') || '').trim().toLowerCase()
  const path = (window.location.pathname || '').trim().toLowerCase().replace(/\/+$/, '') || '/'
  if (hasParentGuestEntry() || hasGuestReleaseEntry()) return false
  const normalizedRole = role || 'guest'
  const allowedTabletRole = isInstitutionalTabletRole(normalizedRole) || GUEST_TABLET_ROLES.includes(normalizedRole)
  if (!allowedTabletRole) return false
  const explicitTabletEntry =
    path === '/portal' ||
    path === '/ipad' ||
    flow === 'ipad' ||
    flow === 'ipad_pro' ||
    flow === 'tablet_access' ||
    flow === 'tablet_shell' ||
    flow === 'institutional_tablet' ||
    shellMode === TABLET_SHELL_MODE ||
    overlay === TABLET_OVERLAY ||
    deviceType === TABLET_DEVICE_TYPE ||
    platform === 'ipad'
  if (explicitTabletEntry) return true
  const runtime = detectRuntimeDeviceProfile()
  const iPadRoleHint = allowedTabletRole
  return (
    runtime.deviceClass === 'ipad' &&
    iPadRoleHint
  )
}

function getParentEntryEmail(): string {
  try {
    return String(getEntryParams().get('email') || '').trim().toLowerCase()
  } catch {
    return ''
  }
}

function shouldUseStoredParentSessionForEntry(): boolean {
  const session = readParentSession()
  const entryEmail = getParentEntryEmail()
  return !!session.lease && isParentGuestRole(session.role) && (!entryEmail || session.email === entryEmail)
}

function hasDemoEntry(): boolean {
  if (!DEMO_TOOLS_ENABLED || NEXUS_PRODUCT_PROFILE !== 'dual_demo') return false
  if (hasAcademyEntry()) return false
  if (isStrictNexusProfile(readNexusBootProfile())) return false
  try {
    if (window.localStorage.getItem('NEXUS_DEMO_CONTROL') === '1') return true
    if (
      window.localStorage.getItem(DEMO_SESSION_KEY) === '1' &&
      String(window.localStorage.getItem(DEMO_OWNER_KEY) || '').trim().toLowerCase() === ROOT_OWNER_EMAIL
    ) {
      window.localStorage.setItem('NEXUS_DEMO_CONTROL', '1')
      return true
    }
    const role = String(
      window.localStorage.getItem('NEXUS_SESSION_ROLE') ||
      window.localStorage.getItem('NEXUS_LAST_ROLE') ||
      ''
    ).trim().toLowerCase()
    if (isDemoControlRole(role)) return true
  } catch {
    // ignore localStorage access errors
  }
  return getEntryParams().get('demo') === '1'
}

function readNexusBootProfile(): NexusBootProfile {
  if (NEXUS_PRODUCT_PROFILE === 'school') return 'production'
  try {
    const params = new URLSearchParams(window.location.search)
    const raw = (
      params.get('profile') ||
      params.get('boot_profile') ||
      window.localStorage.getItem('NEXUS_BOOT_PROFILE') ||
      window.localStorage.getItem('NEXUS_BROWSER_PROFILE') ||
      'dev'
    ).trim().toLowerCase()
    if (raw === 'production-headless' || raw === 'production' || raw === 'recovery') return raw
  } catch {
    // Profile lookup is best effort; dev preserves current behavior.
  }
  return 'dev'
}

function isStrictNexusProfile(profile: NexusBootProfile): boolean {
  return STRICT_BOOT_PROFILES.has(profile)
}

function isDemoControlRole(role: string): boolean {
  return ['developer', 'admin', 'owner', 'root'].includes(String(role || '').trim().toLowerCase())
}

function hasDeviceBindingMarker(): boolean {
  try {
    return Boolean(
      window.localStorage.getItem('NEXUS_DEVICE_BINDING') ||
      window.localStorage.getItem('NEXUS_DEVICE_ID') ||
      window.localStorage.getItem('NEXUS_TRUSTED_DEVICE') ||
      window.localStorage.getItem('NEXUS_PARENT_GUEST_DEVICE_BINDING')
    )
  } catch {
    return false
  }
}

async function browserGateFetch(path: string): Promise<boolean> {
  const base = window.location.protocol === 'file:' ? 'http://127.0.0.1:2801' : ''
  try {
    const response = await fetch(`${base}${path}`, { cache: 'no-store' })
    return response.status >= 200 && response.status < 500
  } catch {
    return false
  }
}

async function validateBrowserGate(profile: NexusBootProfile): Promise<BrowserGateStatus> {
  if (!isStrictNexusProfile(profile)) {
    return { status: 'PASS', profile, checks: DEFAULT_BROWSER_GATE_STATUS(profile).checks, checkedAt: new Date().toISOString() }
  }
  const sessionOk = hasSessionLease()
  const deviceOk = hasDeviceBindingMarker()
  const [gatewayOk, authOk, qbridgeOk] = await Promise.all([
    browserGateFetch('/'),
    browserGateFetch('/auth/status'),
    browserGateFetch('/qbridge/status')
  ])
  const checks: BrowserGateCheck[] = [
    { id: 'core', label: 'Core health', status: gatewayOk && authOk && qbridgeOk ? 'PASS' : 'FAIL' },
    { id: 'gateway', label: 'Gateway', status: gatewayOk ? 'PASS' : 'FAIL' },
    { id: 'auth', label: 'Auth', status: authOk ? 'PASS' : 'FAIL' },
    { id: 'device_binding', label: 'Device binding', status: deviceOk ? 'PASS' : 'FAIL', detail: deviceOk ? 'marker_present' : 'missing_marker' },
    { id: 'session', label: 'Session', status: sessionOk ? 'PASS' : 'FAIL', detail: sessionOk ? 'lease_present' : 'missing_session_lease' }
  ]
  return {
    status: checks.every((check) => check.status === 'PASS') ? 'PASS' : 'FAIL',
    profile,
    checks,
    checkedAt: new Date().toISOString()
  }
}

function initialAuthFlow(): AuthFlow {
  const flow = (getEntryParams().get('flow') || '').trim().toLowerCase()
  return flow === 'signin' || flow === 'academy_signin'
    ? 'signin'
    : 'signup'
}

function shouldUseDirectLanApp(): boolean {
  return false
}

function isDemoModeEnabled(): boolean {
  if (!DEMO_TOOLS_ENABLED || NEXUS_PRODUCT_PROFILE !== 'dual_demo') return false
  if (hasAcademyEntry()) return false
  if (isStrictNexusProfile(readNexusBootProfile())) return false
  try {
    const params = new URLSearchParams(window.location.search)
    if (hasParentGuestEntry()) return false
    if (params.get('demo') === '1') return true
    if (window.localStorage.getItem('NEXUS_DEMO_CONTROL') === '1') return true
    if (
      window.localStorage.getItem(DEMO_SESSION_KEY) === '1' &&
      String(window.localStorage.getItem(DEMO_OWNER_KEY) || '').trim().toLowerCase() === ROOT_OWNER_EMAIL
    ) {
      window.localStorage.setItem('NEXUS_DEMO_CONTROL', '1')
      return true
    }
    const role = String(
      window.localStorage.getItem('NEXUS_SESSION_ROLE') ||
      window.localStorage.getItem('NEXUS_LAST_ROLE') ||
      ''
    ).trim().toLowerCase()
    if (isDemoControlRole(role)) {
      window.localStorage.setItem('NEXUS_DEMO_CONTROL', '1')
      window.localStorage.setItem(DEMO_OWNER_KEY, ROOT_OWNER_EMAIL)
      return true
    }
    const email = String(
      window.localStorage.getItem('NEXUS_SESSION_EMAIL') ||
      window.localStorage.getItem('NEXUS_LAST_EMAIL') ||
      ''
    ).trim().toLowerCase()
    if (email === ROOT_OWNER_EMAIL) {
      window.localStorage.setItem('NEXUS_DEMO_CONTROL', '1')
      window.localStorage.setItem(DEMO_OWNER_KEY, ROOT_OWNER_EMAIL)
      return true
    }
  } catch {
    // ignore
  }
  return false
}

function readParentSession(): ParentSession {
  try {
    const email = String(
      window.localStorage.getItem('NEXUS_SESSION_EMAIL') ||
        window.localStorage.getItem('NEXUS_LAST_EMAIL') ||
        window.localStorage.getItem('NEXUS_EMAIL') ||
        ''
    )
      .trim()
      .toLowerCase()
    const name = String(
      window.localStorage.getItem('NEXUS_SESSION_NAME') ||
        window.localStorage.getItem('NEXUS_USER_NAME') ||
        ''
    ).trim()
    const role = String(
      window.localStorage.getItem('NEXUS_SESSION_ROLE') ||
        window.localStorage.getItem('NEXUS_LAST_ROLE') ||
        ''
    )
      .trim()
      .toLowerCase()
    const lease = String(window.localStorage.getItem(SESSION_KEY) || '').trim()
    return { email, name, role, lease }
  } catch {
    return { email: '', name: '', role: '', lease: '' }
  }
}

function readActiveSessionIdentity(): ParentSession {
  try {
    const email = String(
      window.localStorage.getItem('NEXUS_SESSION_EMAIL') ||
        window.localStorage.getItem('NEXUS_LAST_EMAIL') ||
        window.localStorage.getItem('NEXUS_EMAIL') ||
        ''
    ).trim().toLowerCase()
    const name = String(
      window.localStorage.getItem('NEXUS_SESSION_NAME') ||
        window.localStorage.getItem('NEXUS_USER_NAME') ||
        ''
    ).trim()
    const role = String(
      window.localStorage.getItem('NEXUS_SESSION_ROLE') ||
        window.localStorage.getItem('NEXUS_LAST_ROLE') ||
        ''
    ).trim().toLowerCase()
    const lease = String(window.localStorage.getItem(SESSION_KEY) || '').trim()
    return { email, name, role: role || 'guest', lease }
  } catch {
    return { email: '', name: '', role: 'guest', lease: '' }
  }
}

function prettifyIdentityLabel(value: string): string {
  const local = String(value || '').split('@')[0] || ''
  return local
    .replace(/[._-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

function buildShellPresence(identity: ParentSession = readActiveSessionIdentity(), count = 1, labelOverride = '', tooltipOverride = ''): ShellPresence {
  const safeCount = Number.isFinite(count) ? Math.max(1, Math.round(count)) : 1
  const personalLabel =
    String(identity.name || '').trim() ||
    prettifyIdentityLabel(identity.email) ||
    String(identity.email || '').trim() ||
    '1 usuario'
  const label = String(labelOverride || (safeCount > 1 ? `${safeCount} usuarios` : personalLabel)).trim() || '1 usuario'
  const tooltip = String(
    tooltipOverride ||
      (identity.email && personalLabel !== identity.email ? `${personalLabel} · ${identity.email}` : label)
  ).trim()
  return {
    count: safeCount,
    label,
    tooltip: tooltip || label
  }
}

function normalizeAppStreamRole(role: string): string {
  const normalized = String(role || '').trim().toLowerCase().replace(/-/g, '_')
  if (normalized === 'principal' || normalized === 'administrator') return 'admin'
  if (normalized === 'elder') return 'elder_limmerloop'
  return normalized || 'guest'
}

function isAppStreamAccessAllowed(identity: ParentSession): boolean {
  const email = String(identity.email || '').trim().toLowerCase()
  const role = normalizeAppStreamRole(identity.role)
  return APPSTREAM_EARLY_ACCESS_EMAILS.includes(email) || APPSTREAM_ALLOWED_ROLES.includes(role)
}

function readAppStreamDeviceId(): string {
  try {
    const direct = String(
      window.localStorage.getItem('NEXUS_DEVICE_ID') ||
        window.localStorage.getItem('NEXUS_BOUND_DEVICE_ID') ||
        ''
    ).trim()
    if (direct) return direct.slice(0, 180)
    const rawBinding = String(
      window.localStorage.getItem('NEXUS_DEVICE_BINDING') ||
        window.localStorage.getItem('NEXUS_PARENT_GUEST_DEVICE_BINDING') ||
        ''
    ).trim()
    if (!rawBinding) return ''
    const parsed = JSON.parse(rawBinding) as Record<string, unknown>
    return String(parsed.device_id || parsed.deviceId || parsed.device_id_hash || '').trim().slice(0, 180)
  } catch {
    return ''
  }
}

function readAppStreamSessionId(): string {
  try {
    let sid = String(window.localStorage.getItem('NEXUS_SESSION_ID') || '').trim()
    if (!sid) {
      sid = `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
      window.localStorage.setItem('NEXUS_SESSION_ID', sid)
    }
    return sid
  } catch {
    return `sess_${Date.now().toString(36)}`
  }
}

function appStreamHeaders(identity: ParentSession, accept = 'application/json'): Record<string, string> {
  const deviceId = readAppStreamDeviceId()
  const headers: Record<string, string> = {
    Accept: accept,
    'Content-Type': 'application/json',
    'X-Nexus-Client': 'NexusBrowser',
    'X-Nexus-Overlay': APPSTREAM_OVERLAY_ID,
    'x-session-id': readAppStreamSessionId(),
    'x-user-role': normalizeAppStreamRole(identity.role),
    'x-device-trusted': deviceId ? '1' : '0'
  }
  if (accept !== 'application/json') delete headers['Content-Type']
  if (identity.email) headers['x-user-email'] = identity.email
  if (deviceId) headers['x-device-id'] = deviceId
  if (identity.lease) headers['x-session-lease'] = identity.lease
  return headers
}

function deriveAppStreamStatus(session?: AppStreamSession | null, fallback = 'ready'): string {
  if (!session) return fallback
  const captureState = String(session.capture_state || '').trim()
  if (captureState && captureState !== 'idle') return captureState
  const streamStatus = String(session.stream_status || '').trim()
  if (streamStatus && streamStatus !== 'created') return streamStatus
  return String(session.status || fallback)
}

function formatAppStreamStatus(status: string): string {
  switch (status) {
    case 'creating_session':
      return 'creating session'
    case 'loading_catalog':
      return 'loading catalog'
    case 'app_launching':
    case 'launching_app':
      return 'launching app'
    case 'waiting_for_window':
      return 'waiting for window'
    case 'permission_required':
      return 'permission required'
    case 'connecting_stream':
      return 'connecting stream'
    case 'streaming':
      return 'streaming'
    case 'stream_ended':
    case 'stopped':
      return 'stream ended'
    case 'not_allowed':
      return 'not allowed'
    case 'unavailable':
      return 'unavailable'
    case 'closing':
      return 'closing stream'
    case 'ready':
      return 'ready'
    default:
      return status || 'ready'
  }
}

function describeAppStreamCanvasState(status: string): string {
  switch (status) {
    case 'permission_required':
      return 'Screen Recording permission required for Nexus AppStream Agent.'
    case 'waiting_for_window':
      return 'waiting for window'
    case 'creating_session':
      return 'creating session'
    case 'app_launching':
    case 'launching_app':
      return 'launching app'
    case 'connecting_stream':
      return 'connecting stream'
    case 'streaming':
      return 'streaming'
    case 'stream_ended':
    case 'stopped':
      return 'stream ended'
    case 'not_allowed':
      return 'not allowed'
    case 'unavailable':
      return 'stream unavailable'
    default:
      return status || 'ready'
  }
}

function appendAppStreamBytes(left: Uint8Array, right: Uint8Array): Uint8Array {
  if (!left.length) return right
  const combined = new Uint8Array(left.length + right.length)
  combined.set(left, 0)
  combined.set(right, left.length)
  return combined
}

function findAppStreamBytes(buffer: Uint8Array, needle: Uint8Array, fromIndex = 0): number {
  if (!needle.length || buffer.length < needle.length) return -1
  const lastStart = buffer.length - needle.length
  for (let index = Math.max(0, fromIndex); index <= lastStart; index += 1) {
    let matched = true
    for (let offset = 0; offset < needle.length; offset += 1) {
      if (buffer[index + offset] !== needle[offset]) {
        matched = false
        break
      }
    }
    if (matched) return index
  }
  return -1
}

function getCameraPreviewPolicy(roleValue: string): { allowed: boolean; visible: boolean; reason: string } {
  const role = String(roleValue || '').trim().toLowerCase()
  const supportedRoles = ['owner', 'root', 'developer', 'admin', 'principal', 'teacher', 'student', 'guest', 'elder', 'parent_guest', 'external_guest']
  return { allowed: true, visible: true, reason: supportedRoles.includes(role) ? `${role || 'guest'}_digital_mirror_allowed` : 'digital_mirror_allowed' }
}

function appendDigitalMirrorAudit(action: string, extra: Record<string, unknown> = {}): void {
  try {
    const identity = readActiveSessionIdentity()
    const currentRaw = window.localStorage.getItem(DIGITAL_MIRROR_AUDIT_KEY) || '[]'
    const current = JSON.parse(currentRaw)
    const events = Array.isArray(current) ? current : []
    events.push({
      action,
      role: identity.role,
      email: identity.email,
      localOnly: true,
      transmitted: false,
      createdAt: new Date().toISOString(),
      ...extra
    })
    window.localStorage.setItem(DIGITAL_MIRROR_AUDIT_KEY, JSON.stringify(events.slice(-200)))
  } catch {
    // Local audit must never block camera shutdown.
  }
}

function isParentGuestRole(role: string): boolean {
  const normalized = String(role || '').trim().toLowerCase()
  // Keep Parent Portal strictly for parent/guardian-style access.
  // Guest overlays (guest/invited/external_guest) should stay in institutional UI.
  return ['parent_guest', 'parent'].includes(normalized)
}

function isInstitutionalTabletRole(role: string): boolean {
  const normalized = String(role || '').trim().toLowerCase()
  return ['subprincipal_admin', 'principal_admin', 'principal', 'admin', 'teacher', 'elder_limmerloop'].includes(normalized)
}

function isInstitutionalTabletGuestRole(role: string): boolean {
  const normalized = String(role || '').trim().toLowerCase()
  return GUEST_TABLET_ROLES.includes(normalized)
}

function resolveInstitutionalTabletRole(params: URLSearchParams = getEntryParams(), roleHint = ''): string {
  const hintedRaw = String(roleHint || '').trim()
  const requestedRaw = String(params.get('role') || '').trim()
  const hinted = hintedRaw ? normalizeAppStreamRole(hintedRaw) : ''
  const requested = requestedRaw ? normalizeAppStreamRole(requestedRaw) : ''
  if (hinted && hinted !== 'guest') return hinted
  return requested || hinted || 'guest'
}

function overlayForInstitutionalTabletRole(role: string): string {
  return normalizeAppStreamRole(role) === 'elder_limmerloop' ? APPSTREAM_OVERLAY_ID : TABLET_OVERLAY
}

function detectRuntimeDeviceProfile(): RuntimeDeviceProfile {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  const platform = typeof navigator !== 'undefined' ? navigator.platform || '' : ''
  const maxTouchPoints = typeof navigator !== 'undefined' ? navigator.maxTouchPoints || 0 : 0
  const isTouch = maxTouchPoints > 1
  const isStandalone = typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || Boolean((navigator as any).standalone))
  const isIpad = /iPad/i.test(userAgent) || (platform === 'MacIntel' && isTouch)
  const isIphone = /iPhone|iPod/i.test(userAgent)
  const isAndroid = /Android/i.test(userAgent)
  const isChromeOs = /CrOS/i.test(userAgent)
  const isWindows = /Windows NT/i.test(userAgent)
  const isMacDesktop = !isIpad && /Macintosh|MacIntel/i.test(userAgent)
  const isAndroidTablet = isAndroid && !/Mobile/i.test(userAgent)
  const isAndroidPhone = isAndroid && /Mobile/i.test(userAgent)

  const browserClass: RuntimeBrowserClass =
    /CriOS|Chrome/i.test(userAgent) && !/EdgiOS|Edg\//i.test(userAgent)
      ? 'chrome'
      : /EdgiOS|Edg\//i.test(userAgent)
        ? 'edge'
        : /FxiOS|Firefox/i.test(userAgent)
          ? 'firefox'
          : /Safari/i.test(userAgent) && !/CriOS|Chrome|EdgiOS|Edg\/|FxiOS|Firefox/i.test(userAgent)
            ? 'safari'
            : 'other'

  let deviceClass: RuntimeDeviceClass = 'desktop_other'
  if (isIpad) deviceClass = 'ipad'
  else if (isIphone) deviceClass = 'iphone'
  else if (isAndroidTablet) deviceClass = 'android_tablet'
  else if (isAndroidPhone) deviceClass = 'android_phone'
  else if (isChromeOs) deviceClass = 'chromeos'
  else if (isWindows) deviceClass = 'desktop_windows'
  else if (isMacDesktop) deviceClass = 'desktop_macos'

  const isTablet = deviceClass === 'ipad' || deviceClass === 'android_tablet'
  const label = (() => {
    switch (deviceClass) {
      case 'ipad':
        return 'iPadOS'
      case 'iphone':
        return 'iPhone iOS'
      case 'android_tablet':
        return 'Android tablet'
      case 'android_phone':
        return 'Android phone'
      case 'desktop_macos':
        return 'Desktop macOS'
      case 'desktop_windows':
        return 'Windows desktop'
      case 'chromeos':
        return 'ChromeOS'
      default:
        return 'Desktop browser'
    }
  })()

  return {
    deviceClass,
    browserClass,
    isTouch,
    isTablet,
    isStandalone,
    label
  }
}

function hasInstitutionalTabletSession(): boolean {
  const session = readParentSession()
  const role = String(session.role || '').trim().toLowerCase()
  return !!session.lease && (isInstitutionalTabletRole(role) || isInstitutionalTabletGuestRole(role) || isParentGuestRole(role))
}

function shouldHoldIpadTabletRuntimeForInstall(): boolean {
  if (!hasInstitutionalTabletEntry()) return false
  const runtime = detectRuntimeDeviceProfile()
  return runtime.deviceClass === 'ipad' && !runtime.isStandalone
}

function isInstitutionalTabletEnvironment(): boolean {
  const runtime = detectRuntimeDeviceProfile()
  const isIPad = runtime.deviceClass === 'ipad'
  const isTabletSized = typeof window !== 'undefined' && window.matchMedia('(min-width: 700px) and (max-width: 1400px)').matches
  const portalEntry = (() => {
    try {
      return (window.location.pathname || '').toLowerCase().replace(/\/+$/, '') === '/portal'
    } catch {
      return false
    }
  })()
  return isIPad || (portalEntry && isTabletSized)
}

function institutionalTabletPlatform(): InstitutionalTabletPlatform {
  const runtime = detectRuntimeDeviceProfile()
  const isIPad = runtime.deviceClass === 'ipad'
  const isTablet = runtime.isTablet || (typeof window !== 'undefined' && window.matchMedia('(min-width: 700px) and (max-width: 1400px)').matches)
  const isStandalone = runtime.isStandalone
  return {
    isIPad,
    isTablet,
    isStandalone,
    deviceClass: runtime.deviceClass,
    browserClass: runtime.browserClass,
    label: `${runtime.label}${runtime.browserClass === 'safari' ? ' Safari' : runtime.browserClass === 'chrome' ? ' Chrome' : runtime.browserClass === 'edge' ? ' Edge' : runtime.browserClass === 'firefox' ? ' Firefox' : ''}`.trim(),
    installTitle: isIPad ? 'Install Nexus to iPad Home Screen' : 'Install Nexus mobile shell',
    installSteps: isIPad
      ? [
          'Open this page in Safari.',
          'Tap the Share button.',
          'Tap Add to Home Screen.',
          'Tap Add.',
          'Open Nexus iPad from the new Home Screen icon.',
          'Sign in from the installed iPad workspace.'
        ]
      : [
          'Open the portal link.',
          'Use the browser install option when available.',
          'Open Nexus from the installed icon.'
        ],
    runtimeStatus: isIPad
      ? 'No native app download is required. Home Screen PWA is the institutional iPad runtime for this phase.'
      : 'This device uses the controlled Nexus mobile shell for V1.'
  }
}

function applyPwaContextMetadata(mode: 'ipad' | 'parent'): void {
  try {
    const manifestHref = mode === 'ipad' ? '/ipad.webmanifest' : '/manifest.webmanifest'
    let manifest = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
    if (!manifest) {
      manifest = document.createElement('link')
      manifest.rel = 'manifest'
      document.head.appendChild(manifest)
    }
    manifest.href = manifestHref

    const setMeta = (name: string, content: string) => {
      let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
      if (!meta) {
        meta = document.createElement('meta')
        meta.name = name
        document.head.appendChild(meta)
      }
      meta.content = content
    }

    if (mode === 'ipad') {
      document.title = 'Nexus iPad Access'
      setMeta('apple-mobile-web-app-title', 'Nexus iPad')
      setMeta('description', 'Nexus institutional iPad access for authorized school users.')
      setMeta('theme-color', '#176b54')
    } else {
      setMeta('apple-mobile-web-app-title', 'Parent Portal')
    }
  } catch {
    // Metadata changes are best-effort and must not block access.
  }
}

function persistInstitutionalTabletContext(params: URLSearchParams = getEntryParams(), roleHint = ''): void {
  try {
    const platform = (params.get('platform') || '').trim().toLowerCase()
    const role = resolveInstitutionalTabletRole(params, roleHint)
    const overlay = overlayForInstitutionalTabletRole(role)
    if (platform && !window.localStorage.getItem('NEXUS_CLIENT_PLATFORM')) {
      window.localStorage.setItem('NEXUS_CLIENT_PLATFORM', platform)
    }
    if (platform === 'ipad' || role) {
      window.localStorage.setItem('NEXUS_CLIENT_PLATFORM', 'ipad')
      window.localStorage.setItem('NEXUS_DEVICE_TYPE', TABLET_DEVICE_TYPE)
      window.localStorage.setItem('NEXUS_SHELL_MODE', TABLET_SHELL_MODE)
      window.localStorage.setItem('NEXUS_OVERLAY', overlay)
      window.localStorage.setItem('NEXUS_GUEST_PWA_AUTHORIZED', '1')
      if (role === 'elder_limmerloop') {
        window.localStorage.setItem('NEXUS_SESSION_ROLE', role)
        window.localStorage.setItem('NEXUS_LAST_ROLE', role)
      }
    }
  } catch {
    // Tablet shell metadata is best effort only.
  }
}

function getSessionId(): string {
  try {
    let sid = String(window.localStorage.getItem('NEXUS_SESSION_ID') || '').trim()
    if (!sid) {
      sid = `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`
      window.localStorage.setItem('NEXUS_SESSION_ID', sid)
    }
    return sid
  } catch {
    return `sess_${Date.now().toString(36)}`
  }
}

function parentPortalHeaders(session: ParentSession): HeadersInit {
  const runtime = detectRuntimeDeviceProfile()
  return {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'x-user-email': session.email,
    'x-user-role': session.role || 'parent_guest',
    'x-developer-mode': '0',
    'x-device-trusted': session.lease ? '1' : '0',
    'x-device-platform': runtime.deviceClass,
    'x-device-browser': runtime.browserClass,
    'x-pwa-installed': runtime.isStandalone ? '1' : '0',
    'x-guest-pwa-authorized': hasInstitutionalTabletEntry() && session.lease ? '1' : '0',
    'x-session-id': getSessionId()
  }
}

function readStringValue(value: unknown): string {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim()
}

function readCoreSessionRouteObject(): Record<string, unknown> {
  try {
    const raw = window.localStorage.getItem('NEXUS_CORE_SESSION_ROUTE') || '{}'
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {}
  } catch {
    return {}
  }
}

function persistInstitutionalSessionMetadata(payload: Record<string, unknown>): void {
  try {
    const route = payload.route && typeof payload.route === 'object'
      ? (payload.route as Record<string, unknown>)
      : {}
    const routeUser = route.user && typeof route.user === 'object'
      ? (route.user as Record<string, unknown>)
      : {}
    const user = payload.user && typeof payload.user === 'object'
      ? (payload.user as Record<string, unknown>)
      : {}

    const userId = readStringValue(
      payload.user_id ||
      payload.userId ||
      user.user_id ||
      user.userId ||
      user.id ||
      routeUser.user_id ||
      routeUser.userId ||
      routeUser.id
    )
    const institutionId = readStringValue(
      payload.institution_id ||
      payload.institutionId ||
      payload.realm_id ||
      user.institution_id ||
      user.institutionId ||
      user.realm_id ||
      user.school_id ||
      routeUser.institution_id ||
      routeUser.institutionId ||
      routeUser.realm_id ||
      routeUser.school_id
    )
    const lastSeen = readStringValue(
      payload.last_seen ||
      payload.lastSeen ||
      user.last_seen ||
      user.lastSeen ||
      routeUser.last_seen ||
      routeUser.lastSeen
    )

    if (userId) window.localStorage.setItem('NEXUS_SESSION_USER_ID', userId)
    if (institutionId) window.localStorage.setItem('NEXUS_SESSION_INSTITUTION_ID', institutionId)
    if (lastSeen) window.localStorage.setItem('NEXUS_SESSION_LAST_SEEN', lastSeen)
  } catch {
    // Metadata persistence is best effort and should not block auth.
  }
}

function readInstitutionalSessionMetadata(): Pick<InstitutionalBindingState, 'userId' | 'institutionId' | 'lastSeen'> {
  try {
    const route = readCoreSessionRouteObject()
    const routeUser = route.user && typeof route.user === 'object'
      ? (route.user as Record<string, unknown>)
      : {}
    const userId = readStringValue(
      window.localStorage.getItem('NEXUS_SESSION_USER_ID') ||
      routeUser.user_id ||
      routeUser.userId ||
      routeUser.id
    )
    const institutionId = readStringValue(
      window.localStorage.getItem('NEXUS_SESSION_INSTITUTION_ID') ||
      routeUser.institution_id ||
      routeUser.institutionId ||
      routeUser.realm_id ||
      routeUser.school_id
    )
    const lastSeen = readStringValue(
      window.localStorage.getItem('NEXUS_SESSION_LAST_SEEN') ||
      routeUser.last_seen ||
      routeUser.lastSeen
    )
    return { userId, institutionId, lastSeen }
  } catch {
    return { userId: '', institutionId: '', lastSeen: '' }
  }
}

function parentBytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => Number(b).toString(16).padStart(2, '0'))
    .join('')
}

function parentReadCookie(name: string): string {
  try {
    const encoded = encodeURIComponent(String(name || '')) + '='
    const parts = String(document.cookie || '').split(';')
    for (const part of parts) {
      const trimmed = part.trim()
      if (trimmed.startsWith(encoded)) return decodeURIComponent(trimmed.slice(encoded.length))
    }
  } catch {
    // Ignore cookie access restrictions.
  }
  return ''
}

function parentWriteCookie(name: string, value: string, maxAgeSeconds = 60 * 60 * 24 * 365): void {
  try {
    if (!name) return
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value || '')}; Path=/; Max-Age=${Math.max(0, Number(maxAgeSeconds) || 0)}; SameSite=Lax${secure}`
  } catch {
    // Ignore cookie write restrictions.
  }
}

async function parentSha256Hex(value: string): Promise<string> {
  const encoder = new TextEncoder()
  const digest = await window.crypto.subtle.digest('SHA-256', encoder.encode(value))
  return parentBytesToHex(new Uint8Array(digest))
}

async function getParentBrowserBinding(scopeEmail = '') {
  const scope = scopeEmail.trim().toLowerCase() || 'anonymous'
  const scopeHash = await parentSha256Hex(`parent-guest:${scope}`)
  const scopedId = scopeHash.slice(0, 16)
  const recordKey = `NEXUS_PARENT_BROWSER_BIND_RECORD_V1:${scopedId}`
  const secretKey = `NEXUS_PARENT_BROWSER_BIND_SECRET_V1:${scopedId}`
  const cookieKey = `${secretKey}_COOKIE`
  try {
    const cached = window.localStorage.getItem(recordKey)
    if (cached) {
      const parsed = JSON.parse(cached)
      if (parsed?.device_id && parsed?.device_id_hash) return parsed
    }
  } catch {
    // Recreate below.
  }

  let secret = ''
  secret = parentReadCookie(cookieKey)
  if (secret && secret.length >= 24) {
    try {
      window.localStorage.setItem(secretKey, secret)
    } catch {
      // ignore
    }
  }
  try {
    if (!secret) secret = String(window.localStorage.getItem(secretKey) || '')
  } catch {
    // ignore
  }
  if (secret) parentWriteCookie(cookieKey, secret)
  if (!secret) {
    const bytes = new Uint8Array(24)
    window.crypto.getRandomValues(bytes)
    secret = parentBytesToHex(bytes)
    try {
      window.localStorage.setItem(secretKey, secret)
    } catch {
      // Best effort; the binding can still be used for this session.
    }
    parentWriteCookie(cookieKey, secret)
  }

  const origin = window.location.origin || 'parent-guest-browser'
  const ua = window.navigator.userAgent || 'unknown-browser'
  const bindHash = await parentSha256Hex(['nqc-parent-guest-browser-v1', scope, origin, ua, secret].join('|'))
  const binding = {
    crypto_version: 'nqc-encrypt-secrets-v2',
    device_id: `dev-${bindHash.slice(0, 16)}`,
    device_id_hash: bindHash,
    fingerprint_hash: bindHash,
    hardware_id: bindHash,
    device_public_key_pem: `nqc-parent-guest-browser:${bindHash}`
  }
  try {
    window.localStorage.setItem(recordKey, JSON.stringify(binding))
  } catch {
    // ignore
  }
  return binding
}

function storeParentGuestAuth(result: Record<string, unknown>, email: string, name: string) {
  const lease = readStringValue(result.session_lease)
  const resolvedName = readStringValue(result.user_name) || name
  window.localStorage.setItem('NEXUS_EMAIL', email)
  window.localStorage.setItem('NEXUS_LAST_EMAIL', email)
  window.localStorage.setItem('NEXUS_SESSION_EMAIL', email)
  window.localStorage.setItem('NEXUS_USER_NAME', resolvedName)
  window.localStorage.setItem('NEXUS_SESSION_NAME', resolvedName)
  window.localStorage.setItem('NEXUS_SESSION_ROLE', 'parent_guest')
  window.localStorage.setItem('NEXUS_LAST_ROLE', 'parent_guest')
  window.localStorage.removeItem('NEXUS_DEMO_CONTROL')
  window.localStorage.removeItem(DEMO_OWNER_KEY)
  window.localStorage.removeItem(DEMO_SESSION_KEY)
  if (lease) window.localStorage.setItem(SESSION_KEY, lease)
  try {
    const url = new URL(window.location.href)
    url.searchParams.delete('invite_token')
    url.searchParams.delete('parent_guest_token')
    url.searchParams.delete('guest_token')
    url.searchParams.delete('parent_guest')
    url.searchParams.delete('email')
    if (url.pathname === '/') url.searchParams.set('flow', 'parent_guest')
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`)
  } catch {
    // URL cleanup is convenience only; stored session remains the source.
  }
}

function isBackendParentGuestRole(result: Record<string, unknown>): boolean {
  const role = readStringValue(result.user_role || result.role).toLowerCase()
  return isParentGuestRole(role) || [
    'guest',
    'trusted_guest',
    'invited',
    'external_guest',
    'external_collaborator'
  ].includes(role)
}

function normalizeParentAttachment(value: unknown, index: number): ParentAttachment {
  const item = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  const name = readStringValue(item.name || item.title || item.filename || item.fileName || `Document ${index + 1}`)
  const url = readStringValue(item.url || item.href || item.downloadUrl || item.download_url || item.viewerUrl || item.viewer_url)
  const previewUrl = readStringValue(
    item.previewUrl ||
      item.preview_url ||
      item.thumbnailUrl ||
      item.thumbnail_url ||
      item.imagePreviewUrl ||
      item.image_preview_url
  )
  const path = readStringValue(item.path || item.file_path || item.storagePath || item.storage_path)
  const mime = readStringValue(item.mime || item.mimeType || item.mime_type || item.contentType || item.content_type)
  const type = readStringValue(item.type || (mime.includes('pdf') || /\.pdf($|\?)/i.test(name) ? 'pdf' : 'document'))
  return {
    id: readStringValue(item.id || item.attachmentId || item.attachment_id || `${name}-${index}`),
    name,
    title: readStringValue(item.title || name),
    type,
    url,
    previewUrl,
    path,
    mime
  }
}

function normalizeParentMessage(value: unknown): ParentMessage {
  const item = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  const attachmentsRaw = item.attachments
  const attachments = Array.isArray(attachmentsRaw)
    ? attachmentsRaw.map((attachment, index) => normalizeParentAttachment(attachment, index))
    : []
  const messageId = readStringValue(item.messageId || item.message_id || item.id || `message-${Date.now()}`)
  return {
    messageId,
    senderUserId: readStringValue(item.senderUserId || item.sender_user_id || item.sender_id),
    senderEmail: readStringValue(item.senderEmail || item.sender_email || item.from_email),
    senderName: readStringValue(item.senderName || item.sender_name || item.from_name || 'School'),
    senderRole: readStringValue(item.senderRole || item.sender_role || item.from_role || 'teacher'),
    recipientGuestId: readStringValue(item.recipientGuestId || item.recipient_guest_id || item.recipient_id),
    relatedStudentId: readStringValue(item.relatedStudentId || item.related_student_id || item.student_id),
    relatedStudentName: readStringValue(item.relatedStudentName || item.related_student_name || item.student_name),
    subject: readStringValue(item.subject || 'School message'),
    body: readStringValue(item.body || item.message || item.text),
    messageType: normalizeParentMessageType(readStringValue(item.messageType || item.message_type || item.type || item.kind || item.category)),
    eventDate: readStringValue(item.eventDate || item.event_date),
    attachments,
    createdAt: readStringValue(item.createdAt || item.created_at || item.sentAt || item.sent_at),
    readAt: readStringValue(item.readAt || item.read_at),
    acknowledgedAt: readStringValue(item.acknowledgedAt || item.acknowledged_at),
    status: readStringValue(item.status || 'sent'),
    raw: item
  }
}

function normalizeParentDeliveryNotification(value: unknown): ParentDeliveryNotification {
  const item = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  return {
    notificationId: readStringValue(item.notification_id || item.notificationId || item.id),
    sourceMessageId: readStringValue(item.source_message_id || item.sourceMessageId || item.message_id || item.messageId),
    eventType: readStringValue(item.event_type || item.eventType || 'school_message_created'),
    deliveryStatus: readStringValue(item.delivery_status || item.deliveryStatus || 'created'),
    inboxStatus: readStringValue(item.inbox_status || item.inboxStatus || ''),
    pushStatus: readStringValue(item.push_status || item.pushStatus || ''),
    pushProviderResponse: readStringValue(item.push_provider_response || item.pushProviderResponse || ''),
    pushDisplayStatus: readStringValue(item.push_display_status || item.pushDisplayStatus || ''),
    pushLastAttemptedAt: readStringValue(item.push_last_attempted_at || item.pushLastAttemptedAt || ''),
    pushLastClickAt: readStringValue(item.push_last_click_at || item.pushLastClickAt || ''),
    emailStatus: readStringValue(item.email_status || item.emailStatus || ''),
    emailLastAttemptedAt: readStringValue(item.email_last_attempted_at || item.emailLastAttemptedAt || ''),
    emailLastSentAt: readStringValue(item.email_last_sent_at || item.emailLastSentAt || ''),
    emailLastError: readStringValue(item.email_last_error || item.emailLastError || ''),
    targetUrl: readStringValue(item.target_url || item.targetUrl || ''),
    createdAt: readStringValue(item.created_at || item.createdAt || '')
  }
}

function normalizeParentEarlyDismissal(value: unknown): ParentEarlyDismissal {
  const item = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  const confirmation = (item.pickup_confirmation && typeof item.pickup_confirmation === 'object'
    ? item.pickup_confirmation
    : {}) as Record<string, unknown>
  return {
    earlyDismissalId: readStringValue(item.early_dismissal_id || item.earlyDismissalId || item.id),
    studentId: readStringValue(item.student_id || item.studentId),
    studentName: readStringValue(item.student_name || item.studentName || 'Student'),
    pickupPersonName: readStringValue(item.pickup_person_name || item.pickupPersonName),
    pickupRelationship: readStringValue(item.pickup_relationship || item.pickupRelationship),
    reasonCode: readStringValue(item.reason_code || item.reasonCode),
    reasonNotes: readStringValue(item.reason_notes || item.reasonNotes),
    status: readStringValue(item.status || 'pending'),
    createdAt: readStringValue(item.created_at || item.createdAt),
    expiresAt: readStringValue(item.expires_at || item.expiresAt),
    requiresPhoto: item.requires_photo !== false,
    pickupConfirmation: {
      pickupPersonName: readStringValue(confirmation.pickup_person_name || confirmation.pickupPersonName),
      pickupRelationship: readStringValue(confirmation.pickup_relationship || confirmation.pickupRelationship),
      pickupReason: readStringValue(confirmation.pickup_reason || confirmation.pickupReason),
      secureAttachmentId: readStringValue(confirmation.secure_attachment_id || confirmation.secureAttachmentId),
      submittedAt: readStringValue(confirmation.submitted_at || confirmation.submittedAt)
    }
  }
}

function normalizeGuestReleaseRequest(value: unknown): GuestReleaseRequest {
  const item = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  return {
    earlyDismissalId: readStringValue(item.early_dismissal_id || item.earlyDismissalId || item.id),
    guestLinkId: readStringValue(item.guest_link_id || item.guestLinkId),
    studentName: readStringValue(item.student_name || item.studentName || 'Student'),
    pickupPersonName: readStringValue(item.pickup_person_name || item.pickupPersonName),
    pickupRelationship: readStringValue(item.pickup_relationship || item.pickupRelationship),
    reasonCode: readStringValue(item.reason_code || item.reasonCode),
    reasonNotes: readStringValue(item.reason_notes || item.reasonNotes),
    status: readStringValue(item.status || 'pending'),
    guestSessionStatus: readStringValue(item.guest_session_status || item.guestSessionStatus || 'created'),
    expiresAt: readStringValue(item.expires_at || item.expiresAt),
    openedAt: readStringValue(item.opened_at || item.openedAt),
    submittedAt: readStringValue(item.submitted_at || item.submittedAt)
  }
}

function normalizeParentMessageType(value: string): string {
  const raw = String(value || 'message').trim().toLowerCase()
  const aliases: Record<string, string> = {
    note: 'message',
    school_message: 'message',
    pdf: 'document',
    ack: 'confirmation',
    acknowledgement: 'confirmation',
    acknowledgment: 'confirmation'
  }
  const normalized = aliases[raw] || raw
  return ['message', 'news', 'event', 'document', 'confirmation'].includes(normalized)
    ? normalized
    : 'message'
}

function parentMessageTypeLabel(message: ParentMessage): string {
  if (parentMessageEarlyDismissalId(message)) return 'Pickup confirmation'
  if (message.messageType === 'news') return 'School news'
  if (message.messageType === 'event') return 'School event'
  if (message.messageType === 'document') return 'Shared document'
  if (message.messageType === 'confirmation') return 'Confirmation requested'
  return 'School message'
}

function parentMessageEarlyDismissalId(message: ParentMessage): string {
  const raw = message.raw || {}
  const audience = raw.audience && typeof raw.audience === 'object'
    ? raw.audience as Record<string, unknown>
    : {}
  const metadata = raw.metadata && typeof raw.metadata === 'object'
    ? raw.metadata as Record<string, unknown>
    : {}
  return readStringValue(
    raw.early_dismissal_id ||
      raw.earlyDismissalId ||
      audience.early_dismissal_id ||
      audience.earlyDismissalId ||
      metadata.early_dismissal_id ||
      metadata.earlyDismissalId
  )
}

function normalizeAllowedAction(action: string): string {
  const value = String(action || '').trim().toLowerCase()
  if (value === 'ack') return 'acknowledge'
  if (value === 'view_pdf') return 'view_documents'
  if (value === 'edit') return 'edit_if_allowed'
  return value
}

function collectAllowedActions(source: unknown): Set<string> {
  const out = new Set<string>()
  const push = (value: unknown) => {
    if (!value) return
    if (typeof value === 'string') {
      value.split(/[, ]+/).forEach((part) => {
        const normalized = normalizeAllowedAction(part)
        if (normalized) out.add(normalized)
      })
      return
    }
    if (Array.isArray(value)) {
      value.forEach(push)
      return
    }
    if (typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(([key, enabled]) => {
        if (enabled === true || enabled === 'true' || enabled === 1) out.add(normalizeAllowedAction(key))
      })
    }
  }
  push(source)
  return out
}

function parentMessageAllows(message: ParentMessage, action: 'reply' | 'acknowledge' | 'edit_if_allowed'): boolean {
  const raw = message.raw || {}
  const candidates = [
    (raw as any).allowedActions,
    (raw as any).allowed_actions,
    (raw as any).permissions,
    (raw as any).permission,
    (raw as any).share?.permission,
    (raw as any).share?.permissions,
    (raw as any).share?.allowedActions,
    (raw as any).share?.allowed_actions
  ]
  const allowed = new Set<string>()
  candidates.forEach((candidate) => {
    collectAllowedActions(candidate).forEach((value) => allowed.add(value))
  })
  return allowed.has(action)
}

function parentAttachmentUrl(attachment: ParentAttachment): string {
  const url = attachment.url || attachment.path
  if (!url) return ''
  try {
    if (/^(https?:|data:|blob:)/i.test(url) || url.startsWith('/')) return url
    return `/api/file/raw?path=${encodeURIComponent(url)}`
  } catch {
    return ''
  }
}

function createParentViewerObjectUrl(rawUrl: string): { url: string; objectUrl?: string } {
  if (!/^data:/i.test(rawUrl)) return { url: rawUrl }
  const match = rawUrl.match(/^data:([^;,]+)?(;base64)?,(.*)$/)
  if (!match) return { url: rawUrl }
  try {
    const mime = match[1] || 'application/octet-stream'
    const payload = match[3] || ''
    const raw = match[2]
      ? window.atob(payload)
      : decodeURIComponent(payload)
    const bytes = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i += 1) {
      bytes[i] = raw.charCodeAt(i)
    }
    const objectUrl = URL.createObjectURL(new Blob([bytes], { type: mime }))
    return { url: objectUrl, objectUrl }
  } catch {
    return { url: rawUrl }
  }
}

async function resolveParentViewerUrl(rawUrl: string, session: ParentSession): Promise<{ url: string; objectUrl?: string }> {
  const direct = createParentViewerObjectUrl(rawUrl)
  if (direct.objectUrl || /^(blob:|data:)/i.test(direct.url)) return direct
  const needsProtectedFetch = direct.url.startsWith('/api/') || /\/api\/file\/raw/i.test(direct.url)
  if (!needsProtectedFetch) return direct
  try {
    const response = await fetch(direct.url, {
      method: 'GET',
      headers: parentPortalHeaders(session),
      cache: 'no-store'
    })
    if (!response.ok) return direct
    const blob = await response.blob()
    if (!blob || !blob.size) return direct
    const objectUrl = URL.createObjectURL(blob)
    return { url: objectUrl, objectUrl }
  } catch {
    return direct
  }
}

function readParentPickupPhoto(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file || !file.type.startsWith('image/')) {
      reject(new Error('PHOTO_REQUIRED'))
      return
    }
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('PHOTO_READ_FAILED'))
    reader.onload = () => {
      const raw = String(reader.result || '')
      const image = new Image()
      image.onerror = () => reject(new Error('PHOTO_READ_FAILED'))
      image.onload = () => {
        const maxSide = 1280
        const scale = Math.min(1, maxSide / Math.max(image.width || maxSide, image.height || maxSide))
        const width = Math.max(1, Math.round((image.width || maxSide) * scale))
        const height = Math.max(1, Math.round((image.height || maxSide) * scale))
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const context = canvas.getContext('2d')
        if (!context) {
          resolve(raw)
          return
        }
        context.drawImage(image, 0, 0, width, height)
        resolve(canvas.toDataURL('image/jpeg', 0.82))
      }
      image.src = raw
    }
    reader.readAsDataURL(file)
  })
}

function parentDocumentEmbedUrl(url: string, isPdf: boolean): string {
  if (!url || !isPdf) return url
  if (/^(blob:|data:)/i.test(url)) return url
  return `/pdfjs/web/viewer.html?file=${encodeURIComponent(url)}#zoom=page-width&pagemode=none`
}

function parentPushSupported(): boolean {
  return typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
}

function parentPortalPlatformGuide(): ParentPortalPlatformGuide {
  const runtime = detectRuntimeDeviceProfile()

  if (runtime.deviceClass === 'android_phone') {
    return {
      key: 'android_phone',
      label: 'Android Chrome',
      installTitle: 'Install on Android',
      installSteps: [
        'Open the school link in Chrome.',
        'Tap the install/download icon in the address bar, or open the Chrome menu and choose Install app.',
        'Open Parent Portal from the new phone icon.'
      ],
      alertTitle: 'Enable Android alerts',
      alertSteps: [
        'Open Parent Portal from the installed icon.',
        'Open Notification setup.',
        'Tap Reset alerts if this phone was already tested.',
        'Tap Enable alerts and allow notifications.',
        'Use Test local notification before testing a school message.'
      ],
      note: 'Android can receive Parent Portal alerts from the installed Chrome PWA.',
      installButtonLabel: 'Install Parent Portal',
      requiresStandaloneForPush: false
    }
  }

  if (runtime.deviceClass === 'android_tablet') {
    return {
      key: 'android_tablet',
      label: 'Android tablet',
      installTitle: 'Install on Android tablet',
      installSteps: [
        'Open the school link in Chrome.',
        'Tap Install app from the address bar or menu.',
        'Open Parent Portal from the new tablet icon.'
      ],
      alertTitle: 'Enable Android tablet alerts',
      alertSteps: [
        'Open Parent Portal from the installed icon.',
        'Open Notification setup.',
        'Tap Enable alerts and allow notifications.',
        'Use Test local notification before testing a school message.'
      ],
      note: 'Android tablets can receive Parent Portal alerts from the installed PWA.',
      installButtonLabel: 'Install Parent Portal',
      requiresStandaloneForPush: false
    }
  }

  if (runtime.deviceClass === 'ipad') {
    return {
      key: 'ios_ipad',
      label: 'iPadOS Safari',
      installTitle: 'Add to Home Screen',
      installSteps: [
        'Open the school link in Safari.',
        'Tap Share.',
        'Tap Add to Home Screen.',
        'Open Parent Portal from the Home Screen icon.'
      ],
      alertTitle: 'Enable iPhone/iPad alerts',
      alertSteps: [
        'Use iOS or iPadOS 16.4 or newer.',
        'Open Parent Portal from the Home Screen icon.',
        'Open Notification setup.',
        'Tap Enable alerts and allow notifications.',
        'If Safari is only a browser tab, inbox and PDFs still work, but push alerts require the Home Screen app.'
      ],
      note: 'iPhone and iPad require the Home Screen PWA before web push can be enabled.',
      installButtonLabel: 'How to install on iPhone/iPad',
      requiresStandaloneForPush: true
    }
  }

  if (runtime.deviceClass === 'iphone') {
    return {
      key: 'ios_iphone',
      label: 'iPhone Safari',
      installTitle: 'Add to Home Screen',
      installSteps: [
        'Open the school link in Safari.',
        'Tap Share.',
        'Tap Add to Home Screen.',
        'Open Parent Portal from the Home Screen icon.'
      ],
      alertTitle: 'Enable iPhone alerts',
      alertSteps: [
        'Use iOS 16.4 or newer.',
        'Open Parent Portal from the Home Screen icon.',
        'Open Notification setup.',
        'Tap Enable alerts and allow notifications.',
        'If Safari is only a browser tab, inbox and PDFs still work, but push alerts require the Home Screen app.'
      ],
      note: 'iPhone requires the Home Screen PWA before web push can be enabled.',
      installButtonLabel: 'How to install on iPhone',
      requiresStandaloneForPush: true
    }
  }

  if (runtime.deviceClass === 'desktop_windows') {
    return {
      key: 'desktop_windows',
      label: 'Windows browser',
      installTitle: 'Install on Windows',
      installSteps: [
        'Use the browser install icon when it appears in the address bar.',
        'If no install icon appears, keep Parent Portal pinned/bookmarked.',
        'Open Parent Portal before testing alerts.'
      ],
      alertTitle: 'Enable browser alerts',
      alertSteps: [
        'Open Notification setup.',
        'Tap Enable alerts and allow notifications.',
        'Use Test local notification before testing a school message.'
      ],
      note: 'Windows alerts depend on browser and operating system notification settings.',
      installButtonLabel: 'Install Parent Portal',
      requiresStandaloneForPush: false
    }
  }

  if (runtime.deviceClass === 'desktop_macos') {
    return {
      key: 'desktop_macos',
      label: 'macOS browser',
      installTitle: 'Install on macOS',
      installSteps: [
        'Use the browser install icon when available.',
        'If install is unavailable, keep Parent Portal bookmarked.',
        'Open Parent Portal before testing alerts.'
      ],
      alertTitle: 'Enable browser alerts',
      alertSteps: [
        'Open Notification setup.',
        'Tap Enable alerts and allow notifications.',
        'Use Test local notification before testing a school message.'
      ],
      note: 'macOS alerts depend on browser and system notification settings.',
      installButtonLabel: 'Install Parent Portal',
      requiresStandaloneForPush: false
    }
  }

  if (runtime.deviceClass === 'chromeos') {
    return {
      key: 'chromeos',
      label: 'ChromeOS',
      installTitle: 'Install on Chromebook',
      installSteps: [
        'Open the school link in Chrome.',
        'Use Install app from the address bar or menu.',
        'Open Parent Portal from the launcher icon.'
      ],
      alertTitle: 'Enable Chromebook alerts',
      alertSteps: [
        'Open Notification setup.',
        'Tap Enable alerts and allow notifications.',
        'Use Test local notification before testing a school message.'
      ],
      note: 'ChromeOS notifications depend on Chrome and Chromebook notification settings.',
      installButtonLabel: 'Install Parent Portal',
      requiresStandaloneForPush: false
    }
  }

  return {
    key: 'desktop_other',
    label: 'Desktop browser',
    installTitle: 'Install on this computer',
    installSteps: [
      'Use the browser install icon when it appears in the address bar.',
      'If no install icon appears, keep Parent Portal bookmarked.',
      'Open Parent Portal before testing alerts.'
    ],
    alertTitle: 'Enable browser alerts',
    alertSteps: [
      'Open Notification setup.',
      'Tap Enable alerts and allow notifications.',
      'Use Test local notification before testing a school message.'
    ],
    note: 'Desktop alerts depend on browser and operating system notification settings.',
    installButtonLabel: 'Install Parent Portal',
    requiresStandaloneForPush: false
  }
}

function parentPortalRouteTarget(): { type: 'message' | 'document' | 'alert' | 'early_dismissal' | ''; id: string } {
  try {
    const earlyMatch = window.location.pathname.match(/^\/(?:parent-portal|portal)\/early-dismiss\/([^/?#]+)/i)
    if (earlyMatch) return { type: 'early_dismissal', id: decodeURIComponent(earlyMatch[1] || '') }
    const match = window.location.pathname.match(/^\/(?:parent-portal|portal)\/(messages|documents|alerts)\/([^/?#]+)/i)
    if (!match) return { type: '', id: '' }
    const rawType = (match[1] || '').toLowerCase()
    return {
      type: rawType === 'documents' ? 'document' : rawType === 'alerts' ? 'alert' : 'message',
      id: decodeURIComponent(match[2] || '')
    }
  } catch {
    return { type: '', id: '' }
  }
}

function guestReleaseTokenFromRoute(): string {
  try {
    const match = window.location.pathname.match(/^\/guest-release\/early-dismiss\/([^/?#]+)/i)
    return match ? decodeURIComponent(match[1] || '') : ''
  } catch {
    return ''
  }
}

function parentPushApplicationServerKey(value: string): ArrayBuffer {
  const padding = '='.repeat((4 - (value.length % 4)) % 4)
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = window.atob(base64)
  const buffer = new ArrayBuffer(raw.length)
  const output = new Uint8Array(buffer)
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
  return buffer
}

function getEduApiBase(): string {
  try {
    if (window.location.protocol === 'file:') return 'http://127.0.0.1:8090'
    const host = window.location.hostname || '127.0.0.1'
    return `${window.location.protocol}//${host}:8090`
  } catch {
    return 'http://127.0.0.1:8090'
  }
}

function normalizeAcademyGrade(value: unknown): AcademyGrade {
  const grade = String(value || '').trim() as AcademyGrade
  return ACADEMY_ALLOWED_GRADES.includes(grade) ? grade : ACADEMY_DEFAULT_GRADE
}

function readAcademyActiveGrade(): AcademyGrade {
  try {
    return normalizeAcademyGrade(window.localStorage.getItem('NEXUS_ACADEMY_ACTIVE_GRADE'))
  } catch {
    return ACADEMY_DEFAULT_GRADE
  }
}

function buildAcademyGradeContext(gradeValue: unknown): AcademyGradeContext {
  const grade = normalizeAcademyGrade(gradeValue)
  return ACADEMY_GRADE_REGISTRY.find((item) => item.grade === grade) || ACADEMY_GRADE_REGISTRY[2]
}

function persistAcademyGradeContext(gradeValue: unknown): AcademyGradeContext {
  const context = buildAcademyGradeContext(gradeValue)
  try {
    window.localStorage.setItem('NEXUS_ACADEMY_ACTIVE_GRADE', context.grade)
    window.localStorage.setItem('NEXUS_ACADEMY_TRACK', context.academyTrack)
    window.localStorage.setItem('NEXUS_ACADEMY_ENROLLMENT_SCOPE', context.enrollmentScope)
    window.localStorage.setItem('NEXUS_ACADEMY_GRADE_PACKAGE', context.gradePackageId)
    window.localStorage.setItem('NEXUS_ACADEMY_CURRICULUM_SCOPE', context.curriculumScope)
    window.localStorage.setItem('NEXUS_ACADEMY_RAG_NAMESPACE', context.ragNamespace)
    window.localStorage.setItem('NEXUS_ACADEMY_CURRICULUM_MANIFEST', context.curriculumManifestPath)
    window.localStorage.setItem('NEXUS_ACADEMY_CARD_DECK', context.cardDeckPath)
    window.localStorage.setItem('NEXUS_ACADEMY_MODULE_MANIFEST', context.moduleManifestPath)
    window.localStorage.setItem('NEXUS_ACADEMY_PROGRESS_LEDGER_SCOPE', context.progressLedgerScope)
    window.localStorage.setItem('NEXUS_ACADEMY_REVIEW_QUEUE_SCOPE', context.reviewQueueScope)
    window.localStorage.setItem('NEXUS_ACADEMY_CERTIFICATE_WORKFLOW_SCOPE', context.certificateWorkflowScope)
    window.localStorage.setItem('NEXUS_ACADEMY_CERTIFICATE_PATH', context.certificatePath)
    const audit = {
      event: 'academy_grade_context_set',
      active_overlay: ACADEMY_OVERLAY_ID,
      tenant_type: ACADEMY_TENANT_PROFILE.tenant_type,
      active_grade: context.grade,
      enrollment_scope: context.enrollmentScope,
      grade_package_id: context.gradePackageId,
      rag_namespace: context.ragNamespace,
      progress_ledger_scope: context.progressLedgerScope,
      review_queue_scope: context.reviewQueueScope,
      certificate_workflow_scope: context.certificateWorkflowScope,
      work_item_filter: { grade: context.grade, package_id: context.gradePackageId },
      metadata_only: true,
      at: new Date().toISOString()
    }
    window.localStorage.setItem('NEXUS_ACADEMY_LAST_AUDIT', JSON.stringify(audit))
  } catch {
    // Academy context is recoverable from the active profile and registry.
  }
  return context
}

function persistAcademyDemoProfileContext(gradeValue: unknown = readAcademyActiveGrade()): AcademyGradeContext {
  const context = persistAcademyGradeContext(gradeValue)
  try {
    window.localStorage.setItem('NEXUS_DEMO_CONTROL', '1')
    window.localStorage.setItem(DEMO_SESSION_KEY, '1')
    window.localStorage.setItem(DEMO_OWNER_KEY, ROOT_OWNER_EMAIL)
    window.localStorage.setItem(ACTIVE_PRODUCT_MODE_KEY, 'academy')
    window.localStorage.setItem('NEXUS_TENANT_TYPE', ACADEMY_TENANT_PROFILE.tenant_type)
    window.localStorage.setItem('NEXUS_DELIVERY_MODEL', ACADEMY_TENANT_PROFILE.delivery_model)
    window.localStorage.setItem('NEXUS_STUDENT_PORTAL_ONLY', '1')
    window.localStorage.setItem('NEXUS_HUMAN_TEACHER_ACCESS', '0')
    window.localStorage.setItem('NEXUS_VIRTUAL_TEACHERS_ENABLED', '1')
    window.localStorage.setItem('NEXUS_ALLOWED_ROLES', JSON.stringify(ACADEMY_TENANT_PROFILE.allowed_roles))
    window.localStorage.setItem('NEXUS_OVERLAY', ACADEMY_OVERLAY_ID)
    window.localStorage.setItem('NEXUS_ACTIVE_OVERLAY', ACADEMY_OVERLAY_ID)
    window.localStorage.setItem('NEXUS_KIOSK_POLICY', ACADEMY_TENANT_PROFILE.kiosk_policy)
    window.localStorage.setItem('NEXUS_ACADEMY_DEMO_PROFILE', '1')
    window.localStorage.setItem('NEXUS_ACADEMY_MULTIGRADE_ENABLED', '1')
    window.localStorage.setItem('NEXUS_ACADEMY_ALLOWED_GRADES', JSON.stringify(ACADEMY_ALLOWED_GRADES))
    window.localStorage.setItem('NEXUS_ACADEMY_ASSIGNED_GRADE', ACADEMY_DEFAULT_GRADE)
    window.localStorage.setItem('NEXUS_ACADEMY_DEFAULT_GRADE', ACADEMY_DEFAULT_GRADE)
    window.localStorage.setItem('NEXUS_ACADEMY_PROFILE', JSON.stringify({
      user_id: 'STU-101',
      display_name: 'Lucía Hernández',
      email: DEMO_TOOLS_ENABLED ? 'student.demo@nqai' : '',
      base_role: 'student',
      active_overlay: ACADEMY_OVERLAY_ID,
      tenant_type: ACADEMY_TENANT_PROFILE.tenant_type,
      enrollment_type: 'demo',
      demo_profile: true,
      multigrade_enabled: true,
      assigned_grade: ACADEMY_DEFAULT_GRADE,
      allowed_grades: ACADEMY_ALLOWED_GRADES,
      default_grade: ACADEMY_DEFAULT_GRADE,
      active_grade: context.grade,
      academy_track: context.academyTrack,
      enrollment_scope: context.enrollmentScope,
      grade_package_id: context.gradePackageId,
      rag_namespace: context.ragNamespace,
      curriculum_manifest_path: context.curriculumManifestPath,
      card_deck_path: context.cardDeckPath,
      work_item_filter: { grade: context.grade, package_id: context.gradePackageId },
      progress_ledger_scope: context.progressLedgerScope,
      review_queue_scope: context.reviewQueueScope,
      certificate_workflow_scope: context.certificateWorkflowScope,
      certificate_path: context.certificatePath,
      school_class_id: null,
      teacher_dashboard_scope: null
    }))
  } catch {
    // The visible shell still renders from in-memory defaults.
  }
  return context
}

function applyDemoPersonaToStorage(persona: DemoPersona): string {
  if (!DEMO_TOOLS_ENABLED || NEXUS_PRODUCT_PROFILE !== 'dual_demo') {
    throw new Error('Demo personas are disabled by the product profile')
  }
  if (isDistrictDemoPersona(persona)) {
    throw new Error('District Demo V1 is memory-only and cannot create a demo session')
  }
  const lease = `demo-lease-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const nextIdentityKey = `${persona.email.toLowerCase()}|${persona.role.toLowerCase()}|${persona.name.toLowerCase()}`
  const prevIdentityKey = window.localStorage.getItem('eugene_session_identity_key') || ''
  clearAcademyRuntimeContext()
  window.localStorage.setItem(ACTIVE_PRODUCT_MODE_KEY, persona.productMode)
  window.localStorage.setItem(ACTIVE_DEMO_PERSONA_KEY, persona.id)
  if (persona.productMode === 'academy') persistAcademyDemoProfileContext()
  window.localStorage.setItem('NEXUS_DEMO_CONTROL', '1')
  window.localStorage.setItem(DEMO_SESSION_KEY, '1')
  window.localStorage.setItem(DEMO_OWNER_KEY, ROOT_OWNER_EMAIL)
  window.localStorage.setItem('NEXUS_LAST_EMAIL', persona.email)
  window.localStorage.setItem('NEXUS_SESSION_EMAIL', persona.email)
  window.localStorage.setItem('NEXUS_EMAIL', persona.email)
  window.localStorage.setItem('NEXUS_SESSION_NAME', persona.name)
  window.localStorage.setItem('NEXUS_USER_NAME', persona.name)
  window.localStorage.setItem('NEXUS_LAST_ROLE', persona.role)
  window.localStorage.setItem('NEXUS_SESSION_ROLE', persona.role)
  if (prevIdentityKey && prevIdentityKey !== nextIdentityKey) {
    window.localStorage.removeItem('eugene_session_id')
  }
  if (!window.localStorage.getItem(SESSION_KEY)) {
    window.localStorage.setItem(SESSION_KEY, lease)
  }
  return window.localStorage.getItem(SESSION_KEY) || lease
}

function clearDemoControlStorage() {
  try {
    // NEXUS_DEMO_CONTROL is a per-device demo authorization for Saul's linked demo devices.
    // Do not clear it on logout; otherwise Presenter Mode and the five-role overlay switcher disappear.
    window.localStorage.removeItem(DEMO_SESSION_KEY)
    window.localStorage.removeItem(DEMO_OWNER_KEY)
  } catch {
    // ignore
  }
}

const PRESENTER_STOP_WORDS = new Set([
  'the',
  'and',
  'for',
  'con',
  'que',
  'los',
  'las',
  'una',
  'uno',
  'del',
  'como',
  'what',
  'how',
  'why',
  'does',
  'can',
  'is',
  'are',
  'about',
  'module',
  'modulo'
])

function normalizePresenterSearchText(value: unknown): string {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
}

function presenterSearchTokens(value: string): string[] {
  return normalizePresenterSearchText(value)
    .split(/[^a-z0-9]+/)
    .map((token) => token.trim())
    .filter((token) => token.length > 2 && !PRESENTER_STOP_WORDS.has(token))
}

function presenterModuleHaystack(module: PresenterModule): string {
  return normalizePresenterSearchText([
    module.id,
    module.title,
    module.role,
    module.view,
    module.module,
    module.summary,
    module.business,
    module.pedagogical,
    module.technical,
    ...(module.aliases || []),
    ...(module.questionsItCanAnswer || []),
    ...(module.relatedDocs || [])
  ].filter(Boolean).join(' '))
}

function findPresenterModuleMatches(
  moduleMap: PresenterModuleMap,
  question: string,
  currentStep?: DemoStep,
  limit = 3
): PresenterModule[] {
  const modules = Array.isArray(moduleMap.modules) ? moduleMap.modules : []
  if (!modules.length) return []

  const normalizedQuestion = normalizePresenterSearchText(question)
  const tokens = presenterSearchTokens(question)
  const currentModule = normalizePresenterSearchText(currentStep?.module || currentStep?.title || '')

  const ranked = modules
    .map((module) => {
      const haystack = presenterModuleHaystack(module)
      let score = 0

      if (normalizedQuestion.includes(normalizePresenterSearchText(module.title))) score += 8
      if (module.id && normalizedQuestion.includes(normalizePresenterSearchText(module.id))) score += 8
      if (module.module && normalizedQuestion.includes(normalizePresenterSearchText(module.module))) score += 5
      if (currentModule && haystack.includes(currentModule)) score += 2
      if (currentStep?.id === module.id) score += 3

      for (const alias of module.aliases || []) {
        const normalizedAlias = normalizePresenterSearchText(alias)
        if (normalizedAlias && normalizedQuestion.includes(normalizedAlias)) score += 5
      }

      for (const token of tokens) {
        if (haystack.includes(token)) score += 1
      }

      return { module, score }
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score)

  if (!ranked.length && currentStep?.module) {
    const fallback = modules.find((module) => presenterModuleHaystack(module).includes(currentModule))
    return fallback ? [fallback] : []
  }

  return ranked.slice(0, limit).map((entry) => entry.module)
}

const PRESENTER_ADVANCE_COMMANDS = new Set([
  'next',
  'next step',
  'continue',
  'go next',
  'advance',
  'siguiente',
  'siguiente paso',
  'continua',
  'continue',
  'continuen',
  'continua por favor',
  'siga',
  'adelante',
  'avanza',
  'avanzar',
  'proximo',
  'proxima'
])

function isPresenterAdvanceCommand(value: string): boolean {
  const normalized = normalizePresenterSearchText(value).replace(/\s+/g, ' ').trim()
  if (!normalized || normalized.length > 42) return false
  return PRESENTER_ADVANCE_COMMANDS.has(normalized)
}

function presenterAvatarStateLabel(state: PresenterAvatarState): string {
  if (state === 'speaking') return 'Speaking'
  if (state === 'listening') return 'Listening'
  if (state === 'thinking') return 'Thinking'
  if (state === 'paused') return 'Paused'
  if (state === 'error') return 'Needs attention'
  if (state === 'fallback_audio_only') return 'Voice-only fallback'
  return 'Ready'
}

function presenterAvatarPrompt(step?: DemoStep): string {
  if (!step?.listenAfter) return 'Manual Q&A ready.'
  return 'Questions open after this module.'
}

function createPresenterAvatarAdapter(
  provider: PresenterAvatarProvider,
  onState: (state: PresenterAvatarState) => void
): PresenterAvatarAdapter {
  return {
    provider,
    initAvatarSession: async () => {
      onState('idle')
    },
    sendAudioOrText: async () => undefined,
    startSpeaking: () => onState('speaking'),
    stopSpeaking: () => onState('idle'),
    startListening: () => onState('listening'),
    stopListening: () => onState('idle'),
    destroySession: () => onState('idle'),
    fallbackToVoiceOnly: () => onState('fallback_audio_only')
  }
}

const AVATAR_RECORDING_SCRIPTS: AvatarRecordingScript[] = [
  {
    kind: 'consent',
    label: 'Consentimiento',
    fileName: '01_consent_saul_owner_v1.webm',
    durationHint: '15-25 seconds',
    body: 'My name is Saul Olalde. I am the owner and authorized presenter for Nexus Quantum AI. I give permission to use this recording to create and operate my Presenter Avatar inside Nexus Browser and Nexus QCloud demos, in English and Spanish, for institutional product presentations controlled by Nexus Quantum AI.'
  },
  {
    kind: 'source',
    label: 'Avatar Source EN',
    fileName: '02_avatar_source_saul_owner_v1.webm',
    durationHint: '60-90 seconds',
    body: 'Hello. My name is Saul Olalde, founder of Nexus Quantum AI. Nexus Browser was created to help institutions adopt artificial intelligence without losing control of their data, their users, or their daily workflows. In a school district, that means administrators, teachers, students, staff, and parents can work inside one controlled environment. Nexus brings together sovereign cloud, QBox storage, filtered offline-first internet, role-based access, assignments, documents, communication, and a local intelligent agent called Eugenie. The goal is simple: powerful AI, but inside the institution rules. The data returns to the institution. The workflow stays under control. The technology becomes useful, safe, and understandable. Now I will guide you through Nexus Browser, module by module, and you can ask questions at any point.'
  },
  {
    kind: 'spanish',
    label: 'Avatar Source ES',
    fileName: '03_spanish_source_saul_owner_v1.webm',
    durationHint: '45-75 seconds',
    body: 'Hola. Mi nombre es Saul Olalde, fundador de Nexus Quantum AI. Nexus Browser fue creado para que una institución pueda adoptar inteligencia artificial sin perder control de su data, sus usuarios y sus procesos diarios. Para un distrito escolar, esto significa administradores, maestros, estudiantes, empleados y padres trabajando dentro de un entorno controlado. Nexus integra cloud soberano, QBox, internet filtrado y offline-first, roles, tareas, documentos, comunicación y un agente inteligente local llamado Eugenie. La idea es clara: inteligencia artificial poderosa, pero dentro de las reglas de la institución. La data vuelve a la institución. El trabajo se mantiene bajo control.'
  }
]

const PRESENTER_AVATAR_MEDIA_SOURCES: Record<PresenterAvatarLanguage, string> = {
  en: '/presenter-avatar/media/source.webm',
  es: '/presenter-avatar/media/spanish.webm'
}

const PRESENTER_AVATAR_POSTER_SOURCES: Record<PresenterAvatarLanguage, string> = {
  en: '/presenter-avatar/poster/source.jpg',
  es: '/presenter-avatar/poster/spanish.jpg'
}

const PRESENTER_AVATAR_LANGUAGE_LABELS: Record<PresenterAvatarLanguage, string> = {
  en: 'Avatar EN',
  es: 'Avatar ES'
}

export default function App() {
  const [stage, setStage] = useState<Stage>(() => {
    if (NEXUS_PRODUCT_PROFILE === 'school' && hasExplicitAcademyContext()) {
      return 'profileDenied'
    }
    const profile = readNexusBootProfile()
    const strictProfile = isStrictNexusProfile(profile)
    if (hasGuestReleaseEntry()) {
      return 'guestRelease'
    }
    if (hasParentGuestEntry()) {
      return shouldUseStoredParentSessionForEntry() ? 'app' : 'parentAccess'
    }
    if (shouldUseStoredParentSessionForEntry()) {
      return 'app'
    }
    if (hasInstitutionalTabletEntry()) {
      if (shouldHoldIpadTabletRuntimeForInstall()) return 'tabletAccess'
      return hasInstitutionalTabletSession() ? 'app' : 'tabletAccess'
    }
    if (hasSessionLease() || hasDemoEntry()) return strictProfile ? 'browserGate' : 'app'
    return hasIncomingAuthFlow() ? 'auth' : 'landing'
  })
  const bootProfile = useMemo(() => readNexusBootProfile(), [])
  const strictBootProfile = useMemo(() => isStrictNexusProfile(bootProfile), [bootProfile])
  const [browserGateStatus, setBrowserGateStatus] = useState<BrowserGateStatus>(() => DEFAULT_BROWSER_GATE_STATUS(bootProfile))
  const [authFlow, setAuthFlow] = useState<AuthFlow>(() => initialAuthFlow())
  const [key, setKey] = useState(0)
  const [internalTabs, setInternalTabs] = useState<InternalTab[]>([])
  const [activeInternalTabId, setActiveInternalTabId] = useState('home')
  const [draggingInternalTabId, setDraggingInternalTabId] = useState('')
  const internalTabDragStateRef = useRef<{
    tabId: string
    pointerId: number
    startX: number
    hasMoved: boolean
    lastTargetId: string
  } | null>(null)
  const suppressInternalTabClickRef = useRef(false)
  const [parentInviteToken, setParentInviteToken] = useState<string>('')
  const [liveSession, setLiveSession] = useState<ParentSession>(() => readParentSession())
  const [shellPresence, setShellPresence] = useState<ShellPresence>(() => buildShellPresence(readActiveSessionIdentity()))
  const [demoModeEnabled, setDemoModeEnabled] = useState<boolean>(() => strictBootProfile ? false : isDemoModeEnabled())
  const [demoPersonaId, setDemoPersonaId] = useState<string>(() => readStoredDemoPersonaId())
  const [activeDemoPersonaId, setActiveDemoPersonaId] = useState<string>(() => readStoredDemoPersonaId())
  const [districtMirrorState, setDistrictMirrorState] = useState<DistrictMirrorState | null>(null)
  const [districtMirrorStatus, setDistrictMirrorStatus] = useState<string>('')
  const [demoStatus, setDemoStatus] = useState<string>('')
  const [demoBusy, setDemoBusy] = useState<boolean>(false)
  const [academyActiveGrade, setAcademyActiveGrade] = useState<AcademyGrade>(() => readAcademyActiveGrade())
  const [appControlDockOpen, setAppControlDockOpen] = useState<boolean>(false)
  const demoDockAutoOpenedRef = useRef(false)
  const [appSurfaceTheme, setAppSurfaceTheme] = useState<NexusSurfaceTheme>('dark')
  const [demoScript, setDemoScript] = useState<DemoScript | null>(null)
  const [demoScriptError, setDemoScriptError] = useState<string>('')
  const [presenterModuleMap, setPresenterModuleMap] = useState<PresenterModuleMap>(() => FALLBACK_PRESENTER_MODULE_MAP)
  const [presenterRagPack, setPresenterRagPack] = useState<PresenterRagPack>(() => FALLBACK_PRESENTER_RAG_PACK)
  const [presenterQuestion, setPresenterQuestion] = useState<string>('')
  const [presenterListening, setPresenterListening] = useState<boolean>(false)
  const [presenterMicStatus, setPresenterMicStatus] = useState<string>('')
  const [presenterVoiceStatus, setPresenterVoiceStatus] = useState<string>('')
  const [presenterAvatarState, setPresenterAvatarState] = useState<PresenterAvatarState>('idle')
  const [presenterAvatarProvider] = useState<PresenterAvatarProvider>('local_video')
  const [presenterAvatarLanguage, setPresenterAvatarLanguage] = useState<PresenterAvatarLanguage>('en')
  const [presenterAvatarMediaReady, setPresenterAvatarMediaReady] = useState<boolean>(false)
  const [presenterAvatarMediaStatus, setPresenterAvatarMediaStatus] = useState<string>('Avatar visual listo; esperando audio.')
  const [presenterAvatarVoiceActive, setPresenterAvatarVoiceActive] = useState<boolean>(false)
  const [presenterAvatarVoiceStatus, setPresenterAvatarVoiceStatus] = useState<string>('Avatar voice listo.')
  const [presenterAvatarLipSyncActive, setPresenterAvatarLipSyncActive] = useState<boolean>(false)
  const [presenterAvatarLipSyncStatus, setPresenterAvatarLipSyncStatus] = useState<string>('Avatar sync listo.')
  const [presenterAvatarShowcase, setPresenterAvatarShowcase] = useState<boolean>(false)
  const [presenterAutoListenEnabled, setPresenterAutoListenEnabled] = useState<boolean>(false)
  const [avatarRecorderOpen, setAvatarRecorderOpen] = useState<boolean>(false)
  const [avatarRecordingKind, setAvatarRecordingKind] = useState<AvatarRecordingKind>('consent')
  const [avatarRecordingActive, setAvatarRecordingActive] = useState<boolean>(false)
  const [avatarRecordingStatus, setAvatarRecordingStatus] = useState<string>('Listo para grabar.')
  const [avatarRecordingSavedPath, setAvatarRecordingSavedPath] = useState<string>('')
  const [avatarRecordingDurationMs, setAvatarRecordingDurationMs] = useState<number>(0)
  const [demoPresenterOpen, setDemoPresenterOpen] = useState<boolean>(false)
  const [demoPresenterCompact, setDemoPresenterCompact] = useState<boolean>(true)
  const [demoStepIndex, setDemoStepIndex] = useState<number>(0)
  const [demoPaused, setDemoPaused] = useState<boolean>(false)
  const [demoVoiceEnabled, setDemoVoiceEnabled] = useState<boolean>(true)
  const [cameraPreviewOpen, setCameraPreviewOpen] = useState<boolean>(false)
  const [cameraPreviewActive, setCameraPreviewActive] = useState<boolean>(false)
  const [cameraPreviewError, setCameraPreviewError] = useState<string>('')
  const [cameraPreviewRole, setCameraPreviewRole] = useState<string>(() => readActiveSessionIdentity().role)
  const [cameraPreviewZoom, setCameraPreviewZoom] = useState<number>(1)
  const [cameraPreviewMirrored, setCameraPreviewMirrored] = useState<boolean>(true)
  const [appStreamIdentity, setAppStreamIdentity] = useState<ParentSession>(() => readActiveSessionIdentity())
  const [appStreamOpen, setAppStreamOpen] = useState<boolean>(false)
  const [appStreamCatalog, setAppStreamCatalog] = useState<AppStreamCatalogApp[]>([])
  const [appStreamSession, setAppStreamSession] = useState<AppStreamSession | null>(null)
  const [appStreamBusy, setAppStreamBusy] = useState<boolean>(false)
  const [appStreamStatus, setAppStreamStatus] = useState<string>('idle')
  const [appStreamError, setAppStreamError] = useState<string>('')
  const [appStreamFrameActive, setAppStreamFrameActive] = useState<boolean>(false)
  const [uiUpdateStatus, setUiUpdateStatus] = useState<string>('')
  const [uiUpdateAvailable, setUiUpdateAvailable] = useState<boolean>(false)
  const [uiUpdateManifest, setUiUpdateManifest] = useState<Record<string, unknown> | null>(null)
  const [uiUpdateBusy, setUiUpdateBusy] = useState<boolean>(false)
  const isWindowsClient = window.nexusBridge?.platform === 'win32'
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const appStreamCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const appStreamAbortRef = useRef<AbortController | null>(null)
  const appStreamFrameActiveRef = useRef<boolean>(false)
  const cameraPanelRef = useRef<HTMLElement | null>(null)
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null)
  const cameraStreamRef = useRef<MediaStream | null>(null)
  const cameraDragRef = useRef<{
    pointerId: number
    startX: number
    startY: number
    startLeft: number
    startTop: number
    moved: boolean
  } | null>(null)
  const presenterRecognitionRef = useRef<any>(null)
  const presenterMediaRecorderRef = useRef<MediaRecorder | null>(null)
  const presenterMediaStreamRef = useRef<MediaStream | null>(null)
  const presenterAudioChunksRef = useRef<Blob[]>([])
  const presenterMicTimerRef = useRef<number | null>(null)
  const presenterDiscardAudioRef = useRef<boolean>(false)
  const presenterAutoListenTimerRef = useRef<number | null>(null)
  const presenterAutoListeningRef = useRef<boolean>(false)
  const presenterAvatarOrbRef = useRef<HTMLButtonElement | null>(null)
  const presenterAvatarVideoRef = useRef<HTMLImageElement | null>(null)
  const presenterAvatarVoiceRafRef = useRef<number | null>(null)
  const presenterAvatarLipSyncRafRef = useRef<number | null>(null)
  const presenterAvatarVoiceClockAudioRef = useRef<HTMLAudioElement | null>(null)
  const presenterAvatarLipSyncAudioRef = useRef<HTMLAudioElement | null>(null)
  const presenterAvatarVoiceTokenRef = useRef<number>(0)
  const presenterAvatarLipSyncTokenRef = useRef<number>(0)
  const avatarVideoRef = useRef<HTMLVideoElement | null>(null)
  const avatarRecordStreamRef = useRef<MediaStream | null>(null)
  const avatarMediaRecorderRef = useRef<MediaRecorder | null>(null)
  const avatarRecordChunksRef = useRef<Blob[]>([])
  const avatarRecordStartedAtRef = useRef<number>(0)
  const avatarRecordTimerRef = useRef<number | null>(null)

  const baseUrl = useMemo(() => {
    const v = (import.meta as any).env?.BASE_URL
    return typeof v === 'string' && v.trim() ? v : '/'
  }, [])
  const presenterAvatarAdapter = useMemo(
    () => createPresenterAvatarAdapter(presenterAvatarProvider, setPresenterAvatarState),
    [presenterAvatarProvider]
  )

  useEffect(() => {
    setPresenterAvatarMediaReady(false)
    setPresenterAvatarMediaStatus(`Cargando ${PRESENTER_AVATAR_LANGUAGE_LABELS[presenterAvatarLanguage]} fijo local...`)
  }, [presenterAvatarLanguage])

  const inviteToken = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      return (
        params.get('invite_token') ||
        params.get('pre_enrollment_token') ||
        params.get('token') ||
        params.get('enrollment_token') ||
        params.get('academy_enrollment_ticket') ||
        params.get('academy_ticket') ||
        params.get('academy_token') ||
        params.get('enrollment_ticket') ||
        params.get('parent_guest_token') ||
        params.get('guest_token') ||
        window.sessionStorage.getItem('NEXUS_PREBOUND_INVITE_TOKEN') ||
        window.localStorage.getItem('NEXUS_PREBOUND_INVITE_TOKEN') ||
        readAppCookie('NEXUS_PREBOUND_INVITE_TOKEN_COOKIE') ||
        ''
      ).trim()
    } catch {
      return ''
    }
  }, [])

  const entryParams = useMemo(() => getEntryParams(), [key, stage])
  const academyEntryActive = useMemo(() => hasAcademyEntry(entryParams), [entryParams])
  const academyDemoActive = useMemo(() => (
    NEXUS_PRODUCT_PROFILE === 'dual_demo' &&
    demoModeEnabled &&
    activeDemoPersonaId === ACADEMY_OVERLAY_ID &&
    AVAILABLE_DEMO_PERSONAS.some((persona) => persona.id === activeDemoPersonaId && persona.productMode === 'academy')
  ), [activeDemoPersonaId, demoModeEnabled])
  const academyExperienceActive = academyEntryActive || academyDemoActive
  const isSchoolFirstAccess = useMemo(() => (
    (entryParams.get('flow') || '').trim().toLowerCase() === 'school_first_access'
  ), [entryParams])
  const isParentAccessFlow = useMemo(() => (
    hasParentGuestEntry()
  ), [])
  const effectiveInviteToken = inviteToken || parentInviteToken
  const selectedDemoPersona = useMemo(() => (
    AVAILABLE_DEMO_PERSONAS.find((p) => p.id === demoPersonaId) || DEFAULT_DEMO_PERSONA
  ), [demoPersonaId])
  const handleAcademyGradeChange = useCallback((grade: AcademyGrade) => {
    const nextGrade = normalizeAcademyGrade(grade)
    setAcademyActiveGrade(nextGrade)
    try {
      window.localStorage.setItem('NEXUS_ACADEMY_ACTIVE_GRADE', nextGrade)
    } catch {
      // Grade state is also held in React; storage is only a convenience.
    }
  }, [])
  const institutionalTabletEntryActive = hasInstitutionalTabletEntry()
  const institutionalTabletSessionRole =
    isInstitutionalTabletRole(liveSession.role) ||
    isInstitutionalTabletGuestRole(liveSession.role) ||
    isParentGuestRole(liveSession.role)
  const institutionalTabletReady =
    institutionalTabletEntryActive &&
    institutionalTabletSessionRole &&
    isInstitutionalTabletEnvironment() &&
    !shouldHoldIpadTabletRuntimeForInstall()
  const parentPortalActive = stage === 'app' && !demoModeEnabled && isParentGuestRole(liveSession.role) && !institutionalTabletReady
  const tabletMirrorEnabled = (entryParams.get('tablet_mirror') || '').trim() === '1'
  const institutionalTabletActive = stage === 'app' && !demoModeEnabled && (tabletMirrorEnabled || institutionalTabletEntryActive) && institutionalTabletReady
  const institutionalTabletReactActive =
    institutionalTabletActive &&
    !isInstitutionalTabletGuestRole(liveSession.role) &&
    !isParentGuestRole(liveSession.role)
  const parentAuthActive = stage === 'auth' && isParentAccessFlow
  const cameraPreviewPolicy = useMemo(() => getCameraPreviewPolicy(cameraPreviewRole), [cameraPreviewRole])
  const appStreamAccessAllowed = useMemo(() => (
    isAppStreamAccessAllowed(appStreamIdentity) && !academyExperienceActive && !parentPortalActive
  ), [academyExperienceActive, appStreamIdentity, parentPortalActive])
  const primaryAppStreamApp = appStreamCatalog.find((app) => app.app_id === 'jw_library') || appStreamCatalog[0]
  const appStreamViewStatus = useMemo(() => {
    const derived = deriveAppStreamStatus(appStreamSession, appStreamStatus)
    if (appStreamStatus === 'closing') return 'closing'
    if (derived === 'streaming' && !appStreamFrameActive) return 'connecting_stream'
    if (derived === 'created') return appStreamStatus || 'ready'
    return derived
  }, [appStreamFrameActive, appStreamSession, appStreamStatus])
  const appStreamStateClass = appStreamViewStatus.replace(/[^a-z0-9_-]/gi, '_')
  const appStreamStateLabel = formatAppStreamStatus(appStreamViewStatus)
  const appStreamCanvasMessage = describeAppStreamCanvasState(appStreamViewStatus)
  const appStreamPlaceholderHidden = appStreamViewStatus === 'streaming' && appStreamFrameActive

  const clearAppStreamCanvas = useCallback(() => {
    const canvas = appStreamCanvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context) return
    context.fillStyle = '#020617'
    context.fillRect(0, 0, canvas.width, canvas.height)
  }, [])

  const markAppStreamFrameActive = useCallback(() => {
    if (appStreamFrameActiveRef.current) return
    appStreamFrameActiveRef.current = true
    setAppStreamFrameActive(true)
    setAppStreamStatus('streaming')
  }, [])

  const drawAppStreamFrame = useCallback(async (frameBytes: Uint8Array) => {
    const canvas = appStreamCanvasRef.current
    const context = canvas?.getContext('2d')
    if (!canvas || !context || !frameBytes.length) return
    const blob = new Blob([frameBytes.slice()], { type: 'image/jpeg' })
    const drawSource = (source: CanvasImageSource, sourceWidth: number, sourceHeight: number) => {
      if (!sourceWidth || !sourceHeight) return
      context.fillStyle = '#020617'
      context.fillRect(0, 0, canvas.width, canvas.height)
      context.imageSmoothingEnabled = true
      context.imageSmoothingQuality = 'high'
      const scale = Math.min(canvas.width / sourceWidth, canvas.height / sourceHeight)
      const targetWidth = Math.max(1, Math.round(sourceWidth * scale))
      const targetHeight = Math.max(1, Math.round(sourceHeight * scale))
      const targetX = Math.round((canvas.width - targetWidth) / 2)
      const targetY = Math.round((canvas.height - targetHeight) / 2)
      context.drawImage(source, targetX, targetY, targetWidth, targetHeight)
      markAppStreamFrameActive()
    }

    if ('createImageBitmap' in window) {
      try {
        const bitmap = await window.createImageBitmap(blob)
        try {
          drawSource(bitmap, bitmap.width, bitmap.height)
        } finally {
          bitmap.close()
        }
        return
      } catch {
        // Fall through to the Image decoder for WebViews that expose but reject createImageBitmap.
      }
    }

    const objectUrl = URL.createObjectURL(blob)
    try {
      const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve(img)
        img.onerror = () => reject(new Error('frame_decode_failed'))
        img.src = objectUrl
      })
      drawSource(image, image.naturalWidth || image.width, image.naturalHeight || image.height)
    } finally {
      URL.revokeObjectURL(objectUrl)
    }
  }, [markAppStreamFrameActive])

  useEffect(() => {
    if (academyEntryActive) {
      persistAcademyRuntimeContext(entryParams)
      setDemoModeEnabled(false)
      return
    }
    if (academyDemoActive) {
      persistAcademyDemoProfileContext(academyActiveGrade)
      return
    }
    clearAcademyRuntimeContext()
  }, [academyActiveGrade, academyDemoActive, academyEntryActive, entryParams])

  useEffect(() => {
    if (!academyEntryActive) return
    const clearOnAcademyExit = () => clearAcademyRuntimeContext()
    window.addEventListener('pagehide', clearOnAcademyExit)
    return () => window.removeEventListener('pagehide', clearOnAcademyExit)
  }, [academyEntryActive])

  useEffect(() => {
    if (!academyExperienceActive || stage !== 'app') return
    const timers: number[] = []
    const mountAcademyBoundary = () => {
      const frame = iframeRef.current
      const frameWindow = frame?.contentWindow
      const frameDocument = frame?.contentDocument || frameWindow?.document
      if (!frameWindow || !frameDocument?.head) return
      injectAcademyCanvasView(frameDocument)
      frameWindow.postMessage(
        {
          __nexusBrowser: true,
          type: 'nexus-open-academy-home',
          product_mode: 'academy',
          view: 'academy_classroom',
          active_overlay: ACADEMY_OVERLAY_ID,
          active_grade: normalizeAcademyGrade(academyActiveGrade),
          display_name: String(window.localStorage.getItem('NEXUS_SESSION_NAME') || 'Lucía Hernández'),
          demo_profile: academyDemoActive,
          multigrade_enabled: true
        },
        window.location.origin
      )
    }
    ;[0, 250, 900, 1800].forEach((delay) => {
      timers.push(window.setTimeout(mountAcademyBoundary, delay))
    })
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [academyActiveGrade, academyDemoActive, academyExperienceActive, key, stage])

  useEffect(() => {
    if (hasInstitutionalTabletEntry()) {
      persistInstitutionalTabletContext(entryParams)
      applyPwaContextMetadata('ipad')
      return
    }
    if (isParentAccessFlow || parentPortalActive) {
      applyPwaContextMetadata('parent')
    }
  }, [entryParams, isParentAccessFlow, parentPortalActive])

  useEffect(() => {
    const refreshIdentity = () => {
      const nextIdentity = readActiveSessionIdentity()
      setAppStreamIdentity((currentIdentity) => (
        currentIdentity.email === nextIdentity.email &&
        currentIdentity.role === nextIdentity.role &&
        currentIdentity.lease === nextIdentity.lease &&
        currentIdentity.name === nextIdentity.name
          ? currentIdentity
          : nextIdentity
      ))
    }
    refreshIdentity()
    window.addEventListener('storage', refreshIdentity)
    const timer = window.setInterval(refreshIdentity, 1500)
    return () => {
      window.removeEventListener('storage', refreshIdentity)
      window.clearInterval(timer)
    }
  }, [])

  const appStreamRequestContext = useCallback((identity: ParentSession = readActiveSessionIdentity()) => ({
    session_id: readAppStreamSessionId(),
    overlay: APPSTREAM_OVERLAY_ID,
    role: normalizeAppStreamRole(identity.role),
    user_email: identity.email,
    device_id: readAppStreamDeviceId()
  }), [])

  const loadAppStreamCatalog = useCallback(async () => {
    const identity = readActiveSessionIdentity()
    setAppStreamIdentity(identity)
    if (!isAppStreamAccessAllowed(identity)) {
      setAppStreamCatalog([])
      setAppStreamStatus('not_allowed')
      setAppStreamError('not_allowed')
      return
    }
    setAppStreamStatus('loading_catalog')
    setAppStreamError('')
    try {
      const ctx = appStreamRequestContext(identity)
      const qs = new URLSearchParams({
        session_id: ctx.session_id,
        overlay: APPSTREAM_OVERLAY_ID,
        role: ctx.role,
        user_email: ctx.user_email,
        device_id: ctx.device_id
      })
      const resp = await fetch(`/api/appstream/catalog?${qs.toString()}`, {
        headers: appStreamHeaders(identity),
        cache: 'no-store'
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !(data as any)?.ok) {
        throw new Error(resp.status === 403 ? 'not_allowed' : String((data as any)?.detail?.error || (data as any)?.error || `HTTP_${resp.status}`))
      }
      const apps = Array.isArray((data as any).apps) ? (data as any).apps as AppStreamCatalogApp[] : []
      setAppStreamCatalog(apps)
      setAppStreamStatus(apps.length ? 'ready' : 'unavailable')
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      setAppStreamCatalog([])
      setAppStreamStatus(msg === 'not_allowed' ? 'not_allowed' : 'unavailable')
      setAppStreamError(msg)
    }
  }, [appStreamRequestContext])

  const buildEldersOverlayContext = useCallback((identity: ParentSession = readActiveSessionIdentity()) => {
    const normalizedRole = normalizeAppStreamRole(identity.role)
    const email = String(identity.email || '').trim().toLowerCase()
    const role = normalizedRole === 'elder_limmerloop'
      ? 'elder_limmerloop'
      : normalizedRole
    const sessionId = readAppStreamSessionId()
    const deviceId = readAppStreamDeviceId()
    return {
      __nexusBrowser: true,
      type: 'nexus-overlay-context',
      overlay: APPSTREAM_OVERLAY_ID,
      active_overlay: APPSTREAM_OVERLAY_ID,
      role,
      user_email: email,
      email,
      session_id: sessionId,
      device_id: deviceId
    }
  }, [])

  const propagateEldersOverlayContext = useCallback((identity: ParentSession = readActiveSessionIdentity()) => {
    if (!isAppStreamAccessAllowed(identity)) return
    const context = buildEldersOverlayContext(identity)
    try {
      window.localStorage.setItem('NEXUS_OVERLAY', APPSTREAM_OVERLAY_ID)
      window.localStorage.setItem('NEXUS_SESSION_ROLE', context.role)
      window.localStorage.setItem('NEXUS_LAST_ROLE', context.role)
      if (context.session_id) window.localStorage.setItem('NEXUS_SESSION_ID', context.session_id)
      if (context.device_id) {
        window.localStorage.setItem('NEXUS_DEVICE_ID', context.device_id)
        window.localStorage.setItem('nexus_device_id', context.device_id)
      }
      if (context.user_email) {
        window.localStorage.setItem('NEXUS_SESSION_EMAIL', context.user_email)
        window.localStorage.setItem('NEXUS_LAST_EMAIL', context.user_email)
        window.localStorage.setItem('NEXUS_EMAIL', context.user_email)
      }
    } catch {
      // The postMessage bridge still carries the context if storage is unavailable.
    }
    iframeRef.current?.contentWindow?.postMessage(context, '*')
  }, [buildEldersOverlayContext])

  const openAppStream = useCallback(() => {
    const identity = readActiveSessionIdentity()
    setAppStreamIdentity(identity)
    propagateEldersOverlayContext(identity)
    setAppStreamOpen(true)
    window.setTimeout(() => propagateEldersOverlayContext(identity), 250)
    void loadAppStreamCatalog()
  }, [loadAppStreamCatalog, propagateEldersOverlayContext])

  const clearTransientEldersOverlayContext = useCallback(() => {
    const identity = readActiveSessionIdentity()
    if (normalizeAppStreamRole(identity.role) === 'elder_limmerloop') return
    try {
      if (window.localStorage.getItem('NEXUS_OVERLAY') === APPSTREAM_OVERLAY_ID) {
        window.localStorage.removeItem('NEXUS_OVERLAY')
      }
    } catch {
      // best effort only
    }
  }, [])

  const stopAppStreamVideo = useCallback(() => {
    appStreamAbortRef.current?.abort()
    appStreamAbortRef.current = null
    appStreamFrameActiveRef.current = false
    setAppStreamFrameActive(false)
    clearAppStreamCanvas()
  }, [clearAppStreamCanvas])

  const refreshAppStreamSession = useCallback(async (sessionId: string, identity: ParentSession = appStreamIdentity) => {
    if (!sessionId) return
    try {
      const ctx = appStreamRequestContext(identity)
      const qs = new URLSearchParams({
        role: ctx.role,
        user_email: ctx.user_email,
        device_id: ctx.device_id
      })
      const resp = await fetch(`/api/appstream/session/${encodeURIComponent(sessionId)}/status?${qs.toString()}`, {
        headers: appStreamHeaders(identity),
        cache: 'no-store'
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !(data as any)?.ok) {
        throw new Error(resp.status === 403 ? 'not_allowed' : String((data as any)?.detail?.error || (data as any)?.error || `HTTP_${resp.status}`))
      }
      const nextSession = (data as any).session as AppStreamSession
      setAppStreamSession(nextSession)
      setAppStreamStatus(deriveAppStreamStatus(nextSession, 'ready'))
      if (nextSession.last_error && nextSession.capture_state !== 'streaming') {
        setAppStreamError(nextSession.last_error)
      } else if (nextSession.capture_state === 'streaming') {
        setAppStreamError('')
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      setAppStreamError(msg)
      setAppStreamStatus(msg === 'not_allowed' ? 'not_allowed' : 'unavailable')
    }
  }, [appStreamIdentity, appStreamRequestContext])

  const startAppStream = useCallback(async (app?: AppStreamCatalogApp) => {
    const targetApp = app || primaryAppStreamApp
    if (!targetApp || appStreamBusy) return
    const identity = readActiveSessionIdentity()
    setAppStreamIdentity(identity)
    if (!isAppStreamAccessAllowed(identity)) {
      setAppStreamStatus('not_allowed')
      setAppStreamError('not_allowed')
      return
    }
    setAppStreamBusy(true)
    stopAppStreamVideo()
    setAppStreamError('')
    setAppStreamStatus('creating_session')
    try {
      const ctx = appStreamRequestContext(identity)
      const createResp = await fetch('/api/appstream/session/create', {
        method: 'POST',
        headers: appStreamHeaders(identity),
        body: JSON.stringify({
          ...ctx,
          app_id: targetApp.app_id
        })
      })
      const created = await createResp.json().catch(() => ({}))
      if (!createResp.ok || !(created as any)?.ok || !(created as any)?.session?.session_id) {
        throw new Error(createResp.status === 403 ? 'not_allowed' : String((created as any)?.detail?.error || (created as any)?.error || `HTTP_${createResp.status}`))
      }
      const session = (created as any).session as AppStreamSession
      setAppStreamSession(session)
      setAppStreamStatus('app_launching')
      const startResp = await fetch('/api/appstream/session/start', {
        method: 'POST',
        headers: appStreamHeaders(identity),
        body: JSON.stringify({
          ...ctx,
          session_id: session.session_id,
          app_id: targetApp.app_id
        })
      })
      const started = await startResp.json().catch(() => ({}))
      if (!startResp.ok || !(started as any)?.ok) {
        throw new Error(startResp.status === 403 ? 'not_allowed' : String((started as any)?.detail?.error || (started as any)?.error || `HTTP_${startResp.status}`))
      }
      const nextSession = (started as any).session as AppStreamSession
      setAppStreamSession(nextSession)
      setAppStreamStatus(deriveAppStreamStatus(nextSession, 'stream_pending'))
      if (nextSession.last_error && nextSession.capture_state !== 'streaming') {
        setAppStreamError(nextSession.last_error)
      }
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      setAppStreamError(msg)
      setAppStreamStatus(msg === 'not_allowed' ? 'not_allowed' : 'unavailable')
    } finally {
      setAppStreamBusy(false)
    }
  }, [appStreamBusy, appStreamRequestContext, primaryAppStreamApp, stopAppStreamVideo])

  const stopAppStream = useCallback(async () => {
    const sessionId = appStreamSession?.session_id
    if (!sessionId || appStreamBusy) {
      stopAppStreamVideo()
      clearTransientEldersOverlayContext()
      setAppStreamOpen(false)
      return
    }
    const identity = readActiveSessionIdentity()
    stopAppStreamVideo()
    setAppStreamBusy(true)
    setAppStreamStatus('closing')
    try {
      const ctx = appStreamRequestContext(identity)
      const resp = await fetch(`/api/appstream/session/${encodeURIComponent(sessionId)}/stop`, {
        method: 'POST',
        headers: appStreamHeaders(identity),
        body: JSON.stringify(ctx)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !(data as any)?.ok) {
        throw new Error(String((data as any)?.detail?.error || (data as any)?.error || `HTTP_${resp.status}`))
      }
      setAppStreamSession((data as any).session as AppStreamSession)
      setAppStreamStatus('stream_ended')
      clearTransientEldersOverlayContext()
      setAppStreamOpen(false)
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      setAppStreamError(msg)
      setAppStreamStatus('unavailable')
    } finally {
      setAppStreamBusy(false)
    }
  }, [appStreamBusy, appStreamRequestContext, appStreamSession?.session_id, clearTransientEldersOverlayContext, stopAppStreamVideo])

  const closeAppStreamPanel = useCallback(() => {
    if (appStreamSession?.session_id) {
      void stopAppStream()
      return
    }
    stopAppStreamVideo()
    clearTransientEldersOverlayContext()
    setAppStreamOpen(false)
  }, [appStreamSession?.session_id, clearTransientEldersOverlayContext, stopAppStream, stopAppStreamVideo])

  useEffect(() => {
    if (!appStreamOpen || !appStreamAccessAllowed) return
    void loadAppStreamCatalog()
  }, [appStreamAccessAllowed, appStreamOpen, loadAppStreamCatalog])

  useEffect(() => {
    if (!appStreamOpen || !appStreamSession?.session_id) return
    const timer = window.setInterval(() => {
      void refreshAppStreamSession(appStreamSession.session_id)
    }, 2500)
    return () => window.clearInterval(timer)
  }, [appStreamOpen, appStreamSession?.session_id, refreshAppStreamSession])

  useEffect(() => {
    const streamUrl = appStreamSession?.stream_url || ''
    const readyForStream = (
      appStreamSession?.capture_state === 'streaming' ||
      appStreamSession?.stream_status === 'streaming' ||
      appStreamSession?.status === 'streaming'
    )
    if (!appStreamOpen || !appStreamAccessAllowed || !streamUrl || !readyForStream) return

    const identity = readActiveSessionIdentity()
    const controller = new AbortController()
    appStreamAbortRef.current?.abort()
    appStreamAbortRef.current = controller
    appStreamFrameActiveRef.current = false
    setAppStreamFrameActive(false)
    setAppStreamError('')
    setAppStreamStatus('connecting_stream')

    const runStream = async () => {
      const boundaryBytes = new TextEncoder().encode(`--${APPSTREAM_MJPEG_BOUNDARY}`)
      const headerEndBytes = new Uint8Array([13, 10, 13, 10])
      const headerDecoder = new TextDecoder('ascii')
      let buffer = new Uint8Array()

      try {
        const response = await fetch(streamUrl, {
          headers: appStreamHeaders(identity, 'multipart/x-mixed-replace'),
          cache: 'no-store',
          signal: controller.signal
        })
        if (!response.ok || !response.body) {
          const data = await response.json().catch(() => ({}))
          throw new Error(response.status === 403 ? 'not_allowed' : String((data as any)?.detail?.error || (data as any)?.error || `HTTP_${response.status}`))
        }

        const reader = response.body.getReader()
        try {
          while (!controller.signal.aborted) {
            const { done, value } = await reader.read()
            if (done) break
            if (!value?.length) continue
            buffer = appendAppStreamBytes(buffer, value)

            while (!controller.signal.aborted) {
              const boundaryIndex = findAppStreamBytes(buffer, boundaryBytes)
              if (boundaryIndex < 0) {
                if (buffer.length > boundaryBytes.length) {
                  buffer = buffer.slice(Math.max(0, buffer.length - boundaryBytes.length))
                }
                break
              }
              if (boundaryIndex > 0) buffer = buffer.slice(boundaryIndex)
              const headerEndIndex = findAppStreamBytes(buffer, headerEndBytes, boundaryBytes.length)
              if (headerEndIndex < 0) break
              const headerText = headerDecoder.decode(buffer.slice(boundaryBytes.length, headerEndIndex))
              const lengthMatch = /content-length:\s*(\d+)/i.exec(headerText)
              const frameStart = headerEndIndex + headerEndBytes.length
              if (!lengthMatch) {
                buffer = buffer.slice(frameStart)
                continue
              }
              const frameLength = Number(lengthMatch[1])
              const frameEnd = frameStart + frameLength
              if (!Number.isFinite(frameLength) || frameLength <= 0) {
                buffer = buffer.slice(frameStart)
                continue
              }
              if (buffer.length < frameEnd) break
              const frame = buffer.slice(frameStart, frameEnd)
              buffer = buffer.slice(frameEnd)
              try {
                await drawAppStreamFrame(frame)
              } catch (error) {
                console.warn('[Nexus AppStream] frame decode skipped:', error)
              }
            }
          }
        } finally {
          reader.releaseLock()
        }
      } catch (error) {
        if (controller.signal.aborted) return
        const msg = error instanceof Error ? error.message : String(error)
        setAppStreamError(msg)
        setAppStreamStatus(msg === 'permission_required' ? 'permission_required' : msg === 'not_allowed' ? 'not_allowed' : 'unavailable')
      } finally {
        if (appStreamAbortRef.current === controller) appStreamAbortRef.current = null
        if (!controller.signal.aborted) {
          appStreamFrameActiveRef.current = false
          setAppStreamFrameActive(false)
          setAppStreamStatus((currentStatus) => currentStatus === 'streaming' || currentStatus === 'connecting_stream' ? 'stream_ended' : currentStatus)
        }
      }
    }

    void runStream()
    return () => {
      controller.abort()
      if (appStreamAbortRef.current === controller) appStreamAbortRef.current = null
      appStreamFrameActiveRef.current = false
      setAppStreamFrameActive(false)
    }
  }, [
    appStreamAccessAllowed,
    appStreamOpen,
    appStreamSession?.capture_state,
    appStreamSession?.session_id,
    appStreamSession?.status,
    appStreamSession?.stream_status,
    appStreamSession?.stream_url,
    drawAppStreamFrame
  ])

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search)
      const candidateToken = (
        params.get('invite_token') ||
        params.get('pre_enrollment_token') ||
        params.get('token') ||
        params.get('enrollment_token') ||
        params.get('guest_token') ||
        params.get('parent_guest_token') ||
        ''
      ).trim()
      if (!candidateToken) return
      window.localStorage.setItem('NEXUS_PREBOUND_INVITE_TOKEN', candidateToken)
      window.sessionStorage.setItem('NEXUS_PREBOUND_INVITE_TOKEN', candidateToken)
      writeAppCookie('NEXUS_PREBOUND_INVITE_TOKEN_COOKIE', candidateToken)
    } catch {
      // Token persistence is best effort and should not block onboarding.
    }
  }, [entryParams])

  useEffect(() => {
    if (!hasInstitutionalTabletEntry()) return
    try {
      window.localStorage.removeItem('NEXUS_DEMO_CONTROL')
      window.localStorage.removeItem(DEMO_OWNER_KEY)
    } catch {
      // Ignore localStorage restrictions.
    }
    setDemoModeEnabled(false)
  }, [])

  useEffect(() => {
    if (!hasInstitutionalTabletEntry()) return
    const session = readParentSession()
    if (!session.lease) return
    const normalizedRole = String(session.role || '').trim().toLowerCase()
    const allowedRole =
      isInstitutionalTabletRole(normalizedRole) ||
      ['guest', 'trusted_guest', 'invited', 'external_guest', 'parent_guest', 'parent'].includes(normalizedRole)
    if (allowedRole) return
    try {
      window.localStorage.removeItem(SESSION_KEY)
      window.localStorage.removeItem('NEXUS_SESSION_ROLE')
      window.localStorage.removeItem('NEXUS_LAST_ROLE')
      window.localStorage.removeItem('NEXUS_SESSION_EMAIL')
      window.localStorage.removeItem('NEXUS_SESSION_NAME')
    } catch {
      // Ignore stale non-staff session cleanup failures.
    }
    setLiveSession(readParentSession())
    setStage('tabletAccess')
  }, [])

  useEffect(() => {
    if (!isParentAccessFlow) return
    const entryEmail = (entryParams.get('email') || '').trim().toLowerCase()
    if (!entryEmail || !liveSession.email || liveSession.email === entryEmail) return
    setStage('parentAccess')
  }, [entryParams, isParentAccessFlow, liveSession.email])

  const stopCameraPreview = useCallback((reason = 'user_stop') => {
    const stream = cameraStreamRef.current
    cameraStreamRef.current = null
    if (stream) {
      stream.getTracks().forEach((track) => track.stop())
      appendDigitalMirrorAudit('digital_mirror_closed', { reason })
    }
    if (cameraVideoRef.current) {
      cameraVideoRef.current.srcObject = null
    }
    setCameraPreviewActive(false)
  }, [])

  const closeCameraPreview = useCallback(() => {
    stopCameraPreview('panel_closed')
    setCameraPreviewOpen(false)
    setCameraPreviewError('')
  }, [stopCameraPreview])

  const openCameraPreviewPanel = useCallback(() => {
    const identity = readActiveSessionIdentity()
    setCameraPreviewRole(identity.role)
    setCameraPreviewOpen(true)
    setCameraPreviewError('')
  }, [])

  const startCameraPreview = useCallback(async () => {
    const identity = readActiveSessionIdentity()
    const policy = getCameraPreviewPolicy(identity.role)
    setCameraPreviewRole(identity.role)
    setCameraPreviewOpen(true)
    setCameraPreviewError('')

    if (!policy.allowed) {
      setCameraPreviewError('Digital Mirror no está disponible para este rol en este momento.')
      appendDigitalMirrorAudit('permission_denied', { reason: policy.reason })
      return
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraPreviewError('Digital Mirror necesita un navegador con acceso a cámara.')
      appendDigitalMirrorAudit('permission_denied', { reason: 'media_devices_unavailable' })
      return
    }

    try {
      stopCameraPreview('restart')
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: false
      })
      cameraStreamRef.current = stream
      setCameraPreviewActive(true)
      appendDigitalMirrorAudit('digital_mirror_opened', {
        videoTracks: stream.getVideoTracks().length,
        audioTracks: stream.getAudioTracks().length
      })
      window.setTimeout(() => {
        const videoEl = cameraVideoRef.current
        if (!videoEl || cameraStreamRef.current !== stream) return
        videoEl.srcObject = stream
        videoEl.play().catch(() => {})
      }, 0)
    } catch (error) {
      const name = error instanceof DOMException ? error.name : ''
      const message = name === 'NotAllowedError' || name === 'PermissionDeniedError'
        ? 'Permiso de cámara denegado. Puedes activarlo desde los permisos del navegador o del sistema.'
        : 'No se pudo abrir Digital Mirror. Revisa que otra app no esté usando la cámara.'
      setCameraPreviewActive(false)
      setCameraPreviewError(message)
      appendDigitalMirrorAudit('permission_denied', {
        reason: name || (error instanceof Error ? error.message : 'unknown_error')
      })
    }
  }, [stopCameraPreview])

  const adjustCameraPreviewZoom = useCallback((delta: number) => {
    setCameraPreviewZoom((value) => {
      const next = Number((value + delta).toFixed(2))
      return Math.min(2.2, Math.max(1, next))
    })
  }, [])

  const resetCameraPreviewView = useCallback(() => {
    setCameraPreviewZoom(1)
    setCameraPreviewMirrored(true)
  }, [])

  const canDragCameraPreview = useCallback(() => {
    if (typeof window === 'undefined') return false
    const portraitTablet = window.innerWidth < window.innerHeight && window.innerWidth < 1024
    return window.innerWidth >= 900 && window.innerHeight >= 620 && !portraitTablet
  }, [])

  const centerCameraPreviewPanel = useCallback((clearStoredPosition = false) => {
    const panel = cameraPanelRef.current
    if (!panel) return
    panel.style.left = '50%'
    panel.style.top = '50%'
    panel.style.right = 'auto'
    panel.style.bottom = 'auto'
    panel.style.transform = 'translate(-50%, -50%)'
    panel.classList.remove('is-dragging')
    if (clearStoredPosition) {
      try {
        window.sessionStorage.removeItem(DIGITAL_MIRROR_POSITION_KEY)
      } catch {
        // Session storage is optional for Digital Mirror placement.
      }
    }
  }, [])

  const clearCameraPreviewInlinePosition = useCallback(() => {
    const panel = cameraPanelRef.current
    if (!panel) return
    panel.style.left = ''
    panel.style.top = ''
    panel.style.right = ''
    panel.style.bottom = ''
    panel.style.transform = ''
    panel.classList.remove('is-dragging')
  }, [])

  const clampCameraPreviewPanel = useCallback((left: number, top: number, persist = false): DigitalMirrorPosition | null => {
    const panel = cameraPanelRef.current
    if (!panel || typeof window === 'undefined') return null
    const rect = panel.getBoundingClientRect()
    const width = rect.width || panel.offsetWidth
    const height = rect.height || panel.offsetHeight
    if (!width || !height) return null

    const margin = 12
    const maxLeft = Math.max(margin, window.innerWidth - width - margin)
    const maxTop = Math.max(margin, window.innerHeight - height - margin)
    const next = {
      left: Math.min(Math.max(margin, left), maxLeft),
      top: Math.min(Math.max(margin, top), maxTop)
    }

    panel.style.left = `${Math.round(next.left)}px`
    panel.style.top = `${Math.round(next.top)}px`
    panel.style.right = 'auto'
    panel.style.bottom = 'auto'
    panel.style.transform = 'none'

    if (persist) {
      try {
        window.sessionStorage.setItem(DIGITAL_MIRROR_POSITION_KEY, JSON.stringify(next))
      } catch {
        // Placement memory is best effort only.
      }
    }

    return next
  }, [])

  const applyCameraPreviewStoredPosition = useCallback(() => {
    const panel = cameraPanelRef.current
    if (!panel) return
    if (!canDragCameraPreview()) {
      clearCameraPreviewInlinePosition()
      return
    }

    try {
      const raw = window.sessionStorage.getItem(DIGITAL_MIRROR_POSITION_KEY)
      if (!raw) {
        centerCameraPreviewPanel(false)
        return
      }
      const parsed = JSON.parse(raw) as Partial<DigitalMirrorPosition>
      const left = Number(parsed.left)
      const top = Number(parsed.top)
      const rect = panel.getBoundingClientRect()
      const width = rect.width || panel.offsetWidth
      const height = rect.height || panel.offsetHeight
      const invalid =
        !Number.isFinite(left) ||
        !Number.isFinite(top) ||
        left > window.innerWidth - 24 ||
        top > window.innerHeight - 24 ||
        left + width < 24 ||
        top + height < 24

      if (invalid) {
        centerCameraPreviewPanel(true)
        return
      }
      clampCameraPreviewPanel(left, top, true)
    } catch {
      centerCameraPreviewPanel(true)
    }
  }, [canDragCameraPreview, centerCameraPreviewPanel, clampCameraPreviewPanel, clearCameraPreviewInlinePosition])

  const handleCameraPreviewPointerDown = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!canDragCameraPreview()) return
    const target = event.target as HTMLElement | null
    if (target?.closest('button, a, input, textarea, select, [data-no-drag]')) return
    const panel = cameraPanelRef.current
    if (!panel) return
    const rect = panel.getBoundingClientRect()
    cameraDragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startLeft: rect.left,
      startTop: rect.top,
      moved: false
    }
    event.currentTarget.setPointerCapture?.(event.pointerId)
    event.preventDefault()
  }, [canDragCameraPreview])

  const handleCameraPreviewPointerMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = cameraDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    const deltaX = event.clientX - drag.startX
    const deltaY = event.clientY - drag.startY
    if (!drag.moved && Math.max(Math.abs(deltaX), Math.abs(deltaY)) < DIGITAL_MIRROR_DRAG_THRESHOLD) return
    drag.moved = true
    cameraPanelRef.current?.classList.add('is-dragging')
    clampCameraPreviewPanel(drag.startLeft + deltaX, drag.startTop + deltaY, false)
    event.preventDefault()
  }, [clampCameraPreviewPanel])

  const handleCameraPreviewPointerEnd = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = cameraDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return
    if (drag.moved && cameraPanelRef.current) {
      const rect = cameraPanelRef.current.getBoundingClientRect()
      clampCameraPreviewPanel(rect.left, rect.top, true)
    }
    cameraPanelRef.current?.classList.remove('is-dragging')
    event.currentTarget.releasePointerCapture?.(event.pointerId)
    cameraDragRef.current = null
  }, [clampCameraPreviewPanel])

  useEffect(() => {
    const refreshCameraRole = () => setCameraPreviewRole(readActiveSessionIdentity().role)
    refreshCameraRole()
    window.addEventListener('storage', refreshCameraRole)
    const interval = window.setInterval(refreshCameraRole, 1000)
    return () => {
      window.removeEventListener('storage', refreshCameraRole)
      window.clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    if (!cameraPreviewOpen) return
    const frame = window.requestAnimationFrame(applyCameraPreviewStoredPosition)
    return () => window.cancelAnimationFrame(frame)
  }, [applyCameraPreviewStoredPosition, cameraPreviewOpen])

  useEffect(() => {
    if (!cameraPreviewOpen) return
    const clampOnResize = () => {
      window.requestAnimationFrame(applyCameraPreviewStoredPosition)
    }
    window.addEventListener('resize', clampOnResize)
    window.addEventListener('orientationchange', clampOnResize)
    return () => {
      window.removeEventListener('resize', clampOnResize)
      window.removeEventListener('orientationchange', clampOnResize)
    }
  }, [applyCameraPreviewStoredPosition, cameraPreviewOpen])

  useEffect(() => {
    if (cameraPreviewActive && cameraVideoRef.current && cameraStreamRef.current) {
      cameraVideoRef.current.srcObject = cameraStreamRef.current
      cameraVideoRef.current.play().catch(() => {})
    }
  }, [cameraPreviewActive])

  useEffect(() => {
    if (stage !== 'app') {
      stopCameraPreview('left_app_stage')
      setCameraPreviewOpen(false)
    }
  }, [stage, stopCameraPreview])

  useEffect(() => () => {
    const stream = cameraStreamRef.current
    if (stream) stream.getTracks().forEach((track) => track.stop())
    cameraStreamRef.current = null
  }, [])

  useEffect(() => {
    if (canonicalizeLanOrigin()) return

    // WebAuthn is far more reliable on a real hostname than on 127.0.0.1.
    // Redirect once so rpId becomes `localhost`.
    try {
      if (window.location.hostname === '127.0.0.1') {
        const u = new URL(window.location.href)
        u.hostname = 'localhost'
        window.location.replace(u.toString())
      }
    } catch {
      // ignore
    }
  }, [])

  useEffect(() => {
    const refreshDemoMode = () => setDemoModeEnabled(isDemoModeEnabled())
    refreshDemoMode()
    window.addEventListener('storage', refreshDemoMode)
    return () => window.removeEventListener('storage', refreshDemoMode)
  }, [])

  useEffect(() => {
    if (stage !== 'app') {
      setAppControlDockOpen(false)
      demoDockAutoOpenedRef.current = false
    }
  }, [stage])

  useEffect(() => {
    if (
      stage !== 'app' ||
      !demoModeEnabled ||
      academyEntryActive ||
      parentPortalActive ||
      parentAuthActive ||
      institutionalTabletActive
    ) {
      if (!demoModeEnabled) demoDockAutoOpenedRef.current = false
      return
    }
    if (demoDockAutoOpenedRef.current) return
    setAppControlDockOpen(true)
    demoDockAutoOpenedRef.current = true
  }, [academyEntryActive, demoModeEnabled, institutionalTabletActive, parentAuthActive, parentPortalActive, stage])

  useEffect(() => {
    const handleSurfaceTheme = (event: MessageEvent) => {
      const data = event.data as Record<string, unknown> | null
      if (!data || data.__nexusBrowser !== true || data.type !== 'nexus-theme-effective') return
      const theme = String(data.theme || '').trim().toLowerCase()
      if (theme === 'light' || theme === 'dark') {
        setAppSurfaceTheme(theme)
      }
    }
    window.addEventListener('message', handleSurfaceTheme)
    return () => window.removeEventListener('message', handleSurfaceTheme)
  }, [])

  useEffect(() => {
    if (!isParentAccessFlow && !academyEntryActive) return
    try {
      window.localStorage.removeItem('NEXUS_DEMO_CONTROL')
      window.localStorage.removeItem(DEMO_OWNER_KEY)
      window.localStorage.removeItem(DEMO_SESSION_KEY)
    } catch {
      // Parent Guest and Academy links must not inherit demo/developer presentation state.
    }
    setDemoModeEnabled(false)
  }, [academyEntryActive, isParentAccessFlow])

  useEffect(() => {
    const refreshSession = () => {
      setLiveSession(readParentSession())
      setShellPresence((current) => (
        current.count > 1 ? current : buildShellPresence(readActiveSessionIdentity())
      ))
    }
    refreshSession()
    window.addEventListener('storage', refreshSession)
    const interval = window.setInterval(refreshSession, 700)
    return () => {
      window.removeEventListener('storage', refreshSession)
      window.clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    const handleMirrorPresence = (event: MessageEvent) => {
      const iframeWin = iframeRef.current?.contentWindow
      if (!iframeWin || event.source !== iframeWin) return
      const msg = event.data as any
      if (!msg || msg.__nexusBrowser !== true || msg.type !== 'nexus-mirror-presence') return
      setShellPresence(buildShellPresence(
        readActiveSessionIdentity(),
        Number(msg.count || 1),
        String(msg.label || ''),
        String(msg.tooltip || '')
      ))
    }

    window.addEventListener('message', handleMirrorPresence)
    return () => window.removeEventListener('message', handleMirrorPresence)
  }, [])

  useEffect(() => {
    const overrideId = 'nexus-demo-control-override-style'

    const clearOverride = () => {
      const existing = document.getElementById(overrideId)
      if (existing?.parentNode) existing.parentNode.removeChild(existing)
    }

    if (!demoModeEnabled) {
      clearOverride()
      return undefined
    }

    const keepDemoControlsVisible = () => {
      try {
        window.localStorage.setItem('NEXUS_DEMO_CONTROL', '1')
        if (!window.localStorage.getItem(DEMO_OWNER_KEY)) {
          window.localStorage.setItem(DEMO_OWNER_KEY, ROOT_OWNER_EMAIL)
        }
      } catch {
        // ignore storage failures
      }

      let style = document.getElementById(overrideId)
      if (!style) {
        style = document.createElement('style')
        style.id = overrideId
        document.head.appendChild(style)
      }
      style.textContent = [
        'html body .app-control-dock{display:flex!important;visibility:visible!important;opacity:1!important;}',
        'html body .app-control-dock .demo-role-switcher{display:grid!important;visibility:visible!important;opacity:1!important;}',
        'html body .app-control-dock .demo-role-switcher .control-select,html body .app-control-dock .demo-role-switcher .control-btn{display:inline-flex!important;visibility:visible!important;opacity:1!important;}',
        'html body .demo-status-banner{display:block!important;visibility:visible!important;opacity:1!important;}',
        'html body .demo-presenter-panel{display:block!important;visibility:visible!important;opacity:var(--nexus-presenter-visible-opacity,1)!important;}'
      ].join('\n')
      if (style.parentNode === document.head && style !== document.head.lastElementChild) {
        document.head.appendChild(style)
      }
    }

    keepDemoControlsVisible()
    const interval = window.setInterval(keepDemoControlsVisible, 1000)
    return () => {
      window.clearInterval(interval)
      clearOverride()
    }
  }, [demoModeEnabled])

  useEffect(() => {
    if (stage !== 'app' || !demoModeEnabled) return
    let cancelled = false
    const url = new URL('demo_script.json', window.location.origin + baseUrl).pathname

    fetch(url, { cache: 'no-store' })
      .then((resp) => {
        if (!resp.ok) throw new Error(`HTTP_${resp.status}`)
        return resp.json()
      })
      .then((data) => {
        if (cancelled) return
        const candidate = data as DemoScript
        if (!candidate || !Array.isArray(candidate.steps) || candidate.steps.length === 0) {
          throw new Error('DEMO_SCRIPT_EMPTY')
        }
        setDemoScript(candidate)
        setDemoVoiceEnabled(candidate.voice?.enabled !== false)
        setDemoScriptError('')
      })
      .catch((error) => {
        if (cancelled) return
        const msg = error instanceof Error ? error.message : String(error)
        setDemoScript(FALLBACK_DEMO_SCRIPT)
        setDemoVoiceEnabled(FALLBACK_DEMO_SCRIPT.voice?.enabled !== false)
        setDemoScriptError(`demo_script.json no cargó; usando fallback local (${msg}).`)
      })

    return () => {
      cancelled = true
    }
  }, [baseUrl, demoModeEnabled, stage])

  useEffect(() => {
    if (stage !== 'app' || !demoModeEnabled) return
    let cancelled = false
    const url = new URL('presenter-module-map.json', window.location.origin + baseUrl).pathname

    fetch(url, { cache: 'no-store' })
      .then((resp) => {
        if (!resp.ok) throw new Error(`HTTP_${resp.status}`)
        return resp.json()
      })
      .then((data) => {
        if (cancelled) return
        const candidate = data as PresenterModuleMap
        if (!candidate || !Array.isArray(candidate.modules) || candidate.modules.length === 0) {
          throw new Error('PRESENTER_MODULE_MAP_EMPTY')
        }
        setPresenterModuleMap(candidate)
      })
      .catch(() => {
        if (cancelled) return
        setPresenterModuleMap(FALLBACK_PRESENTER_MODULE_MAP)
      })

    return () => {
      cancelled = true
    }
  }, [baseUrl, demoModeEnabled, stage])

  useEffect(() => {
    if (stage !== 'app' || !demoModeEnabled) return
    let cancelled = false
    const url = new URL('presenter-rag-pack.json', window.location.origin + baseUrl).pathname

    fetch(url, { cache: 'no-store' })
      .then((resp) => {
        if (!resp.ok) throw new Error(`HTTP_${resp.status}`)
        return resp.json()
      })
      .then((data) => {
        if (cancelled) return
        const candidate = data as PresenterRagPack
        if (!candidate || !Array.isArray(candidate.chunks) || candidate.chunks.length === 0) {
          throw new Error('PRESENTER_RAG_PACK_EMPTY')
        }
        setPresenterRagPack(candidate)
      })
      .catch(() => {
        if (cancelled) return
        setPresenterRagPack(FALLBACK_PRESENTER_RAG_PACK)
      })

    return () => {
      cancelled = true
    }
  }, [baseUrl, demoModeEnabled, stage])

  const checkUiUpdate = useCallback(async (silent = true) => {
    if (!isWindowsClient) {
      setUiUpdateAvailable(false)
      setUiUpdateManifest(null)
      setUiUpdateStatus('')
      return
    }
    if (!window.nexusBridge?.checkUiUpdate) return
    const role = String(readActiveSessionIdentity().role || '').trim().toLowerCase()
    if (!['owner', 'root', 'developer'].includes(role)) return
    try {
      if (!silent) setUiUpdateStatus('Buscando actualización...')
      const result = await window.nexusBridge.checkUiUpdate()
      const available = Boolean(result?.ok && result?.available)
      setUiUpdateAvailable(available)
      setUiUpdateManifest(available ? result : null)
      if (!silent) {
        setUiUpdateStatus(available ? 'Actualización lista para instalar.' : 'Nexus ya está actualizado.')
      }
    } catch {
      if (!silent) setUiUpdateStatus('No se pudo revisar la actualización.')
    }
  }, [isWindowsClient])

  useEffect(() => {
    if (stage !== 'app' && stage !== 'auth') return
    const timer = window.setTimeout(() => {
      void checkUiUpdate(true)
    }, 1800)
    return () => window.clearTimeout(timer)
  }, [checkUiUpdate, stage])

  const installUiUpdate = useCallback(async () => {
    if (!isWindowsClient) {
      setUiUpdateStatus('')
      return
    }
    if (!window.nexusBridge?.installUiUpdate) return
    const role = String(readActiveSessionIdentity().role || '').trim().toLowerCase()
    if (!['owner', 'root', 'developer'].includes(role)) return
    setUiUpdateBusy(true)
    setUiUpdateStatus('Instalando actualización de interfaz...')
    try {
      const result = await window.nexusBridge.installUiUpdate(uiUpdateManifest || undefined)
      if (!result?.ok) {
        setUiUpdateStatus(`No se pudo actualizar: ${String(result?.error || 'error')}`)
        return
      }
      setUiUpdateAvailable(false)
      setUiUpdateStatus('Actualización instalada. Reiniciando Nexus...')
      if (window.nexusBridge?.restartAfterUiUpdate) {
        await window.nexusBridge.restartAfterUiUpdate()
      }
    } catch {
      setUiUpdateStatus('No se pudo instalar la actualización.')
    } finally {
      setUiUpdateBusy(false)
    }
  }, [isWindowsClient, uiUpdateManifest])

  const authSrc = useMemo(() => {
    const isIpadSchoolFirstAccess =
      isSchoolFirstAccess &&
      (entryParams.get('platform') || '').trim().toLowerCase() === 'ipad'
    const entryPath = (window.location.pathname || '').trim().toLowerCase().replace(/\/+$/, '') || '/'
    const isIpadInstitutionalAuth =
      !isParentAccessFlow &&
      hasInstitutionalTabletEntry() &&
      (
        isIpadSchoolFirstAccess ||
        entryPath === '/ipad' ||
        (entryParams.get('platform') || '').trim().toLowerCase() === 'ipad'
      )
    const url = new URL(
      isIpadInstitutionalAuth ? '/hub/' : 'genui/UI_index.html',
      window.location.origin + baseUrl
    )
    url.searchParams.set('flow', isSchoolFirstAccess ? 'school_first_access' : authFlow)
    if (effectiveInviteToken) url.searchParams.set('invite_token', effectiveInviteToken)
    if (isParentAccessFlow) {
      url.searchParams.set('source', 'parent_guest')
      url.searchParams.set('role', 'parent_guest')
      const parentEmail = entryParams.get('email') || window.localStorage.getItem('NEXUS_PARENT_GUEST_EMAIL') || ''
      if (parentEmail) url.searchParams.set('email', parentEmail)
      const entryParentName = `${entryParams.get('parentFirstName') || entryParams.get('first_name') || ''} ${entryParams.get('parentLastName') || entryParams.get('last_name') || ''}`.trim()
      const parentName = entryParentName || window.localStorage.getItem('NEXUS_PARENT_GUEST_NAME') || window.localStorage.getItem('NEXUS_USER_NAME') || ''
      if (parentName) url.searchParams.set('name', parentName)
    }
    if (academyExperienceActive) {
      const ticket = getAcademyEnrollmentTicket(entryParams) || effectiveInviteToken
      url.searchParams.set('source', 'academy')
      url.searchParams.set('tenant_type', ACADEMY_TENANT_PROFILE.tenant_type)
      url.searchParams.set('delivery_model', ACADEMY_TENANT_PROFILE.delivery_model)
      url.searchParams.set('student_portal_only', '1')
      url.searchParams.set('human_teacher_access', '0')
      url.searchParams.set('virtual_teachers_enabled', '1')
      url.searchParams.set('role', 'student')
      url.searchParams.set('overlay', ACADEMY_TENANT_PROFILE.overlay)
      url.searchParams.set('kiosk_policy', ACADEMY_TENANT_PROFILE.kiosk_policy)
      if (ticket) {
        url.searchParams.set('academy_enrollment_ticket', ticket)
        if (!url.searchParams.get('invite_token')) url.searchParams.set('invite_token', ticket)
      }
    }
    if (isSchoolFirstAccess || isIpadInstitutionalAuth) {
      [
        'prebound',
        'pre_enrollment_token',
        'pre_enrollment_id',
        'enrollment_id',
        'source',
        'platform',
        'device_type',
        'shell_mode',
        'overlay',
        'role',
        'email',
        'launch_id',
        'school_id',
        'lunch_id',
        'asset_tag',
        'device_number',
      ].forEach((keyName) => {
        const value = entryParams.get(keyName)
        if (value) url.searchParams.set(keyName, value)
      })
      if (isIpadInstitutionalAuth && !url.searchParams.get('platform')) {
        url.searchParams.set('platform', 'ipad')
      }
      if (isIpadInstitutionalAuth) {
        url.searchParams.set('device_type', TABLET_DEVICE_TYPE)
        url.searchParams.set('shell_mode', TABLET_SHELL_MODE)
        url.searchParams.set('overlay', TABLET_OVERLAY)
        if (!url.searchParams.get('role')) url.searchParams.set('role', 'guest')
      }
    }
    return url.pathname + url.search
  }, [academyExperienceActive, authFlow, baseUrl, effectiveInviteToken, entryParams, isParentAccessFlow, isSchoolFirstAccess])

  const appSrc = useMemo(() => {
    const effectiveTabletRole = resolveInstitutionalTabletRole(entryParams, liveSession.role)
    const effectiveTabletOverlay = overlayForInstitutionalTabletRole(effectiveTabletRole)
    const tabletWorkspace =
      hasInstitutionalTabletEntry() &&
      (
        effectiveTabletRole === 'elder_limmerloop' ||
        isInstitutionalTabletGuestRole(effectiveTabletRole) ||
        isParentGuestRole(effectiveTabletRole)
      )
    const url = new URL(
      tabletWorkspace ? 'ipad-workspace/Jenny_UI_index.html' : 'genui/Jenny_UI_index.html',
      window.location.origin + baseUrl
    )
    url.searchParams.set('product_mode', academyExperienceActive ? 'academy' : 'school')
    if (academyExperienceActive) {
      const ticket = getAcademyEnrollmentTicket(entryParams) || window.localStorage.getItem('NEXUS_ACADEMY_ENROLLMENT_TICKET') || ''
      url.searchParams.set('source', 'academy')
      url.searchParams.set('tenant_type', ACADEMY_TENANT_PROFILE.tenant_type)
      url.searchParams.set('delivery_model', ACADEMY_TENANT_PROFILE.delivery_model)
      url.searchParams.set('student_portal_only', '1')
      url.searchParams.set('role', 'student')
      url.searchParams.set('overlay', ACADEMY_TENANT_PROFILE.overlay)
      url.searchParams.set('kiosk_policy', ACADEMY_TENANT_PROFILE.kiosk_policy)
      if (ticket) url.searchParams.set('academy_enrollment_ticket', ticket)
    }
    if (hasInstitutionalTabletEntry()) {
      url.searchParams.set('platform', 'ipad')
      url.searchParams.set('source', 'tablet_shell')
      url.searchParams.set('device_type', TABLET_DEVICE_TYPE)
      url.searchParams.set('shell_mode', TABLET_SHELL_MODE)
      url.searchParams.set('overlay', effectiveTabletOverlay)
      url.searchParams.set('v', '20260611-elder-contractless')
      url.searchParams.set('role', effectiveTabletRole)
    }
    return url.pathname + url.search
  }, [academyExperienceActive, baseUrl, entryParams, liveSession.role])
  const isIpadGuestWorkspaceFrame = stage === 'app' && appSrc.startsWith('/ipad-workspace/')

  const openApp = useCallback(() => {
    if (shouldHoldIpadTabletRuntimeForInstall()) {
      setStage('tabletAccess')
      setKey((k) => k + 1)
      return
    }
    const parentEntryActive = hasParentGuestEntry()
    const parentSessionActive = isParentGuestRole(readParentSession().role)
    if (
      strictBootProfile &&
      browserGateStatus.status !== 'PASS' &&
      !hasInstitutionalTabletEntry() &&
      !parentEntryActive &&
      !parentSessionActive
    ) {
      setStage('browserGate')
      return
    }
    setStage('app')
    setKey((k) => k + 1)
  }, [browserGateStatus.status, strictBootProfile])

  useEffect(() => {
    if (stage !== 'browserGate') return
    let cancelled = false
    setBrowserGateStatus((current) => ({ ...current, status: 'CHECKING' }))
    validateBrowserGate(bootProfile).then((result) => {
      if (!cancelled) setBrowserGateStatus(result)
    })
    return () => {
      cancelled = true
    }
  }, [bootProfile, stage, key])

  useEffect(() => {
    if (stage === 'app' && (shouldUseDirectLanApp() || appSrc.startsWith('/ipad-workspace/'))) {
      window.location.replace(appSrc)
    }
  }, [appSrc, stage])

  const handleReload = () => {
    if (activeInternalTabId !== 'home') {
      setInternalTabs((tabs) => tabs.map((tab) => (
        tab.id === activeInternalTabId ? { ...tab, url: cacheBustUrl(tab.url) } : tab
      )))
      return
    }
    setKey((k) => k + 1)
  }

  const goAuth = (flow: AuthFlow) => {
    setAuthFlow(flow)
    setStage('auth')
    setKey((k) => k + 1)
  }

  const goParentSignup = (token: string, email: string, name: string, flow: AuthFlow = 'signup') => {
    const normalizedEmail = email.trim().toLowerCase()
    const cleanName = name.trim()
    try {
      if (normalizedEmail) {
        window.localStorage.setItem('NEXUS_PARENT_GUEST_EMAIL', normalizedEmail)
        window.localStorage.setItem('NEXUS_EMAIL', normalizedEmail)
      }
      if (cleanName) window.localStorage.setItem('NEXUS_USER_NAME', cleanName)
      if (cleanName) window.localStorage.setItem('NEXUS_PARENT_GUEST_NAME', cleanName)
      if (token) window.localStorage.setItem('NEXUS_PREBOUND_INVITE_TOKEN', token)
      window.localStorage.setItem('NEXUS_PARENT_GUEST_CONSENT', 'accepted')
    } catch {
      // Storage is a convenience; the invite token still travels through the auth frame.
    }
    setParentInviteToken(token)
    setAuthFlow(flow)
    setStage('auth')
    setKey((k) => k + 1)
  }

  const goLanding = () => {
    setStage('landing')
    setKey((k) => k + 1)
  }

  const goApp = openApp

  const postDistrictMirrorStateToIframe = useCallback((state: DistrictMirrorState) => {
    const iframeWindow = iframeRef.current?.contentWindow
    if (!iframeWindow) return
    iframeWindow.postMessage(
      { __nexusBrowser: true, type: 'nexus-district-mirror-state', state },
      window.location.origin,
    )
  }, [])

  const requestDistrictMirrorPanel = useCallback(async () => {
    setDistrictMirrorStatus('Requesting server-approved synthetic District preview…')
    try {
      const response = await fetch(`${districtMirrorGatewayOrigin()}/api/mirror/panel`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // The Browser sends intent only. It deliberately sends no role, email,
        // binding, capability, selector, or storage-derived authority.
        body: JSON.stringify({ panel: 'district' }),
        cache: 'no-store',
      })
      const payload = await response.json().catch(() => null) as { state?: unknown; detail?: unknown } | null
      if (!response.ok || !isDistrictMirrorState(payload?.state)) {
        throw new Error(typeof payload?.detail === 'string' ? payload.detail : 'DISTRICT_PANEL_DENIED')
      }
      setDistrictMirrorState(payload.state)
      postDistrictMirrorStateToIframe(payload.state)
      setDistrictMirrorStatus('District Mirror panel approved by the server synthetic-preview capability.')
      setDemoStatus('Nexus District Command Center — read-only synthetic Mirror panel')
    } catch (error) {
      setDistrictMirrorState(null)
      setDistrictMirrorStatus(`District preview unavailable (fail-closed): ${(error as Error).message}`)
      setDemoStatus('District preview unavailable — server synthetic-preview capability denied.')
    }
  }, [postDistrictMirrorStateToIframe])

  const handleLogout = async () => {
    if (stage === 'app') {
      try {
        const role = String(
          window.localStorage.getItem('NEXUS_SESSION_ROLE') ||
          window.localStorage.getItem('NEXUS_LAST_ROLE') ||
          ''
        ).trim().toLowerCase()
        if (role === 'student' && window.nexusBridge?.requestStudentProtectedExit) {
          const result = await window.nexusBridge.requestStudentProtectedExit()
          if (result?.handled) return
        }
      } catch {
        // If shell-level authorization is unavailable, preserve existing non-kiosk behavior.
      }
    }
    try {
      window.localStorage.removeItem(SESSION_KEY)
    } catch {
      // ignore
    }
    if (academyExperienceActive) clearAcademyRuntimeContext()
    clearDemoControlStorage()
    goLanding()
  }

  const handleDemoPersonaSwitch = useCallback(() => {
    try {
      if (isDistrictDemoPersona(selectedDemoPersona)) {
        // This selector path deliberately does not call applyDemoPersonaToStorage,
        // issue a lease, post a session, or write identity/role metadata.
        setActiveDemoPersonaId(DISTRICT_DEMO_PERSONA_ID)
        // Keep the existing Eugene / Work Mirror shell visible instead of
        // replacing it with another app or an internal-tab iframe. The
        // server still decides whether a District panel can appear.
        setActiveInternalTabId('home')
        setDistrictMirrorState(null)
        setDistrictMirrorStatus('')
        setDemoStatus('District preview requested through Mirror API…')
        setStage('app')
        window.setTimeout(() => { void requestDistrictMirrorPanel() }, 0)
        return
      }
      const lease = applyDemoPersonaToStorage(selectedDemoPersona)
      const academyPersona = isAcademyDemoPersona(selectedDemoPersona)
      const academyGrade = normalizeAcademyGrade(academyActiveGrade)
      setActiveDemoPersonaId(selectedDemoPersona.id)
      setShellPresence(buildShellPresence({
        email: selectedDemoPersona.email,
        name: selectedDemoPersona.name,
        role: selectedDemoPersona.role,
        lease,
      }))
      setDistrictMirrorState(null)
      setDistrictMirrorStatus('')
      setDemoStatus(academyPersona ? `Academy My Class listo: Lucía Hernández · Grade ${academyGrade}` : '')
      setStage('app')
      // Document frames belong to the previous persona, not to the demo owner.
      setInternalTabs([])
      setActiveInternalTabId('home')
      setKey((k) => k + 1)
      const postDemoSession = () => {
        const iframeWin = iframeRef.current?.contentWindow
        if (!iframeWin) return
        iframeWin.postMessage(
          {
            __nexusBrowser: true,
            type: 'nexus-session-sync',
            email: selectedDemoPersona.email,
            name: selectedDemoPersona.name,
            role: selectedDemoPersona.role,
            product_mode: selectedDemoPersona.productMode,
            session_lease: lease
          },
          '*'
        )
        if (academyPersona) {
          iframeWin.postMessage(
            {
              __nexusBrowser: true,
              type: 'nexus-open-academy-home',
              product_mode: 'academy',
              view: 'academy_classroom',
              active_grade: academyGrade,
              display_name: selectedDemoPersona.name,
              email: selectedDemoPersona.email,
              demo_profile: true,
              multigrade_enabled: true
            },
            '*'
          )
        }
      }
      window.setTimeout(postDemoSession, 250)
      if (academyPersona) window.setTimeout(postDemoSession, 900)
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      setDemoStatus(`No se pudo cambiar rol: ${msg}`)
    }
  }, [academyActiveGrade, requestDistrictMirrorPanel, selectedDemoPersona])

  const callDemoTenantApi = useCallback(async (path: string, body: Record<string, unknown>) => {
    const resp = await fetch(`${getEduApiBase()}${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    })
    const data = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      throw new Error(String((data as any)?.detail || (data as any)?.error || `HTTP_${resp.status}`))
    }
    return data as any
  }, [])

  const handleDemoResetSeed = useCallback(async () => {
    if (demoBusy) return
    if (activeDemoPersonaId !== 'developer') {
      setDemoStatus('Demo Reset+Seed requiere la persona School Developer.')
      return
    }
    const confirmed = window.confirm(
      'Esto restablece y vuelve a sembrar el Demo Tenant. Se reemplazará el estado demo actual. ¿Continuar?'
    )
    if (!confirmed) {
      setDemoStatus('Demo Reset+Seed cancelado.')
      return
    }
    setDemoBusy(true)
    setDemoStatus('Reseteando y sembrando Demo Tenant...')
    try {
      await callDemoTenantApi('/api/demo/tenant/seed', { confirm: DEMO_CONFIRM, reset: true })
      setDemoStatus('Demo Tenant listo: Admin/Teacher/Student/Guest conectados.')
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      setDemoStatus(`Error seed demo: ${msg}`)
    } finally {
      setDemoBusy(false)
    }
  }, [activeDemoPersonaId, callDemoTenantApi, demoBusy])

  const stopPresenterAvatarLipSync = useCallback((nextStatus = 'Avatar sync listo.') => {
    presenterAvatarLipSyncTokenRef.current += 1
    if (presenterAvatarLipSyncRafRef.current) {
      window.cancelAnimationFrame(presenterAvatarLipSyncRafRef.current)
      presenterAvatarLipSyncRafRef.current = null
    }
    const syncAudio = presenterAvatarLipSyncAudioRef.current
    presenterAvatarLipSyncAudioRef.current = null
    if (syncAudio) {
      try {
        syncAudio.pause()
        syncAudio.removeAttribute('src')
        syncAudio.load()
      } catch {}
    }
    setPresenterAvatarLipSyncActive(false)
    setPresenterAvatarLipSyncStatus(nextStatus)
  }, [])

  const base64ToArrayBuffer = useCallback((value: string) => {
    const clean = String(value || '').includes(',')
      ? String(value || '').split(',').pop() || ''
      : String(value || '')
    const raw = window.atob(clean.replace(/\s+/g, ''))
    const out = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i)
    return out.buffer
  }, [])

  const analyzePresenterAudioEnvelope = useCallback(async (audioBase64: string): Promise<PresenterAudioEnvelope> => {
    const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext
    if (!AudioContextCtor) throw new Error('AUDIO_CONTEXT_UNAVAILABLE')
    const audioContext = new AudioContextCtor()
    try {
      const arrayBuffer = base64ToArrayBuffer(audioBase64)
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0))
      const channel = audioBuffer.getChannelData(0)
      const sampleRate = audioBuffer.sampleRate || 44100
      const frameMs = 33.333
      const frameSize = Math.max(256, Math.floor(sampleRate * (frameMs / 1000)))
      const sampleStride = Math.max(1, Math.floor(frameSize / 220))
      const frameCount = Math.max(1, Math.ceil(channel.length / frameSize))
      const rawLevels: number[] = []
      let peak = 0
      for (let frame = 0; frame < frameCount; frame += 1) {
        const start = frame * frameSize
        const end = Math.min(channel.length, start + frameSize)
        let sum = 0
        let count = 0
        for (let i = start; i < end; i += sampleStride) {
          const v = channel[i] || 0
          sum += v * v
          count += 1
        }
        const rms = count ? Math.sqrt(sum / count) : 0
        rawLevels.push(rms)
        if (rms > peak) peak = rms
      }
      const floor = Math.max(0.008, peak * 0.08)
      const scale = Math.max(0.018, peak - floor)
      let smooth = 0
      const levels = rawLevels.map((level) => {
        const normalized = Math.max(0, Math.min(1, (level - floor) / scale))
        const shaped = Math.pow(normalized, 0.72)
        smooth = smooth * 0.52 + shaped * 0.48
        return Math.max(0, Math.min(1, smooth))
      })
      return {
        frameMs,
        durationMs: Math.max(0, audioBuffer.duration * 1000),
        levels
      }
    } finally {
      try {
        await audioContext.close()
      } catch {}
    }
  }, [base64ToArrayBuffer])

  const startPresenterAvatarLipSync = useCallback(async (payload: Record<string, unknown>) => {
    const audioBase64 = String(payload.audio_base64 || payload.audioBase64 || '').trim()
    if (!audioBase64) return
    const mime = String(payload.mime || 'audio/mpeg').trim() || 'audio/mpeg'
    const token = presenterAvatarLipSyncTokenRef.current + 1
    stopPresenterAvatarLipSync('Avatar voice preparando audio...')
    presenterAvatarLipSyncTokenRef.current = token
    setPresenterAvatarLipSyncStatus('Avatar · preparando voz del presentador.')
    try {
      const envelope = await analyzePresenterAudioEnvelope(audioBase64)
      if (presenterAvatarLipSyncTokenRef.current !== token) return
      const cleanBase64 = audioBase64.includes(',') ? audioBase64.split(',').pop() || '' : audioBase64
      const syncAudio = new Audio(`data:${mime};base64,${cleanBase64.replace(/\s+/g, '')}`)
      syncAudio.muted = true
      syncAudio.volume = 0
      syncAudio.preload = 'auto'
      presenterAvatarLipSyncAudioRef.current = syncAudio
      setPresenterAvatarLipSyncActive(true)
      setPresenterAvatarLipSyncStatus('Avatar · voz activa con el audio actual.')
      void syncAudio.play().catch(() => undefined)
      const startedAt = window.performance.now()
      const draw = () => {
        if (presenterAvatarLipSyncTokenRef.current !== token) return
        const elapsedSeconds = Number.isFinite(syncAudio.currentTime) && syncAudio.currentTime > 0
          ? syncAudio.currentTime
          : Math.max(0, (window.performance.now() - startedAt) / 1000)
        if (elapsedSeconds * 1000 >= envelope.durationMs + 180 || syncAudio.ended) {
          setPresenterAvatarState((state) => (state === 'speaking' ? 'idle' : state))
          setPresenterVoiceStatus((status) => status.startsWith('Voice playing')
            ? status.replace('Voice playing', 'Voice ready')
            : status)
          stopPresenterAvatarLipSync('Avatar voice listo.')
          return
        }
        presenterAvatarLipSyncRafRef.current = window.requestAnimationFrame(draw)
      }
      presenterAvatarLipSyncRafRef.current = window.requestAnimationFrame(draw)
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      stopPresenterAvatarLipSync(`Avatar voice no disponible: ${msg}`)
    }
  }, [analyzePresenterAudioEnvelope, stopPresenterAvatarLipSync])

  const clearPresenterAutoListenTimer = useCallback(() => {
    if (presenterAutoListenTimerRef.current) {
      window.clearTimeout(presenterAutoListenTimerRef.current)
      presenterAutoListenTimerRef.current = null
    }
  }, [])

  const postDemoStepToIframe = useCallback((step: DemoStep, index: number, repeat = false, voiceEnabledOverride?: boolean) => {
    const iframeWin = iframeRef.current?.contentWindow
    if (!iframeWin) return
    iframeWin.postMessage(
      {
        __nexusBrowser: true,
        type: 'nexus-demo-presenter-step',
        step,
        index,
        repeat,
        total: demoScript?.steps.length || 0,
        moduleMap: presenterModuleMap,
        ragPack: presenterRagPack,
        avatarProvider: presenterAvatarProvider,
        avatarLanguage: presenterAvatarLanguage,
        voiceEnabled: voiceEnabledOverride ?? demoVoiceEnabled,
        voice: demoScript?.voice || FALLBACK_DEMO_SCRIPT.voice
      },
      '*'
    )
  }, [demoScript?.steps.length, demoScript?.voice, demoVoiceEnabled, presenterAvatarLanguage, presenterAvatarProvider, presenterModuleMap, presenterRagPack])

  const applyDemoStep = useCallback((index: number, repeat = false, voiceEnabledOverride?: boolean) => {
    const script = demoScript || FALLBACK_DEMO_SCRIPT
    const total = script.steps.length
    if (!total) return
    const nextIndex = Math.max(0, Math.min(index, total - 1))
    const step = script.steps[nextIndex]
    if (!step) return

    try {
      const roleKey = String(step.role || '').trim().toLowerCase()
      const persona = AVAILABLE_DEMO_PERSONAS.find((p) => p.id === roleKey || p.role === roleKey)
      if (persona) {
        const lease = applyDemoPersonaToStorage(persona)
        setActiveDemoPersonaId(persona.id)
        if (isParentGuestRole(persona.role)) {
          setInternalTabs([])
          setActiveInternalTabId('home')
        }
        window.setTimeout(() => {
          iframeRef.current?.contentWindow?.postMessage(
            {
              __nexusBrowser: true,
              type: 'nexus-session-sync',
              email: persona.email,
              name: persona.name,
              role: persona.role,
              product_mode: persona.productMode,
              session_lease: lease
            },
            '*'
          )
        }, 40)
      }
    } catch {
      // keep the presentation moving even if localStorage is temporarily unavailable
    }

    setStage('app')
    clearPresenterAutoListenTimer()
    stopPresenterAvatarLipSync('Avatar sync listo.')
    presenterAutoListeningRef.current = false
    void presenterAvatarAdapter.initAvatarSession()
    setDemoPresenterOpen(true)
    setDemoPaused(false)
    setDemoStepIndex(nextIndex)
    setDemoStatus(`Presenter Mode: ${nextIndex + 1}/${total} · ${step.title}`)
    setPresenterVoiceStatus((voiceEnabledOverride ?? demoVoiceEnabled) ? 'Voice On · preparing narration...' : 'Voice Off')
    setPresenterAvatarState((voiceEnabledOverride ?? demoVoiceEnabled) ? 'thinking' : 'fallback_audio_only')
    window.setTimeout(() => postDemoStepToIframe(step, nextIndex, repeat, voiceEnabledOverride), 90)
  }, [clearPresenterAutoListenTimer, demoScript, demoVoiceEnabled, postDemoStepToIframe, presenterAvatarAdapter, stopPresenterAvatarLipSync])

  const handlePresenterStart = useCallback(() => {
    const script = demoScript || FALLBACK_DEMO_SCRIPT
    const startVoiceEnabled = script.voice?.enabled !== false
    setDemoVoiceEnabled(startVoiceEnabled)
    setPresenterAutoListenEnabled(false)
    setDemoPresenterCompact(true)
    setPresenterAvatarShowcase(false)
    applyDemoStep(demoStepIndex || 0, false, startVoiceEnabled)
  }, [applyDemoStep, demoScript, demoStepIndex])

  const handlePresenterNext = useCallback(() => {
    if (demoPaused) return
    applyDemoStep(demoStepIndex + 1)
  }, [applyDemoStep, demoPaused, demoStepIndex])

  const handlePresenterPrevious = useCallback(() => {
    if (demoPaused) return
    applyDemoStep(demoStepIndex - 1)
  }, [applyDemoStep, demoPaused, demoStepIndex])

  const handlePresenterRepeat = useCallback(() => {
    const script = demoScript || FALLBACK_DEMO_SCRIPT
    const step = script.steps[Math.max(0, Math.min(demoStepIndex, script.steps.length - 1))]
    if (!step) return
    setDemoPaused(false)
    setDemoStatus(`Presenter Mode: repitiendo · ${step.title}`)
    postDemoStepToIframe(step, demoStepIndex, true)
  }, [demoScript, demoStepIndex, postDemoStepToIframe])

  const handlePresenterPause = useCallback(() => {
    const nextPaused = !demoPaused
    clearPresenterAutoListenTimer()
    presenterAutoListeningRef.current = false
    setDemoPaused(nextPaused)
    try {
      window.speechSynthesis?.cancel()
      void window.nexusBridge?.stopPresenterSpeech?.()
    } catch {}
    stopPresenterAvatarLipSync(nextPaused ? 'Avatar sync pausado.' : 'Avatar sync listo.')
    iframeRef.current?.contentWindow?.postMessage(
      {
        __nexusBrowser: true,
        type: nextPaused ? 'nexus-demo-presenter-pause' : 'nexus-demo-presenter-resume'
      },
      '*'
    )
    setDemoStatus(nextPaused ? 'Presenter Mode pausado.' : 'Presenter Mode reanudado.')
    setPresenterAvatarState(nextPaused ? 'paused' : 'idle')
  }, [clearPresenterAutoListenTimer, demoPaused, stopPresenterAvatarLipSync])

  const handlePresenterRestart = useCallback(() => {
    try {
      window.speechSynthesis?.cancel()
      void window.nexusBridge?.stopPresenterSpeech?.()
    } catch {}
    stopPresenterAvatarLipSync('Avatar sync reiniciado.')
    clearPresenterAutoListenTimer()
    presenterAutoListeningRef.current = false
    applyDemoStep(0)
  }, [applyDemoStep, clearPresenterAutoListenTimer, stopPresenterAvatarLipSync])

  const handlePresenterExit = useCallback(() => {
    try {
      window.speechSynthesis?.cancel()
      void window.nexusBridge?.stopPresenterSpeech?.()
    } catch {}
    stopPresenterAvatarLipSync('Avatar sync listo.')
    try {
      presenterRecognitionRef.current?.stop?.()
    } catch {}
    try {
      presenterDiscardAudioRef.current = true
      if (presenterMicTimerRef.current) {
        window.clearTimeout(presenterMicTimerRef.current)
        presenterMicTimerRef.current = null
      }
      const recorder = presenterMediaRecorderRef.current
      if (recorder && recorder.state !== 'inactive') recorder.stop()
      presenterMediaStreamRef.current?.getTracks().forEach((track) => track.stop())
    } catch {}
    clearPresenterAutoListenTimer()
    presenterAutoListeningRef.current = false
    presenterMediaRecorderRef.current = null
    presenterMediaStreamRef.current = null
    presenterAudioChunksRef.current = []
    presenterRecognitionRef.current = null
    setPresenterListening(false)
    setPresenterMicStatus('')
    iframeRef.current?.contentWindow?.postMessage(
      {
        __nexusBrowser: true,
        type: 'nexus-demo-presenter-exit'
      },
      '*'
    )
    setDemoPaused(false)
    setDemoPresenterOpen(false)
    setDemoPresenterCompact(true)
    setPresenterQuestion('')
    presenterAvatarAdapter.destroySession()
    setPresenterAvatarShowcase(false)
    setDemoStatus('Presenter Mode cerrado.')
  }, [clearPresenterAutoListenTimer, presenterAvatarAdapter, stopPresenterAvatarLipSync])

  const submitPresenterQuestion = useCallback((rawQuestion: string) => {
    const question = rawQuestion.trim()
    if (!question) return

    const script = demoScript || FALLBACK_DEMO_SCRIPT
    const step = script.steps[Math.max(0, Math.min(demoStepIndex, script.steps.length - 1))]
    const matches = findPresenterModuleMatches(presenterModuleMap, question, step, 3)
    const primaryMatch = matches[0]

    try {
      const roleKey = String(primaryMatch?.role || '').trim().toLowerCase()
      const persona = AVAILABLE_DEMO_PERSONAS.find((p) => p.id === roleKey || p.role === roleKey)
      if (persona) {
        const lease = applyDemoPersonaToStorage(persona)
        setActiveDemoPersonaId(persona.id)
        iframeRef.current?.contentWindow?.postMessage(
          {
            __nexusBrowser: true,
            type: 'nexus-session-sync',
            email: persona.email,
            name: persona.name,
            role: persona.role,
            product_mode: persona.productMode,
            session_lease: lease
          },
          '*'
        )
      }
    } catch {
      // Audience Q&A keeps moving even if role sync is unavailable.
    }

    setPresenterQuestion('')
    setDemoPresenterOpen(true)
    setPresenterAvatarState('thinking')
    setDemoStatus(
      primaryMatch
        ? `Pregunta Presenter: ${primaryMatch.title}`
        : 'Pregunta Presenter: mapa local sin coincidencia exacta'
    )
    iframeRef.current?.contentWindow?.postMessage(
      {
        __nexusBrowser: true,
        type: 'nexus-demo-presenter-question',
        question,
        matches,
        currentStep: step,
        moduleMapTitle: presenterModuleMap.title,
        moduleMapVersion: presenterModuleMap.schemaVersion || '',
        ragPack: presenterRagPack,
        voiceEnabled: demoVoiceEnabled,
        voice: demoScript?.voice || FALLBACK_DEMO_SCRIPT.voice
      },
      '*'
    )
  }, [demoScript, demoStepIndex, demoVoiceEnabled, presenterModuleMap, presenterRagPack])

  const handlePresenterQuestionSubmit = useCallback((event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submitPresenterQuestion(presenterQuestion)
  }, [presenterQuestion, submitPresenterQuestion])

  const handlePresenterSpokenInput = useCallback((rawInput: string, autoMode = false) => {
    const spoken = rawInput.trim()
    const script = demoScript || FALLBACK_DEMO_SCRIPT
    const isLastStep = demoStepIndex >= script.steps.length - 1
    presenterAutoListeningRef.current = false

    if (!spoken) {
      if (autoMode && presenterAutoListenEnabled && !demoPaused) {
        setPresenterMicStatus('Auto Q · sin pregunta detectada. Control manual activo.')
        setPresenterAvatarState('idle')
      }
      return
    }

    if (isPresenterAdvanceCommand(spoken)) {
      if (isLastStep) {
        setPresenterMicStatus('Comando recibido, pero ya estamos en el cierre.')
        setPresenterAvatarState('idle')
        return
      }
      setPresenterQuestion('')
      setPresenterMicStatus(`Comando de voz: "${spoken}" · avanzando.`)
      setPresenterAvatarState('thinking')
      applyDemoStep(demoStepIndex + 1)
      return
    }

    submitPresenterQuestion(spoken)
  }, [applyDemoStep, demoPaused, demoScript, demoStepIndex, presenterAutoListenEnabled, submitPresenterQuestion])

  const stopPresenterMic = useCallback((discard = false) => {
    presenterDiscardAudioRef.current = discard
    if (presenterMicTimerRef.current) {
      window.clearTimeout(presenterMicTimerRef.current)
      presenterMicTimerRef.current = null
    }
    try {
      const recorder = presenterMediaRecorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        recorder.stop()
      }
    } catch {}
    try {
      presenterRecognitionRef.current?.stop?.()
    } catch {}
    presenterRecognitionRef.current = null
    if (!presenterMediaRecorderRef.current) {
      presenterMediaStreamRef.current?.getTracks().forEach((track) => track.stop())
      presenterMediaStreamRef.current = null
      setPresenterListening(false)
      if (discard) setPresenterMicStatus('')
    }
  }, [])

  const startPresenterSpeechRecognitionFallback = useCallback((autoMode = false) => {
    const SpeechRecognitionCtor = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
    if (!SpeechRecognitionCtor) {
      setDemoPresenterCompact(false)
      setPresenterMicStatus('Micrófono no disponible en este runtime.')
      setPresenterAvatarState('fallback_audio_only')
      return false
    }

    try {
      const recognition = new SpeechRecognitionCtor()
      const finalSegments: string[] = []
      presenterRecognitionRef.current = recognition
      recognition.lang = 'es-MX'
      recognition.continuous = false
      recognition.interimResults = true
      recognition.maxAlternatives = 1
      recognition.onstart = () => {
        setPresenterListening(true)
        setPresenterAvatarState('listening')
        setPresenterMicStatus('Mic ON · escuchando pregunta. Presiona Stop para enviar.')
      }
      recognition.onerror = (event: any) => {
        const code = String(event?.error || 'mic_error')
        setPresenterListening(false)
        setPresenterAvatarState('error')
        setPresenterMicStatus(code === 'not-allowed' ? 'Permiso de micrófono denegado.' : `Micrófono: ${code}`)
      }
      recognition.onresult = (event: any) => {
        let interim = ''
        for (let i = event.resultIndex || 0; i < event.results.length; i += 1) {
          const result = event.results[i]
          const transcript = String(result?.[0]?.transcript || '').trim()
          if (!transcript) continue
          if (result.isFinal) finalSegments.push(transcript)
          else interim = transcript
        }
        const spoken = finalSegments.join(' ').trim()
        setPresenterQuestion(spoken || interim)
      }
      recognition.onend = () => {
        const question = finalSegments.join(' ').trim()
        presenterRecognitionRef.current = null
        setPresenterListening(false)
        setPresenterMicStatus(question ? `Pregunta enviada por voz: "${question}"` : 'No escuché una pregunta completa.')
        setPresenterAvatarState(question ? 'thinking' : 'idle')
        handlePresenterSpokenInput(question, autoMode)
      }
      recognition.start()
      return true
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      presenterRecognitionRef.current = null
      setPresenterListening(false)
      setDemoPresenterCompact(false)
      setPresenterAvatarState('error')
      setPresenterMicStatus(`No se pudo iniciar micrófono: ${msg}`)
      return false
    }
  }, [handlePresenterSpokenInput])

  const blobToBase64 = useCallback((blob: Blob) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = String(reader.result || '')
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result)
    }
    reader.onerror = () => reject(reader.error || new Error('AUDIO_READ_FAILED'))
    reader.readAsDataURL(blob)
  }), [])

  const stopAvatarRecording = useCallback(() => {
    const recorder = avatarMediaRecorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      setAvatarRecordingStatus('Deteniendo y guardando...')
      recorder.stop()
      return
    }
    setAvatarRecordingActive(false)
  }, [])

  const startAvatarRecording = useCallback(async () => {
    if (avatarRecordingActive) {
      stopAvatarRecording()
      return
    }
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setAvatarRecordingStatus('Esta version del Browser no puede grabar video.')
      return
    }
    const script = AVATAR_RECORDING_SCRIPTS.find((item) => item.kind === avatarRecordingKind) || AVATAR_RECORDING_SCRIPTS[0]
    setAvatarRecordingSavedPath('')
    setAvatarRecordingDurationMs(0)
    setAvatarRecordingStatus('Pidiendo permiso de camara y microfono...')
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        }
      })
      avatarRecordStreamRef.current = stream
      if (avatarVideoRef.current) {
        avatarVideoRef.current.srcObject = stream
        await avatarVideoRef.current.play().catch(() => undefined)
      }
      const preferredMime = [
        'video/webm;codecs=vp9,opus',
        'video/webm;codecs=vp8,opus',
        'video/webm'
      ].find((mime) => {
        try {
          return MediaRecorder.isTypeSupported(mime)
        } catch {
          return false
        }
      }) || ''
      const recorder = new MediaRecorder(stream, preferredMime ? { mimeType: preferredMime } : undefined)
      avatarMediaRecorderRef.current = recorder
      avatarRecordChunksRef.current = []
      avatarRecordStartedAtRef.current = Date.now()
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) avatarRecordChunksRef.current.push(event.data)
      }
      recorder.onerror = (event: any) => {
        setAvatarRecordingStatus(`Error de grabacion: ${String(event?.error?.message || event?.error || 'recording_error')}`)
      }
      recorder.onstop = async () => {
        const chunks = avatarRecordChunksRef.current.slice()
        const durationMs = Math.max(0, Date.now() - avatarRecordStartedAtRef.current)
        const mime = recorder.mimeType || preferredMime || 'video/webm'
        avatarMediaRecorderRef.current = null
        avatarRecordChunksRef.current = []
        avatarRecordStreamRef.current?.getTracks().forEach((track) => track.stop())
        avatarRecordStreamRef.current = null
        if (avatarVideoRef.current) avatarVideoRef.current.srcObject = null
        if (avatarRecordTimerRef.current) {
          window.clearInterval(avatarRecordTimerRef.current)
          avatarRecordTimerRef.current = null
        }
        setAvatarRecordingActive(false)
        setAvatarRecordingDurationMs(durationMs)
        if (!chunks.length) {
          setAvatarRecordingStatus('No se capturo video suficiente. Intenta de nuevo.')
          return
        }
        setAvatarRecordingStatus('Guardando video...')
        try {
          const blob = new Blob(chunks, { type: mime })
          const videoBase64 = await blobToBase64(blob)
          const result = await window.nexusBridge?.savePresenterAvatarRecording?.({
            kind: script.kind,
            mime,
            video_base64: videoBase64,
            duration_ms: durationMs
          })
          if (!result?.ok) throw new Error(String(result?.error || 'SAVE_FAILED'))
          setAvatarRecordingSavedPath(String(result.path || result.file_name || script.fileName))
          setAvatarRecordingStatus(`Guardado: ${result.file_name || script.fileName}`)
        } catch (error) {
          const msg = error instanceof Error ? error.message : String(error)
          setAvatarRecordingStatus(`No se pudo guardar: ${msg}`)
        }
      }
      recorder.start(1000)
      setAvatarRecordingActive(true)
      setAvatarRecordingStatus(`Grabando ${script.label}...`)
      avatarRecordTimerRef.current = window.setInterval(() => {
        setAvatarRecordingDurationMs(Math.max(0, Date.now() - avatarRecordStartedAtRef.current))
      }, 250)
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      avatarRecordStreamRef.current?.getTracks().forEach((track) => track.stop())
      avatarRecordStreamRef.current = null
      avatarMediaRecorderRef.current = null
      if (avatarVideoRef.current) avatarVideoRef.current.srcObject = null
      setAvatarRecordingActive(false)
      setAvatarRecordingStatus(msg.includes('Permission') || msg.includes('denied') ? 'Permiso de camara/microfono denegado.' : `No se pudo iniciar grabacion: ${msg}`)
    }
  }, [avatarRecordingActive, avatarRecordingKind, blobToBase64, stopAvatarRecording])

  const closeAvatarRecorder = useCallback(() => {
    if (avatarRecordingActive) {
      stopAvatarRecording()
      return
    }
    avatarRecordStreamRef.current?.getTracks().forEach((track) => track.stop())
    avatarRecordStreamRef.current = null
    if (avatarVideoRef.current) avatarVideoRef.current.srcObject = null
    if (avatarRecordTimerRef.current) {
      window.clearInterval(avatarRecordTimerRef.current)
      avatarRecordTimerRef.current = null
    }
    setAvatarRecorderOpen(false)
  }, [avatarRecordingActive, stopAvatarRecording])

  const transcribePresenterAudio = useCallback(async (blob: Blob): Promise<string> => {
    const audioBase64 = await blobToBase64(blob)
    const resp = await fetch(`${window.location.origin.replace(/\/+$/, '')}/api/presenter/transcribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        session_id: 'nexus_browser_presenter_mic',
        audio_base64: audioBase64,
        mime: blob.type || 'audio/webm',
        filename: 'presenter-question.webm',
        language: 'es',
        prompt: 'Nexus QCloud Presenter Mode audience question.'
      })
    })
    const data = await resp.json().catch(() => ({}))
    if (!resp.ok || !data?.ok) {
      throw new Error(String(data?.error || data?.detail || `HTTP_${resp.status}`))
    }
    return String(data.text || '').trim()
  }, [blobToBase64])

  const startPresenterAudioRecorder = useCallback(async (autoMode = false): Promise<boolean> => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') return false
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
      const preferredMime = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/mpeg'
      ].find((mime) => {
        try {
          return MediaRecorder.isTypeSupported(mime)
        } catch {
          return false
        }
      }) || ''
      const recorder = new MediaRecorder(stream, preferredMime ? { mimeType: preferredMime } : undefined)
      presenterMediaStreamRef.current = stream
      presenterMediaRecorderRef.current = recorder
      presenterAudioChunksRef.current = []
      presenterDiscardAudioRef.current = false
      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) presenterAudioChunksRef.current.push(event.data)
      }
      recorder.onerror = (event: any) => {
        setPresenterMicStatus(`Micrófono: ${String(event?.error?.message || event?.error || 'recording_error')}`)
      }
      recorder.onstop = async () => {
        const discard = presenterDiscardAudioRef.current
        const chunks = presenterAudioChunksRef.current.slice()
        const mime = recorder.mimeType || preferredMime || 'audio/webm'
        presenterMediaRecorderRef.current = null
        presenterAudioChunksRef.current = []
        presenterMediaStreamRef.current?.getTracks().forEach((track) => track.stop())
        presenterMediaStreamRef.current = null
        if (presenterMicTimerRef.current) {
          window.clearTimeout(presenterMicTimerRef.current)
          presenterMicTimerRef.current = null
        }
        setPresenterListening(false)
        if (discard) {
          setPresenterMicStatus('')
          setPresenterAvatarState('idle')
          return
        }
        if (!chunks.length) {
          setPresenterMicStatus(autoMode ? 'Auto Q · no escuché audio suficiente.' : 'No escuché audio suficiente.')
          setPresenterAvatarState('idle')
          handlePresenterSpokenInput('', autoMode)
          return
        }
        setPresenterAvatarState('thinking')
        setPresenterMicStatus(autoMode ? 'Auto Q · transcribiendo pregunta...' : 'Mic detenido · transcribiendo pregunta con OpenAI...')
        try {
          const transcript = await transcribePresenterAudio(new Blob(chunks, { type: mime }))
          setPresenterQuestion(transcript)
          setPresenterMicStatus(transcript ? `Pregunta enviada por voz: "${transcript}"` : 'No escuché una pregunta completa.')
          setPresenterAvatarState(transcript ? 'thinking' : 'idle')
          handlePresenterSpokenInput(transcript, autoMode)
        } catch (error) {
          const msg = error instanceof Error ? error.message : String(error)
          setPresenterMicStatus(`Transcripción no disponible: ${msg}. Puedes escribir la pregunta.`)
          setPresenterAvatarState('fallback_audio_only')
          if (autoMode) handlePresenterSpokenInput('', true)
        }
      }
      recorder.start()
      setPresenterListening(true)
      setPresenterAvatarState('listening')
      setPresenterMicStatus(autoMode ? 'Auto Q · te escucho por 5 segundos.' : 'Mic ON · grabando pregunta (20s max). Presiona Stop para enviar.')
      presenterMicTimerRef.current = window.setTimeout(() => {
        try {
          if (presenterMediaRecorderRef.current?.state === 'recording') presenterMediaRecorderRef.current.stop()
        } catch {}
      }, autoMode ? 5000 : 20000)
      return true
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error)
      setPresenterMicStatus(msg.includes('denied') || msg.includes('Permission') ? 'Permiso de micrófono denegado.' : `Micrófono: ${msg}`)
      presenterMediaStreamRef.current?.getTracks().forEach((track) => track.stop())
      presenterMediaStreamRef.current = null
      presenterMediaRecorderRef.current = null
      setPresenterListening(false)
      setPresenterAvatarState('error')
      return false
    }
  }, [handlePresenterSpokenInput, transcribePresenterAudio])

  const handlePresenterMicToggle = useCallback(async () => {
    if (presenterListening) {
      setPresenterMicStatus('Mic detenido · preparando transcripción...')
      presenterAutoListeningRef.current = false
      stopPresenterMic(false)
      return
    }

    setPresenterQuestion('')
    presenterAutoListeningRef.current = false
    clearPresenterAutoListenTimer()
    setPresenterAvatarState('listening')
    setPresenterMicStatus('Solicitando permiso de micrófono...')
    const recorderStarted = await startPresenterAudioRecorder()
    if (!recorderStarted) startPresenterSpeechRecognitionFallback()
  }, [clearPresenterAutoListenTimer, presenterListening, startPresenterAudioRecorder, startPresenterSpeechRecognitionFallback, stopPresenterMic])

  const schedulePresenterAutoListen = useCallback(() => {
    clearPresenterAutoListenTimer()
    if (!presenterAutoListenEnabled || presenterListening || demoPaused || !demoPresenterOpen) return

    const script = demoScript || FALLBACK_DEMO_SCRIPT
    const step = script.steps[Math.max(0, Math.min(demoStepIndex, script.steps.length - 1))]
    if (!step || step.listenAfter === false) return

    const maxSeconds = typeof step.maxNarrationSeconds === 'number' && Number.isFinite(step.maxNarrationSeconds)
      ? step.maxNarrationSeconds
      : 10
    const delayMs = Math.min(Math.max(maxSeconds * 1000 + 500, 4500), 45000)
    setPresenterMicStatus(`Auto Q armado · escucharé después de este módulo.`)
    presenterAutoListenTimerRef.current = window.setTimeout(async () => {
      if (!presenterAutoListenEnabled || presenterListening || demoPaused || !demoPresenterOpen) return
      presenterAutoListeningRef.current = true
      setPresenterQuestion('')
      setPresenterAvatarState('listening')
      setPresenterMicStatus('Auto Q · ¿Tienes alguna pregunta sobre este módulo? Te escucho.')
      const recorderStarted = await startPresenterAudioRecorder(true)
      if (!recorderStarted) startPresenterSpeechRecognitionFallback(true)
    }, delayMs)
  }, [
    clearPresenterAutoListenTimer,
    demoPaused,
    demoPresenterOpen,
    demoScript,
    demoStepIndex,
    presenterAutoListenEnabled,
    presenterListening,
    startPresenterAudioRecorder,
    startPresenterSpeechRecognitionFallback
  ])

  useEffect(() => () => {
    stopPresenterMic(true)
    clearPresenterAutoListenTimer()
    stopPresenterAvatarLipSync('Avatar sync listo.')
  }, [clearPresenterAutoListenTimer, stopPresenterAvatarLipSync, stopPresenterMic])

  useEffect(() => () => {
    try {
      avatarRecordStreamRef.current?.getTracks().forEach((track) => track.stop())
    } catch {}
    if (avatarRecordTimerRef.current) {
      window.clearInterval(avatarRecordTimerRef.current)
      avatarRecordTimerRef.current = null
    }
  }, [])

  const openInternalTab = useCallback((urlValue: string, titleValue?: string) => {
    try {
      const parsedUrl = new URL(urlValue, window.location.origin)
      if (
        NEXUS_PRODUCT_PROFILE === 'school' &&
        parsedUrl.origin === window.location.origin &&
        (
          parsedUrl.pathname === '/academy' ||
          parsedUrl.pathname.startsWith('/academy/') ||
          parsedUrl.pathname === '/api/academy' ||
          parsedUrl.pathname.startsWith('/api/academy/')
        )
      ) {
        throw new Error('Academy route disabled by School product profile')
      }
      const url = parsedUrl.toString()
      const title = (titleValue || inferInternalTabTitle(url)).trim()
      const existingKey = normalizeInternalTabKey(url)

      setInternalTabs((tabs) => {
        const existing = tabs.find((tab) => normalizeInternalTabKey(tab.url) === existingKey)
        if (existing) {
          setActiveInternalTabId(existing.id)
          return tabs.map((tab) => (tab.id === existing.id ? { ...tab, title, url } : tab))
        }
        const tab = {
          id: `tab-${Date.now()}-${Math.random().toString(16).slice(2)}`,
          title,
          url
        }
        setActiveInternalTabId(tab.id)
        return sortInternalTabsByStoredOrder([...tabs, tab])
      })
    } catch (error) {
      console.error('[NexusBrowser] internal tab url invalid:', urlValue, error)
    }
  }, [])

  const closeInternalTab = useCallback((tabId: string) => {
    setInternalTabs((tabs) => {
      const idx = tabs.findIndex((tab) => tab.id === tabId)
      const next = tabs.filter((tab) => tab.id !== tabId)
      if (activeInternalTabId === tabId) {
        const fallback = next[idx - 1] || next[idx] || null
        setActiveInternalTabId(fallback ? fallback.id : 'home')
      }
      return next
    })
  }, [activeInternalTabId])

  const reorderInternalTab = useCallback((sourceId: string, targetId: string) => {
    if (!sourceId || !targetId || sourceId === targetId) return
    setInternalTabs((tabs) => {
      const sourceIndex = tabs.findIndex((tab) => tab.id === sourceId)
      const targetIndex = tabs.findIndex((tab) => tab.id === targetId)
      if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) return tabs

      const next = [...tabs]
      const [moving] = next.splice(sourceIndex, 1)
      next.splice(targetIndex, 0, moving)
      return next
    })
  }, [])

  const clearInternalTabDrag = useCallback(() => {
    internalTabDragStateRef.current = null
    setDraggingInternalTabId('')
  }, [])

  const handleInternalTabPointerDown = useCallback((event: ReactPointerEvent<HTMLButtonElement>, tabId: string) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return
    const target = event.target as HTMLElement | null
    if (target?.closest('.nexus-tab-close')) return

    internalTabDragStateRef.current = {
      tabId,
      pointerId: event.pointerId,
      startX: event.clientX,
      hasMoved: false,
      lastTargetId: tabId
    }
    setDraggingInternalTabId(tabId)
    try {
      event.currentTarget.setPointerCapture(event.pointerId)
    } catch {}
  }, [])

  const handleInternalTabPointerMove = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = internalTabDragStateRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    if (!drag.hasMoved && Math.abs(event.clientX - drag.startX) < 8) return
    drag.hasMoved = true
    suppressInternalTabClickRef.current = true

    const element = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null
    const target = element?.closest('[data-nexus-tab-id]') as HTMLElement | null
    const targetId = target?.dataset?.nexusTabId || ''
    if (!targetId || targetId === drag.tabId || targetId === drag.lastTargetId) return

    drag.lastTargetId = targetId
    reorderInternalTab(drag.tabId, targetId)
  }, [reorderInternalTab])

  const handleInternalTabPointerEnd = useCallback((event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = internalTabDragStateRef.current
    if (drag?.pointerId === event.pointerId) {
      try {
        event.currentTarget.releasePointerCapture(event.pointerId)
      } catch {}
      clearInternalTabDrag()
    }
  }, [clearInternalTabDrag])

  const handleInternalTabClick = useCallback((tabId: string) => {
    if (suppressInternalTabClickRef.current) {
      suppressInternalTabClickRef.current = false
      return
    }
    setActiveInternalTabId(tabId)
  }, [])

  useEffect(() => {
    try {
      window.localStorage.setItem(
        INTERNAL_TAB_ORDER_STORAGE_KEY,
        JSON.stringify(internalTabs.map((tab) => normalizeInternalTabKey(tab.url)))
      )
    } catch {}
  }, [internalTabs])

  const handleIframeLoad = useCallback(() => {
    const doc = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document
    if (!doc) return
    try {
      if (stage === 'app') {
        if (appSrc.startsWith('/ipad-workspace/')) {
          injectJennyTopBar(doc, { tagLabel: 'Nexus Quantum Bridge' })
        } else {
          injectJennyTopBar(doc)
          injectRoleOverlay(doc)
        }
        injectNexusInfoHomeLinks(doc, openInternalTab)
        if (academyExperienceActive) injectAcademyCanvasView(doc)
        injectDigitalMirrorLauncher(doc)
        if (demoModeEnabled && !academyExperienceActive) injectDemoPresenterBridge(doc)
        injectMirrorWebSocket(doc)
        injectDistrictMirrorStateBridge(doc)
        syncSessionToIframe()
        if (districtMirrorState) postDistrictMirrorStateToIframe(districtMirrorState)
        if (appStreamOpen) {
          window.setTimeout(() => propagateEldersOverlayContext(readActiveSessionIdentity()), 80)
        }
      }
      if (stage === 'auth') injectHubWebauthnBridge(doc)
    } catch (error) {
      console.error('[NexusBrowser] no se pudo inyectar bridge:', (error as Error).message)
    }
  }, [academyExperienceActive, appSrc, appStreamOpen, demoModeEnabled, districtMirrorState, openInternalTab, postDistrictMirrorStateToIframe, propagateEldersOverlayContext, stage])

  const syncSessionToIframe = useCallback(() => {
    const iframeWin = iframeRef.current?.contentWindow
    if (!iframeWin) return
    try {
      const email =
        String(
          window.localStorage.getItem('NEXUS_SESSION_EMAIL') ||
            window.localStorage.getItem('NEXUS_LAST_EMAIL') ||
            window.localStorage.getItem('NEXUS_EMAIL') ||
            window.localStorage.getItem('NEXUS_CLOUD_OWNER_EMAIL') ||
            ''
        )
          .trim()
          .toLowerCase() || ''

      const lease = String(window.localStorage.getItem('NEXUS_SESSION_LEASE') || '').trim()
      const name = String(
        window.localStorage.getItem('NEXUS_SESSION_NAME') ||
          window.localStorage.getItem('NEXUS_USER_NAME') ||
          ''
      ).trim()
      const role = String(
        window.localStorage.getItem('NEXUS_SESSION_ROLE') ||
          window.localStorage.getItem('NEXUS_LAST_ROLE') ||
          ''
      )
        .trim()
        .toLowerCase()
      const academyRuntime = academyExperienceActive ? readAcademyRuntimePayload() : {}
      const academyRole = academyRuntime.tenant_type === ACADEMY_TENANT_PROFILE.tenant_type ? 'student' : ''

      iframeWin.postMessage(
        {
          __nexusBrowser: true,
          type: 'nexus-session-sync',
          email,
          name,
          role: academyRole || role,
          product_mode: academyExperienceActive ? 'academy' : 'school',
          session_lease: lease,
          core_session_id: String(window.localStorage.getItem('NEXUS_CORE_SESSION_ID') || '').trim(),
          core_session_route: (() => {
            try {
              return JSON.parse(window.localStorage.getItem('NEXUS_CORE_SESSION_ROUTE') || '{}')
            } catch {
              return {}
            }
          })(),
          ...academyRuntime
        },
        '*'
      )
    } catch {
      // ignore
    }
  }, [academyExperienceActive])

  useEffect(() => {
    const pending = new Map<
      string,
      {
        resolve: (value: any) => void
        reject: (reason?: any) => void
        timeoutId: number
      }
    >()

    const isTrustedSource = (event: MessageEvent) => {
      const iframeWin = iframeRef.current?.contentWindow
      return (
        event.origin === window.location.origin &&
        !!iframeWin &&
        event.source === iframeWin
      )
    }

    const bufToB64url = (buf: ArrayBuffer) => {
      const bytes = new Uint8Array(buf)
      let binary = ''
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i] ?? 0)
      const base64 = window.btoa(binary)
      return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
    }

    const b64urlToBuf = (str: string) => {
      const s = (str || '').replace(/-/g, '+').replace(/_/g, '/')
      const pad = s.length % 4 ? '='.repeat(4 - (s.length % 4)) : ''
      const raw = window.atob(s + pad)
      const out = new Uint8Array(raw.length)
      for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
      return out.buffer
    }

    const postJson = async (url: string, body: any) => {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body || {})
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        const msg = (data as any)?.error ? String((data as any).error) : `HTTP_${res.status}`
        throw new Error(msg)
      }
      return data
    }

    const webauthnRegister = async (email: string) => {
      const opts = (await postJson('/auth/webauthn/register/options', { email })) as any
      if (!opts?.publicKey || !opts?.token) throw new Error('WEBAUTHN_OPTIONS_INVALID')

      const pk = opts.publicKey
      pk.challenge = b64urlToBuf(pk.challenge)
      pk.user = pk.user || {}
      pk.user.id = b64urlToBuf(pk.user.id)
      if (Array.isArray(pk.excludeCredentials)) {
        pk.excludeCredentials = pk.excludeCredentials.map((c: any) => ({
          ...c,
          id: b64urlToBuf(c.id)
        }))
      }

      const cred = (await navigator.credentials.create({ publicKey: pk })) as any
      if (!cred) throw new Error('WEBAUTHN_CREATE_FAILED')

      const payload = {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        response: {
          clientDataJSON: bufToB64url(cred.response.clientDataJSON),
          attestationObject: bufToB64url(cred.response.attestationObject)
        }
      }

      return (await postJson('/auth/webauthn/register/verify', {
        token: opts.token,
        email,
        credential: payload
      })) as any
    }

    const webauthnAssert = async (email: string) => {
      const opts = (await postJson('/auth/webauthn/auth/options', { email })) as any
      if (!opts?.publicKey || !opts?.token) throw new Error('WEBAUTHN_OPTIONS_INVALID')

      const pk = opts.publicKey
      pk.challenge = b64urlToBuf(pk.challenge)
      if (Array.isArray(pk.allowCredentials)) {
        pk.allowCredentials = pk.allowCredentials.map((c: any) => ({
          ...c,
          id: b64urlToBuf(c.id)
        }))
      }

      const cred = (await navigator.credentials.get({ publicKey: pk })) as any
      if (!cred) throw new Error('WEBAUTHN_GET_FAILED')

      const payload = {
        id: cred.id,
        rawId: bufToB64url(cred.rawId),
        type: cred.type,
        response: {
          clientDataJSON: bufToB64url(cred.response.clientDataJSON),
          authenticatorData: bufToB64url(cred.response.authenticatorData),
          signature: bufToB64url(cred.response.signature),
          userHandle: cred.response.userHandle ? bufToB64url(cred.response.userHandle) : null
        }
      }

      return (await postJson('/auth/webauthn/auth/verify', {
        token: opts.token,
        email,
        credential: payload
      })) as any
    }

    const onMessage = async (event: MessageEvent) => {
      if (!isTrustedSource(event)) return
      const msg = event.data as any
      if (!msg) return

      if (msg.__nexusBrowserAuth === true && msg.type === 'auth-success') {
        try {
          const sessionRole = isParentAccessFlow ? 'parent_guest' : academyExperienceActive ? 'student' : String(msg.role || '')
          if (msg.email) window.localStorage.setItem('NEXUS_SESSION_EMAIL', String(msg.email))
          if (msg.email) window.localStorage.setItem('NEXUS_LAST_EMAIL', String(msg.email))
          if (msg.email) window.localStorage.setItem('NEXUS_EMAIL', String(msg.email))
          if (msg.name) window.localStorage.setItem('NEXUS_USER_NAME', String(msg.name))
          if (sessionRole) window.localStorage.setItem('NEXUS_SESSION_ROLE', sessionRole)
          else window.localStorage.removeItem('NEXUS_SESSION_ROLE')
          if (msg.session_lease) window.localStorage.setItem(SESSION_KEY, String(msg.session_lease))
          if (msg.core_session_id) window.localStorage.setItem('NEXUS_CORE_SESSION_ID', String(msg.core_session_id))
          if (academyEntryActive) persistAcademyRuntimeContext(getEntryParams())
          if (hasInstitutionalTabletEntry()) {
            persistInstitutionalTabletContext(getEntryParams(), sessionRole)
          }
          persistInstitutionalSessionMetadata(msg as Record<string, unknown>)
        } catch {
          // ignore
        }
        setLiveSession(readParentSession())
        if (isParentAccessFlow) setCameraPreviewRole('parent_guest')
        setDemoModeEnabled(isDemoModeEnabled())
        goApp()
        return
      }

      if (msg.__nexusBrowserAuth === true && msg.type === 'core-session-ready') {
        try {
          if (msg.core_session_id) window.localStorage.setItem('NEXUS_CORE_SESSION_ID', String(msg.core_session_id))
          if (msg.route) window.localStorage.setItem('NEXUS_CORE_SESSION_ROUTE', JSON.stringify(msg.route))
          if (academyEntryActive) {
            persistAcademyRuntimeContext(getEntryParams())
            window.localStorage.setItem('NEXUS_SESSION_ROLE', 'student')
            window.localStorage.setItem('NEXUS_LAST_ROLE', 'student')
          } else if (isParentAccessFlow) window.localStorage.setItem('NEXUS_SESSION_ROLE', 'parent_guest')
          else if (msg.user?.role) window.localStorage.setItem('NEXUS_SESSION_ROLE', String(msg.user.role))
          if (msg.user?.email) window.localStorage.setItem('NEXUS_SESSION_EMAIL', String(msg.user.email))
          if (hasInstitutionalTabletEntry()) {
            persistInstitutionalTabletContext(getEntryParams(), String(msg.user?.role || ''))
          }
          persistInstitutionalSessionMetadata(msg as Record<string, unknown>)
        } catch {
          // ignore
        }
        setLiveSession(readParentSession())
        if (isParentAccessFlow) setCameraPreviewRole('parent_guest')
        setDemoModeEnabled(isDemoModeEnabled())
        return
      }

      if (msg.__nexusBrowser === true && msg.type === 'open-internal-tab') {
        if (event.origin !== window.location.origin) return
        const url = typeof msg.url === 'string' ? msg.url.trim() : ''
        if (!url) return
        openInternalTab(url, typeof msg.title === 'string' ? msg.title : undefined)
        return
      }

      if (msg.__nexusBrowser === true && msg.type === 'nexus-open-digital-mirror') {
        if (event.origin !== window.location.origin) return
        openCameraPreviewPanel()
        return
      }

      if (msg.__nexusBrowser === true && msg.type === 'nexus-academy-grade-change') {
        if (!academyExperienceActive || String(msg.product_mode || '').trim().toLowerCase() !== 'academy') return
        const grade = normalizeAcademyGrade(msg.grade)
        handleAcademyGradeChange(grade)
        return
      }

      if (msg.__nexusBrowser === true && msg.type === 'nexus-presenter-native-speak') {
        try {
          const speakPresenterText = window.nexusBridge?.speakPresenterText
          const result = speakPresenterText
            ? await speakPresenterText((msg.payload || {}) as Record<string, unknown>)
            : { ok: false, error: 'NEXUS_BRIDGE_UNAVAILABLE' }
          ;(event.source as Window).postMessage(
            { __nexusBrowser: true, type: 'nexus-presenter-native-speak-response', id: msg.id, result },
            event.origin
          )
        } catch (e: any) {
          ;(event.source as Window).postMessage(
            { __nexusBrowser: true, type: 'nexus-presenter-native-speak-response', id: msg.id, result: { ok: false, error: String(e?.message || e) } },
            event.origin
          )
        }
        return
      }

      if (msg.__nexusBrowser === true && msg.type === 'nexus-presenter-openai-audio') {
        const payload = (msg.payload || {}) as Record<string, unknown>
        void startPresenterAvatarLipSync(payload)
        try {
          const playPresenterAudio = window.nexusBridge?.playPresenterAudio
          const result = playPresenterAudio
            ? await playPresenterAudio(payload)
            : { ok: false, error: 'NEXUS_BRIDGE_UNAVAILABLE' }
          if (!result?.ok) stopPresenterAvatarLipSync('Avatar sync detenido: audio no disponible.')
          ;(event.source as Window).postMessage(
            { __nexusBrowser: true, type: 'nexus-presenter-openai-audio-response', id: msg.id, result },
            event.origin
          )
        } catch (e: any) {
          stopPresenterAvatarLipSync('Avatar sync detenido: error de audio.')
          ;(event.source as Window).postMessage(
            { __nexusBrowser: true, type: 'nexus-presenter-openai-audio-response', id: msg.id, result: { ok: false, error: String(e?.message || e) } },
            event.origin
          )
        }
        return
      }

      if (msg.__nexusBrowser === true && msg.type === 'nexus-presenter-native-stop') {
        try {
          await window.nexusBridge?.stopPresenterSpeech?.()
        } catch {
          // best-effort presenter audio stop
        }
        stopPresenterAvatarLipSync('Avatar sync detenido.')
        return
      }

      if (msg.type === 'nexus-demo-presenter-voice-status') {
        const engine = typeof msg.engine === 'string' ? msg.engine : ''
        const voiceName = typeof msg.voiceName === 'string' ? msg.voiceName : ''
        const model = typeof msg.model === 'string' ? msg.model : ''
        const error = typeof msg.error === 'string' ? msg.error : ''
        const phase = typeof msg.phase === 'string' ? msg.phase : ''
        if (error) {
          setPresenterVoiceStatus(`Voice error · ${error}`)
          presenterAvatarAdapter.fallbackToVoiceOnly(error)
        } else if (phase === 'generating') {
          setPresenterVoiceStatus('Voice On · generating narration...')
          setPresenterAvatarState('thinking')
        } else if (phase === 'playing') {
          setPresenterVoiceStatus(`Voice playing · ${voiceName || 'presenter'}${engine ? ` · ${engine}` : ''}${model ? ` · ${model}` : ''}`)
          presenterAvatarAdapter.startSpeaking()
          schedulePresenterAutoListen()
        } else {
          setPresenterVoiceStatus(`Voice ready · ${voiceName || 'presenter'}${engine ? ` · ${engine}` : ''}`)
          setPresenterAvatarState('idle')
          stopPresenterAvatarLipSync('Avatar sync listo.')
        }
        return
      }

      // Screenshot request from iframe (Eugene needs vision)
      if (msg.type === 'nexus-screenshot-request') {
        try {
          const captureScreenshot = window.nexusBridge?.captureScreenshot
          const dataUrl = captureScreenshot ? await captureScreenshot() : null
          ;(event.source as Window).postMessage(
            { type: 'nexus-screenshot-response', dataUrl },
            event.origin
          )
        } catch {
          ;(event.source as Window).postMessage(
            { type: 'nexus-screenshot-response', dataUrl: null },
            event.origin
          )
        }
        return
      }

      if (msg.__nexusWebauthn !== true) return

      // Responses
      if (msg.type === 'response' && typeof msg.id === 'string') {
        const rec = pending.get(msg.id)
        if (!rec) return
        pending.delete(msg.id)
        window.clearTimeout(rec.timeoutId)
        if (msg.ok) rec.resolve(msg.data)
        else rec.reject(new Error(String(msg.error || 'WEBAUTHN_FAILED')))
        return
      }

      // Requests from iframe
      if (msg.type === 'request' && typeof msg.id === 'string') {
        try {
          const email = String(msg.email || '').trim().toLowerCase()
          if (!email) throw new Error('EMAIL_REQUIRED')
          if (!window.isSecureContext) throw new Error('NOT_SECURE_CONTEXT')

          let data: any
          if (msg.kind === 'register') data = await webauthnRegister(email)
          else if (msg.kind === 'auth') data = await webauthnAssert(email)
          else throw new Error('KIND_INVALID')

          ;(event.source as Window).postMessage(
            { __nexusWebauthn: true, type: 'response', id: msg.id, ok: true, data },
            event.origin
          )
        } catch (e: any) {
          const err = e?.message ? String(e.message) : String(e)
          ;(event.source as Window).postMessage(
            { __nexusWebauthn: true, type: 'response', id: msg.id, ok: false, error: err },
            event.origin
          )
        }
      }
    }

    window.addEventListener('message', onMessage)
    return () => {
      window.removeEventListener('message', onMessage)
      for (const rec of pending.values()) window.clearTimeout(rec.timeoutId)
      pending.clear()
    }
  }, [academyEntryActive, academyExperienceActive, handleAcademyGradeChange, isParentAccessFlow, openCameraPreviewPanel, openInternalTab, presenterAvatarAdapter, schedulePresenterAutoListen, startPresenterAvatarLipSync, stopPresenterAvatarLipSync])

  useEffect(() => {
    if (stage !== 'auth') return

    const maybeAdvance = () => {
      if (hasSessionLease()) {
        goApp()
      }
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key === SESSION_KEY && event.newValue) {
        maybeAdvance()
      }
    }

    window.addEventListener('storage', onStorage)
    const t = window.setInterval(maybeAdvance, 350)
    maybeAdvance()

    return () => {
      window.removeEventListener('storage', onStorage)
      window.clearInterval(t)
    }
  }, [stage])

  // Screenshots are requested on demand by Jenny. Periodic push contaminated chat turns.
  const activeDemoScript = demoScript || FALLBACK_DEMO_SCRIPT
  const activeDemoStep = activeDemoScript.steps[Math.max(0, Math.min(demoStepIndex, activeDemoScript.steps.length - 1))]
  const activeAvatarRecordingScript = AVATAR_RECORDING_SCRIPTS.find((item) => item.kind === avatarRecordingKind) || AVATAR_RECORDING_SCRIPTS[0]
  const activePresenterAvatarMediaSrc = PRESENTER_AVATAR_MEDIA_SOURCES[presenterAvatarLanguage]
  const activePresenterAvatarPosterSrc = PRESENTER_AVATAR_POSTER_SOURCES[presenterAvatarLanguage]
  const activePresenterAvatarLanguageLabel = PRESENTER_AVATAR_LANGUAGE_LABELS[presenterAvatarLanguage]
  const avatarRecordingSeconds = Math.max(0, Math.floor(avatarRecordingDurationMs / 1000))
  const isLastDemoStep = demoStepIndex >= activeDemoScript.steps.length - 1
  const demoProgressPercent = activeDemoScript.steps.length
    ? Math.round(((demoStepIndex + 1) / activeDemoScript.steps.length) * 100)
    : 0
  const demoVoiceLocked = activeDemoScript.voice?.enabled === false
  // Visual-only District identity is derived from a validated synthetic
  // snapshot. It never replaces the persisted school session or role.
  const districtVisualPresence = useMemo<ShellPresence | null>(() => {
    if (!isDistrictMirrorState(districtMirrorState)) return null
    const snapshot = districtSnapshotFromMirrorState(districtMirrorState)
    return {
      count: 1,
      label: snapshot.visual_actor,
      tooltip: `${snapshot.visual_actor} · ${snapshot.visual_role} · synthetic read-only context`,
    }
  }, [districtMirrorState])
  const displayedShellPresence = districtVisualPresence || shellPresence
  const shellPresenceTitle = displayedShellPresence.tooltip || displayedShellPresence.label
  return (
    <div className={`genui-wrapper ${stage === 'app' && !parentPortalActive && !institutionalTabletReactActive && internalTabs.length ? 'has-internal-tabs' : ''} ${parentPortalActive || stage === 'guestRelease' || stage === 'tabletAccess' || institutionalTabletReactActive ? 'parent-portal-active' : ''} ${parentAuthActive ? 'parent-auth-active' : ''}`}>
      {stage !== 'landing' && stage !== 'browserGate' && stage !== 'profileDenied' && stage !== 'parentAccess' && stage !== 'guestRelease' && stage !== 'tabletAccess' && !parentPortalActive && !institutionalTabletReactActive && (
        <iframe
          key={`${stage}-${authFlow}-${key}`}
          src={stage === 'app' ? appSrc : authSrc}
          title={parentAuthActive ? 'Parent Guest Access' : (stage === 'app' ? 'Jenny UI (Workbench)' : 'Nexus Quantum Hub (Auth)')}
          className={`genui-frame ${stage === 'app' && activeInternalTabId !== 'home' ? 'is-hidden' : ''} ${stage === 'app' && internalTabs.length ? 'with-tabs' : ''}`}
          sandbox={isIpadGuestWorkspaceFrame ? undefined : 'allow-scripts allow-same-origin allow-forms allow-pointer-lock'}
          allow="camera; microphone; display-capture; publickey-credentials-get; publickey-credentials-create"
          ref={iframeRef}
          onLoad={handleIframeLoad}
        />
      )}

      {stage === 'landing' && (
        <Landing heroSrc={new URL('landing-hero.png', window.location.origin + baseUrl).pathname} />
      )}

      {stage === 'browserGate' && (
        <BrowserGate
          status={browserGateStatus}
          onRetry={() => setKey((k) => k + 1)}
          onSignIn={() => goAuth('signin')}
          onContinue={openApp}
        />
      )}

      {stage === 'profileDenied' && (
        <section className="browser-gate" role="alert" aria-live="assertive">
          <div className="browser-gate-panel">
            <span className="browser-gate-kicker">Nexus Browser School</span>
            <h1>Esta instalación no incluye Nexus Academy.</h1>
            <p>La ruta solicitada pertenece a otro producto. Inicia sesión desde el acceso escolar autorizado.</p>
            <button className="control-btn" onClick={() => { window.history.replaceState({}, '', '/'); setStage('landing') }}>
              Volver a School
            </button>
          </div>
        </section>
      )}

      {stage === 'parentAccess' && (
        <ParentGuestAccess
          inviteToken={inviteToken}
          entryParams={entryParams}
          onContinue={goParentSignup}
          onAuthenticated={() => {
            setLiveSession(readParentSession())
            setDemoModeEnabled(false)
            setCameraPreviewRole('parent_guest')
            goApp()
          }}
          onCancel={goLanding}
        />
      )}

      {stage === 'tabletAccess' && (
        <InstitutionalTabletAccess
          entryParams={entryParams}
          onSignIn={() => {
            setAuthFlow('signin')
            setStage('auth')
            setKey((k) => k + 1)
          }}
        />
      )}

      {stage === 'guestRelease' && (
        <GuestReleasePortal token={guestReleaseTokenFromRoute()} />
      )}

      {parentPortalActive && (
        <ParentGuestPortal
          session={liveSession}
          onLogout={handleLogout}
        />
      )}

      {institutionalTabletReactActive && (
        <InstitutionalTabletPortal
          session={liveSession}
          onLogout={handleLogout}
        />
      )}

      {stage === 'app' && !parentPortalActive && internalTabs.length > 0 && (
        <div className="nexus-tab-shell">
          <div className="nexus-tab-strip">
            <button
              className={`nexus-tab nexus-tab-home ${activeInternalTabId === 'home' ? 'active' : ''}`}
              onClick={() => setActiveInternalTabId('home')}
              title="Volver a Eugene"
            >
              Eugene
            </button>
            {internalTabs.map((tab) => (
              <button
                key={tab.id}
                className={`nexus-tab ${activeInternalTabId === tab.id ? 'active' : ''} ${draggingInternalTabId === tab.id ? 'dragging' : ''}`}
                data-nexus-tab-id={tab.id}
                onClick={() => handleInternalTabClick(tab.id)}
                onPointerDown={(event) => handleInternalTabPointerDown(event, tab.id)}
                onPointerMove={handleInternalTabPointerMove}
                onPointerUp={handleInternalTabPointerEnd}
                onPointerCancel={handleInternalTabPointerEnd}
                aria-grabbed={draggingInternalTabId === tab.id}
                title={`${tab.title} · arrastra para ordenar`}
              >
                <span>{tab.title}</span>
                <span
                  className="nexus-tab-close"
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation()
                    closeInternalTab(tab.id)
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      event.stopPropagation()
                      closeInternalTab(tab.id)
                    }
                  }}
                  aria-label={`Cerrar ${tab.title}`}
                >
                  ×
                </span>
              </button>
            ))}
          </div>
          <div className={`nexus-tab-stage ${activeInternalTabId === 'home' ? 'home-active' : ''}`}>
            {internalTabs.map((tab) => (
              <iframe
                key={tab.id}
                src={tab.url}
                title={tab.title}
                className={`nexus-tab-frame ${activeInternalTabId === tab.id ? 'active' : ''}`}
                allow="fullscreen; clipboard-read; clipboard-write"
              />
            ))}
          </div>
        </div>
      )}

      {!parentPortalActive && !parentAuthActive && stage !== 'browserGate' && stage !== 'profileDenied' && stage !== 'parentAccess' && stage !== 'tabletAccess' && !institutionalTabletActive && (
        <>
          <div className="genui-overlay">
            {stage !== 'landing' && (
              <div className="brand">
                <span className="brand-title">Nexus Browser</span>
                <span className="brand-subtitle">Quantum Secure Channel</span>
              </div>
            )}
            {stage !== 'app' && (
              <div className="controls">
                {stage === 'landing' && (
                  <>
                    <button className="control-btn" onClick={() => goAuth('signin')}>
                      Sign In
                    </button>
                    <button className="control-btn" onClick={() => goAuth('signup')}>
                      Sign Up
                    </button>
                  </>
                )}
                {stage !== 'landing' && (
                  <button className="control-btn" onClick={handleReload}>
                    Recargar
                  </button>
                )}
                {stage === 'auth' && (
                  <button className="control-btn" onClick={handleLogout}>
                    Cancelar
                  </button>
                )}
              </div>
            )}
          </div>
          {stage === 'app' && (
            <aside className={`app-control-dock ${appControlDockOpen ? 'open' : 'collapsed'} theme-${appSurfaceTheme}`} aria-label="Nexus Browser tools">
              <div className="app-control-dock-rail">
                <div className="app-session-chip" title={shellPresenceTitle} aria-label={`Usuario activo: ${displayedShellPresence.label}`} aria-live="polite">
                  <span className={`app-session-chip-dot ${displayedShellPresence.count > 1 ? 'active' : ''}`} aria-hidden="true" />
                  <span className="app-session-chip-text">{displayedShellPresence.label}</span>
                </div>
                <button
                  className={`app-control-dock-tab ${appControlDockOpen ? 'active' : ''}`}
                  onClick={() => setAppControlDockOpen((open) => !open)}
                  aria-label={appControlDockOpen ? 'Cerrar herramientas de Nexus Browser' : 'Abrir herramientas de Nexus Browser'}
                  aria-expanded={appControlDockOpen}
                  aria-controls="nexus-app-control-dock-panel"
                  title={appControlDockOpen ? 'Cerrar herramientas' : 'Abrir herramientas'}
                >
                  {appControlDockOpen ? <PanelRightClose size={16} aria-hidden="true" /> : <SlidersHorizontal size={16} aria-hidden="true" />}
                  <span>Tools</span>
                </button>
              </div>
              {appControlDockOpen && (
                <div id="nexus-app-control-dock-panel" className="app-control-dock-panel">
                  <div className="app-control-dock-header">
                    <div>
                      <span className="app-control-dock-kicker">Nexus Browser</span>
                      <strong>Tools</strong>
                    </div>
                    <button className="app-control-icon-btn" onClick={() => setAppControlDockOpen(false)} aria-label="Cerrar herramientas">
                      <X size={16} aria-hidden="true" />
                    </button>
                  </div>
                  {(districtMirrorStatus || demoStatus) && (
                    <div className="app-control-status" role="status">
                      {districtMirrorStatus || demoStatus}
                    </div>
                  )}
                  {DEMO_TOOLS_ENABLED && demoModeEnabled && (
                    <div className="app-control-section">
                      <span className="app-control-section-label">Demo</span>
                      <div className="demo-role-switcher">
                        <select
                          className="control-select"
                          value={demoPersonaId}
                          onChange={(event) => setDemoPersonaId(event.target.value)}
                          disabled={demoBusy}
                        >
                          {AVAILABLE_DEMO_PERSONAS.map((persona) => (
                            <option key={persona.id} value={persona.id}>
                              {persona.label}
                            </option>
                          ))}
                        </select>
                        <button className="control-btn" onClick={handleDemoPersonaSwitch} disabled={demoBusy}>
                          Cambiar Rol
                        </button>
                        {activeDemoPersonaId === 'developer' && (
                          <button className="control-btn" onClick={handleDemoResetSeed} disabled={demoBusy}>
                            Demo Reset+Seed
                          </button>
                        )}
                        <button className={`control-btn ${demoPresenterOpen ? 'active' : ''}`} onClick={handlePresenterStart} disabled={demoBusy || !activeDemoScript.steps.length}>
                          Presenter Mode
                        </button>
                        <button className={`control-btn ${avatarRecorderOpen ? 'active' : ''}`} onClick={() => setAvatarRecorderOpen(true)} disabled={demoBusy}>
                          Avatar Rec
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="app-control-section">
                    <span className="app-control-section-label">Session</span>
                    <div className="app-control-stack">
                      <button className="control-btn" onClick={handleReload}>
                        Recargar
                      </button>
                      {isWindowsClient && uiUpdateAvailable && ['owner', 'root', 'developer'].includes(cameraPreviewRole) && (
                        <button className="control-btn active" onClick={installUiUpdate} disabled={uiUpdateBusy}>
                          {uiUpdateBusy ? 'Actualizando...' : 'Actualizar Nexus'}
                        </button>
                      )}
                      {cameraPreviewPolicy.visible && (
                        <button
                          className={`control-btn camera-preview-control ${cameraPreviewActive ? 'active' : ''}`}
                          onClick={startCameraPreview}
                          title="Vista local de cámara"
                          aria-label="Abrir vista local de cámara"
                        >
                          <Camera size={15} aria-hidden="true" />
                          <span>Cámara</span>
                        </button>
                      )}
                      {appStreamAccessAllowed && (
                        <button
                          className={`control-btn appstream-control ${appStreamOpen ? 'active' : ''}`}
                          onClick={openAppStream}
                          title="Elders Limmerloop"
                          aria-label="Abrir Elders Limmerloop AppStream"
                        >
                          <Monitor size={15} aria-hidden="true" />
                          <span>Elders</span>
                        </button>
                      )}
                      <button className="control-btn" onClick={handleLogout}>
                        Salir
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </aside>
          )}
        </>
      )}
      {isWindowsClient && uiUpdateStatus && cameraPreviewRole !== 'student' && (
        <div className="demo-status-banner">{uiUpdateStatus}</div>
      )}
      {stage === 'app' && appStreamOpen && (
        <section className="appstream-panel" role="dialog" aria-modal="true" aria-label="Elders Limmerloop AppStream">
          <div className="appstream-head">
            <div>
              <div className="appstream-kicker">Elders Limmerloop</div>
              <h2>Nexus AppStream</h2>
            </div>
            <button className="camera-icon-btn" onClick={closeAppStreamPanel} aria-label="Cerrar AppStream">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          <div className="appstream-layout">
            <aside className="appstream-sidebar">
              <div className="appstream-access">
                <Lock size={15} aria-hidden="true" />
                <span>{appStreamIdentity.email || 'authorized session'}</span>
              </div>
              <button
                className={`appstream-app-button ${primaryAppStreamApp?.app_id === 'jw_library' ? 'active' : ''}`}
                onClick={() => startAppStream(primaryAppStreamApp)}
                disabled={appStreamBusy || !primaryAppStreamApp || primaryAppStreamApp.installed === false || appStreamStatus === 'not_allowed'}
              >
                <Play size={15} aria-hidden="true" />
                <span>{primaryAppStreamApp?.display_name || 'JW Library'}</span>
              </button>
              <button className="appstream-app-button" disabled title="Control remoto disponible en Phase 2">
                <MousePointer2 size={15} aria-hidden="true" />
                <span>Request Control (Phase 2)</span>
              </button>
              <button className="appstream-app-button" onClick={stopAppStream} disabled={appStreamBusy || !appStreamSession}>
                <Square size={15} aria-hidden="true" />
                <span>Close Stream</span>
              </button>
              <div className={`appstream-state state-${appStreamStateClass}`}>
                {appStreamBusy ? 'working' : appStreamStateLabel}
              </div>
            </aside>
            <div className="appstream-stage">
              <div className="appstream-canvas-wrap">
                <canvas
                  ref={appStreamCanvasRef}
                  className={`appstream-canvas ${appStreamFrameActive ? 'is-streaming' : ''}`}
                  width={1280}
                  height={800}
                  aria-label="JW Library AppStream canvas"
                />
                <div className={`appstream-placeholder ${appStreamPlaceholderHidden ? 'hidden' : ''}`}>
                  <Monitor size={42} aria-hidden="true" />
                  <strong>{appStreamSession?.display_name || primaryAppStreamApp?.display_name || 'JW Library'}</strong>
                  <span>{appStreamCanvasMessage}</span>
                </div>
              </div>
              <div className="appstream-meta">
                <span>{appStreamSession?.window_only === false ? 'blocked: not window-only' : 'window-only'}</span>
                <span>audio off</span>
                <span>recording off</span>
                <span>{String(primaryAppStreamApp?.streaming?.target_fps || 30)} fps target</span>
                {appStreamSession?.window_name && <span>{appStreamSession.window_name}</span>}
              </div>
              {appStreamError && (
                <div className="appstream-error" role="status">
                  {appStreamError === 'not_allowed'
                    ? 'not allowed'
                    : appStreamError === 'permission_required'
                      ? 'Screen Recording permission required for Nexus AppStream Agent.'
                      : appStreamError}
                </div>
              )}
            </div>
          </div>
        </section>
      )}
      {cameraPreviewOpen && (
        <section ref={cameraPanelRef} className={`camera-preview-panel theme-${appSurfaceTheme}`} role="dialog" aria-modal="true" aria-label="Digital Mirror">
          <div
            className="camera-preview-head"
            data-digital-mirror-drag-handle
            title="Drag to move. Double click to center."
            onPointerDown={handleCameraPreviewPointerDown}
            onPointerMove={handleCameraPreviewPointerMove}
            onPointerUp={handleCameraPreviewPointerEnd}
            onPointerCancel={handleCameraPreviewPointerEnd}
            onDoubleClick={() => centerCameraPreviewPanel(true)}
          >
            <div>
              <h2>Digital Mirror</h2>
              <p>Check your appearance before joining a session.</p>
            </div>
            <button className="camera-icon-btn" onClick={closeCameraPreview} aria-label="Close Digital Mirror">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          <div className={`camera-preview-stage ${cameraPreviewActive ? 'active' : ''}`}>
            <video
              ref={cameraVideoRef}
              className="camera-preview-video"
              autoPlay
              playsInline
              muted
              style={{ transform: `scale(${cameraPreviewZoom}) scaleX(${cameraPreviewMirrored ? -1 : 1})` }}
            />
            {!cameraPreviewActive && (
              <div className="camera-preview-empty">
                {cameraPreviewError || 'Preview your camera privately before a class, meeting, or presentation.'}
              </div>
            )}
            {cameraPreviewActive && (
              <div className="camera-live-indicator">
                <span />
                Mirror on
              </div>
            )}
          </div>
          <div className="camera-preview-meta">
            Private local mirror. Nothing is recorded or uploaded.
          </div>
          <div className="camera-preview-actions">
            <div className="camera-preview-zoom-controls" aria-label="Digital Mirror visual controls">
              <button className="control-btn" onClick={() => adjustCameraPreviewZoom(-0.1)} disabled={!cameraPreviewActive || cameraPreviewZoom <= 1.01}>
                <ZoomOut size={15} aria-hidden="true" />
                Zoom -
              </button>
              <button className="control-btn" onClick={() => adjustCameraPreviewZoom(0.1)} disabled={!cameraPreviewActive || cameraPreviewZoom >= 2.19}>
                <ZoomIn size={15} aria-hidden="true" />
                Zoom +
              </button>
              <button className="control-btn" onClick={resetCameraPreviewView}>
                <RotateCcw size={15} aria-hidden="true" />
                Reset
              </button>
              <button className={`control-btn ${cameraPreviewMirrored ? 'active' : ''}`} onClick={() => setCameraPreviewMirrored((value) => !value)} disabled={!cameraPreviewActive}>
                <FlipHorizontal size={15} aria-hidden="true" />
                Flip
              </button>
            </div>
            <div className="camera-preview-primary-actions">
              {cameraPreviewActive && (
                <button className="control-btn camera-stop-btn" onClick={() => stopCameraPreview('user_stop')}>
                  <CameraOff size={15} aria-hidden="true" />
                  Stop
                </button>
              )}
              {!cameraPreviewActive && (
                <button className="control-btn camera-start-btn" onClick={startCameraPreview}>
                  <Camera size={15} aria-hidden="true" />
                  Start Mirror
                </button>
              )}
              <button className="control-btn" onClick={closeCameraPreview}>
                <X size={15} aria-hidden="true" />
                Close
              </button>
            </div>
          </div>
        </section>
      )}
      {stage === 'app' && demoModeEnabled && avatarRecorderOpen && (
        <section className="avatar-recorder-panel" role="dialog" aria-modal="true" aria-label="Avatar Presenter Recording Studio">
          <div className="avatar-recorder-head">
            <div>
              <div className="avatar-recorder-kicker">Presenter Avatar Studio</div>
              <h2>Graba tu avatar source pack</h2>
              <p>Un botón para grabar, detener y guardar en la ruta segura del Avatar Presenter Layer.</p>
            </div>
            <button className="camera-icon-btn" onClick={closeAvatarRecorder} aria-label="Cerrar grabadora de avatar">
              <X size={18} aria-hidden="true" />
            </button>
          </div>
          <div className="avatar-recorder-layout">
            <div className={`avatar-recorder-preview ${avatarRecordingActive ? 'recording' : ''}`}>
              <video ref={avatarVideoRef} autoPlay playsInline muted />
              {!avatarRecordingActive && !avatarRecordingSavedPath && (
                <div className="avatar-recorder-empty">Camara lista. El preview aparece al grabar.</div>
              )}
              {avatarRecordingActive && (
                <div className="avatar-recorder-live">
                  <span />
                  REC {avatarRecordingSeconds}s
                </div>
              )}
            </div>
            <div className="avatar-recorder-script">
              <div className="avatar-recorder-tabs" aria-label="Seleccionar guion de avatar">
                {AVATAR_RECORDING_SCRIPTS.map((script) => (
                  <button
                    key={script.kind}
                    className={avatarRecordingKind === script.kind ? 'active' : ''}
                    onClick={() => {
                      if (avatarRecordingActive) return
                      setAvatarRecordingKind(script.kind)
                      setAvatarRecordingStatus('Listo para grabar.')
                      setAvatarRecordingSavedPath('')
                      setAvatarRecordingDurationMs(0)
                    }}
                    disabled={avatarRecordingActive}
                  >
                    {script.label}
                  </button>
                ))}
              </div>
              <div className="avatar-recorder-script-card">
                <div className="avatar-recorder-file">{activeAvatarRecordingScript.fileName} · {activeAvatarRecordingScript.durationHint}</div>
                <p>{activeAvatarRecordingScript.body}</p>
              </div>
              <div className="avatar-recorder-actions">
                <button
                  className={`avatar-record-button ${avatarRecordingActive ? 'recording' : ''}`}
                  onClick={startAvatarRecording}
                >
                  {avatarRecordingActive ? 'Detener y guardar' : `Grabar ${activeAvatarRecordingScript.label}`}
                </button>
                <div className="avatar-recorder-status">
                  <span>{avatarRecordingStatus}</span>
                  {avatarRecordingSavedPath && <strong>{avatarRecordingSavedPath}</strong>}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}
      {stage === 'app' && demoModeEnabled && !institutionalTabletActive && demoPresenterOpen && activeDemoStep && (
        <section className={`demo-presenter-panel ${demoPaused ? 'paused' : ''} ${demoPresenterCompact ? 'compact' : ''} ${presenterAvatarShowcase ? 'avatar-showcase' : ''}`} aria-label="Nexus QCloud Demo Presenter Mode">
          <div className="demo-presenter-head">
            <div className="demo-presenter-host">
              <button
                ref={presenterAvatarOrbRef}
                className={`presenter-avatar-orb state-${presenterAvatarState} ${presenterAvatarMediaReady ? 'has-local-visual' : ''} ${presenterAvatarLipSyncActive ? 'lip-sync-active' : ''}`}
                type="button"
                onClick={() => {
                  if (demoPresenterCompact) {
                    setDemoPresenterCompact(false)
                    return
                  }
                  setPresenterAvatarShowcase(false)
                  setDemoPresenterCompact(true)
                }}
                aria-label={demoPresenterCompact ? 'Mostrar controles del Presenter Host' : 'Cambiar modo visual del Presenter Host'}
                title={demoPresenterCompact ? 'Mostrar controles del Presenter Host' : (presenterAvatarShowcase ? 'Volver a modo discreto' : 'Mostrar Presenter Host')}
              >
                <img
                  ref={presenterAvatarVideoRef}
                  className="presenter-avatar-video"
                  src={activePresenterAvatarPosterSrc}
                  alt="Saul Olalde Presenter Avatar"
                  data-source-video={activePresenterAvatarMediaSrc}
                  onLoad={() => {
                    setPresenterAvatarMediaReady(true)
                    setPresenterAvatarMediaStatus(`${activePresenterAvatarLanguageLabel} listo; esperando audio del Presenter.`)
                  }}
                  onError={() => {
                    setPresenterAvatarMediaReady(false)
                    setPresenterAvatarMediaStatus(`${activePresenterAvatarLanguageLabel} no disponible; usando fallback visual.`)
                  }}
                />
                <span className="presenter-avatar-pulse" aria-hidden="true" />
                <span className="presenter-avatar-initials">SO</span>
                <span className="presenter-avatar-local-label">{activePresenterAvatarLanguageLabel}</span>
              </button>
              <div className="presenter-host-copy">
                <div className="demo-presenter-kicker">Avatar Presenter · Local Avatar</div>
                <h2>{demoPresenterCompact && !presenterAvatarShowcase ? activeDemoStep.title : activeDemoScript.title}</h2>
                <p>
                  {presenterListening
                    ? 'Listening for an audience question.'
                    : demoPresenterCompact
                      ? `Step ${demoStepIndex + 1}/${activeDemoScript.steps.length} · ${presenterAvatarStateLabel(presenterAvatarState)} · ${activeDemoStep.role || 'demo'}`
                      : (activeDemoScript.subtitle || 'Eugenie guides this presentation inside Nexus QCloud.')}
                </p>
                <div className="presenter-avatar-meta">
                  <span>{presenterAvatarStateLabel(presenterAvatarState)}</span>
                  <span>{activePresenterAvatarLanguageLabel}</span>
                  <span>Saul Owner</span>
                  <span>{presenterAvatarLipSyncActive ? 'Voice Active' : 'Voice Ready'}</span>
                  <span>{activeDemoStep.avatarEmotion || 'executive'}</span>
                  <span>{presenterAvatarPrompt(activeDemoStep)}</span>
                </div>
              </div>
            </div>
            <div className="demo-presenter-head-actions">
              <button
                className={`demo-mic-toggle ${presenterListening ? 'listening' : ''}`}
                onClick={handlePresenterMicToggle}
                aria-pressed={presenterListening}
                title={presenterListening ? 'Detener escucha' : 'Preguntar por voz al Presenter'}
              >
                <Mic size={14} aria-hidden="true" />
                <span>{presenterListening ? 'Stop' : (demoPresenterCompact ? 'Voice Q' : 'Pregunta voz')}</span>
              </button>
              <button
                className={`demo-auto-toggle ${presenterAutoListenEnabled ? 'enabled' : ''}`}
                onClick={() => {
                  const nextEnabled = !presenterAutoListenEnabled
                  setPresenterAutoListenEnabled(nextEnabled)
                  if (!nextEnabled) {
                    clearPresenterAutoListenTimer()
                    presenterAutoListeningRef.current = false
                    setPresenterMicStatus('')
                  } else {
                    setPresenterMicStatus('Auto Q listo para escuchar despues de la narracion.')
                  }
                }}
                title={presenterAutoListenEnabled ? 'Auto Q activado: escucha despues de cada modulo' : 'Auto Q desactivado'}
              >
                {demoPresenterCompact ? (presenterAutoListenEnabled ? 'Auto Q' : 'Manual Q') : (presenterAutoListenEnabled ? 'Auto Q On' : 'Auto Q Off')}
              </button>
              <button
                className={`demo-avatar-language-toggle ${presenterAvatarLanguage}`}
                onClick={() => setPresenterAvatarLanguage((value) => (value === 'en' ? 'es' : 'en'))}
                title={presenterAvatarLanguage === 'en' ? 'Cambiar avatar visual a español' : 'Switch visual avatar to English'}
              >
                {activePresenterAvatarLanguageLabel}
              </button>
              <button
                className="demo-panel-toggle"
                onClick={() => setDemoPresenterCompact((v) => !v)}
                title={demoPresenterCompact ? 'Expandir controles del presentador' : 'Minimizar controles del presentador'}
              >
                {demoPresenterCompact ? 'Expandir' : 'Minimizar'}
              </button>
              <button
                className={`demo-panel-toggle demo-showcase-toggle ${presenterAvatarShowcase ? 'enabled' : ''}`}
                onClick={() => {
                  if (presenterAvatarShowcase) {
                    setPresenterAvatarShowcase(false)
                    setDemoPresenterCompact(true)
                    return
                  }
                  setPresenterAvatarShowcase(true)
                }}
                title={presenterAvatarShowcase ? 'Modo discreto' : 'Modo showcase'}
              >
                {presenterAvatarShowcase ? 'Discreto' : 'Showcase'}
              </button>
              <button
                className={`demo-voice-toggle ${demoVoiceEnabled && !demoVoiceLocked ? 'enabled' : ''}`}
                disabled={demoVoiceLocked}
                onClick={() => {
                if (demoVoiceLocked) return
                try {
                  window.speechSynthesis?.cancel()
                  void window.nexusBridge?.stopPresenterSpeech?.()
                } catch {}
                setDemoVoiceEnabled((v) => {
                  const next = !v
                  setPresenterAvatarState(next ? 'idle' : 'fallback_audio_only')
                  return next
                })
              }}
                title={demoVoiceLocked ? 'Voz TTS deshabilitada para demo institucional' : (demoVoiceEnabled ? 'Voice On: click to turn presenter voice off' : 'Voice Off: click to turn presenter voice on')}
              >
                {demoVoiceLocked
                  ? (demoPresenterCompact ? 'Chat' : 'Narration: Chat')
                  : demoVoiceEnabled
                    ? (demoPresenterCompact ? 'Voice On' : 'Natural Voice On')
                    : (demoPresenterCompact ? 'Voice Off' : 'Voice Off')}
              </button>
            </div>
          </div>
          {(presenterVoiceStatus || presenterMicStatus || (presenterQuestion && demoPresenterCompact)) && (
            <div className="demo-presenter-live-status">
              {presenterVoiceStatus && <span>{presenterVoiceStatus}</span>}
              {presenterMicStatus && <span>{presenterMicStatus}</span>}
              {presenterQuestion && demoPresenterCompact && <span>Question: {presenterQuestion}</span>}
            </div>
          )}
          {presenterAvatarShowcase && (
            <div className="presenter-avatar-showcase-caption">
              <span>{presenterAvatarLipSyncActive ? presenterAvatarLipSyncStatus : presenterAvatarMediaStatus}</span>
              <strong>{presenterAvatarLipSyncActive ? 'Presenter Avatar · voice active' : 'Presenter Avatar · ready'}</strong>
            </div>
          )}
          {!demoPresenterCompact && demoScriptError && <div className="demo-script-warning">{demoScriptError}</div>}
          <div className="demo-progress-track" aria-hidden="true">
            <span style={{ width: `${demoProgressPercent}%` }} />
          </div>
          {!demoPresenterCompact && (
            <>
              <div className="demo-stage-card">
                <div className="demo-stage-act">{activeDemoStep.stageAct || `Act ${demoStepIndex + 1}`}</div>
                <div className="demo-stage-headline">{activeDemoStep.stageHeadline || activeDemoStep.title}</div>
                {(activeDemoStep.stageSubhead || activeDemoStep.stageRisk) && (
                  <div className="demo-stage-subhead">
                    {activeDemoStep.stageSubhead || activeDemoStep.stageRisk}
                  </div>
                )}
                {Array.isArray(activeDemoStep.stageProof) && activeDemoStep.stageProof.length > 0 && (
                  <div className="demo-stage-proof">
                    {activeDemoStep.stageProof.slice(0, 3).map((proof) => (
                      <span key={proof}>{proof}</span>
                    ))}
                  </div>
                )}
                {activeDemoStep.operatorCue && (
                  <div className="demo-stage-cue">{activeDemoStep.operatorCue}</div>
                )}
              </div>
              <div className="demo-step-card">
                <div className="demo-step-count">Step {demoStepIndex + 1} of {activeDemoScript.steps.length}</div>
                <div className="demo-step-title">{activeDemoStep.title}</div>
                <div className="demo-step-meta">
                  Role: {activeDemoStep.role || 'current'} · View: {activeDemoStep.view || 'current'} · Module: {activeDemoStep.module || 'mapped'}
                </div>
              </div>
              <div className="demo-packet-strip" aria-label="Demo context packet checks">
                {(activeDemoStep.expectedEvidence || []).slice(0, 3).map((item) => (
                  <span key={`evidence-${item}`}>{item}</span>
                ))}
                {(activeDemoStep.qaChecks || []).slice(0, 2).map((item) => (
                  <span key={`qa-${item}`}>{item}</span>
                ))}
              </div>
              <form className="demo-audience-form" onSubmit={handlePresenterQuestionSubmit}>
                <input
                  value={presenterQuestion}
                  onChange={(event) => setPresenterQuestion(event.target.value)}
                  placeholder="Pregunta del publico sobre modulo, rol o flujo"
                  aria-label="Pregunta del publico"
                />
                <button className="control-btn" type="submit" disabled={!presenterQuestion.trim()}>
                  Preguntar
                </button>
              </form>
              {presenterMicStatus && <div className="demo-mic-status">{presenterMicStatus}</div>}
            </>
          )}
          <div className={`demo-presenter-actions ${demoPresenterCompact ? 'compact' : ''}`}>
            <button className="control-btn" onClick={handlePresenterPrevious} disabled={demoPaused || demoStepIndex <= 0}>
              {demoPresenterCompact ? 'Atras' : 'Anterior'}
            </button>
            <button className="control-btn active" onClick={handlePresenterNext} disabled={demoPaused || isLastDemoStep}>
              {demoPresenterCompact ? 'Sig.' : 'Siguiente'}
            </button>
            <button className="control-btn" onClick={handlePresenterPause}>
              {demoPaused ? 'Reanudar' : (demoPresenterCompact ? 'Pausa' : 'Pausar')}
            </button>
            <button className="control-btn" onClick={handlePresenterRepeat}>
              {demoPresenterCompact ? 'Repite' : 'Repetir'}
            </button>
            <button className="control-btn" onClick={handlePresenterRestart}>
              {demoPresenterCompact ? 'Inicio' : 'Reiniciar Demo'}
            </button>
            <button
              className="control-btn demo-presenter-exit"
              onClick={handlePresenterExit}
              aria-label="Salir de Presenter Mode"
              title="Salir de Presenter Mode"
            >
              Salir
            </button>
          </div>
        </section>
      )}
    </div>
  )
}

function cacheBustUrl(urlValue: string): string {
  try {
    const url = new URL(urlValue, window.location.origin)
    url.searchParams.set('__nexus_reload', String(Date.now()))
    return url.toString()
  } catch {
    return urlValue
  }
}

function normalizeInternalTabKey(urlValue: string): string {
  try {
    const url = new URL(urlValue, window.location.origin)
    url.searchParams.delete('__nexus_reload')
    return `${url.origin}${url.pathname}${url.search}${url.hash}`
  } catch {
    return urlValue
  }
}

function readInternalTabOrder(): string[] {
  try {
    const raw = window.localStorage.getItem(INTERNAL_TAB_ORDER_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : []
    return Array.isArray(parsed) ? parsed.filter((item): item is string => typeof item === 'string' && item.trim().length > 0) : []
  } catch {
    return []
  }
}

function sortInternalTabsByStoredOrder(tabs: InternalTab[]): InternalTab[] {
  const order = readInternalTabOrder()
  if (!order.length || tabs.length < 2) return tabs

  const originalIndex = new Map(tabs.map((tab, index) => [tab.id, index]))
  const orderIndex = new Map(order.map((key, index) => [key, index]))
  return [...tabs].sort((a, b) => {
    const aOrder = orderIndex.get(normalizeInternalTabKey(a.url))
    const bOrder = orderIndex.get(normalizeInternalTabKey(b.url))
    if (aOrder == null && bOrder == null) return (originalIndex.get(a.id) || 0) - (originalIndex.get(b.id) || 0)
    if (aOrder == null) return 1
    if (bOrder == null) return -1
    return aOrder - bOrder
  })
}

function inferInternalTabTitle(urlValue: string): string {
  try {
    const url = new URL(urlValue, window.location.origin)
    if (url.pathname.includes('/pdfjs/web/viewer.html')) {
      const file = url.searchParams.get('file') || ''
      const name = decodeURIComponent(file.split('/').pop() || '').trim()
      return name ? `PDF · ${name}` : 'PDF Viewer'
    }
    if (url.pathname.includes('/api/office/edit')) {
      const file = url.searchParams.get('path') || ''
      const name = decodeURIComponent(file.split('/').pop() || '').trim()
      return name ? `Office · ${name}` : 'OnlyOffice'
    }
    return url.hostname || 'Nexus Tab'
  } catch {
    return 'Nexus Tab'
  }
}

function injectHubWebauthnBridge(doc: Document) {
  const w = doc.defaultView as any
  if (!w || w.__nexusHubWebauthnInjected) return
  w.__nexusHubWebauthnInjected = true

  const script = doc.createElement('script')
  script.type = 'text/javascript'
  script.text = String.raw`
(function () {
  if (window.__nexusHubWebauthnBridge) return;
  window.__nexusHubWebauthnBridge = true;
  if (window.__nexusValidateOwnsAuthFlow) {
    console.info('[NexusBrowser] Hub validate.js owns auth flow; bridge will not override sign-in/signup.');
    return;
  }

  function errText(e) {
    try {
      if (!e) return 'UNKNOWN_ERROR';
      if (typeof e === 'string') return e;
      if (e instanceof Error) return e.message || e.name || 'ERROR';
      if (typeof e === 'object') {
        // DOMException (WebAuthn / Permissions)
        if (e.name && e.message) return String(e.name) + ': ' + String(e.message);
        if (e.error) return String(e.error);
        if (e.message) return String(e.message);
        if (e.name) return String(e.name);
        return JSON.stringify(e);
      }
      return String(e);
    } catch {
      return String(e);
    }
  }

  function setStatus(el, text, kind) {
    if (!el) return;
    el.textContent = text;
    el.className = 'status-text' + (kind ? (' ' + kind) : '');
  }

  function bufToB64url(buf) {
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    const base64 = btoa(binary);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
  }

  function b64urlToBuf(str) {
    const s = (str || '').replace(/-/g, '+').replace(/_/g, '/');
    const pad = s.length % 4 ? '='.repeat(4 - (s.length % 4)) : '';
    const raw = atob(s + pad);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out.buffer;
  }

  const GATEWAY_BASE = window.location.origin || 'http://127.0.0.1:2801';
  const IS_LOCAL_BROWSER = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const NATIVE_VALIDATOR = IS_LOCAL_BROWSER ? 'http://127.0.0.1:2718' : (GATEWAY_BASE + '/auth');
  const NATIVE_QBRIDGE = GATEWAY_BASE;

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(body || {})
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = (data && data.error) ? String(data.error) : ('HTTP_' + res.status);
      throw new Error(msg);
    }
    return data;
  }

  async function getLocalBinding() {
    const res = await fetch(NATIVE_VALIDATOR + '/hardware_binding', {
      method: 'GET',
      headers: { Accept: 'application/json' }
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error((data && data.error) ? String(data.error) : ('HTTP_' + res.status));
    return data;
  }

  function _inviteToken() {
    try {
      return (new URLSearchParams(window.location.search).get('invite_token') || '').trim();
    } catch {
      return '';
    }
  }

  async function webauthnRegister(email) {
    // Must be executed within a user gesture; keep it inside iframe click handler.
    const opts = await postJson('/auth/webauthn/register/options', { email: email });
    if (!opts || !opts.publicKey || !opts.token) throw new Error('WEBAUTHN_OPTIONS_INVALID');
    const pk = opts.publicKey;
    pk.challenge = b64urlToBuf(pk.challenge);
    pk.user = pk.user || {};
    pk.user.id = b64urlToBuf(pk.user.id);
    if (Array.isArray(pk.excludeCredentials)) {
      pk.excludeCredentials = pk.excludeCredentials.map((c) => ({ ...c, id: b64urlToBuf(c.id) }));
    }

    if (!window.isSecureContext) {
      throw new Error('NOT_SECURE_CONTEXT');
    }

    const cred = await navigator.credentials.create({ publicKey: pk });
    if (!cred) throw new Error('WEBAUTHN_CREATE_FAILED');
    const rawId = bufToB64url(cred.rawId);
    const response = cred.response || {};
    const payload = {
      id: cred.id,
      rawId: rawId,
      type: cred.type,
      response: {
        clientDataJSON: bufToB64url(response.clientDataJSON),
        attestationObject: bufToB64url(response.attestationObject)
      }
    };
    const verified = await postJson('/auth/webauthn/register/verify', { token: opts.token, email: email, credential: payload });
    return verified;
  }

  async function webauthnAssert(email) {
    const opts = await postJson('/auth/webauthn/auth/options', { email: email });
    if (!opts || !opts.publicKey || !opts.token) throw new Error('WEBAUTHN_OPTIONS_INVALID');
    const pk = opts.publicKey;
    pk.challenge = b64urlToBuf(pk.challenge);
    if (Array.isArray(pk.allowCredentials)) {
      pk.allowCredentials = pk.allowCredentials.map((c) => ({ ...c, id: b64urlToBuf(c.id) }));
    }

    if (!window.isSecureContext) {
      throw new Error('NOT_SECURE_CONTEXT');
    }

    const cred = await navigator.credentials.get({ publicKey: pk });
    if (!cred) throw new Error('WEBAUTHN_GET_FAILED');
    const rawId = bufToB64url(cred.rawId);
    const response = cred.response || {};
    const payload = {
      id: cred.id,
      rawId: rawId,
      type: cred.type,
      response: {
        clientDataJSON: bufToB64url(response.clientDataJSON),
        authenticatorData: bufToB64url(response.authenticatorData),
        signature: bufToB64url(response.signature),
        userHandle: response.userHandle ? bufToB64url(response.userHandle) : null
      }
    };
    const verified = await postJson('/auth/webauthn/auth/verify', { token: opts.token, email: email, credential: payload });
    return verified;
  }

  window.createAccount = async function () {
    const email = (document.getElementById('signup-email')?.value || '').trim().toLowerCase();
    const name = (document.getElementById('signup-name')?.value || '').trim();
    const password = (document.getElementById('signup-password')?.value || '');
    const statusEl = document.getElementById('status-signup');
    const inviteToken = _inviteToken();

    if (!email || !name || !password) {
      setStatus(statusEl, 'Completa todos los campos.', 'error');
      return;
    }
    if (!inviteToken) {
      setStatus(statusEl, 'Error: falta invite_token en el link.', 'error');
      return;
    }

    try {
      setStatus(statusEl, 'Registrando dispositivo (WebAuthn)...', '');
      const reg = await webauthnRegister(email);
      if (!reg.ok || !reg.registration_token) throw new Error(reg.error || 'WEBAUTHN_REGISTER_FAILED');

      setStatus(statusEl, 'Creando cuenta en Nexus Q Cloud...', '');
      const res = await postJson('/qbridge/auth/signup', {
        invite_token: inviteToken,
        email: email,
        name: name,
        password: password,
        accept_bind: true,
        webauthn_registration_token: reg.registration_token
      });

      if (!res.ok) {
        setStatus(statusEl, 'Error: ' + (res.error || 'No se pudo crear la cuenta'), 'error');
        return;
      }

      document.getElementById('panel-signup')?.classList.add('hidden');
      document.getElementById('panel-qr')?.classList.remove('hidden');

      const otpUri = res.otp_uri;
      const otpSecret = res.otp_secret;

      if (otpUri) {
        const qrUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' + encodeURIComponent(otpUri);
        document.getElementById('qr-image').src = qrUrl;
        document.getElementById('otp-secret-display').textContent = 'Secret: ' + (otpSecret || '');
      }

      localStorage.setItem('NEXUS_EMAIL', email);
      localStorage.setItem('NEXUS_USER_NAME', name);
      if (res.device_id) localStorage.setItem('NEXUS_DEVICE_ID', res.device_id);
      setStatus(statusEl, '¡Cuenta creada! Escanea el QR.', 'ok');
    } catch (e) {
      setStatus(statusEl, 'Error: ' + errText(e), 'error');
    }
  };

  window.signIn = async function () {
    const email = (document.getElementById('signin-email')?.value || '').trim().toLowerCase();
    const password = (document.getElementById('signin-password')?.value || '');
    const otp = (document.getElementById('signin-otp')?.value || '').trim();
    const statusEl = document.getElementById('status-signin');

    if (!email || !password || !otp) {
      setStatus(statusEl, 'Completa todos los campos.', 'error');
      return;
    }

    try {
      const isLocalHost = (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

      // Local compatibility for Nexus Browser:
      // use real validator binding + host QBridge runtime so role/device flows stay aligned.
      if (isLocalHost) {
        setStatus(statusEl, 'Validando credenciales... (' + window.location.origin + ')', '');
        const binding = await getLocalBinding();
        if (!binding || !binding.device_id || !binding.hardware_id) {
          throw new Error('DEVICE_BINDING_INVALID');
        }

        const ticket = await postJson(NATIVE_VALIDATOR + '/signin_begin', {
          email: email,
          device_id: binding.device_id
        });
        if (!ticket.ok || !ticket.entry_ticket) {
          throw new Error(ticket.error || 'ENTRY_TICKET_FAILED');
        }

        const legacy = await postJson(NATIVE_VALIDATOR + '/signin', {
          email: email,
          password: password,
          otp_code: otp,
          device_id: binding.device_id,
          hardware_id: binding.hardware_id,
          entry_ticket: ticket.entry_ticket,
          device_bind_hash: binding.device_id_hash || undefined
        });

        if (!legacy.ok) {
          setStatus(
            statusEl,
            'Error: ' + (legacy.error || 'Credenciales invalidas') + ' (email: ' + email + ')',
            'error'
          );
          return;
        }

        setStatus(statusEl, '¡Acceso concedido!', 'ok');
        try {
          localStorage.setItem('NEXUS_EMAIL', email);
          localStorage.setItem('NEXUS_LAST_EMAIL', email);
          localStorage.setItem('NEXUS_SESSION_EMAIL', email);
          if (legacy.user_name) localStorage.setItem('NEXUS_USER_NAME', String(legacy.user_name));
          if (legacy.user_role) localStorage.setItem('NEXUS_SESSION_ROLE', String(legacy.user_role));
          else localStorage.removeItem('NEXUS_SESSION_ROLE');
          if (legacy.office_code) localStorage.setItem('NEXUS_SESSION_OFFICE', String(legacy.office_code));
          if (legacy.realm_id) localStorage.setItem('NEXUS_SESSION_REALM', String(legacy.realm_id));
        } catch {}
        if (legacy.session_lease) localStorage.setItem('NEXUS_SESSION_LEASE', legacy.session_lease);
        try {
          window.parent.postMessage({
            __nexusBrowserAuth: true,
            type: 'auth-success',
            email: email,
            name: legacy.user_name || '',
            role: legacy.user_role || '',
            session_lease: legacy.session_lease || ''
          }, window.location.origin);
        } catch {}
        return;
      }

      let deviceId = '';
      let assertionToken = '';
      let enrollmentToken = '';

      try {
        setStatus(statusEl, 'Validando dispositivo (WebAuthn)...', '');
        const auth = await webauthnAssert(email);
        if (!auth.ok || !auth.assertion_token || !auth.device_id) {
          throw new Error(auth.error || 'WEBAUTHN_ASSERT_FAILED');
        }
        deviceId = auth.device_id;
        assertionToken = auth.assertion_token;
      } catch (e) {
        const msg = (e && e.message) ? e.message : String(e);
        if (!String(msg).includes('NO_DEVICE_CREDENTIALS')) throw e;

        setStatus(statusEl, 'Este usuario no tiene dispositivo WebAuthn. Enrolando...', '');
        const reg = await webauthnRegister(email);
        if (!reg.ok || !reg.registration_token) throw new Error(reg.error || 'WEBAUTHN_REGISTER_FAILED');
        enrollmentToken = reg.registration_token;
      }

      let entryTicket = null;
      if (deviceId && assertionToken) {
        setStatus(statusEl, 'Solicitando ticket de acceso...', '');
        const ticket = await postJson('/qbridge/auth/mint_entry_ticket', { email: email, device_id: deviceId });
        if (!ticket.ok || !ticket.entry_ticket) throw new Error(ticket.error || 'ENTRY_TICKET_FAILED');
        entryTicket = ticket.entry_ticket;
      }

      setStatus(statusEl, 'Ingresando...', '');
      const res = await postJson('/qbridge/auth/signin', {
        email: email,
        password: password,
        otp_code: otp,
        device_id: deviceId || undefined,
        entry_ticket: entryTicket || undefined,
        webauthn_assertion_token: assertionToken || undefined,
        webauthn_registration_token: enrollmentToken || undefined
      });

      if (!res.ok) {
        setStatus(statusEl, 'Error: ' + (res.error || 'Credenciales invalidas'), 'error');
        return;
      }

      setStatus(statusEl, '¡Acceso concedido!', 'ok');
      try {
        localStorage.setItem('NEXUS_LAST_EMAIL', email);
        localStorage.setItem('NEXUS_SESSION_EMAIL', email);
        localStorage.setItem('NEXUS_EMAIL', email);
        if (res.user_name) localStorage.setItem('NEXUS_USER_NAME', String(res.user_name));
        if (res.user_role) localStorage.setItem('NEXUS_SESSION_ROLE', String(res.user_role));
        else localStorage.removeItem('NEXUS_SESSION_ROLE');
      } catch {}
      if (res.session_lease) localStorage.setItem('NEXUS_SESSION_LEASE', res.session_lease);
      try {
        window.parent.postMessage({
          __nexusBrowserAuth: true,
          type: 'auth-success',
          email: email,
          name: res.user_name || '',
          role: res.user_role || '',
          session_lease: res.session_lease || ''
        }, window.location.origin);
      } catch {}
    } catch (e) {
      setStatus(statusEl, 'Error: ' + errText(e), 'error');
    }
  };
})();
`
  doc.head.appendChild(script)
}

function Landing({ heroSrc }: { heroSrc: string }) {
  return (
    <div className="landing">
      <div className="landing-bg" style={{ backgroundImage: `url(${heroSrc})` }} />
    </div>
  )
}

function BrowserGate({
  status,
  onRetry,
  onSignIn,
  onContinue
}: {
  status: BrowserGateStatus
  onRetry: () => void
  onSignIn: () => void
  onContinue: () => void
}) {
  return (
    <section className="browser-gate" aria-label="Nexus Browser Gate">
      <div className="browser-gate-panel">
        <span className="browser-gate-kicker">Nexus Production Gate</span>
        <h1>Core verification required</h1>
        <p>Profile: {status.profile}. Remote start prepares infrastructure; it does not authenticate a user session.</p>
        <div className="browser-gate-checks">
          {status.checks.map((check) => (
            <div className={`browser-gate-check ${check.status.toLowerCase()}`} key={check.id}>
              <span>{check.label}</span>
              <strong>{check.status}</strong>
            </div>
          ))}
        </div>
        <div className="browser-gate-actions">
          <button className="control-btn" onClick={onRetry} type="button">Recheck</button>
          <button className="control-btn" onClick={onSignIn} type="button">Sign In</button>
          <button className="control-btn active" onClick={onContinue} disabled={status.status !== 'PASS'} type="button">Continue</button>
        </div>
      </div>
    </section>
  )
}

function InstitutionalTabletAccess({
  entryParams,
  onSignIn
}: {
  entryParams: URLSearchParams
  onSignIn: () => void
}) {
  const platform = institutionalTabletPlatform()
  const requestedRole = (entryParams.get('role') || 'guest').trim().toLowerCase()
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent || '' : ''
  const isIosChrome = /CriOS/i.test(userAgent)
  const isIosFirefox = /FxiOS/i.test(userAgent)
  const isIosEdge = /EdgiOS/i.test(userAgent)
  const isSafari = /Safari/i.test(userAgent) && !isIosChrome && !isIosFirefox && !isIosEdge
  const guestRole = isInstitutionalTabletGuestRole(requestedRole)
  const roleLabel = requestedRole === 'teacher'
    ? 'Teacher'
    : ['subprincipal_admin', 'principal_admin', 'principal', 'admin'].includes(requestedRole)
      ? 'Subprincipal Admin'
      : requestedRole === 'parent_guest' || requestedRole === 'parent'
        ? 'Guest / Parent Guest'
        : 'Guest / Trusted Guest'
  const portalUrl = (() => {
    try {
      const url = new URL('/ipad', window.location.origin)
      url.searchParams.set('role', requestedRole || 'guest')
      url.searchParams.set('platform', 'ipad')
      url.searchParams.set('device_type', TABLET_DEVICE_TYPE)
      url.searchParams.set('shell_mode', TABLET_SHELL_MODE)
      url.searchParams.set('overlay', TABLET_OVERLAY)
      return url.toString()
    } catch {
      return `https://nexusquantumbridgenetworks.ai/ipad?role=${encodeURIComponent(requestedRole || 'guest')}&platform=ipad&device_type=${encodeURIComponent(TABLET_DEVICE_TYPE)}&shell_mode=${encodeURIComponent(TABLET_SHELL_MODE)}&overlay=${encodeURIComponent(TABLET_OVERLAY)}`
    }
  })()
  const [status, setStatus] = useState('')

  const copyPortalLink = async () => {
    try {
      await navigator.clipboard.writeText(portalUrl)
      setStatus('iPad link copied. Open it in Safari, add to Home Screen, then sign in from the Nexus icon.')
    } catch {
      setStatus(portalUrl)
    }
  }

  const continueToSignIn = () => {
    try {
      const current = new URL(window.location.href)
      const inviteToken = (
        current.searchParams.get('invite_token') ||
        current.searchParams.get('pre_enrollment_token') ||
        window.sessionStorage.getItem('NEXUS_PREBOUND_INVITE_TOKEN') ||
        window.localStorage.getItem('NEXUS_PREBOUND_INVITE_TOKEN') ||
        ''
      ).trim()
      if (!current.searchParams.get('role')) {
        current.searchParams.set('role', requestedRole || 'guest')
      }
      if (platform.isIPad && !current.searchParams.get('platform')) {
        current.searchParams.set('platform', 'ipad')
      }
      current.searchParams.set('device_type', TABLET_DEVICE_TYPE)
      current.searchParams.set('shell_mode', TABLET_SHELL_MODE)
      current.searchParams.set('overlay', TABLET_OVERLAY)
      if (inviteToken) {
        current.searchParams.set('flow', 'school_first_access')
        current.searchParams.set('prebound', '1')
        current.searchParams.set('pre_enrollment_token', inviteToken)
        current.searchParams.set('invite_token', inviteToken)
      }
      const nextUrl = `${current.pathname}${current.search}${current.hash}`
      window.history.replaceState({}, '', nextUrl)
    } catch {
      // URL normalization is best effort.
    }
    onSignIn()
  }

  return (
    <main className="institutional-tablet-access">
      <section className="institutional-tablet-panel">
        <span className="parent-access-kicker">Nexus Tablet Shell</span>
        <h1>iPad institutional access</h1>
        <p>
          iPad runs a dedicated institutional tablet shell. Install once from Safari, then open Nexus from the Home Screen icon.
        </p>
        {platform.isIPad && !platform.isStandalone && (
          <div className="institutional-tablet-easy-mode">
            <strong>Install first, then sign in</strong>
            <ol>
              <li>Open this page in Safari.</li>
              <li>Tap Share.</li>
              <li>Tap Add to Home Screen.</li>
              <li>Open the new Nexus icon.</li>
              <li>Tap Enter Nexus and sign in.</li>
            </ol>
            {!isSafari && (
              <p>
                For iPad, Safari is required for install and best stability.
              </p>
            )}
          </div>
        )}
        <div className="institutional-tablet-grid">
          <article>
            <span>Detected device</span>
            <strong>{platform.label}</strong>
            <p>{platform.isStandalone ? 'Opened as installed app.' : 'Opened in browser tab.'}</p>
          </article>
          <article>
            <span>Requested role</span>
            <strong>{roleLabel}</strong>
            <p>{guestRole ? 'Guest access uses secure messages, documents, PDF, and limited Eugene only.' : 'OTP is required for institutional staff sign in.'}</p>
          </article>
          <article>
            <span>Runtime</span>
            <strong>{platform.isStandalone ? 'iPad Workspace active' : 'Install required'}</strong>
            <p>{platform.runtimeStatus}</p>
          </article>
        </div>
        <div className="institutional-tablet-actions">
          {platform.isStandalone ? (
            <button className="parent-primary-btn" type="button" onClick={continueToSignIn}>
              Open Nexus
            </button>
          ) : (
            <button className="parent-primary-btn" type="button" onClick={() => setStatus('Install first: Safari > Share > Add to Home Screen. Then open the Nexus icon and continue.')}>
              Install Nexus for iPad
            </button>
          )}
          <button className="parent-secondary-btn" type="button" onClick={copyPortalLink}>
            Copy iPad link
          </button>
        </div>
        <div className="institutional-tablet-install">
          <h2>{platform.installTitle}</h2>
          <ol>
            {platform.installSteps.map((step) => <li key={step}>{step}</li>)}
          </ol>
          <p>
            The Home Screen icon is the official iPad runtime for this phase. No native app store install is required.
          </p>
        </div>
        {status && <div className="parent-action-status">{status}</div>}
      </section>
    </main>
  )
}

function normalizeInstitutionalEarlyDismissal(value: unknown): InstitutionalEarlyDismissalSummary {
  const item = (value && typeof value === 'object' ? value : {}) as Record<string, any>
  return {
    earlyDismissalId: readStringValue(item.early_dismissal_id || item.id),
    studentName: readStringValue(item.student_name || item.studentName),
    pickupPersonName: readStringValue(item.pickup_person_name || item.pickupPersonName),
    status: readStringValue(item.status || 'pending'),
    liveStatus: readStringValue(item.live_status || item.status || 'pending'),
    createdAt: readStringValue(item.created_at || item.createdAt)
  }
}

function InstitutionalTabletPortal({
  session,
  onLogout
}: {
  session: ParentSession
  onLogout: () => void
}) {
  const [activeWorkspace, setActiveWorkspace] = useState<'overview' | 'earlyDismissal' | 'messages' | 'documents' | 'reports'>('overview')
  const [messages, setMessages] = useState<ParentMessage[]>([])
  const [earlyDismissals, setEarlyDismissals] = useState<InstitutionalEarlyDismissalSummary[]>([])
  const [binding, setBinding] = useState<InstitutionalBindingState>({ ...EMPTY_INSTITUTIONAL_BINDING })
  const [documentViewer, setDocumentViewer] = useState<ParentDocumentViewerState | null>(null)
  const [messageStatus, setMessageStatusState] = useState<Record<string, string>>({})
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({})
  const [replyOpen, setReplyOpen] = useState<Record<string, boolean>>({})
  const [searchQuery, setSearchQuery] = useState('')
  const [eugeneInput, setEugeneInput] = useState('')
  const [eugeneTurns, setEugeneTurns] = useState<Array<{ id: string; sender: 'user' | 'eugene'; text: string }>>([])
  const [loading, setLoading] = useState(true)
  const [status, setStatus] = useState('')
  const platform = institutionalTabletPlatform()
  const role = String(session.role || '').trim().toLowerCase()
  const isTeacher = role === 'teacher'
  const isSubprincipal = ['subprincipal_admin', 'principal_admin', 'principal', 'admin'].includes(role)
  const isGuestRuntime = isInstitutionalTabletGuestRole(role) || isParentGuestRole(role)

  const loadTabletData = useCallback(async () => {
    if (!session.email || !session.lease) {
      setStatus('Sign in with the pre-enrolled iPad account.')
      setLoading(false)
      return
    }
    setLoading(true)
    setStatus('')
    try {
      const bindingInfo = await getParentBrowserBinding(session.email)
      const metadata = readInstitutionalSessionMetadata()
      setBinding({
        deviceId: readStringValue((bindingInfo as any).device_id || (bindingInfo as any).device_id_hash),
        hardwareId: readStringValue((bindingInfo as any).hardware_id || (bindingInfo as any).fingerprint_hash),
        label: platform.isIPad ? 'iPad Pro' : 'Tablet',
        userId: metadata.userId,
        institutionId: metadata.institutionId,
        lastSeen: metadata.lastSeen
      })
    } catch {
      setBinding({ ...EMPTY_INSTITUTIONAL_BINDING, label: platform.isIPad ? 'iPad Pro' : 'Tablet' })
    }
    try {
      const resp = await fetch('/api/institutional/messages?box=inbox', {
        cache: 'no-store',
        headers: parentPortalHeaders(session)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(`MESSAGES_${resp.status}`)
      const rawMessages = Array.isArray((data as any).messages) ? (data as any).messages : []
      setMessages(rawMessages.map(normalizeParentMessage))
    } catch {
      setStatus('Messages are unavailable for this session.')
    }
    if (isSubprincipal) {
      try {
        const resp = await fetch('/api/institutional/early-dismiss', {
          cache: 'no-store',
          headers: parentPortalHeaders(session)
        })
        const data = await resp.json().catch(() => ({}))
        if (!resp.ok) throw new Error(`EARLY_DISMISS_${resp.status}`)
        const rows = Array.isArray((data as any).requests) ? (data as any).requests : Array.isArray((data as any).results) ? (data as any).results : []
        setEarlyDismissals(rows.map(normalizeInstitutionalEarlyDismissal).slice(0, 12))
      } catch {
        setEarlyDismissals([])
      }
    } else {
      setEarlyDismissals([])
    }
    setLoading(false)
  }, [session, isSubprincipal, platform.isIPad])

  useEffect(() => {
    void loadTabletData()
  }, [loadTabletData])

  const documents = messages.flatMap((message) => (
    message.attachments.map((attachment) => ({ message, attachment }))
  ))
  const normalizedQuery = searchQuery.trim().toLowerCase()
  const visibleMessages = normalizedQuery
    ? messages.filter((message) => [
        message.subject,
        message.body,
        message.senderName,
        message.relatedStudentName,
        parentMessageTypeLabel(message)
      ].join(' ').toLowerCase().includes(normalizedQuery))
    : messages
  const visibleDocuments = normalizedQuery
    ? documents.filter(({ message, attachment }) => [
        attachment.title,
        attachment.name,
        attachment.mime,
        message.subject,
        message.senderName
      ].join(' ').toLowerCase().includes(normalizedQuery))
    : documents
  const dismissalNotices = messages.filter((message) => message.messageType.includes('early_dismissal'))
  const workspaceTitle = isGuestRuntime ? 'Nexus Guest Workspace' : isTeacher ? 'Teacher Workspace' : 'Subprincipal Admin Workspace'
  const workspaceSubtitle = isGuestRuntime
    ? 'Guest institutional workspace for shared messages, PDFs, documents, replies, confirmations, and limited Eugene guidance.'
    : isTeacher
    ? 'Teacher dashboard, messages, documents, reports, and early dismissal notices.'
    : 'Limited admin overlay for front office work: early dismissal, parent/guest links, messages, documents, and reports.'

  const setMessageStatus = (messageId: string, value: string) => {
    setMessageStatusState((current) => ({ ...current, [messageId]: value }))
  }

  const closeDocumentViewer = () => {
    setDocumentViewer((current) => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl)
      return null
    })
  }

  useEffect(() => {
    return () => {
      setDocumentViewer((current) => {
        if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl)
        return null
      })
    }
  }, [])

  const openTabletAttachment = async (message: ParentMessage, attachment: ParentAttachment) => {
    const rawUrl = parentAttachmentUrl(attachment)
    if (!rawUrl) {
      setMessageStatus(message.messageId, 'PDF unavailable.')
      return
    }
    const isPdf = attachment.mime.includes('pdf') || attachment.type.toLowerCase().includes('pdf') || /\.pdf($|\?)/i.test(rawUrl)
    setMessageStatus(message.messageId, isPdf ? 'Opening PDF...' : 'Opening document...')
    const viewerUrl = await resolveParentViewerUrl(rawUrl, session)
    setDocumentViewer((current) => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl)
      return { message, attachment, url: viewerUrl.url, isPdf, objectUrl: viewerUrl.objectUrl, sourceUrl: rawUrl }
    })
    setActiveWorkspace('documents')
  }

  const confirmTabletReceived = async (message: ParentMessage) => {
    if (!parentMessageAllows(message, 'acknowledge')) {
      setMessageStatus(message.messageId, 'Insufficient permission.')
      return
    }
    setMessageStatus(message.messageId, 'Confirming received...')
    try {
      const resp = await fetch(`/api/institutional/messages/${encodeURIComponent(message.messageId)}/read`, {
        method: 'POST',
        headers: parentPortalHeaders(session),
        body: JSON.stringify({ acknowledge: true })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) throw new Error('ACK_FAILED')
      const acknowledgedAt = new Date().toISOString()
      setMessages((current) => current.map((item) => (
        item.messageId === message.messageId ? { ...item, acknowledgedAt, status: 'acknowledged' } : item
      )))
      setMessageStatus(message.messageId, 'Received confirmed.')
    } catch {
      setMessageStatus(message.messageId, 'We could not confirm receipt. Try again.')
    }
  }

  const sendTabletReply = async (message: ParentMessage) => {
    if (!parentMessageAllows(message, 'reply')) {
      setMessageStatus(message.messageId, 'Insufficient permission.')
      return
    }
    const body = (replyDrafts[message.messageId] || '').trim()
    if (!body) {
      setMessageStatus(message.messageId, 'Write your reply.')
      return
    }
    setMessageStatus(message.messageId, 'Sending reply...')
    try {
      const resp = await fetch('/api/institutional/messages', {
        method: 'POST',
        headers: parentPortalHeaders(session),
        body: JSON.stringify({
          recipient_email: message.senderEmail,
          recipient_role: message.senderRole || 'teacher',
          subject: `Re: ${message.subject || 'School message'}`,
          body,
          related_student_id: message.relatedStudentId || undefined,
          thread_id: message.messageId
        })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) throw new Error('REPLY_FAILED')
      setReplyDrafts((current) => ({ ...current, [message.messageId]: '' }))
      setReplyOpen((current) => ({ ...current, [message.messageId]: false }))
      setMessages((current) => current.map((item) => (
        item.messageId === message.messageId ? { ...item, status: 'replied' } : item
      )))
      setMessageStatus(message.messageId, 'Reply sent.')
    } catch {
      setMessageStatus(message.messageId, 'We could not send the reply. Your session is still active.')
    }
  }

  const askTabletEugene = () => {
    const question = eugeneInput.trim()
    if (!question) return
    const normalized = question.toLowerCase()
    const response = normalized.includes('pdf') || normalized.includes('document')
      ? 'Open Documents in the Work Mirror. I can help you find shared PDFs and show whether the school allowed editing.'
      : normalized.includes('reply') || normalized.includes('responder')
        ? 'Open Messages, select the school message, and use Reply only when the sender enabled that permission.'
        : normalized.includes('confirm') || normalized.includes('ack')
          ? 'Use Confirm received on messages that require acknowledgement. I will not expose other school data.'
          : 'I can help with shared school messages, documents, PDFs, replies, confirmations, and permitted edits in this guest workspace.'
    setEugeneTurns((current) => {
      const nextTurns: Array<{ id: string; sender: 'user' | 'eugene'; text: string }> = [
        ...current,
        { id: `u-${Date.now()}`, sender: 'user', text: question },
        { id: `e-${Date.now()}`, sender: 'eugene', text: response }
      ]
      return nextTurns.slice(-12)
    })
    setEugeneInput('')
  }

  return (
    <main className="institutional-nexus-shell">
      <header className="institutional-nexus-topbar">
        <div>
          <span className="institutional-nexus-kicker">Nexus Browser</span>
          <h1>{workspaceTitle}</h1>
          <p>{session.email} · {role || 'institutional role'} · {platform.isStandalone ? 'installed iPad shell' : 'Safari vestibule'}</p>
        </div>
        <label className="institutional-nexus-search">
          <span>Nexus search</span>
          <input
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search messages, PDFs, documents"
          />
        </label>
        <div className="institutional-nexus-actions">
          <button type="button" onClick={loadTabletData} disabled={loading}>Reload</button>
          <button type="button" onClick={onLogout}>Sign out</button>
        </div>
      </header>

      {status && <div className="institutional-nexus-status">{status}</div>}

      <section className="institutional-nexus-body">
        <aside className="institutional-nexus-chat">
          <header>
            <div className="institutional-nexus-avatar">E</div>
            <div>
              <strong>Eugene · Nexus QCloud</strong>
              <span>{isGuestRuntime ? 'Limited guest session' : 'Institutional staff session'}</span>
            </div>
          </header>
          <div className="institutional-nexus-chat-window">
            <div className="institutional-nexus-message system">
              {workspaceSubtitle}
            </div>
            <div className="institutional-nexus-message">
              {isGuestRuntime
                ? 'This iPad opens only the work shared with this guest account.'
                : 'This iPad opens the secure institutional workspace for front office and classroom work.'}
            </div>
            {eugeneTurns.map((turn) => (
              <div className={`institutional-nexus-message ${turn.sender === 'user' ? 'user' : 'compact'}`} key={turn.id}>
                <strong>{turn.sender === 'user' ? 'You' : 'Eugene'}</strong>
                <span>{turn.text}</span>
              </div>
            ))}
            {visibleMessages.slice(0, 3).map((message) => (
              <div className="institutional-nexus-message compact" key={message.messageId || message.subject}>
                <strong>{message.subject || 'Institutional message'}</strong>
                <span>{message.body || parentMessageTypeLabel(message)}</span>
              </div>
            ))}
          </div>
          <div className="institutional-nexus-input">
            <input
              value={eugeneInput}
              onChange={(event) => setEugeneInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') askTabletEugene()
              }}
              placeholder="Ask Eugene about shared work"
            />
            <button type="button" onClick={askTabletEugene}>Send</button>
          </div>
        </aside>

        <section className="institutional-nexus-mirror">
          <header className="institutional-nexus-mirror-head">
              <div>
                <span className="institutional-nexus-kicker">Work Mirror</span>
                <h2>{isGuestRuntime ? 'Guest Canvas' : isTeacher ? 'Teacher Dashboard' : 'Front Office Admin'}</h2>
              </div>
            <div className="institutional-nexus-device">
              <span>{binding.label || (platform.isIPad ? 'iPad Pro' : 'Tablet')}</span>
              <strong>{binding.deviceId ? binding.deviceId.slice(0, 18) : 'Bound session'}</strong>
            </div>
          </header>

          <nav className="institutional-nexus-tabs" aria-label="Nexus iPad workspace">
            <button className={activeWorkspace === 'overview' ? 'active' : ''} onClick={() => setActiveWorkspace('overview')}>Home</button>
            <button className={activeWorkspace === 'messages' ? 'active' : ''} onClick={() => setActiveWorkspace('messages')}>Messages</button>
            <button className={activeWorkspace === 'documents' ? 'active' : ''} onClick={() => setActiveWorkspace('documents')}>Documents</button>
            {!isGuestRuntime && (
              <button className={activeWorkspace === 'earlyDismissal' ? 'active' : ''} onClick={() => setActiveWorkspace('earlyDismissal')}>{isSubprincipal ? 'Early Dismiss' : 'Dismissal Notices'}</button>
            )}
            {!isGuestRuntime && <button className={activeWorkspace === 'reports' ? 'active' : ''} onClick={() => setActiveWorkspace('reports')}>Reports</button>}
          </nav>

          <div className="institutional-nexus-stage">
            {documentViewer && (
              <ParentDocumentViewer viewer={documentViewer} onBack={closeDocumentViewer} />
            )}

            {!documentViewer && activeWorkspace === 'overview' && (
              <div className="institutional-nexus-card-grid">
                <article>
                  <span>Messages</span>
                  <strong>{visibleMessages.length}</strong>
                  <p>{isGuestRuntime ? 'School messages shared with this guest.' : 'Institutional inbox for this staff role.'}</p>
                </article>
                <article>
                  <span>Documents</span>
                  <strong>{visibleDocuments.length}</strong>
                  <p>Shared PDFs and documents.</p>
                </article>
                {!isGuestRuntime && (
                  <article>
                    <span>{isSubprincipal ? 'Early dismiss' : 'Dismissal notices'}</span>
                    <strong>{isSubprincipal ? earlyDismissals.length : dismissalNotices.length}</strong>
                    <p>{isSubprincipal ? 'Release workflow status.' : 'Front office instructions.'}</p>
                  </article>
                )}
                <article>
                  <span>Policy</span>
                  <strong>{isGuestRuntime ? 'Guest' : isTeacher ? 'Teacher' : 'Limited admin'}</strong>
                  <p>{isGuestRuntime ? 'No QBox global, no role switcher, no admin tools.' : 'Institutional staff permissions only.'}</p>
                </article>
              </div>
            )}

            {!documentViewer && activeWorkspace === 'messages' && (
              <div className="institutional-nexus-list">
                {visibleMessages.map((message) => {
                  const canReply = parentMessageAllows(message, 'reply')
                  const canAcknowledge = parentMessageAllows(message, 'acknowledge') && !message.acknowledgedAt && message.status !== 'acknowledged'
                  return (
                  <article key={message.messageId || message.subject}>
                    <strong>{message.subject || 'School message'}</strong>
                    <p>{message.body || parentMessageTypeLabel(message)}</p>
                    <div className="institutional-nexus-inline-actions">
                      {message.attachments.map((attachment) => (
                        <button type="button" key={attachment.id || attachment.name} onClick={() => openTabletAttachment(message, attachment)}>
                          {attachment.mime.includes('pdf') || attachment.type.toLowerCase().includes('pdf') || /\.pdf$/i.test(attachment.name) ? 'Open PDF' : 'Open document'}
                        </button>
                      ))}
                      {canAcknowledge && <button type="button" onClick={() => confirmTabletReceived(message)}>Confirm received</button>}
                      {canReply && <button type="button" onClick={() => setReplyOpen((current) => ({ ...current, [message.messageId]: !current[message.messageId] }))}>Reply</button>}
                    </div>
                    {replyOpen[message.messageId] && (
                      <div className="institutional-nexus-reply">
                        <textarea
                          value={replyDrafts[message.messageId] || ''}
                          onChange={(event) => setReplyDrafts((current) => ({ ...current, [message.messageId]: event.target.value }))}
                          placeholder="Write your reply to the school"
                        />
                        <button type="button" onClick={() => sendTabletReply(message)}>Send reply</button>
                      </div>
                    )}
                    {messageStatus[message.messageId] && <span className="institutional-nexus-note">{messageStatus[message.messageId]}</span>}
                  </article>
                  )
                })}
                {!visibleMessages.length && <p>No institutional messages.</p>}
              </div>
            )}

            {!documentViewer && activeWorkspace === 'documents' && (
              <div className="institutional-nexus-list">
                {visibleDocuments.map(({ message, attachment }) => {
                  const url = parentAttachmentUrl(attachment)
                  const canEdit = parentMessageAllows(message, 'edit_if_allowed')
                  return (
                    <article key={`${message.messageId}-${attachment.id || attachment.url || attachment.name}`}>
                      <strong>{attachment.title || attachment.name || 'Shared document'}</strong>
                      <p>{message.subject || 'Shared by school'}</p>
                      <div className="institutional-nexus-inline-actions">
                        {url && <button type="button" onClick={() => openTabletAttachment(message, attachment)}>Open in Canvas</button>}
                        {url && <a href={url} target="_blank" rel="noreferrer">Open</a>}
                        {url && canEdit && <a href={`${url}${url.includes('?') ? '&' : '?'}mode=edit`} target="_blank" rel="noreferrer">Edit allowed</a>}
                      </div>
                      {!canEdit && <span className="institutional-nexus-note">Read only unless the school enables editing.</span>}
                    </article>
                  )
                })}
                {!visibleDocuments.length && <p>No shared documents.</p>}
              </div>
            )}

            {!documentViewer && activeWorkspace === 'earlyDismissal' && !isGuestRuntime && (
              <div className="institutional-nexus-list">
                {isSubprincipal ? earlyDismissals.map((row) => (
                  <article key={row.earlyDismissalId}>
                    <strong>{row.studentName || 'Student'}</strong>
                    <p>{row.pickupPersonName || 'Pickup person'} · {row.liveStatus || row.status}</p>
                    <span>{row.createdAt || 'Created recently'}</span>
                  </article>
                )) : dismissalNotices.map((message) => (
                  <article key={message.messageId}>
                    <strong>{message.subject || 'Early dismissal notice'}</strong>
                    <p>{message.body || 'Front office notice.'}</p>
                  </article>
                ))}
                {isSubprincipal && !earlyDismissals.length && <p>No active early dismissal records.</p>}
                {isTeacher && !dismissalNotices.length && <p>No early dismissal notices.</p>}
              </div>
            )}

            {!documentViewer && activeWorkspace === 'reports' && !isGuestRuntime && (
              <div className="institutional-nexus-list">
                <article>
                  <strong>{isTeacher ? 'Classroom reports' : 'Student release reports'}</strong>
                  <p>{isTeacher ? 'Assignments, submissions, and classroom notices.' : 'Early dismissal history, delivery status, and audit summaries.'}</p>
                </article>
              </div>
            )}
          </div>
        </section>
      </section>
    </main>
  )
}

function ParentGuestAccess({
  inviteToken,
  entryParams,
  onAuthenticated,
  onCancel
}: {
  inviteToken: string
  entryParams: URLSearchParams
  onContinue: (token: string, email: string, name: string, flow?: AuthFlow) => void
  onAuthenticated: () => void
  onCancel: () => void
}) {
  const [firstName, setFirstName] = useState(entryParams.get('parentFirstName') || entryParams.get('first_name') || '')
  const [lastName, setLastName] = useState(entryParams.get('parentLastName') || entryParams.get('last_name') || '')
  const [email, setEmail] = useState(entryParams.get('email') || '')
  const [studentLunchId, setStudentLunchId] = useState(entryParams.get('studentLunchId') || entryParams.get('lunch_id') || '')
  const [consent, setConsent] = useState(false)
  const [status, setStatus] = useState('')
  const [busy, setBusy] = useState(false)
  const [authMode, setAuthMode] = useState<AuthFlow | null>(
    entryParams.get('auth') === 'signin' || !inviteToken ? 'signin' : entryParams.get('auth') === 'signup' ? 'signup' : null
  )
  const [activeInviteToken, setActiveInviteToken] = useState(inviteToken)
  const [password, setPassword] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpSecret, setOtpSecret] = useState('')
  const [otpUri, setOtpUri] = useState('')

  const inviteStatus = (entryParams.get('status') || entryParams.get('invite_status') || '').trim().toLowerCase()
  const expiresAt = entryParams.get('expiresAt') || entryParams.get('expires_at') || ''
  const linkExpired =
    inviteStatus === 'expired' ||
    (expiresAt ? Number.isFinite(Date.parse(expiresAt)) && Date.parse(expiresAt) < Date.now() : false)
  const iPadContext =
    (entryParams.get('platform') || '').trim().toLowerCase() === 'ipad' ||
    detectRuntimeDeviceProfile().deviceClass === 'ipad'
  const lunchIdRequired = !inviteToken && !iPadContext
  const isIpadGuestInviteContext =
    (entryParams.get('platform') || '').trim().toLowerCase() === 'ipad' &&
    !!inviteToken
  const isIpadGuestRuntime = iPadContext && (
    (entryParams.get('role') || '').trim().toLowerCase() === 'guest' ||
    (entryParams.get('role') || '').trim().toLowerCase() === 'parent_guest' ||
    (entryParams.get('role') || '').trim().toLowerCase() === 'trusted_guest' ||
    (entryParams.get('source') || '').trim().toLowerCase() === 'ipad_guest_pwa'
  )
  const accessKicker = isIpadGuestInviteContext ? 'Nexus iPad Guest Workspace' : 'Parent Portal'
  const accessTitle = authMode
    ? (authMode === 'signin' ? 'Sign in' : 'Create account')
    : (isIpadGuestInviteContext ? 'Continue to secure guest access' : 'Link parent/guardian device')
  const accessCopy = authMode
    ? 'Limited access for messages and documents shared with you.'
    : (isIpadGuestInviteContext
      ? 'Use this invite to finish secure sign up/sign in on this iPad.'
      : 'Enter the details the school authorized for this invitation.')

  const continueWith = async (flow: AuthFlow) => {
    if (busy) return
    if (flow === 'signin') {
      setAuthMode('signin')
      setStatus('')
      return
    }
    const cleanEmail = email.trim().toLowerCase()
    const cleanLunchId = studentLunchId.trim()
    const cleanFirst = firstName.trim()
    const cleanLast = lastName.trim()
    const fullName = `${cleanFirst} ${cleanLast}`.trim()

  if (linkExpired) {
      setStatus('This invitation has expired. Contact the school.')
      return
    }
    if (!cleanFirst || !cleanLast || !cleanEmail || (lunchIdRequired && !cleanLunchId)) {
      setStatus(lunchIdRequired
        ? 'Complete first name, last name, email and Student Lunch ID.'
        : 'Complete first name, last name and email.')
      return
    }
    if (!consent) {
      setStatus('To continue, accept the school communication notice.')
      return
    }

    setBusy(true)
      setStatus('Validating invitation...')
    try {
      if (!inviteToken && isIpadGuestRuntime) {
        if (flow === 'signup') {
          setStatus('This iPad needs a fresh school invite link to create a new account. Use Sign in if this account already exists.')
          return
        }
        setAuthMode('signin')
        setStatus('')
        return
      }
      if (inviteToken) {
        setActiveInviteToken(inviteToken)
        setAuthMode(flow)
        setStatus('')
        return
      }

      const resp = await fetch('/vault/auth/pre_enrollment/resolve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email: cleanEmail,
          school_id: cleanLunchId,
          asset_tag: cleanEmail,
          role: 'parent_guest',
          platform: 'parent_portal',
          ttl_seconds: 3600
        })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || !(data as any)?.ok || !(data as any)?.invite_token) {
        throw new Error('PARENT_GUEST_INVITE_INVALID')
      }
      const resolvedName = readStringValue((data as any)?.name)
      if (resolvedName) {
        const normalizeName = (value: string) => value.toLowerCase().replace(/\s+/g, ' ').trim()
        if (normalizeName(resolvedName) !== normalizeName(fullName)) {
          throw new Error('PARENT_GUEST_INVITE_INVALID')
        }
      }
      try {
        window.localStorage.setItem('NEXUS_PRE_ENROLLMENT_ID', String((data as any)?.pre_enrollment_id || ''))
      } catch {
        // optional local trace
      }
      setActiveInviteToken(String((data as any).invite_token))
      setAuthMode(flow)
      setStatus('')
    } catch {
      setStatus('We could not validate this invitation. Contact the school.')
    } finally {
      setBusy(false)
    }
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await continueWith('signup')
  }

  const handleAuthSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (busy || !authMode) return
    const cleanEmail = email.trim().toLowerCase()
    const cleanName = `${firstName.trim()} ${lastName.trim()}`.trim()
    const cleanPassword = password.trim()
    const cleanOtp = otpCode.trim()
    if (!cleanEmail || !cleanPassword || (authMode === 'signup' && !cleanName)) {
      setStatus(authMode === 'signup' ? 'Complete email, name and password.' : 'Complete email and password.')
      return
    }
    if (authMode === 'signin' && !cleanOtp) {
      setStatus('Enter the 6-digit authenticator code.')
      return
    }

    setBusy(true)
    setStatus(authMode === 'signin' ? 'Validating access...' : 'Creating account...')
    try {
      const binding = await getParentBrowserBinding(cleanEmail)
      const deviceId = binding.device_id || (binding.device_id_hash ? `dev-${String(binding.device_id_hash).slice(0, 16)}` : '')
      const hardwareId = binding.hardware_id || binding.device_id_hash || ''
      if (!deviceId || !hardwareId) throw new Error('PARENT_BINDING_INCOMPLETE')

      if (authMode === 'signup') {
        const resp = await fetch('/vault/auth/signup', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            invite_token: activeInviteToken,
            email: cleanEmail,
            name: cleanName,
            password: cleanPassword,
            accept_bind: true,
            device_id: deviceId,
            hardware_id: hardwareId,
            device_bind: {
              crypto_version: binding.crypto_version,
              device_id_hash: binding.device_id_hash,
              fingerprint_hash: binding.fingerprint_hash,
              device_public_key_pem: binding.device_public_key_pem,
              webauthn_cred_id: ''
            },
            webauthn_registration_token: ''
          })
        })
        const result = (await resp.json().catch(() => ({}))) as Record<string, unknown>
        if (!resp.ok || !result.ok) {
          throw new Error(readStringValue(result.error) || 'Could not create the account')
        }
        if (result.session_lease && !isBackendParentGuestRole(result)) {
          throw new Error('This account is not a Parent Guest account. Use the invited parent/guardian email.')
        }
        const secret = readStringValue(result.otp_secret)
        const uri = readStringValue(result.otp_uri)
        if (secret || uri) {
          setOtpSecret(secret)
          setOtpUri(uri)
          setAuthMode('signin')
          setStatus('Account created. Save the QR or secret in your authenticator, then sign in with the 6-digit code.')
          return
        }
        if (result.session_lease) {
          storeParentGuestAuth(result, cleanEmail, cleanName)
          onAuthenticated()
          return
        }
        setAuthMode('signin')
        setStatus('Account created. Sign in to continue.')
        return
      }

      const ticketResp = await fetch('/auth/signin_begin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: cleanEmail, device_id: deviceId })
      })
      const ticket = (await ticketResp.json().catch(() => ({}))) as Record<string, unknown>
      if (!ticketResp.ok || !ticket.ok || !ticket.entry_ticket) {
        throw new Error(readStringValue(ticket.error) || 'Could not obtain access')
      }

      const signinResp = await fetch('/vault/auth/signin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email: cleanEmail,
          password: cleanPassword,
          otp_code: cleanOtp,
          lunch_id: cleanOtp,
          device_id: deviceId,
          hardware_id: hardwareId,
          entry_ticket: ticket.entry_ticket,
          device_bind_hash: binding.device_id_hash,
          webauthn_assertion_token: ''
        })
      })
      const result = (await signinResp.json().catch(() => ({}))) as Record<string, unknown>
      if (!signinResp.ok || !result.ok) {
        throw new Error(readStringValue(result.error) || 'Invalid credentials')
      }
      if (!isBackendParentGuestRole(result)) {
        throw new Error('This account is not a Parent Guest account. Use the invited parent/guardian email.')
      }
      storeParentGuestAuth(result, cleanEmail, cleanName)
      onAuthenticated()
    } catch (error) {
      const rawMessage = error instanceof Error ? error.message : 'We could not complete access.'
      const message = rawMessage === 'BAD_OTP'
        ? 'Invalid authenticator code. Use the 6-digit code from Google Authenticator.'
        : rawMessage === 'BAD_LUNCH_ID'
          ? 'Invalid Student Lunch ID. Check the school invitation details.'
          : rawMessage
      setStatus(`We could not complete access. ${message}`)
    } finally {
      setBusy(false)
    }
  }

  if (linkExpired) {
    return (
      <main className="parent-access">
        <section className="parent-access-panel">
          <div className="parent-access-kicker">{accessKicker}</div>
          <h1>Expired link</h1>
          <p>We could not validate this invitation. Contact the school.</p>
          <button className="parent-secondary-btn" onClick={onCancel}>Back</button>
        </section>
      </main>
    )
  }

  return (
    <main className="parent-access">
      <section className="parent-access-panel">
        <div className="parent-access-kicker">{accessKicker}</div>
        <h1>{accessTitle}</h1>
        <p className="parent-access-copy">{accessCopy}</p>
        {!authMode ? (
        <form className="parent-access-form" onSubmit={handleSubmit}>
          <div className="parent-access-grid">
            <label>
              First name
              <input value={firstName} onChange={(event) => setFirstName(event.target.value)} autoComplete="given-name" />
            </label>
            <label>
              Last name
              <input value={lastName} onChange={(event) => setLastName(event.target.value)} autoComplete="family-name" />
            </label>
          </div>
          <label>
            Parent/guardian email
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" />
          </label>
          {lunchIdRequired && (
            <label>
              Student Lunch ID
              <input value={studentLunchId} onChange={(event) => setStudentLunchId(event.target.value)} inputMode="text" autoComplete="off" />
            </label>
          )}
          <label className="parent-consent">
            <input checked={consent} onChange={(event) => setConsent(event.target.checked)} type="checkbox" />
            <span>
              Access to this portal is for authorized school communication. By continuing, you accept receiving and responding to messages/documents related to the student or institution. You can only view information shared with you.
            </span>
          </label>
          {status && <div className="parent-access-status" role="status">{status}</div>}
          <div className="parent-access-actions">
            <button className="parent-primary-btn" type="submit" disabled={busy}>
              {busy ? 'Validating...' : 'I agree and continue'}
            </button>
            <button className="parent-secondary-btn" type="button" onClick={() => continueWith('signin')} disabled={busy}>
              I already have an account
            </button>
            {!isIpadGuestRuntime && (
              <button className="parent-secondary-btn" type="button" onClick={onCancel} disabled={busy}>
                Cancel
              </button>
            )}
          </div>
        </form>
        ) : (
        <form className="parent-access-form" onSubmit={handleAuthSubmit}>
          {otpUri && (
            <div className="parent-auth-otp">
              <img alt="Google Authenticator QR" src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(otpUri)}`} />
              {otpSecret && <p>Secret: <strong>{otpSecret}</strong></p>}
            </div>
          )}
          <label>
            Parent/guardian email
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" />
          </label>
          {authMode === 'signup' && <label>
            Full name
            <input value={`${firstName} ${lastName}`.trim()} readOnly />
          </label>}
          <label>
            Password
            <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" autoComplete={authMode === 'signin' ? 'current-password' : 'new-password'} />
          </label>
          {authMode === 'signin' && (
            <label>
              Authenticator code
              <input value={otpCode} onChange={(event) => setOtpCode(event.target.value)} inputMode="numeric" autoComplete="one-time-code" />
            </label>
          )}
          {status && <div className="parent-access-status" role="status">{status}</div>}
          <div className="parent-access-actions">
            <button className="parent-primary-btn" type="submit" disabled={busy}>
              {busy ? 'Validating...' : (authMode === 'signin' ? 'Sign in' : 'Create account')}
            </button>
            {authMode === 'signup' && <button className="parent-secondary-btn" type="button" onClick={() => { setAuthMode('signin'); setStatus('') }} disabled={busy}>
              I already have an account
            </button>}
            {authMode === 'signup' && <button className="parent-secondary-btn" type="button" onClick={() => setAuthMode(null)} disabled={busy}>
              Back
            </button>}
          </div>
        </form>
        )}
      </section>
    </main>
  )
}

function GuestReleasePortal({ token }: { token: string }) {
  const [request, setRequest] = useState<GuestReleaseRequest | null>(null)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [status, setStatus] = useState('')
  const [pickupName, setPickupName] = useState('')
  const [relationship, setRelationship] = useState('')
  const [reason, setReason] = useState('Parent request')
  const [notes, setNotes] = useState('')
  const [idPhoto, setIdPhoto] = useState('')
  const [facePhoto, setFacePhoto] = useState('')
  const [idPhotoStatus, setIdPhotoStatus] = useState('')
  const [facePhotoStatus, setFacePhotoStatus] = useState('')

  const loadGuestRelease = useCallback(async () => {
    if (!token) {
      setStatus('This release link is invalid. Contact the school.')
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const resp = await fetch(`/api/guest-release/early-dismiss/${encodeURIComponent(token)}`, { cache: 'no-store' })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) {
        const detail = String((data as any)?.detail || '')
        if (resp.status === 410 && detail.includes('REVOKED')) throw new Error('revoked')
        if (resp.status === 410 && detail.includes('EXPIRED')) throw new Error('expired')
        throw new Error(resp.status === 410 ? 'unavailable' : 'invalid')
      }
      const normalized = normalizeGuestReleaseRequest((data as any)?.request || data)
      setRequest(normalized)
      setPickupName((current) => current || normalized.pickupPersonName)
      setRelationship((current) => current || normalized.pickupRelationship)
      setReason((current) => current || normalized.reasonCode.replace(/_/g, ' ') || 'Parent request')
      setStatus('')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setRequest(null)
      setStatus(
        msg === 'expired'
          ? 'This release link expired. Contact the school.'
          : msg === 'revoked'
            ? 'This release link was revoked. Contact the school.'
            : msg === 'unavailable'
              ? 'This release link is no longer available. Contact the school.'
              : 'We could not validate this release link. Contact the school.'
      )
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    void loadGuestRelease()
  }, [loadGuestRelease])

  const handlePhoto = async (kind: 'id' | 'face', file: File | null | undefined) => {
    if (!file) return
    const setPhotoStatus = kind === 'id' ? setIdPhotoStatus : setFacePhotoStatus
    const setPhoto = kind === 'id' ? setIdPhoto : setFacePhoto
    setPhotoStatus(kind === 'id' ? 'Preparing ID photo...' : 'Preparing face photo...')
    try {
      const dataUrl = await readParentPickupPhoto(file)
      setPhoto(dataUrl)
      setPhotoStatus(kind === 'id' ? 'ID photo captured.' : 'Face photo captured.')
    } catch {
      setPhoto('')
      setPhotoStatus('We could not read this photo. Try again.')
    }
  }

  const submitGuestRelease = async (event: FormEvent) => {
    event.preventDefault()
    if (!pickupName.trim() || !relationship.trim() || !reason.trim() || !notes.trim() || !idPhoto || !facePhoto) {
      setStatus('Complete every required field, capture ID photo, and capture face photo before submitting.')
      return
    }
    setSubmitting(true)
    setStatus('Submitting verification...')
    try {
      const resp = await fetch(`/api/guest-release/early-dismiss/${encodeURIComponent(token)}/response`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          pickup_person_name: pickupName.trim(),
          pickup_relationship: relationship.trim(),
          pickup_reason: reason.trim(),
          notes: notes.trim(),
          id_photo_data_url: idPhoto,
          face_photo_data_url: facePhoto
        })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) {
        const detail = String((data as any)?.detail || '')
        if (resp.status === 409 && detail.includes('ALREADY_SUBMITTED')) throw new Error('ALREADY_SUBMITTED')
        if (resp.status === 409 && detail.includes('ALREADY_FINAL')) throw new Error('ALREADY_FINAL')
        if (resp.status === 410 && detail.includes('REVOKED')) throw new Error('REVOKED')
        if (resp.status === 410 && detail.includes('EXPIRED')) throw new Error('EXPIRED')
        throw new Error('SUBMIT_FAILED')
      }
      const normalized = normalizeGuestReleaseRequest((data as any)?.request || data)
      setRequest(normalized)
      setStatus('Verification submitted. Please wait for the front office.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      if (msg === 'ALREADY_SUBMITTED') setStatus('This release link was already submitted.')
      else if (msg === 'ALREADY_FINAL') setStatus('This release request is already closed.')
      else if (msg === 'REVOKED') setStatus('This release link was revoked. Contact the school.')
      else if (msg === 'EXPIRED') setStatus('This release link expired. Contact the school.')
      else setStatus('We could not submit this verification. Try again or contact the school.')
    } finally {
      setSubmitting(false)
    }
  }

  const submitted = Boolean(request?.submittedAt || request?.guestSessionStatus === 'submitted')
  const canSubmit = Boolean(pickupName.trim() && relationship.trim() && reason.trim() && notes.trim() && idPhoto && facePhoto)

  return (
    <main className="parent-portal guest-release-portal">
      <header className="parent-portal-header">
        <div>
          <span className="parent-access-kicker">Guest Release</span>
          <h1>Student pickup verification</h1>
          <p>{request?.studentName || 'School front office'}</p>
        </div>
      </header>
      <section className="parent-portal-body">
        {loading ? (
          <ParentPortalEmptyState title="Opening release link" text="Checking this secure pickup request." />
        ) : !request ? (
          <ParentPortalEmptyState title="Release link unavailable" text={status || 'Contact the school.'} />
        ) : submitted ? (
          <ParentPortalEmptyState title="Verification submitted" text="The front office received your form and photos. Please wait for staff instructions." />
        ) : (
          <form className="parent-early-dismiss" onSubmit={submitGuestRelease}>
            <div className="parent-early-dismiss-head">
              <div>
                <span className="parent-card-type">Limited release link</span>
                <h2>Complete pickup verification</h2>
                <p>This link only works for this student release request. It does not open school messages or documents.</p>
              </div>
              <span className="parent-early-dismiss-status status-pending">{request.guestSessionStatus || 'opened'}</span>
            </div>
            <div className="parent-early-dismiss-grid">
              <div>
                <span>Student</span>
                <strong>{request.studentName}</strong>
              </div>
              <div>
                <span>Expires</span>
                <strong>{formatParentDate(request.expiresAt) || 'Soon'}</strong>
              </div>
            </div>
            {request.reasonNotes && <p className="parent-early-dismiss-note">{request.reasonNotes}</p>}
            <div className="parent-early-dismiss-form">
              <label className="parent-early-dismiss-field">
                Pickup person full name
                <input value={pickupName} onChange={(event) => setPickupName(event.target.value)} required />
              </label>
              <label className="parent-early-dismiss-field">
                Relationship to student
                <input value={relationship} onChange={(event) => setRelationship(event.target.value)} required />
              </label>
              <label className="parent-early-dismiss-field">
                Reason for early pickup
                <select value={reason} onChange={(event) => setReason(event.target.value)} required>
                  <option>Parent request</option>
                  <option>Medical appointment</option>
                  <option>School appointment</option>
                  <option>Family emergency</option>
                  <option>Other</option>
                </select>
              </label>
              <label className="parent-early-dismiss-field parent-early-dismiss-field-wide">
                School pickup explanation * Required
                <textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  placeholder="Explain why the student is leaving early."
                  required
                />
              </label>
              <GuestReleasePhotoCapture
                title="ID photo"
                text="Capture the ID document requested by the front office."
                buttonLabel={idPhoto ? 'Retake ID photo' : 'Capture ID photo'}
                capture="environment"
                photoDataUrl={idPhoto}
                status={idPhotoStatus}
                onPhoto={(file) => handlePhoto('id', file)}
              />
              <GuestReleasePhotoCapture
                title="Face photo"
                text="Capture a clear face photo/selfie for staff review."
                buttonLabel={facePhoto ? 'Retake face photo' : 'Capture face photo'}
                capture="user"
                photoDataUrl={facePhoto}
                status={facePhotoStatus}
                onPhoto={(file) => handlePhoto('face', file)}
              />
            </div>
            <div className="parent-early-dismiss-actions">
              <button className="parent-primary-btn" type="submit" disabled={submitting || !canSubmit}>
                {submitting ? 'Submitting...' : 'Submit verification'}
              </button>
              <button className="parent-secondary-btn" type="button" onClick={loadGuestRelease} disabled={submitting}>
                Refresh
              </button>
            </div>
            {status && <div className="parent-action-status">{status}</div>}
          </form>
        )}
      </section>
    </main>
  )
}

function GuestReleasePhotoCapture({
  title,
  text,
  buttonLabel,
  capture,
  photoDataUrl,
  status,
  onPhoto
}: {
  title: string
  text: string
  buttonLabel: string
  capture: 'environment' | 'user'
  photoDataUrl: string
  status: string
  onPhoto: (file: File | null | undefined) => void
}) {
  return (
    <div className="parent-early-dismiss-photo">
      <div>
        <span className="parent-card-type">{title}</span>
        <p>{text}</p>
      </div>
      {photoDataUrl ? (
        <img src={photoDataUrl} alt={`${title} preview`} />
      ) : (
        <div className="parent-early-dismiss-photo-empty">Not captured</div>
      )}
      <label className="parent-secondary-btn parent-photo-btn">
        {buttonLabel}
        <input type="file" accept="image/*" capture={capture} onChange={(event) => onPhoto(event.target.files?.[0])} />
      </label>
      {status && <p>{status}</p>}
    </div>
  )
}

function ParentGuestPortal({
  session,
  onLogout
}: {
  session: ParentSession
  onLogout: () => void
}) {
  const [messages, setMessages] = useState<ParentMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [tab, setTab] = useState<'messages' | 'news' | 'events' | 'documents' | 'confirmations' | 'cloud'>(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem('NEXUS_PARENT_UPDATE_UI') || 'null')
      sessionStorage.removeItem('NEXUS_PARENT_UPDATE_UI')
      if (saved?.email === session.email && Date.now() - saved.savedAt < 600000 && ['messages', 'news', 'events', 'documents', 'confirmations', 'cloud'].includes(saved.tab)) return saved.tab
    } catch { /* A missing update snapshot opens the inbox normally. */ }
    return 'messages'
  })
  const [selectedMessageId, setSelectedMessageId] = useState('')
  const [documentViewer, setDocumentViewer] = useState<ParentDocumentViewerState | null>(null)
  const [replyDrafts, setReplyDrafts] = useState<Record<string, string>>({})
  const [replyOpen, setReplyOpen] = useState<Record<string, boolean>>({})
  const [actionStatus, setActionStatus] = useState<Record<string, string>>({})
  const [pushStatus, setPushStatus] = useState('Enable alerts')
  const [pushBusy, setPushBusy] = useState(false)
  const [pushEnabled, setPushEnabled] = useState(false)
  const [notificationHelpOpen, setNotificationHelpOpen] = useState(false)
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [installStatus, setInstallStatus] = useState('')
  const [refreshing, setRefreshing] = useState(false)
  const [lastCheckedAt, setLastCheckedAt] = useState('')
  const [newMessageNotice, setNewMessageNotice] = useState('')
  const [deliveryNotifications, setDeliveryNotifications] = useState<ParentDeliveryNotification[]>([])
  const [deliveryEmailEnabled, setDeliveryEmailEnabled] = useState(false)
  const [deliveryEmailProvider, setDeliveryEmailProvider] = useState('smtp')
  const [deliveryStatus, setDeliveryStatus] = useState('')
  const [earlyDismissal, setEarlyDismissal] = useState<ParentEarlyDismissal | null>(null)
  const [earlyDismissLoading, setEarlyDismissLoading] = useState(false)
  const [earlyDismissStatus, setEarlyDismissStatus] = useState('')
  const [earlyDismissNote, setEarlyDismissNote] = useState('')
  const [earlyDismissPickupName, setEarlyDismissPickupName] = useState('')
  const [earlyDismissPickupRelationship, setEarlyDismissPickupRelationship] = useState('')
  const [earlyDismissPickupReason, setEarlyDismissPickupReason] = useState('Parent request')
  const [earlyDismissPhotoDataUrl, setEarlyDismissPhotoDataUrl] = useState('')
  const [earlyDismissPhotoStatus, setEarlyDismissPhotoStatus] = useState('')
  const knownMessageIdsRef = useRef<Set<string>>(new Set())
  const initialMessageLoadRef = useRef(false)
  const routeConsumedRef = useRef(false)

  const selectedMessage = messages.find((message) => message.messageId === selectedMessageId) || null
  const documents = messages.flatMap((message) => (
    message.attachments.map((attachment) => ({ message, attachment }))
  ))
  const confirmations = messages.filter((message) => (
    parentMessageAllows(message, 'acknowledge') && !message.acknowledgedAt && message.status !== 'acknowledged'
  ))
  const newsItems = messages.filter((message) => message.messageType === 'news')
  const eventItems = messages.filter((message) => message.messageType === 'event')
  const messageItems = messages.filter((message) => !['news', 'event'].includes(message.messageType))
  const visibleMessages = tab === 'confirmations'
    ? confirmations
    : tab === 'news'
      ? newsItems
      : tab === 'events'
        ? eventItems
        : messageItems
  const linkedStudentName =
    messages.find((message) => message.relatedStudentName)?.relatedStudentName ||
    readStringValue(window.localStorage.getItem('NEXUS_PARENT_GUEST_STUDENT_NAME')) ||
    'Student'
  const notificationPermission = parentPushSupported() ? Notification.permission : 'unsupported'
  const hasNotificationApi = typeof window !== 'undefined' && 'Notification' in window
  const hasServiceWorkerApi = typeof navigator !== 'undefined' && 'serviceWorker' in navigator
  const hasPushManagerApi = typeof window !== 'undefined' && 'PushManager' in window
  const runtimeProfile = detectRuntimeDeviceProfile()
  const standaloneMode = window.matchMedia?.('(display-mode: standalone)').matches || Boolean((navigator as any).standalone)
  const platformGuide = parentPortalPlatformGuide()
  const isIpadWorkspace = runtimeProfile.deviceClass === 'ipad'
  const isTrustedGuest = String(session.role || '').trim().toLowerCase() === 'trusted_guest'
  const workspaceKicker = isIpadWorkspace ? 'Nexus iPad Guest Workspace' : 'Parent Portal'
  const workspaceTitle = isIpadWorkspace ? 'School workspace' : 'School messages'
  const workspaceSubtitle = isTrustedGuest
    ? `${linkedStudentName} · Trusted Guest`
    : linkedStudentName
  const notificationPlatform = platformGuide.label
  const portalOrigin = window.location.origin || 'https://nexusquantumbridgenetworks.ai'
  const latestDelivery = deliveryNotifications[0] || null
  const portalRouteTarget = parentPortalRouteTarget()
  const earlyDismissRouteId = portalRouteTarget.type === 'early_dismissal' ? portalRouteTarget.id : ''

  const checkMessageAlertStatus = async () => {
    if (platformGuide.requiresStandaloneForPush && !standaloneMode) {
      setPushEnabled(false)
      setPushStatus('Add to Home Screen first')
      return
    }
    if (!parentPushSupported()) {
      setPushEnabled(false)
      setPushStatus('Alerts unavailable')
      return
    }
    if (Notification.permission === 'denied') {
      setPushEnabled(false)
      setPushStatus('Alerts blocked')
      return
    }
    if (Notification.permission !== 'granted') {
      setPushEnabled(false)
      setPushStatus('Enable alerts')
      return
    }
    try {
      const registration = await navigator.serviceWorker.getRegistration('/parent-guest-sw.js')
      const subscription = await registration?.pushManager.getSubscription()
      setPushEnabled(!!subscription)
      setPushStatus(subscription ? 'Alerts active' : 'Enable alerts')
    } catch {
      setPushEnabled(false)
      setPushStatus('Enable alerts')
    }
  }

  const loadParentMessages = useCallback(async (options: { silent?: boolean } = {}) => {
    if (!session.email || !session.lease) {
      setMessages([])
      setError('device_unlinked')
      setLoading(false)
      setRefreshing(false)
      return
    }
    if (options.silent) setRefreshing(true)
    else setLoading(true)
    setError('')
    try {
      const resp = await fetch('/api/institutional/messages?box=inbox', {
        cache: 'no-store',
        headers: parentPortalHeaders(session)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) {
        throw new Error(resp.status === 403 ? 'permission' : `HTTP_${resp.status}`)
      }
      const rawMessages: unknown[] = Array.isArray((data as any)?.messages)
        ? (data as any).messages
        : Array.isArray(data)
          ? data
          : []
      const rawNotifications: unknown[] = Array.isArray((data as any)?.notifications)
        ? (data as any).notifications
        : []
      let ackMap: Record<string, string> = {}
      try {
        ackMap = JSON.parse(window.localStorage.getItem('NEXUS_PARENT_GUEST_ACKS_V1') || '{}')
      } catch {
        ackMap = {}
      }
      const nextMessages: ParentMessage[] = rawMessages.map(normalizeParentMessage).map((message: ParentMessage) => {
        const acknowledgedAt = ackMap[message.messageId]
        return typeof acknowledgedAt === 'string' && acknowledgedAt
          ? { ...message, acknowledgedAt, status: 'acknowledged' }
          : message
      })
      const previousIds = knownMessageIdsRef.current
      const newMessages = nextMessages.filter((message) => message.messageId && !previousIds.has(message.messageId))
      const firstLoad = !initialMessageLoadRef.current
      knownMessageIdsRef.current = new Set(nextMessages.map((message) => message.messageId).filter(Boolean))
      initialMessageLoadRef.current = true
      setMessages(nextMessages)
      setDeliveryNotifications(rawNotifications.map(normalizeParentDeliveryNotification))
      setLastCheckedAt(new Date().toISOString())
      if (!firstLoad && newMessages.length > 0) {
        const count = newMessages.length
        setNewMessageNotice(count === 1 ? 'New school message received.' : `${count} new school messages received.`)
        if ('vibrate' in navigator) {
          try {
            navigator.vibrate([120, 60, 120])
          } catch {
            // Android vibration is best effort.
          }
        }
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      setError(msg === 'permission' ? 'permission' : 'invalid')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [session.email, session.lease, session.role])

  useEffect(() => {
    void loadParentMessages()
  }, [loadParentMessages])

  const loadEarlyDismissal = useCallback(async (earlyDismissalId = earlyDismissRouteId) => {
    if (!earlyDismissalId || !session.email || !session.lease) return
    setEarlyDismissLoading(true)
    setEarlyDismissStatus('Opening pickup confirmation...')
    try {
      const resp = await fetch(`/api/parent/early-dismiss/${encodeURIComponent(earlyDismissalId)}`, {
        cache: 'no-store',
        headers: parentPortalHeaders(session)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) throw new Error('EARLY_DISMISSAL_LOAD_FAILED')
      const normalized = normalizeParentEarlyDismissal((data as any)?.request || data)
      setEarlyDismissal(normalized)
      setEarlyDismissPickupName((current) => current || normalized.pickupPersonName)
      setEarlyDismissPickupRelationship((current) => current || normalized.pickupRelationship)
      setEarlyDismissPickupReason((current) => current || normalized.reasonCode.replace(/_/g, ' ') || 'Parent request')
      setEarlyDismissStatus('')
    } catch {
      setEarlyDismissal(null)
      setEarlyDismissStatus('We could not open this pickup confirmation. Contact the school.')
    } finally {
      setEarlyDismissLoading(false)
    }
  }, [earlyDismissRouteId, session.email, session.lease, session.role])

  useEffect(() => {
    if (!earlyDismissRouteId) return
    void loadEarlyDismissal(earlyDismissRouteId)
  }, [earlyDismissRouteId, loadEarlyDismissal])

  useEffect(() => {
    const interval = window.setInterval(() => {
      void loadParentMessages({ silent: true })
    }, 30000)
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') void loadParentMessages({ silent: true })
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [loadParentMessages])

  useEffect(() => () => {
    if (documentViewer?.objectUrl) URL.revokeObjectURL(documentViewer.objectUrl)
  }, [documentViewer])

  useEffect(() => {
    let cancelled = false
    const checkPushStatus = async () => {
      if (!cancelled) await checkMessageAlertStatus()
    }
    void checkPushStatus()
    return () => {
      cancelled = true
    }
  }, [session.email, session.lease])

  useEffect(() => {
    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      setInstallPrompt(event as BeforeInstallPromptEvent)
      setInstallStatus('Parent Portal can be installed on this device.')
    }
    const onAppInstalled = () => {
      setInstallPrompt(null)
      setInstallStatus('Parent Portal installed. Open it from the device icon.')
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt)
    window.addEventListener('appinstalled', onAppInstalled)
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt)
      window.removeEventListener('appinstalled', onAppInstalled)
    }
  }, [])

  const setMessageStatus = (messageId: string, value: string) => {
    setActionStatus((current) => ({ ...current, [messageId]: value }))
  }

  const installParentPortal = async () => {
    if (standaloneMode) {
      setInstallStatus('Parent Portal is already installed on this device.')
      return
    }
    if (!installPrompt) {
      setInstallStatus(platformGuide.key === 'ios_iphone' || platformGuide.key === 'ios_ipad'
        ? 'On iPhone/iPad, use Safari Share, then Add to Home Screen.'
        : 'Use the browser install/download icon or menu, then open Parent Portal from the new icon.')
      return
    }
    try {
      await installPrompt.prompt()
      const choice = await installPrompt.userChoice?.catch(() => undefined)
      setInstallPrompt(null)
      setInstallStatus(choice?.outcome === 'accepted'
        ? 'Parent Portal installed. Open it from the device icon.'
        : 'Installation was dismissed. You can install it later from the browser menu.')
    } catch {
      setInstallStatus('Use the browser install/download icon or menu, then open Parent Portal from the new icon.')
    }
  }

  const enableMessageAlerts = async () => {
    if (!session.email || !session.lease) {
      setPushStatus('Link device first')
      return
    }
    if (platformGuide.requiresStandaloneForPush && !standaloneMode) {
      setNotificationHelpOpen(true)
      setPushStatus('Add to Home Screen first')
      return
    }
    if (!parentPushSupported()) {
      setPushStatus('Alerts unavailable')
      return
    }
    setPushBusy(true)
    setPushStatus('Enabling alerts...')
    try {
      const permission = Notification.permission === 'granted'
        ? 'granted'
        : await Notification.requestPermission()
      if (permission !== 'granted') {
        setPushStatus(permission === 'denied' ? 'Alerts blocked' : 'Enable alerts')
        setPushEnabled(false)
        return
      }
      const registration = await navigator.serviceWorker.register('/parent-guest-sw.js', { scope: '/' })
      const configResp = await fetch('/api/parent/push/vapid-public-key', {
        cache: 'no-store',
        headers: parentPortalHeaders(session)
      })
      const config = await configResp.json().catch(() => ({}))
      const publicKey = readStringValue((config as any).applicationServerKey || (config as any).publicKey)
      if (!configResp.ok || !publicKey) throw new Error('PUSH_KEY_UNAVAILABLE')
      const existing = await registration.pushManager.getSubscription()
      const subscription = existing || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: parentPushApplicationServerKey(publicKey)
      })
      const saveResp = await fetch('/api/parent/push/subscriptions', {
        method: 'POST',
        headers: parentPortalHeaders(session),
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          device_id: getSessionId(),
          platform: navigator.userAgent || 'browser'
        })
      })
      const saveData = await saveResp.json().catch(() => ({}))
      if (!saveResp.ok || ((saveData as any)?.ok === false)) throw new Error('SUBSCRIPTION_SAVE_FAILED')
      setPushEnabled(true)
      setPushStatus('Alerts active')
    } catch {
      setPushEnabled(false)
      setPushStatus('Alerts unavailable')
    } finally {
      setPushBusy(false)
    }
  }

  const loadDeliveryStatus = async () => {
    if (!session.email || !session.lease) return
    try {
      const resp = await fetch('/api/parent/notifications/delivery', {
        cache: 'no-store',
        headers: parentPortalHeaders(session)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) throw new Error('DELIVERY_STATUS_FAILED')
      const rawNotifications: unknown[] = Array.isArray((data as any)?.notifications) ? (data as any).notifications : []
      setDeliveryNotifications(rawNotifications.map(normalizeParentDeliveryNotification))
      setDeliveryEmailEnabled(Boolean((data as any)?.email_enabled))
      setDeliveryEmailProvider(readStringValue((data as any)?.email_provider || 'smtp'))
      setDeliveryStatus('Delivery status updated')
    } catch {
      setDeliveryStatus('Delivery status unavailable')
    }
  }

  const sendLocalNotificationTest = async () => {
    if (!parentPushSupported()) {
      setPushStatus('Alerts unavailable')
      return
    }
    setPushBusy(true)
    setPushStatus('Sending local test...')
    try {
      const registration = await navigator.serviceWorker.ready
      const testId = `local-test-${Date.now()}`
      const notificationOptions = {
        body: 'Test alert from Parent Portal.',
        tag: `parent-local-test-${testId}`,
        renotify: true,
        requireInteraction: true,
        silent: false,
        timestamp: Date.now(),
        vibrate: [220, 90, 220, 90, 320],
        icon: '/landing-hero.png',
        badge: '/landing-hero.png',
        actions: [{ action: 'open', title: 'Open Parent Portal' }],
        data: { url: '/?flow=parent_guest', messageId: testId }
      } as NotificationOptions & {
        renotify?: boolean
        vibrate?: number[]
        timestamp?: number
        badge?: string
        actions?: Array<{ action: string; title: string }>
      }
      await registration.showNotification('Message from school', notificationOptions)
      setPushStatus('Local test sent')
    } catch {
      setPushStatus('Local test failed')
    } finally {
      setPushBusy(false)
    }
  }

  const sendServerPushTest = async () => {
    setPushBusy(true)
    setPushStatus('Sending server push...')
    try {
      const resp = await fetch('/api/parent/push/test', {
        method: 'POST',
        headers: parentPortalHeaders(session),
        body: JSON.stringify({})
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) throw new Error('SERVER_PUSH_FAILED')
      setPushStatus('Server push sent')
      window.setTimeout(() => { void loadDeliveryStatus() }, 1200)
    } catch {
      setPushStatus('Server push failed')
    } finally {
      setPushBusy(false)
    }
  }

  const sendInboxFallbackTest = async () => {
    setPushBusy(true)
    setDeliveryStatus('Creating inbox test...')
    try {
      const resp = await fetch('/api/parent/notifications/test-inbox', {
        method: 'POST',
        headers: parentPortalHeaders(session),
        body: JSON.stringify({})
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) throw new Error('INBOX_TEST_FAILED')
      setDeliveryStatus('Inbox test created')
      await loadParentMessages({ silent: true })
    } catch {
      setDeliveryStatus('Inbox test failed')
    } finally {
      setPushBusy(false)
    }
  }

  const sendEmailFallbackTest = async () => {
    setPushBusy(true)
    setDeliveryStatus('Sending email test...')
    try {
      const resp = await fetch('/api/parent/notifications/test-email', {
        method: 'POST',
        headers: parentPortalHeaders(session),
        body: JSON.stringify({})
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error('EMAIL_TEST_FAILED')
      setDeliveryStatus((data as any)?.ok ? 'Email test sent' : 'Email not configured')
      await loadDeliveryStatus()
    } catch {
      setDeliveryStatus('Email test failed')
    } finally {
      setPushBusy(false)
    }
  }

  const resetMessageAlerts = async () => {
    if (!parentPushSupported()) {
      setPushStatus('Alerts unavailable')
      return
    }
    setPushBusy(true)
    setPushStatus('Resetting alerts...')
    try {
      const registration = await navigator.serviceWorker.getRegistration('/parent-guest-sw.js')
      const subscription = await registration?.pushManager.getSubscription()
      if (subscription) await subscription.unsubscribe()
      setPushEnabled(false)
      setPushStatus('Enable alerts')
    } catch {
      setPushStatus('Enable alerts')
    } finally {
      setPushBusy(false)
    }
  }

  const respondEarlyDismiss = async (response: 'confirm' | 'reject') => {
    const earlyDismissalId = earlyDismissal?.earlyDismissalId || earlyDismissRouteId
    if (!earlyDismissalId) return
    const pickupName = earlyDismissPickupName.trim()
    const pickupRelationship = earlyDismissPickupRelationship.trim()
    const pickupReason = earlyDismissPickupReason.trim()
    const pickupNote = earlyDismissNote.trim()
    if (response === 'confirm' && (!pickupName || !pickupRelationship || !pickupReason || !pickupNote || !earlyDismissPhotoDataUrl)) {
      setEarlyDismissStatus('Complete every required field and take a pickup photo before confirming.')
      return
    }
    setEarlyDismissLoading(true)
    setEarlyDismissStatus(response === 'confirm' ? 'Confirming pickup...' : 'Sending response to school...')
    try {
      const resp = await fetch(`/api/parent/early-dismiss/${encodeURIComponent(earlyDismissalId)}/response`, {
        method: 'POST',
        headers: parentPortalHeaders(session),
        body: JSON.stringify({
          response,
          notes: pickupNote,
          pickup_person_name: response === 'confirm' ? pickupName : undefined,
          pickup_relationship: response === 'confirm' ? pickupRelationship : undefined,
          pickup_reason: response === 'confirm' ? pickupReason : undefined,
          photo_data_url: response === 'confirm' ? earlyDismissPhotoDataUrl : undefined
        })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) throw new Error('EARLY_DISMISSAL_RESPONSE_FAILED')
      setEarlyDismissal(normalizeParentEarlyDismissal((data as any)?.request || data))
      setEarlyDismissStatus(response === 'confirm' ? 'Pickup confirmed. The front office has been notified.' : 'Response sent. The front office has been notified.')
      await loadParentMessages({ silent: true })
    } catch {
      setEarlyDismissStatus('We could not send this response. Try again or call the school.')
    } finally {
      setEarlyDismissLoading(false)
    }
  }

  const handleEarlyDismissPhoto = async (file: File | null | undefined) => {
    if (!file) return
    setEarlyDismissPhotoStatus('Preparing photo...')
    try {
      const dataUrl = await readParentPickupPhoto(file)
      setEarlyDismissPhotoDataUrl(dataUrl)
      setEarlyDismissPhotoStatus('Photo ready.')
    } catch {
      setEarlyDismissPhotoDataUrl('')
      setEarlyDismissPhotoStatus('We could not read this photo. Try again.')
    }
  }

  const closeDocumentViewer = () => {
    setDocumentViewer((current) => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl)
      return null
    })
  }

  const openAttachment = async (message: ParentMessage, attachment: ParentAttachment) => {
    const url = parentAttachmentUrl(attachment)
    if (!url) {
      setMessageStatus(message.messageId, 'PDF unavailable.')
      return
    }
    const isPdf = attachment.mime.includes('pdf') || attachment.type.toLowerCase().includes('pdf') || /\.pdf($|\?)/i.test(url)
    setMessageStatus(message.messageId, isPdf ? 'Opening PDF...' : 'Opening document...')
    const viewerUrl = await resolveParentViewerUrl(url, session)
    setDocumentViewer((current) => {
      if (current?.objectUrl) URL.revokeObjectURL(current.objectUrl)
      return {
        message,
        attachment,
        url: viewerUrl.url,
        isPdf,
        objectUrl: viewerUrl.objectUrl,
        sourceUrl: url
      }
    })
    setSelectedMessageId('')
  }

  useEffect(() => {
    if (routeConsumedRef.current || messages.length === 0) return
    const target = parentPortalRouteTarget()
    if (!target.id) return
    if (target.type === 'document') {
      for (const message of messages) {
        const attachment = message.attachments.find((item) => item.id === target.id || item.name === target.id)
        if (attachment) {
          routeConsumedRef.current = true
          openAttachment(message, attachment)
          return
        }
      }
      return
    }
    const message = messages.find((item) => item.messageId === target.id)
    if (message) {
      routeConsumedRef.current = true
      setSelectedMessageId(message.messageId)
      closeDocumentViewer()
    }
  }, [messages])

  const confirmReceived = async (message: ParentMessage) => {
    if (!parentMessageAllows(message, 'acknowledge')) {
      setMessageStatus(message.messageId, 'Insufficient permission.')
      return
    }
    setMessageStatus(message.messageId, 'Confirming received...')
    try {
      const resp = await fetch(`/api/institutional/messages/${encodeURIComponent(message.messageId)}/read`, {
        method: 'POST',
        headers: parentPortalHeaders(session),
        body: JSON.stringify({ acknowledge: true })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) throw new Error('ACK_FAILED')
      const acknowledgedAt = new Date().toISOString()
      try {
        const ackMap = JSON.parse(window.localStorage.getItem('NEXUS_PARENT_GUEST_ACKS_V1') || '{}')
        ackMap[message.messageId] = acknowledgedAt
        window.localStorage.setItem('NEXUS_PARENT_GUEST_ACKS_V1', JSON.stringify(ackMap))
      } catch {
        // local acknowledgement persistence is best effort
      }
      setMessages((current) => current.map((item) => (
        item.messageId === message.messageId
          ? { ...item, acknowledgedAt, status: 'acknowledged' }
          : item
      )))
      setMessageStatus(message.messageId, 'Received confirmed.')
    } catch {
      setMessageStatus(message.messageId, 'We could not confirm receipt. Try again.')
    }
  }

  const sendReply = async (message: ParentMessage) => {
    if (!parentMessageAllows(message, 'reply')) {
      setMessageStatus(message.messageId, 'Insufficient permission.')
      return
    }
    const body = (replyDrafts[message.messageId] || '').trim()
    if (!body) {
      setMessageStatus(message.messageId, 'Write your reply.')
      return
    }
    setMessageStatus(message.messageId, 'Sending reply...')
    try {
      const resp = await fetch('/api/institutional/messages', {
        method: 'POST',
        headers: parentPortalHeaders(session),
        body: JSON.stringify({
          recipient_email: message.senderEmail,
          recipient_role: message.senderRole || 'teacher',
          subject: `Re: ${message.subject || 'School message'}`,
          body,
          related_student_id: message.relatedStudentId || undefined,
          thread_id: message.messageId
        })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok || ((data as any)?.ok === false)) throw new Error('REPLY_FAILED')
      setReplyDrafts((current) => ({ ...current, [message.messageId]: '' }))
      setReplyOpen((current) => ({ ...current, [message.messageId]: false }))
      setMessages((current) => current.map((item) => (
        item.messageId === message.messageId ? { ...item, status: 'replied' } : item
      )))
      setMessageStatus(message.messageId, 'Reply sent.')
    } catch {
      setMessageStatus(message.messageId, 'We could not send the reply. Your session is still active.')
    }
  }

  const openEarlyDismissalForm = (earlyDismissalId: string) => {
    if (!earlyDismissalId) return
    const target = `/parent-portal/early-dismiss/${encodeURIComponent(earlyDismissalId)}`
    window.location.assign(target)
  }

  const renderMessageCard = (message: ParentMessage, compact = false) => {
    const earlyDismissalId = parentMessageEarlyDismissalId(message)
    const canReply = !earlyDismissalId && parentMessageAllows(message, 'reply')
    const canAcknowledge = !earlyDismissalId && parentMessageAllows(message, 'acknowledge') && !message.acknowledgedAt && message.status !== 'acknowledged'
    const canEdit = !earlyDismissalId && parentMessageAllows(message, 'edit_if_allowed')
    const statusText = actionStatus[message.messageId] || ''
    return (
      <article className={`parent-message-card ${compact ? 'compact' : ''}`} key={message.messageId}>
        <button
          className="parent-card-main"
          onClick={() => {
            if (earlyDismissalId) {
              openEarlyDismissalForm(earlyDismissalId)
              return
            }
            setSelectedMessageId(message.messageId)
          }}
        >
          <span className="parent-card-type">{parentMessageTypeLabel(message)}</span>
          <h3>{message.subject || 'School message'}</h3>
          <p>{message.body || 'No additional content.'}</p>
          <span className="parent-card-meta">
            {message.senderName || 'School'}{message.createdAt ? ` · ${formatParentDate(message.createdAt)}` : ''}{message.eventDate ? ` · Event: ${formatParentEventDate(message.eventDate)}` : ''}
          </span>
        </button>
        {message.attachments.length > 0 && (
          <div className="parent-attachment-row">
            {message.attachments.map((attachment) => (
              <button key={attachment.id} className="parent-secondary-btn" onClick={() => openAttachment(message, attachment)}>
                {attachment.mime.includes('pdf') || attachment.type.toLowerCase().includes('pdf') || /\.pdf$/i.test(attachment.name) ? 'Open PDF' : 'Shared document'}
              </button>
            ))}
          </div>
        )}
        <div className="parent-card-actions">
          {earlyDismissalId && (
            <button className="parent-primary-btn" onClick={() => openEarlyDismissalForm(earlyDismissalId)}>
              Open pickup form
            </button>
          )}
          {canReply && (
            <button className="parent-primary-btn" onClick={() => setReplyOpen((current) => ({ ...current, [message.messageId]: !current[message.messageId] }))}>
              Reply
            </button>
          )}
          {canAcknowledge && (
            <button className="parent-secondary-btn" onClick={() => confirmReceived(message)}>
              Confirm received
            </button>
          )}
          {canEdit && <span className="parent-permission-note">Editing allowed by the school</span>}
          {earlyDismissalId
            ? <span className="parent-permission-note">Form required by front office</span>
            : (!canReply && !canAcknowledge && <span className="parent-permission-note">Read only</span>)}
        </div>
        {replyOpen[message.messageId] && (
          <div className="parent-reply-box">
            <textarea
              value={replyDrafts[message.messageId] || ''}
              onChange={(event) => setReplyDrafts((current) => ({ ...current, [message.messageId]: event.target.value }))}
              placeholder="Write your reply to the school"
            />
            <div className="parent-reply-actions">
              <button className="parent-primary-btn" onClick={() => sendReply(message)}>Send</button>
              <button
                className="parent-secondary-btn"
                onClick={() => setReplyDrafts((current) => ({ ...current, [message.messageId]: 'I am returning this message to the sender for review.' }))}
              >
                Return to sender
              </button>
            </div>
          </div>
        )}
        {statusText && <div className="parent-action-status">{statusText}</div>}
      </article>
    )
  }

  let body: ReactNode
  if (error === 'device_unlinked') {
    body = <ParentPortalEmptyState title="Device not linked" text="Open the secure link from the school to link this device." />
  } else if (earlyDismissRouteId) {
    body = (
      <ParentEarlyDismissalPanel
        request={earlyDismissal}
        loading={earlyDismissLoading}
        status={earlyDismissStatus}
        note={earlyDismissNote}
        pickupName={earlyDismissPickupName}
        pickupRelationship={earlyDismissPickupRelationship}
        pickupReason={earlyDismissPickupReason}
        photoDataUrl={earlyDismissPhotoDataUrl}
        photoStatus={earlyDismissPhotoStatus}
        onNoteChange={setEarlyDismissNote}
        onPickupNameChange={setEarlyDismissPickupName}
        onPickupRelationshipChange={setEarlyDismissPickupRelationship}
        onPickupReasonChange={setEarlyDismissPickupReason}
        onPhotoSelected={handleEarlyDismissPhoto}
        onRefresh={() => loadEarlyDismissal(earlyDismissRouteId)}
        onRespond={respondEarlyDismiss}
      />
    )
  } else if (loading) {
    body = <ParentPortalEmptyState title="Loading messages" text="Opening your school portal." />
  } else if (error === 'permission') {
    body = <ParentPortalEmptyState title="Insufficient permission" text="You do not have permission to view this content. Contact the school." />
  } else if (error) {
    body = <ParentPortalEmptyState title="We could not validate this invitation" text="Contact the school." />
  } else if (documentViewer) {
    body = <ParentDocumentViewer viewer={documentViewer} onBack={closeDocumentViewer} />
  } else if (selectedMessage) {
    body = (
      <section className="parent-detail">
        <button className="parent-secondary-btn parent-back-btn" onClick={() => setSelectedMessageId('')}>Back</button>
        {renderMessageCard(selectedMessage)}
      </section>
    )
  } else if (tab === 'documents') {
    body = documents.length ? (
      <section className="parent-document-grid">
        {documents.map(({ message, attachment }) => (
          <article className="parent-document-card" key={`${message.messageId}-${attachment.id}`}>
            <span>Shared document</span>
            <h3>{attachment.title || attachment.name || 'Shared document'}</h3>
            <p>{message.subject || 'School message'}</p>
            <button className="parent-primary-btn" onClick={() => openAttachment(message, attachment)}>
              {attachment.mime.includes('pdf') || attachment.type.toLowerCase().includes('pdf') || /\.pdf$/i.test(attachment.name) ? 'Open PDF' : 'Open document'}
            </button>
          </article>
        ))}
      </section>
    ) : <ParentPortalEmptyState title="No documents" text="No documents have been shared." />
  } else {
    body = visibleMessages.length ? (
      <section className="parent-message-list">
        {visibleMessages.map((message) => renderMessageCard(message, true))}
      </section>
    ) : (
      <ParentPortalEmptyState
        title={
          tab === 'confirmations'
            ? 'No pending confirmations'
            : tab === 'news'
              ? 'No school news'
              : tab === 'events'
                ? 'No events'
                : 'No messages'
        }
        text={
          tab === 'confirmations'
            ? 'You do not have pending confirmations.'
            : tab === 'news'
              ? 'No school news has been shared.'
              : tab === 'events'
                ? 'No school events have been shared.'
                : 'You do not have school messages.'
        }
      />
    )
  }

  return (
    <main className={`parent-portal ${isIpadWorkspace ? 'parent-portal-tablet' : ''}`}>
      <div className="parent-portal-brandline"><span className="parent-access-kicker">{workspaceKicker}</span><WorkspaceUpdate beforeReload={() => {
        sessionStorage.setItem('NEXUS_PARENT_UPDATE_UI', JSON.stringify({ email: session.email, tab, savedAt: Date.now() }))
      }} /></div>
      <header className="parent-portal-header">
        <div>
          <h1>{workspaceTitle}</h1>
          <p>{workspaceSubtitle}</p>
        </div>
        <div className="parent-header-actions">
          <button
            className="parent-secondary-btn"
            onClick={() => loadParentMessages({ silent: true })}
            disabled={refreshing}
          >
            {refreshing ? 'Checking...' : 'Refresh inbox'}
          </button>
          <button
            className={`parent-secondary-btn parent-alert-btn ${pushEnabled ? 'active' : ''}`}
            onClick={enableMessageAlerts}
            disabled={pushBusy || pushStatus === 'Alerts blocked'}
          >
            {pushBusy ? 'Enabling alerts...' : pushStatus}
          </button>
          <button className="parent-secondary-btn" onClick={() => setNotificationHelpOpen((value) => !value)}>
            Notification setup
          </button>
          {pushEnabled && (
            <button className="parent-secondary-btn" onClick={resetMessageAlerts} disabled={pushBusy}>
              Reset alerts
            </button>
          )}
          <button className="parent-secondary-btn" onClick={onLogout}>Sign out</button>
        </div>
      </header>
      {isIpadWorkspace && (
        <section className={`parent-ipad-banner ${standaloneMode ? 'active' : ''}`}>
          <strong>{standaloneMode ? 'iPad Workspace active.' : 'Install Nexus to Home Screen for full iPad workspace.'}</strong>
          <p>
            {standaloneMode
              ? 'You are running the installed iPad workspace. Messages, documents, camera capture, and forms are enabled for this role.'
              : 'Open this link in Safari, tap Share, then Add to Home Screen. Launch from the Nexus icon to enable full iPad behavior.'}
          </p>
        </section>
      )}
      {notificationHelpOpen && (
        <section className="parent-notification-guide">
          <div className="parent-notification-guide-head">
            <div>
              <span className="parent-card-type">Notification setup</span>
              <h2>Allow school alerts</h2>
              <p>{notificationPlatform} · {portalOrigin}</p>
            </div>
            <span className={`parent-notification-status ${pushEnabled ? 'active' : ''}`}>
              {notificationPermission === 'granted'
                ? (pushEnabled ? 'Push subscribed, display unknown' : 'Allowed')
                : notificationPermission === 'denied'
                  ? 'Blocked'
                  : notificationPermission === 'default'
                    ? 'Not allowed yet'
                    : 'Unavailable'}
            </span>
          </div>
          <div className="parent-platform-guide">
            <article className="parent-platform-card">
              <span>Your device</span>
              <h3>{notificationPlatform}</h3>
              <p>{platformGuide.note}</p>
              <p>{standaloneMode ? 'Opened as installed app.' : 'Opened in browser tab.'}</p>
            </article>
            <article className="parent-platform-card">
              <span>{platformGuide.installTitle}</span>
              <ol>
                {platformGuide.installSteps.map((step) => <li key={step}>{step}</li>)}
              </ol>
            </article>
            <article className="parent-platform-card">
              <span>{platformGuide.alertTitle}</span>
              <ol>
                {platformGuide.alertSteps.map((step) => <li key={step}>{step}</li>)}
              </ol>
            </article>
          </div>
          <div className="parent-diagnostic-grid">
            <div><span>Device class</span><strong>{runtimeProfile.deviceClass}</strong></div>
            <div><span>Browser</span><strong>{runtimeProfile.browserClass}</strong></div>
            <div><span>Notification API</span><strong>{hasNotificationApi ? 'available' : 'missing'}</strong></div>
            <div><span>Service Worker</span><strong>{hasServiceWorkerApi ? 'available' : 'missing'}</strong></div>
            <div><span>PushManager</span><strong>{hasPushManagerApi ? 'available' : 'missing'}</strong></div>
            <div><span>Permission</span><strong>{notificationPermission}</strong></div>
            <div><span>Standalone PWA</span><strong>{standaloneMode ? 'yes' : 'no'}</strong></div>
            <div><span>Email provider</span><strong>{deliveryEmailProvider}</strong></div>
            <div><span>Email fallback</span><strong>{deliveryEmailEnabled ? 'enabled' : 'not configured'}</strong></div>
            <div><span>Latest push</span><strong>{latestDelivery?.pushStatus || 'no status'}</strong></div>
            <div><span>Push display</span><strong>{latestDelivery?.pushDisplayStatus || 'unknown'}</strong></div>
            <div><span>Provider response</span><strong>{latestDelivery?.pushProviderResponse || 'none'}</strong></div>
            <div><span>Latest email</span><strong>{latestDelivery?.emailStatus || 'no status'}</strong></div>
          </div>
          <div className="parent-notification-actions">
            {!standaloneMode && (
              <button className="parent-primary-btn" onClick={installParentPortal} disabled={pushBusy}>
                {platformGuide.installButtonLabel}
              </button>
            )}
            <button className="parent-primary-btn" onClick={enableMessageAlerts} disabled={pushBusy || pushStatus === 'Alerts blocked'}>
              Enable alerts
            </button>
            <button className="parent-secondary-btn" onClick={resetMessageAlerts} disabled={pushBusy}>
              Reset alerts
            </button>
            <button className="parent-secondary-btn" onClick={sendLocalNotificationTest} disabled={pushBusy || !pushEnabled}>
              Test local notification
            </button>
            <button className="parent-secondary-btn" onClick={sendServerPushTest} disabled={pushBusy || !pushEnabled}>
              Test server push
            </button>
            <button className="parent-secondary-btn" onClick={sendInboxFallbackTest} disabled={pushBusy}>
              Test inbox fallback
            </button>
            <button className="parent-secondary-btn" onClick={sendEmailFallbackTest} disabled={pushBusy}>
              Test email fallback
            </button>
            <button className="parent-secondary-btn" onClick={checkMessageAlertStatus} disabled={pushBusy}>
              Check again
            </button>
            <button className="parent-secondary-btn" onClick={loadDeliveryStatus} disabled={pushBusy}>
              Delivery status
            </button>
          </div>
          <p className="parent-notification-note">
            External alerts only say Message from school or New document available. Message content and documents stay inside Parent Portal.
            {deliveryStatus ? ` ${deliveryStatus}.` : ''}
            {installStatus ? ` ${installStatus}` : ''}
          </p>
        </section>
      )}
      {tab === 'messages' && newMessageNotice && (
        <section className={`parent-live-inbox ${newMessageNotice ? 'has-new-message' : ''}`}>
          <div>
            <span className="parent-card-type">Inbox monitor</span>
            <p>
              {newMessageNotice || 'Inbox is checking for new school messages.'}
              {lastCheckedAt ? ` Last checked ${formatParentDate(lastCheckedAt)}.` : ''}
            </p>
          </div>
          <div className="parent-live-inbox-actions">
            {newMessageNotice && (
              <button
                className="parent-primary-btn"
                onClick={() => {
                  setTab('messages')
                  setSelectedMessageId('')
                  closeDocumentViewer()
                  setNewMessageNotice('')
                }}
              >
                View messages
              </button>
            )}
            <button className="parent-secondary-btn" onClick={() => setNewMessageNotice('')}>
              Dismiss
            </button>
          </div>
        </section>
      )}
      <nav className="parent-tabs" aria-label="Parent Portal">
        <button className={tab === 'messages' ? 'active' : ''} onClick={() => { setTab('messages'); setSelectedMessageId(''); closeDocumentViewer() }}>Messages</button>
        <button className={tab === 'news' ? 'active' : ''} onClick={() => { setTab('news'); setSelectedMessageId(''); closeDocumentViewer() }}>News</button>
        <button className={tab === 'events' ? 'active' : ''} onClick={() => { setTab('events'); setSelectedMessageId(''); closeDocumentViewer() }}>Events</button>
        <button className={tab === 'documents' ? 'active' : ''} onClick={() => { setTab('documents'); setSelectedMessageId(''); closeDocumentViewer() }}>Documents</button>
        {(session.role === 'guest' || isParentGuestRole(session.role)) && <button className={tab === 'cloud' ? 'active' : ''} onClick={() => { setTab('cloud'); closeDocumentViewer() }}>Mi nube</button>}
        <button className={tab === 'confirmations' ? 'active' : ''} onClick={() => { setTab('confirmations'); setSelectedMessageId(''); closeDocumentViewer() }}>Confirmations</button>
      </nav>
      {(session.role === 'guest' || isParentGuestRole(session.role)) && <div className="parent-portal-body" hidden={tab !== 'cloud'}><GuestCloud lease={session.lease} email={session.email} active={tab === 'cloud'} /></div>}
      <div className="parent-portal-body" hidden={tab === 'cloud'}>{body}</div>
    </main>
  )
}

function ParentPortalEmptyState({ title, text }: { title: string; text: string }) {
  return (
    <section className="parent-empty-state">
      <h2>{title}</h2>
      <p>{text}</p>
    </section>
  )
}

function ParentEarlyDismissalPanel({
  request,
  loading,
  status,
  note,
  pickupName,
  pickupRelationship,
  pickupReason,
  photoDataUrl,
  photoStatus,
  onNoteChange,
  onPickupNameChange,
  onPickupRelationshipChange,
  onPickupReasonChange,
  onPhotoSelected,
  onRefresh,
  onRespond
}: {
  request: ParentEarlyDismissal | null
  loading: boolean
  status: string
  note: string
  pickupName: string
  pickupRelationship: string
  pickupReason: string
  photoDataUrl: string
  photoStatus: string
  onNoteChange: (value: string) => void
  onPickupNameChange: (value: string) => void
  onPickupRelationshipChange: (value: string) => void
  onPickupReasonChange: (value: string) => void
  onPhotoSelected: (file: File | null | undefined) => void
  onRefresh: () => void
  onRespond: (response: 'confirm' | 'reject') => void
}) {
  if (!request) {
    return (
      <section className="parent-empty-state">
        <h2>Pickup confirmation</h2>
        <p>{status || 'Opening pickup confirmation.'}</p>
        <button className="parent-secondary-btn" onClick={onRefresh} disabled={loading}>
          {loading ? 'Checking...' : 'Try again'}
        </button>
      </section>
    )
  }

  const responseLocked = [
    'guardian_confirmed',
    'guardian_rejected',
    'ready_for_release',
    'released',
    'cancelled',
    'denied',
    'expired'
  ].includes(request.status)
  const reasonLabel = request.reasonCode.replace(/_/g, ' ') || 'Parent request'
  const hasSubmittedForm = Boolean(request.pickupConfirmation?.submittedAt)
  const canConfirm = Boolean(pickupName.trim() && pickupRelationship.trim() && pickupReason.trim() && note.trim() && photoDataUrl)

  return (
    <section className="parent-early-dismiss">
      <div className="parent-early-dismiss-head">
        <div>
          <span className="parent-card-type">Student release</span>
          <h2>Pickup confirmation requested</h2>
          <p>The front office is asking you to review this early dismissal request.</p>
        </div>
        <span className={`parent-early-dismiss-status status-${request.status || 'pending'}`}>
          {request.status.replace(/_/g, ' ') || 'pending'}
        </span>
      </div>
      <div className="parent-early-dismiss-grid">
        <div>
          <span>Student</span>
          <strong>{request.studentName || 'Student'}</strong>
        </div>
        <div>
          <span>Pickup person</span>
          <strong>{request.pickupPersonName || 'Not provided'}</strong>
        </div>
        <div>
          <span>Relationship</span>
          <strong>{request.pickupRelationship || 'Parent/guardian'}</strong>
        </div>
        <div>
          <span>Reason</span>
          <strong>{reasonLabel}</strong>
        </div>
        <div>
          <span>Requested</span>
          <strong>{formatParentDate(request.createdAt) || 'Just now'}</strong>
        </div>
        <div>
          <span>Expires</span>
          <strong>{formatParentDate(request.expiresAt) || 'School policy'}</strong>
        </div>
      </div>
      {request.reasonNotes && <p className="parent-early-dismiss-note">{request.reasonNotes}</p>}
      {hasSubmittedForm && (
        <div className="parent-early-dismiss-submitted">
          <span className="parent-card-type">Submitted confirmation</span>
          <p>
            {request.pickupConfirmation.pickupPersonName || 'Pickup person'} · {request.pickupConfirmation.pickupRelationship || 'relationship'} · {request.pickupConfirmation.pickupReason || 'reason'}
          </p>
          <p>Photo captured for front office review.</p>
        </div>
      )}
      {!responseLocked && (
        <div className="parent-early-dismiss-form">
          <label className="parent-early-dismiss-field">
            Pickup person full name
            <input
              value={pickupName}
              onChange={(event) => onPickupNameChange(event.target.value)}
              placeholder="Full name"
              required
            />
          </label>
          <label className="parent-early-dismiss-field">
            Relationship to student
            <input
              value={pickupRelationship}
              onChange={(event) => onPickupRelationshipChange(event.target.value)}
              placeholder="Father, mother, guardian"
              required
            />
          </label>
          <label className="parent-early-dismiss-field">
            Reason for early pickup
            <select value={pickupReason} onChange={(event) => onPickupReasonChange(event.target.value)} required>
              <option>Parent request</option>
              <option>Medical appointment</option>
              <option>School appointment</option>
              <option>Family emergency</option>
              <option>Other</option>
            </select>
          </label>
          <label className="parent-early-dismiss-field parent-early-dismiss-field-wide">
            School pickup explanation * Required
            <textarea
              value={note}
              onChange={(event) => onNoteChange(event.target.value)}
              placeholder="Explain why the student is leaving early."
              required
            />
          </label>
          <div className="parent-early-dismiss-photo">
            <div>
              <span className="parent-card-type">Pickup photo</span>
              <p>Take a clear photo for the front office record before confirming pickup.</p>
            </div>
            {photoDataUrl ? (
              <img src={photoDataUrl} alt="Pickup verification preview" />
            ) : (
              <div className="parent-early-dismiss-photo-empty">No photo captured</div>
            )}
            <label className="parent-secondary-btn parent-photo-btn">
              Take photo
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={(event) => onPhotoSelected(event.target.files?.[0])}
              />
            </label>
            {photoStatus && <p>{photoStatus}</p>}
          </div>
        </div>
      )}
      <div className="parent-early-dismiss-actions">
        {!responseLocked && (
          <>
            <button className="parent-primary-btn" onClick={() => onRespond('confirm')} disabled={loading || !canConfirm}>
              {loading ? 'Sending...' : 'Confirm pickup'}
            </button>
            <button className="parent-secondary-btn" onClick={() => onRespond('reject')} disabled={loading}>
              Reject / This is not me
            </button>
          </>
        )}
        <button
          className="parent-secondary-btn"
          onClick={() => window.alert('Please call your school front office.')}
        >
          Call school
        </button>
        <button className="parent-secondary-btn" onClick={onRefresh} disabled={loading}>
          Refresh
        </button>
      </div>
      {status && <div className="parent-action-status">{status}</div>}
    </section>
  )
}

function ParentDocumentViewer({ viewer, onBack }: { viewer: ParentDocumentViewerState; onBack: () => void }) {
  const title = viewer.attachment.title || viewer.attachment.name || (viewer.isPdf ? 'Shared PDF' : 'Shared document')
  const downloadName = viewer.attachment.name || (viewer.isPdf ? 'school-document.pdf' : 'school-document')
  const previewUrl = viewer.attachment.previewUrl
  const embedUrl = parentDocumentEmbedUrl(viewer.url, viewer.isPdf)
  return (
    <section className="parent-document-viewer">
      <div className="parent-document-toolbar">
        <button className="parent-secondary-btn parent-back-btn" onClick={onBack}>Back</button>
        <div>
          <span className="parent-card-type">{viewer.isPdf ? 'Shared PDF' : 'Shared document'}</span>
          <h2>{title}</h2>
          <p>{viewer.message.subject || 'School message'}</p>
        </div>
        <div className="parent-document-actions">
          <a className="parent-secondary-btn" href={viewer.url} target="_blank" rel="noreferrer">Open in browser</a>
          <a className="parent-primary-btn" href={viewer.url} download={downloadName}>Download PDF</a>
        </div>
      </div>
      {previewUrl ? (
        <div className="parent-document-mobile-open parent-document-mobile-open--preview">
          <img className="parent-document-preview-image" src={previewUrl} alt={`${title} preview`} />
          <a className="parent-primary-btn" href={viewer.url} target="_blank" rel="noreferrer">
            {viewer.isPdf ? 'Open PDF' : 'Open document'}
          </a>
        </div>
      ) : (
        <div className="parent-document-mobile-open">
          <span className="parent-card-type">{viewer.isPdf ? 'PDF ready' : 'Document ready'}</span>
          <h3>{viewer.isPdf ? 'Open PDF' : 'Open document'}</h3>
          <p>On this device, the document opens in the browser PDF viewer.</p>
          <a className="parent-primary-btn" href={viewer.url} target="_blank" rel="noreferrer">
            {viewer.isPdf ? 'Open PDF' : 'Open document'}
          </a>
        </div>
      )}
      <div className="parent-document-frame-wrap">
        <iframe className="parent-document-frame" src={embedUrl} title={title} />
      </div>
    </section>
  )
}

function formatParentDate(value: string): string {
  try {
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date)
  } catch {
    return ''
  }
}

function formatParentEventDate(value: string): string {
  try {
    const clean = String(value || '').trim()
    const match = clean.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (match) {
      const dateOnly = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(dateOnly)
    }
    return formatParentDate(clean)
  } catch {
    return ''
  }
}

const TOP_BAR_ID = 'nexus-browser-top-bar'
const STYLE_ID = 'nexus-browser-custom-style'
const THEME_BRIDGE_SCRIPT_ID = 'nexus-browser-theme-bridge-script'
const OLLAMA_ONLY_STYLE_ID = 'nexus-browser-ollama-only-style'
const OLLAMA_ONLY_SCRIPT_ID = 'nexus-browser-ollama-only-script'
const ROLE_OVERLAY_STYLE_ID = 'nexus-browser-role-overlay-style'
const ROLE_OVERLAY_SCRIPT_ID = 'nexus-browser-role-overlay-script'
const DEMO_PRESENTER_STYLE_ID = 'nexus-browser-demo-presenter-style'
const DEMO_PRESENTER_SCRIPT_ID = 'nexus-browser-demo-presenter-script'

function injectJennyTopBar(doc: Document, options: { tagLabel?: string } = {}) {
  const shell = doc.querySelector('.shell') as HTMLElement | null
  const searchBubble = doc.querySelector('.chat-input-area .search-bubble') as HTMLElement | null
  if (!shell || !searchBubble) return

  const topBar = getOrCreateTopBar(doc)
  topBar.replaceChildren(searchBubble)

  const tag = searchBubble.querySelector('.search-tag') as HTMLElement | null
  if (tag) {
    tag.textContent = options.tagLabel || 'Nexus Quantum Bridge'
  }

  ensureStyle(doc)
  installThemeBridge(doc)
  enforceOllamaOnlyMode(doc)
}

type NexusInfoTarget = 'solutions' | 'secure-core' | 'secure-internet'

function injectNexusInfoHomeLinks(
  doc: Document,
  openInternalTab: (urlValue: string, titleValue?: string) => void
) {
  const ctaRow = doc.querySelector('.cta-row') as HTMLElement | null
  if (!ctaRow) return

  const primary = ctaRow.querySelector('.btn-primary') as HTMLAnchorElement | null
  const secondary = ctaRow.querySelector('.btn-secondary') as HTMLAnchorElement | null
  if (!primary || !secondary) return

  const microcopy = ctaRow.querySelector('.microcopy') as HTMLElement | null
  let secureCore = ctaRow.querySelector('[data-nexus-info-target="secure-core"]') as HTMLAnchorElement | null
  if (!secureCore) {
    secureCore = doc.createElement('a')
    secureCore.className = 'btn-secondary'
    ctaRow.insertBefore(secureCore, microcopy || null)
  }

  bindNexusInfoHomeLink(doc, primary, 'solutions', 'Solutions', openInternalTab)
  bindNexusInfoHomeLink(doc, secondary, 'secure-internet', 'Secure Internet', openInternalTab)
  bindNexusInfoHomeLink(doc, secureCore, 'secure-core', 'Secure Core', openInternalTab)
}

function bindNexusInfoHomeLink(
  doc: Document,
  link: HTMLAnchorElement,
  target: NexusInfoTarget,
  title: string,
  openInternalTab: (urlValue: string, titleValue?: string) => void
) {
  const href = `/about/nexus-info.html#${target}`
  link.href = href
  link.textContent = title
  link.setAttribute('aria-label', title)
  link.dataset.nexusInfoTarget = target
  link.onclick = (event) => {
    event.preventDefault()
    event.stopPropagation()
    openInternalTab(buildNexusInfoUrl(doc, target), title)
  }
}

function buildNexusInfoUrl(doc: Document, target: NexusInfoTarget): string {
  const theme = String((doc.body as HTMLElement | null)?.dataset?.themeEffective || '').toLowerCase() === 'light'
    ? 'light'
    : 'dark'
  return `/about/nexus-info.html?theme=${encodeURIComponent(theme)}#${target}`
}

/* @nexus-profile academy:start */
function injectAcademyCanvasView(doc: Document) {
  const w = doc.defaultView as any
  if (!w) return
  const runtimeAlreadyInjected = Boolean(w.__nexusAcademyCanvasInjected)
  w.__nexusAcademyCanvasInjected = true

  const styleId = 'nexus-academy-canvas-style'
  if (!doc.getElementById(styleId)) {
    const style = doc.createElement('style')
    style.id = styleId
    style.textContent = `
      .academy-canvas-home {
        display: grid;
        gap: 16px;
      }
      .academy-canvas-hero {
        border: 1px solid rgba(125, 211, 252, 0.22);
        border-radius: 12px;
        background: linear-gradient(135deg, rgba(8, 47, 73, 0.24), rgba(30, 41, 59, 0.16));
        padding: 18px;
        display: grid;
        gap: 8px;
      }
      .academy-canvas-kicker,
      .academy-canvas-card small,
      .academy-canvas-progress span {
        color: rgba(125, 211, 252, 0.78);
        font-size: 11px;
        font-weight: 800;
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }
      .academy-canvas-hero h2 {
        margin: 0;
        color: var(--text-main, #f8fafc);
        font-size: 24px;
        letter-spacing: 0;
      }
      .academy-canvas-hero p,
      .academy-canvas-card span,
      .academy-canvas-empty span {
        margin: 0;
        color: var(--text-soft, rgba(203, 213, 225, 0.78));
        line-height: 1.5;
      }
      .academy-canvas-toolbar {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }
      .academy-canvas-toolbar label {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        color: var(--text-soft, rgba(203, 213, 225, 0.78));
        font-size: 12px;
        font-weight: 800;
      }
      .academy-canvas-toolbar select {
        min-height: 32px;
        border: 1px solid rgba(125, 211, 252, 0.28);
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.72);
        color: var(--text-main, #f8fafc);
        padding: 0 28px 0 12px;
        font: inherit;
        font-size: 12px;
        outline: none;
      }
      .academy-canvas-empty {
        min-height: 116px;
        border: 1px dashed rgba(125, 211, 252, 0.28);
        border-radius: 12px;
        background: rgba(15, 23, 42, 0.22);
        padding: 18px;
        display: grid;
        place-content: center;
        gap: 8px;
        text-align: center;
      }
      .academy-canvas-empty strong {
        color: var(--text-main, #f8fafc);
        font-size: 16px;
      }
      .academy-canvas-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 12px;
      }
      .academy-canvas-card {
        min-height: 108px;
        border: 1px solid rgba(148, 163, 184, 0.16);
        border-radius: 12px;
        background: rgba(15, 23, 42, 0.28);
        padding: 14px;
        display: grid;
        gap: 7px;
      }
      .academy-canvas-card strong {
        color: var(--text-main, #f8fafc);
      }
      .academy-canvas-progress {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
      }
      .academy-canvas-progress div {
        border: 1px solid rgba(148, 163, 184, 0.16);
        border-radius: 12px;
        background: rgba(8, 47, 73, 0.18);
        padding: 14px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
      }
      .academy-canvas-progress strong {
        color: var(--text-main, #f8fafc);
      }
      .academy-canvas-scope-grid {
        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 10px;
      }
      .academy-canvas-scope-card {
        min-height: 78px;
        border: 1px solid rgba(148, 163, 184, 0.15);
        border-radius: 12px;
        background: rgba(15, 23, 42, 0.22);
        padding: 12px;
        display: grid;
        gap: 5px;
      }
      .academy-canvas-scope-card span {
        color: rgba(125, 211, 252, 0.78);
        font-size: 10px;
        font-weight: 800;
        letter-spacing: 0.1em;
        text-transform: uppercase;
      }
      .academy-canvas-scope-card strong {
        color: var(--text-main, #f8fafc);
        font-size: 14px;
      }
      .academy-canvas-section-title {
        margin: 2px 0 -4px;
        color: var(--text-main, #f8fafc);
        font-size: 14px;
        font-weight: 900;
        letter-spacing: 0;
      }
      .academy-canvas-module-list,
      .academy-canvas-card-list {
        display: grid;
        gap: 10px;
      }
      .academy-canvas-module-row,
      .academy-canvas-lesson-card {
        border: 1px solid rgba(148, 163, 184, 0.16);
        border-radius: 12px;
        background: rgba(15, 23, 42, 0.22);
        padding: 13px;
        display: grid;
        gap: 7px;
      }
      .academy-canvas-module-row strong,
      .academy-canvas-lesson-card strong {
        color: var(--text-main, #f8fafc);
        letter-spacing: 0;
      }
      .academy-canvas-module-row p,
      .academy-canvas-lesson-card p,
      .academy-canvas-lesson-card span {
        margin: 0;
        color: var(--text-soft, rgba(203, 213, 225, 0.78));
        line-height: 1.45;
      }
      .academy-canvas-tags {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
      }
      .academy-canvas-tag {
        border: 1px solid rgba(125, 211, 252, 0.2);
        border-radius: 999px;
        color: rgba(186, 230, 253, 0.94);
        background: rgba(14, 116, 144, 0.16);
        padding: 3px 8px;
        font-size: 11px;
        font-weight: 800;
        max-width: 100%;
        overflow-wrap: anywhere;
      }
      .academy-canvas-card-actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin-top: 2px;
      }
      .academy-canvas-status {
        border: 1px solid rgba(148, 163, 184, 0.18);
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.52);
        color: rgba(226, 232, 240, 0.9);
        padding: 5px 9px;
        font-size: 11px;
        font-weight: 900;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .academy-canvas-status-completed {
        border-color: rgba(34, 197, 94, 0.3);
        color: rgba(187, 247, 208, 0.94);
        background: rgba(22, 101, 52, 0.22);
      }
	      .academy-canvas-status-in_progress {
	        border-color: rgba(250, 204, 21, 0.32);
	        color: rgba(254, 240, 138, 0.94);
	        background: rgba(133, 77, 14, 0.22);
	      }
	      .academy-canvas-status-generated_pilot_ready {
	        border-color: rgba(45, 212, 191, 0.32);
	        color: rgba(204, 251, 241, 0.95);
	        background: rgba(20, 184, 166, 0.18);
	      }
      .academy-canvas-action-button {
        border: 1px solid rgba(125, 211, 252, 0.28);
        border-radius: 999px;
        background: rgba(8, 47, 73, 0.36);
        color: rgba(224, 242, 254, 0.96);
        cursor: pointer;
        font-size: 12px;
        font-weight: 900;
        min-height: 30px;
        padding: 0 12px;
      }
      .academy-canvas-action-button:disabled {
        cursor: not-allowed;
        opacity: 0.48;
      }
      .academy-student-shell {
        display: grid;
        gap: 18px;
      }
      .academy-student-hero {
        border: 1px solid rgba(125, 211, 252, 0.28);
        border-radius: 16px;
        background:
          linear-gradient(135deg, rgba(8, 47, 73, 0.72), rgba(30, 41, 59, 0.5) 48%, rgba(88, 28, 135, 0.32));
        padding: 20px;
        display: grid;
        grid-template-columns: minmax(0, 1.45fr) minmax(260px, 0.55fr);
        gap: 18px;
        align-items: stretch;
        overflow: hidden;
      }
      .academy-student-hero-copy {
        display: grid;
        align-content: center;
        gap: 10px;
      }
      .academy-student-hero-copy h2,
      .academy-mission-title {
        margin: 0;
        color: var(--text-main, #f8fafc);
        font-size: 28px;
        letter-spacing: 0;
        line-height: 1.12;
      }
      .academy-student-hero-copy p,
      .academy-mission-summary,
      .academy-lesson-card p,
      .academy-path-card p {
        margin: 0;
        color: rgba(203, 213, 225, 0.82);
        line-height: 1.48;
      }
      .academy-pill-row,
      .academy-mission-meta,
      .academy-lesson-meta {
        display: flex;
        flex-wrap: wrap;
        gap: 7px;
        align-items: center;
      }
      .academy-pill {
        border: 1px solid rgba(148, 163, 184, 0.2);
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.52);
        color: rgba(226, 232, 240, 0.9);
        padding: 5px 9px;
        font-size: 11px;
        font-weight: 850;
        max-width: 100%;
        overflow-wrap: anywhere;
      }
      .academy-pill-strong {
        border-color: rgba(34, 211, 238, 0.36);
        color: rgba(207, 250, 254, 0.96);
        background: rgba(14, 116, 144, 0.28);
      }
      .academy-progress-panel {
        border: 1px solid rgba(148, 163, 184, 0.22);
        border-radius: 14px;
        background: rgba(2, 6, 23, 0.32);
        padding: 16px;
        display: grid;
        gap: 12px;
      }
      .academy-progress-number {
        color: rgba(103, 232, 249, 0.98);
        font-size: 42px;
        font-weight: 950;
        line-height: 1;
      }
      .academy-progress-label {
        color: rgba(203, 213, 225, 0.8);
        font-size: 12px;
        font-weight: 800;
      }
      .academy-progress-bar {
        width: 100%;
        height: 10px;
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.72);
        overflow: hidden;
      }
      .academy-progress-fill {
        height: 100%;
        border-radius: inherit;
        background: linear-gradient(90deg, #22d3ee, #a78bfa, #facc15);
        min-width: 4px;
      }
      .academy-stats-row {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 8px;
      }
      .academy-stat-tile {
        border: 1px solid rgba(148, 163, 184, 0.16);
        border-radius: 10px;
        background: rgba(15, 23, 42, 0.4);
        padding: 10px;
      }
      .academy-stat-tile span {
        display: block;
        color: rgba(125, 211, 252, 0.82);
        font-size: 10px;
        font-weight: 900;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .academy-stat-tile strong {
        color: var(--text-main, #f8fafc);
        font-size: 16px;
      }
      .academy-certificate-journey {
        border: 1px solid rgba(167, 139, 250, 0.26);
        border-radius: 16px;
        background:
          linear-gradient(135deg, rgba(49, 46, 129, 0.48), rgba(8, 47, 73, 0.34) 58%, rgba(15, 23, 42, 0.28));
        padding: 16px;
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(240px, 0.42fr);
        gap: 14px;
        align-items: center;
      }
      .academy-certificate-journey h3 {
        margin: 0;
        color: var(--text-main, #f8fafc);
        font-size: 19px;
        letter-spacing: 0;
      }
      .academy-certificate-journey p {
        margin: 6px 0 0;
        color: rgba(203, 213, 225, 0.82);
        line-height: 1.45;
      }
      .academy-certificate-meter {
        display: grid;
        gap: 9px;
      }
      .academy-certificate-meter strong {
        color: rgba(216, 180, 254, 0.96);
        font-size: 24px;
        line-height: 1;
      }
      .academy-certificate-meter .academy-stats-row {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
      .academy-certificate-meter .academy-stat-tile strong {
        font-size: 14px;
        overflow-wrap: anywhere;
      }
      .academy-mission-card {
        border: 1px solid rgba(250, 204, 21, 0.28);
        border-radius: 16px;
        background:
          linear-gradient(135deg, rgba(146, 64, 14, 0.78), rgba(8, 47, 73, 0.46) 58%, rgba(59, 130, 246, 0.32));
        box-shadow: 0 18px 40px rgba(2, 6, 23, 0.28);
        padding: 18px;
        display: grid;
        grid-template-columns: minmax(64px, 72px) minmax(0, 1fr);
        gap: 16px;
        align-items: start;
      }
      .academy-mission-icon,
      .academy-lesson-icon {
        width: 58px;
        height: 58px;
        border-radius: 16px;
        display: grid;
        place-items: center;
        color: #fff;
        font-size: 15px;
        font-weight: 950;
        letter-spacing: 0.02em;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
      }
      .academy-mission-icon {
        background: rgba(250, 204, 21, 0.18);
        border: 1px solid rgba(253, 230, 138, 0.34);
      }
      .academy-mission-body {
        display: grid;
        gap: 10px;
        min-width: 0;
      }
      .academy-action-row {
        display: flex;
        flex-wrap: wrap;
        gap: 9px;
        align-items: center;
      }
      .academy-subject-menu {
        border: 1px solid rgba(34, 211, 238, 0.22);
        border-radius: 14px;
        background: rgba(8, 47, 73, 0.22);
        padding: 14px;
        display: grid;
        grid-template-columns: minmax(240px, 0.95fr) minmax(0, 1.25fr);
        gap: 14px;
        align-items: end;
      }
      .academy-subject-menu label {
        display: grid;
        gap: 7px;
        color: rgba(226, 232, 240, 0.9);
        font-size: 12px;
        font-weight: 900;
      }
      .academy-subject-select {
        width: 100%;
        min-height: 40px;
        border: 1px solid rgba(34, 211, 238, 0.36);
        border-radius: 12px;
        background: rgba(2, 6, 23, 0.62);
        color: rgba(248, 250, 252, 0.96);
        padding: 0 12px;
        font-size: 14px;
        font-weight: 900;
      }
      .academy-subject-summary {
        display: grid;
        gap: 8px;
        min-width: 0;
      }
      .academy-subject-summary strong {
        color: rgba(248, 250, 252, 0.96);
        font-size: 15px;
      }
      .academy-subject-summary p {
        margin: 0;
        color: rgba(203, 213, 225, 0.78);
        line-height: 1.45;
        font-size: 12px;
      }
      .academy-n31-study-layer {
        border: 1px solid rgba(34, 211, 238, 0.26);
        border-radius: 14px;
        background: linear-gradient(135deg, rgba(8, 47, 73, 0.32), rgba(15, 23, 42, 0.28));
        padding: 15px;
        display: grid;
        gap: 13px;
      }
      .academy-n31-study-layer[data-state="pending"] {
        border-style: dashed;
        background: rgba(15, 23, 42, 0.24);
      }
      .academy-n31-header {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: flex-start;
        flex-wrap: wrap;
      }
      .academy-n31-header h3 {
        margin: 0;
        color: var(--text-main, #f8fafc);
        font-size: 18px;
        letter-spacing: 0;
      }
      .academy-n31-header p,
      .academy-n31-ordered-map p,
      .academy-generation-gate p {
        margin: 0;
        color: rgba(203, 213, 225, 0.8);
        line-height: 1.45;
        font-size: 12px;
      }
      .academy-n31-ordered-map {
        display: grid;
        grid-template-columns: minmax(260px, 0.9fr) minmax(0, 1.1fr);
        gap: 12px;
      }
      .academy-n31-unit-list,
      .academy-n31-chunk-list {
        display: grid;
        gap: 8px;
      }
      .academy-n31-unit,
      .academy-n31-chunk,
      .academy-generation-gate {
        border: 1px solid rgba(125, 211, 252, 0.16);
        border-radius: 10px;
        background: rgba(2, 6, 23, 0.24);
        padding: 10px;
        display: grid;
        gap: 6px;
        min-width: 0;
      }
      .academy-n31-unit strong,
      .academy-n31-chunk strong,
      .academy-generation-gate strong {
        color: rgba(248, 250, 252, 0.96);
        line-height: 1.28;
      }
      .academy-n31-chunk span {
        color: rgba(186, 230, 253, 0.82);
        font-size: 11px;
        font-weight: 850;
        text-transform: uppercase;
      }
      .academy-generation-gate {
        border-color: rgba(250, 204, 21, 0.28);
        background: rgba(113, 63, 18, 0.14);
      }
      .academy-generation-gate .academy-action-row {
        margin-top: 2px;
      }
      .academy-shell-note {
        border: 1px dashed rgba(250, 204, 21, 0.34);
        border-radius: 12px;
        background: rgba(113, 63, 18, 0.16);
        padding: 12px;
        color: rgba(254, 249, 195, 0.88);
        font-size: 12px;
        line-height: 1.42;
      }
      .academy-primary-action {
        border: 0;
        border-radius: 999px;
        background: #facc15;
        color: #111827;
        cursor: pointer;
        font-size: 13px;
        font-weight: 950;
        min-height: 36px;
        padding: 0 15px;
      }
      .academy-secondary-action {
        border: 1px solid rgba(226, 232, 240, 0.24);
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.42);
        color: rgba(226, 232, 240, 0.92);
        cursor: pointer;
        font-size: 12px;
        font-weight: 900;
        min-height: 34px;
        padding: 0 13px;
      }
      .academy-primary-action:disabled,
      .academy-secondary-action:disabled {
        cursor: not-allowed;
        opacity: 0.52;
      }
      .academy-section-header {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: end;
        margin-top: 2px;
      }
      .academy-section-header h3 {
        margin: 0;
        color: var(--text-main, #f8fafc);
        font-size: 17px;
        letter-spacing: 0;
      }
      .academy-section-header span {
        color: rgba(203, 213, 225, 0.74);
        font-size: 12px;
      }
      .academy-classroom-overview {
        display: grid;
        gap: 16px;
      }
      .academy-classroom-overview[hidden] {
        display: none;
      }
      .academy-subject-dashboard-header,
      .academy-subject-workspace-header {
        border: 1px solid rgba(148, 163, 184, 0.18);
        border-radius: 14px;
        background: rgba(2, 6, 23, 0.22);
        padding: 16px;
        display: flex;
        justify-content: space-between;
        gap: 14px;
        align-items: flex-start;
        flex-wrap: wrap;
      }
      .academy-subject-dashboard-header h3,
      .academy-subject-workspace-header h3 {
        margin: 0;
        color: var(--text-main, #f8fafc);
        font-size: 19px;
        letter-spacing: 0;
        line-height: 1.22;
      }
      .academy-subject-dashboard-header p,
      .academy-subject-workspace-header p,
      .academy-subject-card p,
      .academy-subject-targets span {
        margin: 0;
        color: rgba(203, 213, 225, 0.78);
        line-height: 1.45;
        font-size: 12px;
      }
      .academy-subject-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 14px;
      }
      .academy-subject-card {
        border: 1px solid rgba(148, 163, 184, 0.18);
        border-radius: 8px;
        background: rgba(15, 23, 42, 0.3);
        padding: 14px;
        display: grid;
        gap: 12px;
        min-height: 256px;
        min-width: 0;
      }
      .academy-subject-card:hover {
        border-color: rgba(34, 211, 238, 0.38);
        background: rgba(8, 47, 73, 0.28);
      }
      .academy-subject-card h4 {
        margin: 0;
        color: var(--text-main, #f8fafc);
        font-size: 17px;
        letter-spacing: 0;
        line-height: 1.24;
        overflow-wrap: anywhere;
      }
      .academy-subject-meter {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
        gap: 10px;
        align-items: center;
      }
      .academy-subject-percent {
        color: rgba(103, 232, 249, 0.98);
        font-size: 30px;
        font-weight: 950;
        line-height: 1;
        min-width: 68px;
      }
      .academy-subject-targets {
        display: grid;
        gap: 6px;
      }
      .academy-subject-targets span {
        border: 1px solid rgba(148, 163, 184, 0.14);
        border-radius: 8px;
        background: rgba(2, 6, 23, 0.2);
        padding: 8px;
        overflow-wrap: anywhere;
      }
      .academy-subject-targets strong {
        color: rgba(248, 250, 252, 0.94);
        display: block;
        font-size: 10px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
      }
      .academy-subject-card-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        flex-wrap: wrap;
        margin-top: auto;
      }
      .academy-subject-workspace {
        display: grid;
        gap: 16px;
      }
      .academy-subject-workspace-actions {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .academy-subject-event-status {
        min-height: 18px;
        color: rgba(186, 230, 253, 0.88);
        font-size: 12px;
        font-weight: 800;
      }
      .academy-subject-event-status[data-state="error"] {
        color: rgba(252, 165, 165, 0.94);
      }
      .academy-subject-event-status[data-state="success"] {
        color: rgba(134, 239, 172, 0.94);
      }
      .academy-period-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
      }
      .academy-period-card {
        border: 1px solid rgba(125, 211, 252, 0.2);
        border-radius: 14px;
        background: rgba(2, 6, 23, 0.22);
        padding: 14px;
        display: grid;
        gap: 12px;
        min-width: 0;
      }
      .academy-period-card h3 {
        margin: 0;
        color: var(--text-main, #f8fafc);
        font-size: 16px;
        letter-spacing: 0;
        line-height: 1.25;
      }
      .academy-period-card p {
        margin: 0;
        color: rgba(203, 213, 225, 0.78);
        line-height: 1.42;
        font-size: 12px;
      }
      .academy-period-lessons {
        display: grid;
        gap: 8px;
      }
      .academy-period-lesson {
        border: 1px solid rgba(148, 163, 184, 0.15);
        border-radius: 10px;
        background: rgba(15, 23, 42, 0.34);
        padding: 10px;
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 10px;
        align-items: center;
        min-height: 86px;
      }
      .academy-period-lesson-main {
        display: grid;
        gap: 6px;
        min-width: 0;
      }
      .academy-period-lesson-title {
        color: rgba(248, 250, 252, 0.96);
        font-size: 13px;
        font-weight: 950;
        line-height: 1.28;
      }
      .academy-period-lesson-summary {
        color: rgba(203, 213, 225, 0.74);
        font-size: 12px;
        line-height: 1.35;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
      .academy-period-lesson-actions {
        display: flex;
        flex-direction: column;
        gap: 7px;
        align-items: stretch;
      }
      .academy-period-lesson-actions .academy-secondary-action,
      .academy-period-lesson-actions .academy-primary-action {
        min-height: 30px;
        white-space: nowrap;
      }
      .academy-path-grid {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: 10px;
      }
      .academy-path-card {
        border: 1px solid rgba(148, 163, 184, 0.16);
        border-radius: 12px;
        background: rgba(15, 23, 42, 0.26);
        padding: 13px;
        display: grid;
        gap: 9px;
      }
      .academy-path-card strong,
      .academy-lesson-card strong {
        color: var(--text-main, #f8fafc);
        line-height: 1.28;
      }
      .academy-lesson-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 12px;
      }
      .academy-lesson-card {
        border: 1px solid rgba(148, 163, 184, 0.16);
        border-radius: 14px;
        background: rgba(15, 23, 42, 0.28);
        padding: 14px;
        display: grid;
        grid-template-columns: 54px minmax(0, 1fr);
        gap: 12px;
        min-height: 150px;
      }
      .academy-lesson-body {
        display: grid;
        gap: 8px;
        min-width: 0;
      }
      .academy-lesson-card[data-tone="create"],
      .academy-mission-card[data-tone="create"] {
        border-color: rgba(168, 85, 247, 0.32);
        background: linear-gradient(135deg, rgba(88, 28, 135, 0.4), rgba(15, 23, 42, 0.28));
      }
      .academy-lesson-card[data-tone="check"],
      .academy-mission-card[data-tone="check"] {
        border-color: rgba(34, 197, 94, 0.32);
        background: linear-gradient(135deg, rgba(20, 83, 45, 0.42), rgba(15, 23, 42, 0.28));
      }
      .academy-lesson-card[data-tone="read"],
      .academy-mission-card[data-tone="read"] {
        border-color: rgba(245, 158, 11, 0.34);
        background: linear-gradient(135deg, rgba(146, 64, 14, 0.42), rgba(15, 23, 42, 0.28));
      }
      .academy-lesson-card[data-tone="practice"],
      .academy-mission-card[data-tone="practice"] {
        border-color: rgba(59, 130, 246, 0.34);
        background: linear-gradient(135deg, rgba(30, 64, 175, 0.42), rgba(15, 23, 42, 0.28));
      }
      .academy-lesson-card[data-tone="project"],
      .academy-mission-card[data-tone="project"] {
        border-color: rgba(236, 72, 153, 0.34);
        background: linear-gradient(135deg, rgba(157, 23, 77, 0.42), rgba(15, 23, 42, 0.28));
      }
      .academy-lesson-icon[data-tone="create"] { background: #7c3aed; }
      .academy-lesson-icon[data-tone="check"] { background: #16a34a; }
      .academy-lesson-icon[data-tone="read"] { background: #d97706; }
      .academy-lesson-icon[data-tone="practice"] { background: #2563eb; }
      .academy-lesson-icon[data-tone="project"] { background: #db2777; }
      .academy-completed-strip {
        display: grid;
        gap: 8px;
      }
      .academy-completed-row {
        border: 1px solid rgba(34, 197, 94, 0.2);
        border-radius: 12px;
        background: rgba(20, 83, 45, 0.18);
        padding: 12px;
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: center;
      }
      .academy-completed-row strong {
        color: rgba(220, 252, 231, 0.96);
      }
      .academy-completed-row span {
        color: rgba(187, 247, 208, 0.78);
        font-size: 12px;
      }
      .academy-activity-panel {
        border: 1px solid rgba(34, 211, 238, 0.28);
        border-radius: 16px;
        background: linear-gradient(135deg, rgba(8, 47, 73, 0.36), rgba(15, 23, 42, 0.34));
        padding: 18px;
        display: grid;
        gap: 14px;
      }
      .academy-activity-panel[hidden] {
        display: none;
      }
      .academy-lesson-workspace {
        border-color: rgba(250, 204, 21, 0.34);
        background:
          linear-gradient(135deg, rgba(8, 47, 73, 0.44), rgba(15, 23, 42, 0.46) 52%, rgba(63, 63, 70, 0.26));
      }
      .academy-workspace-nav {
        display: flex;
        gap: 8px;
        flex-wrap: wrap;
        align-items: center;
      }
      .academy-workspace-nav button {
        border: 1px solid rgba(148, 163, 184, 0.2);
        border-radius: 999px;
        background: rgba(15, 23, 42, 0.5);
        color: rgba(226, 232, 240, 0.9);
        min-height: 32px;
        padding: 0 12px;
        cursor: pointer;
        font-size: 12px;
        font-weight: 900;
      }
      .academy-workspace-nav button[data-active="true"] {
        border-color: rgba(34, 211, 238, 0.48);
        background: rgba(14, 116, 144, 0.36);
        color: rgba(240, 253, 250, 0.98);
      }
      .academy-workspace-pane {
        display: grid;
        gap: 12px;
      }
      .academy-workspace-pane[hidden] {
        display: none;
      }
      .academy-activity-header {
        display: flex;
        justify-content: space-between;
        gap: 12px;
        align-items: flex-start;
        flex-wrap: wrap;
      }
      .academy-activity-header h3 {
        margin: 0;
        color: var(--text-main, #f8fafc);
        font-size: 22px;
        letter-spacing: 0;
      }
      .academy-activity-header p,
      .academy-activity-block p {
        margin: 0;
        color: rgba(203, 213, 225, 0.82);
        line-height: 1.5;
      }
      .academy-activity-grid {
        display: grid;
        grid-template-columns: minmax(0, 1.15fr) minmax(260px, 0.85fr);
        gap: 12px;
      }
      .academy-activity-block {
        border: 1px solid rgba(148, 163, 184, 0.16);
        border-radius: 12px;
        background: rgba(2, 6, 23, 0.22);
        padding: 13px;
        display: grid;
        gap: 9px;
        min-width: 0;
      }
      .academy-activity-block strong {
        color: var(--text-main, #f8fafc);
      }
      .academy-activity-list,
      .academy-step-list {
        margin: 0;
        padding-left: 18px;
        color: rgba(226, 232, 240, 0.9);
        line-height: 1.52;
      }
      .academy-step-list li,
      .academy-activity-list li {
        margin: 5px 0;
      }
      .academy-activity-content-items {
        display: grid;
        gap: 7px;
      }
      .academy-activity-content-item {
        border: 1px solid rgba(125, 211, 252, 0.16);
        border-radius: 10px;
        background: rgba(8, 47, 73, 0.2);
        padding: 9px;
        color: rgba(226, 232, 240, 0.88);
        overflow-wrap: anywhere;
      }
      .academy-study-stack,
      .academy-question-list,
      .academy-reference-list {
        display: grid;
        gap: 10px;
      }
      .academy-study-page {
        display: grid;
        gap: 10px;
      }
      .academy-study-block {
        border: 1px solid rgba(125, 211, 252, 0.14);
        border-radius: 10px;
        background: rgba(15, 23, 42, 0.34);
        padding: 11px;
        display: grid;
        gap: 7px;
      }
      .academy-study-block strong,
      .academy-question-card strong {
        color: rgba(248, 250, 252, 0.96);
      }
      .academy-study-body {
        white-space: pre-wrap;
      }
      .academy-question-card {
        border: 1px solid rgba(34, 211, 238, 0.18);
        border-radius: 12px;
        background: rgba(2, 6, 23, 0.26);
        padding: 12px;
        display: grid;
        gap: 9px;
      }
      .academy-choice-list {
        margin: 0;
        padding: 0;
        list-style: none;
        display: grid;
        gap: 7px;
      }
      .academy-choice-list li {
        border: 1px solid rgba(148, 163, 184, 0.16);
        border-radius: 10px;
        background: rgba(8, 47, 73, 0.16);
        padding: 8px 10px;
        color: rgba(226, 232, 240, 0.88);
      }
      .academy-choice-button,
      .academy-order-check-button {
        border: 1px solid rgba(148, 163, 184, 0.2);
        border-radius: 10px;
        background: rgba(8, 47, 73, 0.18);
        color: rgba(226, 232, 240, 0.92);
        cursor: pointer;
        font: inherit;
        font-size: 13px;
        line-height: 1.35;
        padding: 10px;
        text-align: left;
        width: 100%;
      }
      .academy-choice-button:hover,
      .academy-order-check-button:hover {
        border-color: rgba(34, 211, 238, 0.42);
        background: rgba(8, 47, 73, 0.3);
      }
      .academy-choice-button.is-correct,
      .academy-order-check-button.is-correct {
        border-color: rgba(34, 197, 94, 0.6);
        background: rgba(22, 101, 52, 0.34);
        color: rgba(220, 252, 231, 0.98);
      }
      .academy-choice-button.is-incorrect,
      .academy-order-check-button.is-incorrect {
        border-color: rgba(248, 113, 113, 0.58);
        background: rgba(127, 29, 29, 0.28);
        color: rgba(254, 226, 226, 0.96);
      }
      .academy-practice-feedback {
        min-height: 18px;
        color: rgba(186, 230, 253, 0.9);
        font-size: 12px;
        font-weight: 850;
      }
      .academy-practice-feedback[data-state="correct"] {
        color: rgba(134, 239, 172, 0.98);
      }
      .academy-practice-feedback[data-state="incorrect"] {
        color: rgba(252, 165, 165, 0.96);
      }
      .academy-practice-feedback[data-state="review"] {
        color: rgba(253, 224, 71, 0.96);
      }
      .academy-short-response-box {
        width: 100%;
        min-height: 88px;
        border: 1px solid rgba(125, 211, 252, 0.24);
        border-radius: 10px;
        background: rgba(2, 6, 23, 0.5);
        color: var(--text-main, #f8fafc);
        padding: 10px;
        font: inherit;
        font-size: 13px;
        line-height: 1.45;
        resize: vertical;
        box-sizing: border-box;
      }
      .academy-reference-list a {
        color: rgba(125, 211, 252, 0.96);
        text-decoration: none;
        overflow-wrap: anywhere;
      }
      .academy-reference-list a:hover {
        text-decoration: underline;
      }
      .academy-submission-form {
        display: grid;
        gap: 10px;
      }
      .academy-submission-form label {
        display: grid;
        gap: 6px;
        color: rgba(226, 232, 240, 0.9);
        font-size: 12px;
        font-weight: 800;
      }
      .academy-submission-form textarea {
        width: 100%;
        min-height: 82px;
        resize: vertical;
        border: 1px solid rgba(125, 211, 252, 0.24);
        border-radius: 10px;
        background: rgba(2, 6, 23, 0.5);
        color: var(--text-main, #f8fafc);
        padding: 10px;
        font: inherit;
        font-size: 13px;
        line-height: 1.45;
        outline: none;
        box-sizing: border-box;
      }
      .academy-submission-form textarea:focus {
        border-color: rgba(34, 211, 238, 0.62);
        box-shadow: 0 0 0 2px rgba(34, 211, 238, 0.12);
      }
      .academy-submission-check {
        display: flex !important;
        grid-template-columns: none !important;
        align-items: flex-start;
        gap: 8px !important;
        font-weight: 700 !important;
        line-height: 1.35;
      }
      .academy-submission-status {
        min-height: 18px;
        color: rgba(186, 230, 253, 0.9);
        font-size: 12px;
        font-weight: 800;
      }
      .academy-submission-status[data-state="error"] {
        color: rgba(252, 165, 165, 0.96);
      }
      .academy-submission-status[data-state="success"] {
        color: rgba(134, 239, 172, 0.96);
      }
      .academy-activity-error {
        border-color: rgba(248, 113, 113, 0.34);
        background: rgba(127, 29, 29, 0.22);
      }
      .academy-canvas-debug {
        border: 1px solid rgba(250, 204, 21, 0.24);
        border-radius: 12px;
        background: rgba(45, 32, 4, 0.2);
        padding: 12px;
        display: grid;
        gap: 7px;
        color: rgba(254, 243, 199, 0.9);
        font-size: 12px;
      }
      .academy-canvas-debug code {
        color: rgba(254, 243, 199, 0.95);
        overflow-wrap: anywhere;
      }
      @media (max-width: 980px) {
        .academy-canvas-grid,
        .academy-canvas-progress,
        .academy-canvas-scope-grid,
        .academy-student-hero,
        .academy-certificate-journey,
        .academy-subject-grid,
        .academy-path-grid,
        .academy-lesson-grid,
        .academy-period-grid,
        .academy-subject-menu,
        .academy-n31-ordered-map {
          grid-template-columns: 1fr;
        }
        .academy-mission-card,
        .academy-lesson-card,
        .academy-activity-grid,
        .academy-period-lesson {
          grid-template-columns: 1fr;
        }
        .academy-period-lesson-actions {
          flex-direction: row;
          flex-wrap: wrap;
        }
      }
    `
    doc.head.appendChild(style)
  }

  const academyStyle = doc.getElementById(styleId) as HTMLStyleElement | null
  if (academyStyle && !w.__nexusAcademyStyleGuard) {
    const academyCssText = academyStyle.textContent || ''
    const styleGuard = new w.MutationObserver(() => {
      if (doc.getElementById(styleId) || !doc.head) return
      const restoredStyle = doc.createElement('style')
      restoredStyle.id = styleId
      restoredStyle.textContent = academyCssText
      doc.head.appendChild(restoredStyle)
    })
    styleGuard.observe(doc.head, { childList: true })
    w.__nexusAcademyStyleGuard = styleGuard
  }

  if (runtimeAlreadyInjected) return

  const script = doc.createElement('script')
  script.type = 'text/javascript'
  script.text = String.raw`
(function () {
  if (window.__nexusAcademyCanvasRuntime) return;
  window.__nexusAcademyCanvasRuntime = true;

  var allowedGrades = ['3', '4', '5', '6', '7', '8'];
  var lastAcademyPayload = null;
  var activeAcademyView = '';
  var academyChatKeeper = null;

  function normalizeGrade(value) {
    var grade = String(value || '').trim();
    return allowedGrades.indexOf(grade) >= 0 ? grade : '5';
  }

  function isExplicitAcademyPayload(payload) {
    payload = payload || {};
    var productMode = String(payload.product_mode || '').trim().toLowerCase();
    if (productMode !== 'academy') return false;
    if (payload.type === 'nexus-open-academy-home') return true;
    var overlay = String(payload.active_overlay || payload.overlay || '').trim().toLowerCase();
    var view = String(payload.view || payload.route || payload.canvas_view || '').trim().toLowerCase();
    return overlay === 'academy' || overlay === 'academy_student' || view === 'academy_home' || view === 'academy_classroom';
  }

  function gradeContext(gradeValue) {
    var grade = normalizeGrade(gradeValue || (lastAcademyPayload && lastAcademyPayload.active_grade) || '5');
    var gradeId = 'grade_' + grade;
    return {
      grade: grade,
      gradeId: gradeId,
      title: 'Grade ' + grade,
      track: 'academy_grade_' + grade,
      enrollmentScope: 'academy_enrollment_grade_' + grade,
      gradePackageId: 'academy_grade_package_' + grade,
      ragNamespace: 'academy.grade_' + grade,
      curriculumManifestPath: 'nexus-academy://curriculum/grades/' + gradeId + '/curriculum_manifest.json',
      lessonCardsPath: 'nexus-academy://curriculum/grades/' + gradeId + '/lesson_cards.json',
      moduleManifestPath: 'nexus-academy://curriculum/grades/' + gradeId + '/module_manifest.json',
      certificatePath: 'nexus-academy://certificates/grades/' + gradeId + '/certificate_manifest.json',
      classScope: 'academy_grade_' + grade,
      cardDeck: 'grade_' + grade,
      assignmentFilter: { grade: grade },
      workItemFilter: { grade: grade, package_id: 'academy_grade_package_' + grade },
      progressScope: 'academy_grade_' + grade,
      progressLedgerScope: 'academy_progress_grade_' + grade,
      reviewQueueScope: 'academy_review_grade_' + grade,
      certificateScope: 'academy_grade_' + grade,
      certificateWorkflowScope: 'academy_certificate_grade_' + grade
    };
  }

  function publishAcademyScope(ctx, displayName) {
    window.__nexusAcademyScope = {
      active_overlay: 'academy',
      tenant_type: 'academy',
      base_role: 'student',
      display_name: displayName,
      active_grade: ctx.grade,
      academy_track: ctx.track,
      enrollment_scope: ctx.enrollmentScope,
      grade_package_id: ctx.gradePackageId,
      rag_namespace: ctx.ragNamespace,
      class_scope: ctx.classScope,
      card_deck: ctx.cardDeck,
      assignment_filter: ctx.assignmentFilter,
      work_item_filter: ctx.workItemFilter,
      progress_scope: ctx.progressScope,
      progress_ledger_scope: ctx.progressLedgerScope,
      review_queue_scope: ctx.reviewQueueScope,
      certificate_scope: ctx.certificateScope,
      certificate_workflow_scope: ctx.certificateWorkflowScope,
      school_class_id: null,
      teacher_dashboard_scope: null
    };
    try {
      localStorage.setItem('NEXUS_ACTIVE_PRODUCT_MODE', 'academy');
      localStorage.setItem('NEXUS_ACTIVE_OVERLAY', 'academy');
      localStorage.setItem('NEXUS_OVERLAY', 'academy');
      localStorage.setItem('NEXUS_TENANT_TYPE', 'academy');
      localStorage.setItem('NEXUS_DELIVERY_MODEL', 'academy_online');
      localStorage.setItem('NEXUS_ACADEMY_DEMO_PROFILE', '1');
      localStorage.setItem('NEXUS_ACADEMY_MULTIGRADE_ENABLED', '1');
      localStorage.setItem('NEXUS_ACADEMY_ACTIVE_GRADE', ctx.grade);
      localStorage.setItem('NEXUS_ACADEMY_DEFAULT_GRADE', ctx.grade);
      localStorage.setItem('NEXUS_ACADEMY_ALLOWED_GRADES', JSON.stringify(allowedGrades));
      localStorage.setItem('NEXUS_ACADEMY_TRACK', ctx.track);
      localStorage.setItem('NEXUS_ACADEMY_ENROLLMENT_SCOPE', ctx.enrollmentScope);
      localStorage.setItem('NEXUS_ACADEMY_GRADE_PACKAGE', ctx.gradePackageId);
      localStorage.setItem('NEXUS_ACADEMY_RAG_NAMESPACE', ctx.ragNamespace);
      localStorage.setItem('NEXUS_ACADEMY_CARD_DECK', ctx.cardDeck);
      localStorage.setItem('NEXUS_ACADEMY_PROGRESS_LEDGER_SCOPE', ctx.progressLedgerScope);
      localStorage.setItem('NEXUS_ACADEMY_REVIEW_QUEUE_SCOPE', ctx.reviewQueueScope);
      localStorage.setItem('NEXUS_ACADEMY_CERTIFICATE_WORKFLOW_SCOPE', ctx.certificateWorkflowScope);
      localStorage.setItem('NEXUS_ACADEMY_PROFILE', JSON.stringify({
        display_name: displayName,
        email: localStorage.getItem('NEXUS_SESSION_EMAIL') || 'student.demo@nqai',
        active_grade: ctx.grade,
        multigrade_enabled: true
      }));
    } catch (e) {}
    try {
      if (typeof window.nexusRefreshModelIndicator === 'function') window.nexusRefreshModelIndicator();
    } catch (e) {}
  }

  function readStoredAcademyPayload() {
    try {
      var activeOverlay = String(localStorage.getItem('NEXUS_ACTIVE_OVERLAY') || localStorage.getItem('NEXUS_OVERLAY') || '').trim().toLowerCase();
      var tenantType = String(localStorage.getItem('NEXUS_TENANT_TYPE') || '').trim().toLowerCase();
      var academyDemo = localStorage.getItem('NEXUS_ACADEMY_DEMO_PROFILE') === '1';
      var multigrade = localStorage.getItem('NEXUS_ACADEMY_MULTIGRADE_ENABLED') === '1';
      var productMode = String(localStorage.getItem('NEXUS_ACTIVE_PRODUCT_MODE') || '').trim().toLowerCase();
      var email = String(localStorage.getItem('NEXUS_SESSION_EMAIL') || localStorage.getItem('NEXUS_LAST_EMAIL') || '').trim().toLowerCase();
      var role = String(localStorage.getItem('NEXUS_SESSION_ROLE') || localStorage.getItem('NEXUS_LAST_ROLE') || '').trim().toLowerCase();
      var name = String(localStorage.getItem('NEXUS_SESSION_NAME') || '').trim();
      var academyLikely = productMode === 'academy' && (academyDemo || multigrade || tenantType === 'academy' || activeOverlay === 'academy' || activeOverlay === 'academy_student');
      if (!academyLikely) return null;
      var profile = {};
      try {
        profile = JSON.parse(localStorage.getItem('NEXUS_ACADEMY_PROFILE') || '{}') || {};
      } catch (e) {
        profile = {};
      }
      var grade = normalizeGrade(localStorage.getItem('NEXUS_ACADEMY_ACTIVE_GRADE') || localStorage.getItem('NEXUS_ACADEMY_DEFAULT_GRADE') || profile.active_grade || '3');
      return {
        __nexusBrowser: true,
        type: 'nexus-open-academy-home',
        product_mode: 'academy',
        view: 'academy_classroom',
        active_overlay: 'academy',
        active_grade: grade,
        display_name: profile.display_name || name || 'Lucía Hernández',
        email: profile.email || email || 'student.demo@nqai',
        demo_profile: true,
        multigrade_enabled: true
      };
    } catch (e) {
      return null;
    }
  }

  function applyChatContext(ctx, displayName) {
    var agentName = document.querySelector('.agent-name');
    if (agentName && agentName.textContent !== 'Eugenie Academy Tutor') agentName.textContent = 'Eugenie Academy Tutor';
    var route = document.getElementById('agent-route-indicator');
    var routeLabel = 'Academy context: ' + displayName + ' · Grade ' + ctx.grade;
    if (route && route.textContent !== routeLabel) route.textContent = routeLabel;
  }

  function updateChatContext(ctx, displayName) {
    applyChatContext(ctx, displayName);
    [80, 300, 900, 1600].forEach(function (delay) {
      window.setTimeout(function () {
        applyChatContext(ctx, displayName);
      }, delay);
    });
    if (academyChatKeeper) window.clearInterval(academyChatKeeper);
    var ticks = 0;
    academyChatKeeper = window.setInterval(function () {
      applyChatContext(ctx, displayName);
      ticks += 1;
      if (ticks >= 32) {
        window.clearInterval(academyChatKeeper);
        academyChatKeeper = null;
      }
    }, 250);
  }

  function canShowAcademyDebug() {
    try {
      if (localStorage.getItem('NEXUS_ACADEMY_DEBUG') !== '1') return false;
      var role = String(
        localStorage.getItem('NEXUS_SESSION_ROLE') ||
        localStorage.getItem('NEXUS_LAST_ROLE') ||
        ''
      ).trim().toLowerCase();
      var previewAllowed = localStorage.getItem('NEXUS_ACADEMY_DEBUG_ALLOWED') === '1';
      return previewAllowed || ['developer', 'admin', 'owner', 'root'].indexOf(role) >= 0;
    } catch (e) {
      return false;
    }
  }

  function postAcademyGradeChange(ctx) {
    try {
      window.parent.postMessage({
        __nexusBrowser: true,
        type: 'nexus-academy-grade-change',
        product_mode: 'academy',
        grade: ctx.grade,
        active_grade: ctx.grade,
        academy_track: ctx.track,
        enrollment_scope: ctx.enrollmentScope,
        grade_package_id: ctx.gradePackageId,
        rag_namespace: ctx.ragNamespace,
        class_scope: ctx.classScope,
        card_deck: ctx.cardDeck,
        assignment_filter: ctx.assignmentFilter,
        work_item_filter: ctx.workItemFilter,
        progress_scope: ctx.progressScope,
        progress_ledger_scope: ctx.progressLedgerScope,
        review_queue_scope: ctx.reviewQueueScope,
        certificate_scope: ctx.certificateScope,
        certificate_workflow_scope: ctx.certificateWorkflowScope,
        school_class_id: null,
        teacher_dashboard_scope: null
      }, window.location.origin);
    } catch (e) {}
  }

  function academyEscape(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch] || ch;
    });
  }

  function getAcademyEduApiBase(payload) {
    try {
      var explicit = (payload && payload.edu_api_base) ||
        window.NEXUS_EDU_API_BASE ||
        window.__NEXUS_EDU_API_BASE__ ||
        '';
      if (explicit) return String(explicit).replace(/\/$/, '');
      var origin = window.location && window.location.origin ? String(window.location.origin).replace(/\/$/, '') : '';
      if (/^https?:\/\//i.test(origin)) return origin;
      return 'http://127.0.0.1:8090';
    } catch (e) {
      return 'http://127.0.0.1:8090';
    }
  }

  function getAcademyEduApiBases(payload) {
    var bases = [];
    function addBase(base) {
      base = String(base || '').replace(/\/$/, '');
      if (base && bases.indexOf(base) === -1) bases.push(base);
    }
    try {
      addBase(getAcademyEduApiBase(payload));
      addBase('http://127.0.0.1:8090');
    } catch (e) {
      addBase('http://127.0.0.1:8090');
    }
    return bases;
  }

  function academyRuntimeWritesEnabled() {
    return false;
  }

  function getAcademyStudentId(payload) {
    try {
      var profile = {};
      try {
        profile = JSON.parse(localStorage.getItem('NEXUS_ACADEMY_PROFILE') || '{}') || {};
      } catch (e) {
        profile = {};
      }
      var value = (payload && (payload.student_id || payload.studentId || payload.studentIdSlug || payload.user_id)) ||
        profile.student_id ||
        profile.studentId ||
        localStorage.getItem('NEXUS_ACADEMY_STUDENT_ID') ||
        'lucia';
      value = String(value || 'lucia').trim().replace(/[^a-zA-Z0-9._:-]+/g, '');
      return value || 'lucia';
    } catch (e) {
      return 'lucia';
    }
  }

  function academyApiGet(path, payload) {
    var bases = getAcademyEduApiBases(payload);
    var lastError = null;
    function tryBase(index) {
      var base = bases[index] || 'http://127.0.0.1:8090';
      return fetch(base + path, {
        method: 'GET',
        credentials: 'omit',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'X-Nexus-Client': 'NexusBrowser'
        }
      }).then(function (response) {
      return response.json().catch(function () {
        return { ok: false, error: { code: 'academy.invalid_response', message: 'Academy API returned invalid JSON.' } };
      }).then(function (body) {
        if (!response.ok || !body || body.ok === false) {
          var err = new Error((body && body.error && body.error.message) || 'Academy API request failed.');
          err.code = (body && body.error && body.error.code) || ('http_' + response.status);
          err.payload = body;
          throw err;
        }
        return body.data || {};
      });
      }).catch(function (err) {
        lastError = err;
        if (index + 1 < bases.length) return tryBase(index + 1);
        throw lastError || err;
      });
    }
    return tryBase(0);
  }

  function academyApiPostOptional(path, requestBody, payload) {
    var bases = getAcademyEduApiBases(payload);
    var lastError = null;
    function tryBase(index) {
      var base = bases[index] || 'http://127.0.0.1:8090';
      return fetch(base + path, {
        method: 'POST',
        credentials: 'omit',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Nexus-Client': 'NexusBrowser'
        },
        body: JSON.stringify(requestBody || {})
      }).then(function (response) {
        return response.json().catch(function () {
          return { ok: false, error: { code: 'academy.invalid_response', message: 'Academy API returned invalid JSON.' } };
        }).then(function (body) {
          if (!response.ok || !body || body.ok === false) {
            var err = new Error((body && body.error && body.error.message) || 'Academy API request failed.');
            err.code = (body && body.error && body.error.code) || ('http_' + response.status);
            err.payload = body;
            throw err;
          }
          return { ok: true, data: body.data || {}, raw: body };
        });
      }).catch(function (err) {
        lastError = err;
        if (index + 1 < bases.length) return tryBase(index + 1);
        return {
          ok: false,
          error: lastError || err,
          code: (lastError && lastError.code) || (err && err.code) || 'academy.optional_post_unavailable'
        };
      });
    }
    return tryBase(0);
  }

  function academyApiPost(path, requestBody, payload) {
    if (!academyRuntimeWritesEnabled() && String(path || '').indexOf('/api/academy/') === 0) {
      var blocked = new Error('Academy runtime writes are disabled for this read-only UI pass.');
      blocked.code = 'academy.runtime_writes_disabled';
      return Promise.reject(blocked);
    }
    var bases = getAcademyEduApiBases(payload);
    var lastError = null;
    function tryBase(index) {
      var base = bases[index] || 'http://127.0.0.1:8090';
      return fetch(base + path, {
        method: 'POST',
        credentials: 'omit',
        cache: 'no-store',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Nexus-Client': 'NexusBrowser'
        },
        body: JSON.stringify(requestBody || {})
      }).then(function (response) {
        return response.json().catch(function () {
          return { ok: false, error: { code: 'academy.invalid_response', message: 'Academy API returned invalid JSON.' } };
        }).then(function (body) {
          if (!response.ok || !body || body.ok === false) {
            var err = new Error((body && body.error && body.error.message) || 'Academy API request failed.');
            err.code = (body && body.error && body.error.code) || ('http_' + response.status);
            err.payload = body;
            throw err;
          }
          return body.data || {};
        });
      }).catch(function (err) {
        lastError = err;
        if (index + 1 < bases.length) return tryBase(index + 1);
        throw lastError || err;
      });
    }
    return tryBase(0);
  }

  function academyGradeKey(value) {
    var grade = normalizeGrade(value);
    return 'grade_' + String(grade || '5').padStart(2, '0');
  }

  function academyLedgerForGrade(progressData, grade) {
    var key = academyGradeKey(grade);
    if (progressData && progressData.progress_ledgers && progressData.progress_ledgers[key]) {
      return progressData.progress_ledgers[key];
    }
    return {};
  }

  function academyLessonProgressForCard(progressData, grade, card) {
    var ledger = academyLedgerForGrade(progressData, grade);
    var rows = Array.isArray(ledger.lesson_card_progress) ? ledger.lesson_card_progress : [];
    var cardId = String((card && (card.lesson_card_id || card.card_id || card.id)) || '');
    for (var i = 0; i < rows.length; i += 1) {
      var rowId = String(rows[i].lesson_card_id || rows[i].card_id || '');
      if (rowId === cardId) return rows[i];
    }
    return { status: 'not_started' };
  }

  function academyStatusClass(status) {
    return String(status || 'not_started').toLowerCase().replace(/[^a-z0-9_]+/g, '_');
  }

	  function academyFriendlyStatus(status) {
	    var value = String(status || 'not_started').toLowerCase();
	    if (value === 'completed') return 'Done';
	    if (value === 'in_progress') return 'In progress';
	    if (value === 'submitted') return 'Turned in';
    if (value === 'needs_human_review') return 'Review';
    if (value === 'returned_for_revision') return 'Revise';
    if (value === 'needs_more_info') return 'More info';
	    if (value === 'approved') return 'Approved';
	    if (value === 'shell_pending_review') return 'Review shell';
	    if (value === 'generated_pilot_ready') return 'Pilot ready';
	    if (value === 'published_canvas_ready') return 'Published';
	    return 'Ready';
	  }

  function academyCertificateLabel(status) {
    var value = String(status || 'not_started').toLowerCase();
    if (value === 'issued' || value === 'certificate_issued') return 'Issued';
    if (value === 'approved') return 'Approved';
    if (value === 'ready_for_issue') return 'Ready for issue';
    if (value === 'ready_for_review' || value === 'certificate_ready') return 'Ready';
    if (value === 'review_pending') return 'Review';
    if (value === 'not_ready') return 'Building';
    return 'Not started';
  }

  function academyCertificateRequirementText(readiness) {
    var rows = academyArray(readiness && readiness.missing_requirements);
    if (!rows.length) return readiness && readiness.ready_for_issue ? 'All certificate requirements are ready for admin issue review.' : 'No missing requirements returned yet.';
    var row = rows[0] || {};
    var title = row.title || row.card_id || 'next lesson';
    var reason = String(row.reason || 'pending').replace(/[_-]+/g, ' ');
    var remaining = rows.length - 1;
    return title + ' - ' + reason + (remaining > 0 ? ' +' + remaining + ' more' : '');
  }

  function academyTitleCase(value) {
    return String(value || 'lesson').replace(/[_-]+/g, ' ').replace(/\w\S*/g, function (word) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  }

  function academyCardTone(card) {
    var text = String((card && ((card.activity_type || '') + ' ' + (card.title || '') + ' ' + (card.deliverable || ''))) || '').toLowerCase();
    if (/project|portfolio|capstone|share|artifact/.test(text)) return 'project';
    if (/create|build|draft|chart|story|make|design|draw/.test(text)) return 'create';
    if (/check|quiz|claim|error|review|diagnostic|spot/.test(text)) return 'check';
    if (/read|research|source|note|annotation/.test(text)) return 'read';
    return 'practice';
  }

  function academyCardIcon(card) {
    var tone = academyCardTone(card);
    if (tone === 'project') return 'P';
    if (tone === 'create') return 'M';
    if (tone === 'check') return 'C';
    if (tone === 'read') return 'R';
    return 'GO';
  }

  function academyActionButtons(cardId, status, primary, canvasInfo) {
    var writesEnabled = academyRuntimeWritesEnabled();
    var canStart = writesEnabled && ['not_started', 'assigned', 'returned_for_revision', 'needs_more_info'].indexOf(status) !== -1;
    var canComplete = writesEnabled && ['in_progress', 'submitted', 'ai_reviewed', 'approved'].indexOf(status) !== -1;
    var startClass = primary && canStart ? 'academy-primary-action' : 'academy-secondary-action';
    var completeClass = primary && canComplete ? 'academy-primary-action' : 'academy-secondary-action';
    var lessonLabel = canvasInfo ? 'Study + Questions' : 'Open Lesson';
    return '<div class="academy-action-row">' +
      '<span class="academy-canvas-status academy-canvas-status-' + academyEscape(academyStatusClass(status)) + '">' + academyEscape(academyFriendlyStatus(status)) + '</span>' +
      '<span class="academy-pill">Writes disabled</span>' +
      '<button type="button" class="' + startClass + '" data-academy-progress-action="start" data-academy-card-id="' + academyEscape(cardId) + '"' + (canStart ? '' : ' disabled') + '>' + (status === 'returned_for_revision' || status === 'needs_more_info' ? 'Restart' : 'Start') + '</button>' +
      '<button type="button" class="' + completeClass + '" data-academy-progress-action="complete" data-academy-card-id="' + academyEscape(cardId) + '"' + (canComplete ? '' : ' disabled') + '>Complete</button>' +
      '<button type="button" class="academy-secondary-action" data-academy-open-activity="1" data-academy-card-id="' + academyEscape(cardId) + '">' + academyEscape(lessonLabel) + '</button>' +
    '</div>';
  }

  function academyCompactLessonActions(cardId, status, canvasInfo) {
    var writesEnabled = academyRuntimeWritesEnabled();
    var canStart = writesEnabled && ['not_started', 'assigned', 'returned_for_revision', 'needs_more_info'].indexOf(status) !== -1;
    var canComplete = writesEnabled && ['in_progress', 'submitted', 'ai_reviewed', 'approved'].indexOf(status) !== -1;
    var lessonLabel = canvasInfo ? 'Study + Questions' : 'Open Lesson';
    return '<div class="academy-period-lesson-actions">' +
      '<span class="academy-canvas-status academy-canvas-status-' + academyEscape(academyStatusClass(status)) + '">' + academyEscape(academyFriendlyStatus(status)) + '</span>' +
      '<span class="academy-pill">Read-only</span>' +
      '<button type="button" class="academy-secondary-action" data-academy-open-activity="1" data-academy-card-id="' + academyEscape(cardId) + '">' + academyEscape(lessonLabel) + '</button>' +
      (canStart ? '<button type="button" class="academy-secondary-action" data-academy-progress-action="start" data-academy-card-id="' + academyEscape(cardId) + '">Start</button>' : '') +
      (canComplete ? '<button type="button" class="academy-secondary-action" data-academy-progress-action="complete" data-academy-card-id="' + academyEscape(cardId) + '">Complete</button>' : '') +
    '</div>';
  }

	  function academyGeneratedLessonActions(cardId, subjectId, ready) {
	    var statusLabel = ready ? 'Pilot ready' : 'Review shell';
	    var statusClass = ready ? 'generated_pilot_ready' : 'shell_pending_review';
	    return '<div class="academy-period-lesson-actions">' +
	      '<span class="academy-canvas-status academy-canvas-status-' + academyEscape(statusClass) + '">' + academyEscape(statusLabel) + '</span>' +
	      '<button type="button" class="academy-secondary-action" data-academy-open-generated-lesson="1" data-academy-card-id="' + academyEscape(cardId) + '" data-academy-subject-id="' + academyEscape(subjectId || '') + '">Study / Practice / Submit</button>' +
	    '</div>';
	  }

  function academyCanvasInfoPills(canvasInfo) {
    if (!canvasInfo) return '';
    var studyCount = Number(canvasInfo.study_material_ref_count || 0);
    var questionCount = Number(canvasInfo.assessment_item_ref_count || 0);
    return '<span class="academy-pill">Study ' + academyEscape(studyCount) + '</span>' +
      '<span class="academy-pill">Questions ' + academyEscape(questionCount) + '</span>';
  }

  function academyArray(value) {
    return Array.isArray(value) ? value : [];
  }

  function academyOptionalApiGet(path, payload, fallback) {
    return academyApiGet(path, payload).catch(function (err) {
      var data = fallback && typeof fallback === 'object' ? Object.assign({}, fallback) : {};
      data.unavailable = true;
      data.error_code = (err && err.code) || 'academy.optional_endpoint_unavailable';
      return data;
    });
  }

  function academySubjectStorageKey(ctx) {
    return 'NEXUS_ACADEMY_SELECTED_SUBJECT_GRADE_' + normalizeGrade(ctx && ctx.grade);
  }

  function academyGeneratedAliasFromSubjectId(subjectId, grade) {
    var value = String(subjectId || '').trim();
    var prefix = 'grade_' + String(normalizeGrade(grade)).padStart(2, '0') + '_';
    if (value.indexOf(prefix) === 0) value = value.slice(prefix.length);
    if (value === 'project_portfolio_lab') return 'projects_portfolio';
    if (value === 'technology_applications') return 'coding_data_agentic_learning';
    return value;
  }

  function academyNormalizedSubjectToken(value, grade) {
    var text = String(value || '').trim().toLowerCase();
    var prefix = 'grade_' + String(normalizeGrade(grade)).padStart(2, '0') + '_';
    if (text.indexOf(prefix) === 0) text = text.slice(prefix.length);
    return text.replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function academyLegacySubjectAliasTargets(value, grade) {
    var text = academyNormalizedSubjectToken(value, grade);
    var targets = {
      agentic_learning: ['coding_data_agentic_learning'],
      agentic_learning_project_lab: ['projects_portfolio'],
      ai_literacy_builder_evaluator: ['ai_literacy_digital_foundations'],
      ai_literacy_research_sources: ['ai_literacy_digital_foundations', 'coding_data_agentic_learning'],
      ai_literacy_responsible_ai: ['ai_literacy_digital_foundations', 'coding_data_agentic_learning'],
      catch_up_ai_foundations: ['ai_literacy_digital_foundations'],
      data_literacy: ['coding_data_agentic_learning']
    };
    return targets[text] || [];
  }

  function academySubjectMapDisplayLabel(subject, generated, ctx) {
    subject = subject || {};
    generated = generated || null;
    var grade = ctx && ctx.grade;
    var subjectId = String(subject.subject_id || '');
    var generatedId = academyGeneratedAliasFromSubjectId(subjectId, grade);
    var rawKey = academyNormalizedSubjectToken(subjectId, grade);
    var generatedKey = academyNormalizedSubjectToken(generatedId, grade);
    if (generated && generated.subject_label && rawKey && generatedKey && rawKey !== generatedKey) {
      return generated.subject_label;
    }
    return subject.subject_label || generated && generated.subject_label || academyTitleCase(subjectId);
  }

	  function academyGeneratedItemArray(packageData, key) {
	    packageData = packageData || {};
	    if (key && Array.isArray(packageData[key])) return packageData[key];
	    var itemKey = packageData.item_key;
	    if (itemKey && Array.isArray(packageData[itemKey])) return packageData[itemKey];
	    return [];
	  }

	  function academyGeneratedLessonId(item) {
	    item = item || {};
	    return String(item.lesson_card_id || item.source_existing_lesson_card_id || item.source_blueprint_lesson_id || item.card_id || '');
	  }

	  function academyGeneratedStudentReady(item) {
	    item = item || {};
	    if (item.student_visible === true || item.student_facing === true) return true;
	    var status = String(item.content_status || item.activity_status || item.student_prompt_status || item.render_status || item.review_status || '').toLowerCase();
	    return /student_visible|content_ready|authored_pilot|reviewed_for_pilot|prompt_ready/.test(status);
	  }

	  function academyGeneratedPackageHasReadyContent(packageData) {
	    packageData = packageData || {};
	    var lessonCards = academyArray(packageData.lessonCards).filter(academyGeneratedStudentReady);
	    var studyRows = academyArray(packageData.learningMaterials).filter(function (item) {
	      return academyGeneratedStudentReady(item) && academyArray(item && item.content_blocks).length;
	    });
	    var assessmentRows = academyArray(packageData.assessments).filter(function (item) {
	      return academyGeneratedStudentReady(item) && String(item && item.student_prompt || '').trim();
	    });
	    var activityRows = academyArray(packageData.activities).filter(function (item) {
	      return academyGeneratedStudentReady(item) && (String(item && item.student_prompt || '').trim() || academyArray(item && item.artifact_instructions).length);
	    });
	    return !!(lessonCards.length && (studyRows.length || assessmentRows.length || activityRows.length));
	  }

	  function academyGeneratedSubjectIdMap(generatedPayload) {
	    var map = {};
	    academyArray(generatedPayload && generatedPayload.subjects).forEach(function (subject) {
	      var id = String(subject && subject.subject_id || '');
	      if (id) map[id] = true;
	    });
	    return map;
	  }

	  function academyGeneratedPackageIdsForSelection(selectedOption, selectedSubjectDetail, generatedPayload, ctx) {
	    var ids = [];
	    var seen = {};
	    var known = academyGeneratedSubjectIdMap(generatedPayload);
	    function addId(value) {
	      var id = String(value || '').trim();
	      if (!id || seen[id]) return;
	      if (Object.keys(known).length && !known[id]) return;
	      seen[id] = true;
	      ids.push(id);
	    }
	    academyArray(selectedSubjectDetail && selectedSubjectDetail.units).forEach(function (unit) {
	      addId(unit && unit.package_subject_id);
	    });
	    if (selectedOption && selectedOption.mode === 'generated_shell') {
	      addId(selectedOption.package_subject_id || selectedOption.subject_id);
	    }
	    addId(selectedOption && selectedOption.package_subject_id);
	    if (!ids.length && selectedOption && selectedOption.subject_id) {
	      addId(academyGeneratedAliasFromSubjectId(selectedOption.subject_id, ctx && ctx.grade));
	    }
	    return ids;
	  }

	  function academyGeneratedSequenceValue(card) {
	    var sequence = Number(card && (card.sequence || card.lesson_sequence || card.sort_order));
	    return Number.isFinite(sequence) ? sequence : 9999;
	  }

	  function academySortGeneratedLessonCards(cards) {
	    return academyArray(cards).slice().sort(function (a, b) {
	      var bySequence = academyGeneratedSequenceValue(a) - academyGeneratedSequenceValue(b);
	      if (bySequence) return bySequence;
	      return String(academyGeneratedLessonId(a)).localeCompare(String(academyGeneratedLessonId(b)));
	    });
	  }

	  function academyMergeGeneratedPackages(packageRows, subjectLabel) {
	    var merged = {
	      subjectIds: [],
	      subjectLabel: subjectLabel || '',
	      lessonCards: [],
	      canvasCards: [],
	      learningMaterials: [],
	      assessments: [],
	      activities: [],
	      pdfManifests: [],
	      parentSummaries: [],
	      unavailable: false
	    };
	    var subjectSeen = {};
	    function pushItems(targetKey, rows) {
	      academyArray(rows).forEach(function (row) {
	        merged[targetKey].push(row);
	      });
	    }
	    academyArray(packageRows).forEach(function (row) {
	      row = row || {};
	      var subjectId = String(row.subjectId || '');
	      if (subjectId && !subjectSeen[subjectId]) {
	        subjectSeen[subjectId] = true;
	        merged.subjectIds.push(subjectId);
	      }
	      if (!merged.subjectLabel && row.subjectLabel) merged.subjectLabel = row.subjectLabel;
	      if (row.unavailable) merged.unavailable = true;
	      pushItems('lessonCards', row.lessonCards);
	      pushItems('canvasCards', row.canvasCards);
	      pushItems('learningMaterials', row.learningMaterials);
	      pushItems('assessments', row.assessments);
	      pushItems('activities', row.activities);
	      pushItems('pdfManifests', row.pdfManifests);
	      if (row.parentSummary) merged.parentSummaries.push(row.parentSummary);
	    });
	    merged.lessonCards = academySortGeneratedLessonCards(merged.lessonCards);
	    return merged;
	  }

	  function academyGeneratedPackageFromResponses(subjectId, subjectLabel, responses) {
	    responses = academyArray(responses);
	    return {
	      subjectId: subjectId,
	      subjectLabel: subjectLabel || (responses[0] && responses[0].subject && responses[0].subject.subject_label) || '',
	      unavailable: responses.some(function (response) { return !!(response && response.unavailable); }),
	      lessonCards: academyGeneratedItemArray(responses[0], 'lesson_cards'),
	      canvasCards: academyGeneratedItemArray(responses[1], 'canvas_card_shells'),
	      learningMaterials: academyGeneratedItemArray(responses[2], 'learning_material_shells'),
	      assessments: academyGeneratedItemArray(responses[3], 'assessment_item_shells'),
	      activities: academyGeneratedItemArray(responses[4], 'activity_pack_shells'),
	      pdfManifests: academyGeneratedItemArray(responses[5], 'pdf_manifests'),
	      parentSummary: responses[6] || {}
	    };
	  }

	  function academyFilterGeneratedPackageToLessonIds(packageData, lessonIdSet) {
	    if (!lessonIdSet || !Object.keys(lessonIdSet).length) return packageData || {};
	    packageData = packageData || {};
	    function keepByLessonId(item) {
	      return !!lessonIdSet[academyGeneratedLessonId(item)];
	    }
	    function keepPdf(row) {
	      var ids = academyArray(row && row.lesson_card_ids);
	      if (!ids.length && row && row.lesson_card_id) ids = [row.lesson_card_id];
	      for (var i = 0; i < ids.length; i += 1) {
	        if (lessonIdSet[String(ids[i] || '')]) return true;
	      }
	      return false;
	    }
	    return Object.assign({}, packageData, {
	      lessonCards: academySortGeneratedLessonCards(academyArray(packageData.lessonCards).filter(keepByLessonId)),
	      canvasCards: academyArray(packageData.canvasCards).filter(keepByLessonId),
	      learningMaterials: academyArray(packageData.learningMaterials).filter(keepByLessonId),
	      assessments: academyArray(packageData.assessments).filter(keepByLessonId),
	      activities: academyArray(packageData.activities).filter(keepByLessonId),
	      pdfManifests: academyArray(packageData.pdfManifests).filter(keepPdf)
	    });
	  }

  function academyComparableSubjectId(value, grade) {
    var text = academyNormalizedSubjectToken(value, grade);
    if (text === 'project_portfolio_lab' || text === 'project_portfolio') return 'projects_portfolio';
    if (text === 'technology_applications') return 'coding_data_agentic_learning';
    if (text === 'certifications_pathway_support' || text === 'certifications_readiness') return 'certifications';
    if (text === 'social_studies') return 'social_studies_civics';
    return text;
  }

  function academyN31MaterialRows(payload) {
    payload = payload || {};
    var rows = [];
    function addRows(value) {
      academyArray(value).forEach(function (row) {
        if (row && typeof row === 'object') rows.push(row);
      });
    }
    addRows(payload.student_material_expansions);
    addRows(payload.live_materials);
    addRows(payload.materials);
    addRows(payload.subject_materials);
    addRows(payload.items);
    addRows(payload.rows);
    if (!rows.length && payload.material && typeof payload.material === 'object') rows.push(payload.material);
    if (!rows.length && payload.subject && typeof payload.subject === 'object' && (payload.subject.student_material || payload.subject.study_material || payload.subject.material_id || payload.subject.title)) rows.push(payload.subject);
    if (!rows.length && payload.subject_id && (payload.student_material || payload.material_id || payload.title)) rows.push(payload);
    return rows;
  }

  function academyN31SubjectId(row) {
    row = row || {};
    return String(row.subject_id || row.subjectId || row.subject && row.subject.subject_id || row.material && row.material.subject_id || '').trim();
  }

  function academyN31SubjectLabel(row) {
    row = row || {};
    return String(row.subject_label || row.subjectLabel || row.subject && row.subject.subject_label || row.material && row.material.subject_label || academyN31SubjectId(row) || '').trim();
  }

  function academyN31MaterialStudent(row) {
    row = row || {};
    return row.student_material || row.studentMaterial || row.study_material || row.studyMaterial || {};
  }

  function academyN31MaterialUnits(row) {
    row = row || {};
    var student = academyN31MaterialStudent(row);
    var units = academyArray(row.source_units_covered);
    if (!units.length) units = academyArray(row.units);
    if (!units.length) units = academyArray(student.source_units_covered);
    if (!units.length) units = academyArray(student.units);
    return units.slice().sort(function (a, b) {
      var aSeq = Number(a && (a.sequence || a.unit_sequence || a.sort_order));
      var bSeq = Number(b && (b.sequence || b.unit_sequence || b.sort_order));
      aSeq = Number.isFinite(aSeq) ? aSeq : 9999;
      bSeq = Number.isFinite(bSeq) ? bSeq : 9999;
      if (aSeq !== bSeq) return aSeq - bSeq;
      return String((a && (a.unit_id || a.title)) || '').localeCompare(String((b && (b.unit_id || b.title)) || ''));
    });
  }

  function academyN31SubjectRows(payload) {
    payload = payload || {};
    var rows = [];
    var seen = {};
    function addSubject(row) {
      row = row || {};
      var subjectId = academyN31SubjectId(row);
      var label = academyN31SubjectLabel(row);
      var key = academyComparableSubjectId(subjectId || label, payload.grade || payload.grade_id || '');
      if (!key || seen[key]) return;
      seen[key] = true;
      rows.push({
        subject_id: subjectId || key,
        subject_label: label || academyTitleCase(subjectId || key),
        material: row,
        unit_count: academyN31MaterialUnits(row).length,
        status: row.review_status || row.approval_status || 'n31_study_material_ready'
      });
    }
    academyArray(payload.subjects).forEach(addSubject);
    academyN31MaterialRows(payload).forEach(addSubject);
    return rows;
  }

  function academyN31FindMaterialForSubject(payload, subjectId, subjectLabel, grade) {
    var wantedId = academyComparableSubjectId(subjectId, grade);
    var wantedLabel = academyComparableSubjectId(subjectLabel, grade);
    var rows = academyN31MaterialRows(payload);
    for (var i = 0; i < rows.length; i += 1) {
      var rowId = academyComparableSubjectId(academyN31SubjectId(rows[i]), grade);
      var rowLabel = academyComparableSubjectId(academyN31SubjectLabel(rows[i]), grade);
      if ((wantedId && rowId === wantedId) || (wantedLabel && rowLabel === wantedLabel)) return rows[i];
    }
    return null;
  }

  function academySubjectOptionKey(option, ctx) {
    option = option || {};
    var grade = ctx && ctx.grade;
    return academyComparableSubjectId(
      option.subject_id ||
      option.n31_subject_id ||
      option.package_subject_id ||
      option.label ||
      option.option_id,
      grade
    );
  }

  function academyMergeSubjectOption(base, incoming) {
    base = base || {};
    incoming = incoming || {};
    if (!base.n31_available && incoming.n31_available) {
      base.n31_available = true;
      base.n31_material = incoming.n31_material || base.n31_material;
      base.n31_subject_id = incoming.n31_subject_id || base.n31_subject_id;
    }
    if (!base.generated_subject && incoming.generated_subject) base.generated_subject = incoming.generated_subject;
    if (!base.package_subject_id && incoming.package_subject_id) base.package_subject_id = incoming.package_subject_id;
    if (!base.subject && incoming.subject) base.subject = incoming.subject;
    if (!base.subject_id && incoming.subject_id) base.subject_id = incoming.subject_id;
    if (!base.label && incoming.label) base.label = incoming.label;
    base.unit_count = Math.max(Number(base.unit_count || 0), Number(incoming.unit_count || 0));
    base.lesson_count = Math.max(Number(base.lesson_count || 0), Number(incoming.lesson_count || 0));
    if (String(base.status || '').indexOf('pending') !== -1 && incoming.status) base.status = incoming.status;
    return base;
  }

  function academyDedupeSubjectOptions(options, ctx) {
    var rows = [];
    var byKey = {};
    academyArray(options).forEach(function (option) {
      var key = academySubjectOptionKey(option, ctx);
      if (!key) {
        rows.push(option);
        return;
      }
      if (!byKey[key]) {
        byKey[key] = option;
        rows.push(option);
        return;
      }
      academyMergeSubjectOption(byKey[key], option);
    });
    return rows;
  }

  function academyBuildSubjectOptions(subjectsPayload, generatedPayload, ctx, n31Payload) {
    var n1Subjects = academyArray(subjectsPayload && subjectsPayload.subjects);
    var generatedSubjects = academyArray(generatedPayload && generatedPayload.subjects);
    var n31Subjects = academyN31SubjectRows(n31Payload);
    var generatedById = {};
    var usedGenerated = {};
    var usedN31 = {};
    generatedSubjects.forEach(function (subject) {
      var id = String(subject && subject.subject_id || '');
      if (id) generatedById[id] = subject;
    });
    var options = [];
    n1Subjects.forEach(function (subject) {
      subject = subject || {};
      var subjectId = String(subject.subject_id || '');
      var units = academyArray(subject.units);
      var visible = subject.student_visible !== false;
      if (!subjectId || (!visible && !units.length)) return;
      var generatedId = academyGeneratedAliasFromSubjectId(subjectId, ctx && ctx.grade);
      var generated = generatedById[generatedId] || null;
      if (generated) usedGenerated[generatedId] = true;
      var n31Material = academyN31FindMaterialForSubject(n31Payload, subjectId, subject.subject_label || generated && generated.subject_label, ctx && ctx.grade);
      var n31SubjectId = academyN31SubjectId(n31Material) || subjectId;
      if (n31Material) usedN31[academyComparableSubjectId(n31SubjectId, ctx && ctx.grade)] = true;
      var lessonCount = 0;
      units.forEach(function (unit) {
        lessonCount += academyArray(unit && unit.lesson_card_ids).length;
      });
      options.push({
        option_id: 'subject:' + subjectId,
        mode: 'subject_map',
        subject_id: subjectId,
        package_subject_id: generated ? generatedId : '',
        label: academySubjectMapDisplayLabel(subject, generated, ctx),
        subject: subject,
        generated_subject: generated,
        n31_material: n31Material,
        n31_subject_id: n31Material ? n31SubjectId : '',
        n31_available: !!n31Material,
        is_shell: false,
        student_visible: visible,
        unit_count: Math.max(units.length, academyN31MaterialUnits(n31Material).length),
        lesson_count: lessonCount || Number(subject.lesson_count || 0),
        status: n31Material ? 'n31_study_material_ready' : subject.availability_status || subject.review_status || 'available'
      });
    });
    generatedSubjects.forEach(function (subject) {
      subject = subject || {};
      var subjectId = String(subject.subject_id || '');
      if (!subjectId || usedGenerated[subjectId]) return;
      var n31Material = academyN31FindMaterialForSubject(n31Payload, subjectId, subject.subject_label, ctx && ctx.grade);
      var n31SubjectId = academyN31SubjectId(n31Material) || subjectId;
      if (n31Material) usedN31[academyComparableSubjectId(n31SubjectId, ctx && ctx.grade)] = true;
      options.push({
        option_id: 'generated:' + subjectId,
        mode: 'generated_shell',
        subject_id: subjectId,
        package_subject_id: subjectId,
        label: subject.subject_label || academyTitleCase(subjectId),
        subject: null,
        generated_subject: subject,
        n31_material: n31Material,
        n31_subject_id: n31Material ? n31SubjectId : '',
        n31_available: !!n31Material,
        is_shell: true,
        student_visible: subject.student_visible === true,
        unit_count: Math.max(Number(subject.unit_count || 0), academyN31MaterialUnits(n31Material).length),
        lesson_count: Number(subject.lesson_count || subject.lesson_card_count || 0),
        status: subject.production_status || subject.package_status || 'pending_internal_review'
      });
    });
    n31Subjects.forEach(function (subject) {
      subject = subject || {};
      var subjectId = String(subject.subject_id || '');
      var key = academyComparableSubjectId(subjectId || subject.subject_label, ctx && ctx.grade);
      if (!subjectId || usedN31[key]) return;
      usedN31[key] = true;
      options.push({
        option_id: 'n31:' + subjectId,
        mode: 'n31_study_material',
        subject_id: subjectId,
        package_subject_id: '',
        label: subject.subject_label || academyTitleCase(subjectId),
        subject: null,
        generated_subject: null,
        n31_material: subject.material || null,
        n31_subject_id: subjectId,
        n31_available: true,
        is_shell: false,
        student_visible: true,
        unit_count: Number(subject.unit_count || academyN31MaterialUnits(subject.material).length || 0),
        lesson_count: 0,
        status: subject.status || 'n31_study_material_ready'
      });
    });
    if (!options.length) {
      options.push({
        option_id: 'legacy:academy_path',
        mode: 'legacy',
        subject_id: 'academy_path',
        package_subject_id: '',
        label: 'Academy Path',
        subject: null,
        generated_subject: null,
        n31_material: null,
        n31_subject_id: '',
        n31_available: false,
        is_shell: false,
        student_visible: true,
        unit_count: 0,
        lesson_count: 0,
        status: 'fallback'
      });
    }
    return academyRemoveCoveredLegacyAliasSubjects(academyDedupeSubjectOptions(options, ctx), generatedSubjects, ctx);
  }

  function academyChooseSubjectOption(ctx, options, requestedOptionId) {
    options = academyArray(options);
    if (!options.length) return null;
    var wanted = String(requestedOptionId || '');
    if (!wanted) {
      try {
        wanted = String(localStorage.getItem(academySubjectStorageKey(ctx)) || '');
      } catch (e) {
        wanted = '';
      }
    }
    for (var i = 0; i < options.length; i += 1) {
      if (String(options[i].option_id || '') === wanted) return options[i];
    }
    for (var j = 0; j < options.length; j += 1) {
      if (options[j].student_visible && !options[j].is_shell) return options[j];
    }
    return options[0];
  }

  function academySubjectSetFromUnits(units, key) {
    var set = {};
    academyArray(units).forEach(function (unit) {
      academyArray(unit && unit[key]).forEach(function (id) {
        var value = String(id || '');
        if (value) set[value] = true;
      });
    });
    return set;
  }

  function academySubjectMenuHtml(ctx, options, selectedOption, n3Unavailable) {
	    selectedOption = selectedOption || {};
	    var optionHtml = academyArray(options).map(function (option) {
	      var suffix = option.is_shell ? ' - generated core' : option.student_visible ? '' : ' - hidden';
	      if (option.n31_available) suffix += ' - Study Material';
	      return '<option value="' + academyEscape(option.option_id) + '"' + (option.option_id === selectedOption.option_id ? ' selected' : '') + '>' +
	        academyEscape(option.label + suffix) +
	      '</option>';
	    }).join('');
	    var statusText = selectedOption.n31_available ? 'N31 Study Material Ready' : selectedOption.is_shell ? academyTitleCase(selectedOption.status || 'generated_core_package') : academyTitleCase(selectedOption.status || 'available');
    var lessonsText = selectedOption.lesson_count ? selectedOption.lesson_count + ' lessons' : 'lesson count pending';
    var unitsText = selectedOption.unit_count ? selectedOption.unit_count + ' units' : 'units pending';
    var layerText = selectedOption.n31_available ? 'N31 Study Material is loaded as a read-only layer for this subject. Use Study, Practice, and Submit preparation without runtime writes.' : n3Unavailable ? 'Subject taxonomy endpoints are not live yet, so this view is using the current grade package fallback.' : 'Only the selected subject is rendered below. Change subjects when you want to work in another course area.';
    return '<section class="academy-subject-menu" aria-label="Academy subject selector">' +
      '<label><span>Subject</span><select class="academy-subject-select" data-academy-subject-select="1">' + optionHtml + '</select></label>' +
      '<div class="academy-subject-summary">' +
        '<div class="academy-pill-row">' +
          '<span class="academy-pill academy-pill-strong">Grade ' + academyEscape(ctx.grade) + '</span>' +
          '<span class="academy-pill">' + academyEscape(statusText) + '</span>' +
          '<span class="academy-pill">' + academyEscape(unitsText) + '</span>' +
          '<span class="academy-pill">' + academyEscape(lessonsText) + '</span>' +
          '<span class="academy-pill">Writes disabled</span>' +
        '</div>' +
        '<strong>' + academyEscape(selectedOption.label || 'Academy Path') + '</strong>' +
        '<p>' + academyEscape(layerText) + '</p>' +
      '</div>' +
    '</section>';
  }

  function academySubjectProgressRows(payload) {
    payload = payload || {};
    var rows = [];
    function addRows(value) {
      academyArray(value).forEach(function (row) {
        if (row && typeof row === 'object') rows.push(row);
      });
    }
    function addMap(value) {
      if (!value || Array.isArray(value) || typeof value !== 'object') return;
      Object.keys(value).forEach(function (key) {
        var row = value[key];
        if (row && typeof row === 'object') {
          if (!row.subject_id) row.subject_id = key;
          rows.push(row);
        }
      });
    }
    addRows(payload.subject_progress);
    addMap(payload.subject_progress);
    addRows(payload.subjectProgress);
    addMap(payload.subjectProgress);
    addRows(payload.subjects);
    addRows(payload.progress);
    addMap(payload.progress);
    addRows(payload.rows);
    addRows(payload.items);
    addRows(payload.data && payload.data.subjects);
    if (!rows.length && payload.subject_id) rows.push(payload);
    return rows;
  }

  function academySubjectOptionLessonIds(option) {
    option = option || {};
    var ids = {};
    academyArray(option.subject && option.subject.units).forEach(function (unit) {
      academyArray(unit && unit.lesson_card_ids).forEach(function (id) {
        id = String(id || '');
        if (id) ids[id] = true;
      });
    });
    return Object.keys(ids);
  }

  function academySubjectLessonIds(subject) {
    subject = subject || {};
    var ids = {};
    academyArray(subject.lesson_card_ids).forEach(function (id) {
      id = String(id || '');
      if (id) ids[id] = true;
    });
    academyArray(subject.units).forEach(function (unit) {
      academyArray(unit && unit.lesson_card_ids).forEach(function (id) {
        id = String(id || '');
        if (id) ids[id] = true;
      });
    });
    return Object.keys(ids);
  }

  function academyRemoveCoveredLegacyAliasSubjects(options, generatedSubjects, ctx) {
    options = academyArray(options);
    generatedSubjects = academyArray(generatedSubjects);
    if (!options.length || !generatedSubjects.length) return options;
    var generatedLessonCoverage = {};
    generatedSubjects.forEach(function (subject) {
      var key = academyComparableSubjectId(subject && subject.subject_id, ctx && ctx.grade);
      if (!key) return;
      if (!generatedLessonCoverage[key]) generatedLessonCoverage[key] = {};
      academySubjectLessonIds(subject).forEach(function (id) {
        generatedLessonCoverage[key][id] = true;
      });
    });
    function hasCanonicalOption(target, currentOption) {
      for (var i = 0; i < options.length; i += 1) {
        if (options[i] === currentOption) continue;
        if (academySubjectOptionKey(options[i], ctx) === target) return true;
      }
      return false;
    }
    function lessonsCoveredByTargets(lessonIds, targets) {
      if (!lessonIds.length || !targets.length) return false;
      for (var i = 0; i < lessonIds.length; i += 1) {
        var covered = false;
        for (var t = 0; t < targets.length; t += 1) {
          if (generatedLessonCoverage[targets[t]] && generatedLessonCoverage[targets[t]][lessonIds[i]]) {
            covered = true;
            break;
          }
        }
        if (!covered) return false;
      }
      return true;
    }
    return options.filter(function (option) {
      var targets = academyLegacySubjectAliasTargets(option && option.subject_id || option && option.label, ctx && ctx.grade);
      if (!targets.length) return true;
      for (var i = 0; i < targets.length; i += 1) {
        if (!hasCanonicalOption(targets[i], option)) return true;
      }
      return !lessonsCoveredByTargets(academySubjectOptionLessonIds(option), targets);
    });
  }

  function academySubjectProgressPercent(row) {
    row = row || {};
    var direct = Number(
      row.progress_percent != null ? row.progress_percent :
      row.percent_complete != null ? row.percent_complete :
      row.completion_percent != null ? row.completion_percent :
      row.progressPercentage != null ? row.progressPercentage :
      row.percent
    );
    if (Number.isFinite(direct)) return Math.min(100, Math.max(0, Math.round(direct)));
    var completed = Number(row.completed_lessons != null ? row.completed_lessons : row.lessons_completed);
    var total = Number(row.total_lessons != null ? row.total_lessons : row.lessons_total);
    if (Number.isFinite(completed) && Number.isFinite(total) && total > 0) {
      return Math.min(100, Math.max(0, Math.round((completed / total) * 100)));
    }
    return 0;
  }

  function academySubjectTargetText(value, fallback) {
    if (value && typeof value === 'object') {
      return String(
        value.title ||
        value.lesson_title ||
        value.target_title ||
        value.unit_title ||
        value.card_title ||
        value.lesson_card_id ||
        value.card_id ||
        fallback ||
        ''
      ).trim();
    }
    return String(value || fallback || '').trim();
  }

  function academySubjectProgressForOption(subjectProgressPayload, option, ctx) {
    option = option || {};
    var wanted = [
      option.subject_id,
      option.package_subject_id,
      option.n31_subject_id,
      option.label
    ].map(function (value) {
      return academyComparableSubjectId(value, ctx && ctx.grade);
    }).filter(Boolean);
    var rows = academySubjectProgressRows(subjectProgressPayload);
    for (var i = 0; i < rows.length; i += 1) {
      var row = rows[i] || {};
      var keys = [
        row.subject_id,
        row.subjectId,
        row.package_subject_id,
        row.n31_subject_id,
        row.subject_label,
        row.subjectLabel,
        row.label
      ].map(function (value) {
        return academyComparableSubjectId(value, ctx && ctx.grade);
      }).filter(Boolean);
      for (var k = 0; k < keys.length; k += 1) {
        if (wanted.indexOf(keys[k]) >= 0) return row;
      }
    }
    return null;
  }

  function academySubjectFallbackFromLedger(option, progressData, grade, cardMap) {
    var ids = academySubjectOptionLessonIds(option);
    var ledger = academyLedgerForGrade(progressData || {}, grade);
    var rows = academyArray(ledger.lesson_card_progress);
    var byCard = {};
    rows.forEach(function (row) {
      var id = String(row && (row.lesson_card_id || row.card_id) || '');
      if (id) byCard[id] = row;
    });
    var completed = 0;
    var inProgressId = '';
    var nextId = '';
    ids.forEach(function (id) {
      var status = String((byCard[id] && byCard[id].status) || 'not_started').toLowerCase();
      if (status === 'completed' || status === 'approved') completed += 1;
      else if (!nextId) nextId = id;
      if (!inProgressId && (status === 'in_progress' || status === 'returned_for_revision' || status === 'needs_more_info')) inProgressId = id;
    });
    var total = ids.length || Number(option.lesson_count || 0);
    var resumeId = inProgressId || nextId || ids[0] || '';
    var resumeCard = cardMap && resumeId ? cardMap[resumeId] : null;
    var percent = total ? Math.min(100, Math.round((completed / total) * 100)) : 0;
    return {
      progress_percent: percent,
      status: total && completed >= total ? 'completed' : inProgressId ? 'in_progress' : 'not_started',
      completed_lessons: completed,
      total_lessons: total,
      resume_target_title: resumeCard && (resumeCard.title || resumeCard.lesson_title),
      last_target_title: inProgressId && resumeCard ? resumeCard.title || resumeCard.lesson_title : ''
    };
  }

  function academySubjectProgressModel(option, subjectProgressPayload, progressData, ctx, cardMap) {
    option = option || {};
    var row = academySubjectProgressForOption(subjectProgressPayload, option, ctx);
    var fallback = academySubjectFallbackFromLedger(option, progressData, ctx && ctx.grade, cardMap || {});
    var source = row || fallback;
    var percent = academySubjectProgressPercent(source);
    var status = String(source.status || source.progress_status || source.subject_status || fallback.status || option.status || 'not_started');
    var resume = academySubjectTargetText(source.resume_target || source.resumeTarget || source.resume_target_title || source.resume_lesson_title || source.next_lesson_title || source.current_lesson_title, fallback.resume_target_title || 'Next lesson');
    var last = academySubjectTargetText(source.last_target || source.lastTarget || source.last_target_title || source.last_lesson_title || source.last_activity_title || source.updated_at, fallback.last_target_title || (percent ? 'Recent activity' : 'No activity yet'));
    var total = Number(source.total_lessons != null ? source.total_lessons : source.lessons_total);
    var completed = Number(source.completed_lessons != null ? source.completed_lessons : source.lessons_completed);
    if (!Number.isFinite(total)) total = Number(fallback.total_lessons || option.lesson_count || 0);
    if (!Number.isFinite(completed)) completed = Number(fallback.completed_lessons || 0);
    var action = percent > 0 && status !== 'completed' && status !== 'approved' ? 'continue' : 'start';
    if (status === 'completed' || status === 'approved' || percent >= 100) action = 'review';
    return {
      optionId: option.option_id,
      subjectId: option.subject_id || option.n31_subject_id || option.package_subject_id || option.option_id,
      label: option.label || 'Subject',
      percent: percent,
      status: status,
      statusLabel: academyFriendlyStatus(status),
      resumeTarget: resume,
      lastTarget: last,
      completedLessons: completed,
      totalLessons: total,
      action: action,
      actionLabel: action === 'continue' ? 'Continue' : action === 'review' ? 'Review' : 'Start',
      n31Available: !!option.n31_available,
      units: Number(option.unit_count || 0),
      lessons: Number(option.lesson_count || total || 0),
      source: row ? 'n32_subject_progress' : 'local_ledger_fallback'
    };
  }

  function academySubjectCardHtml(model) {
    model = model || {};
    var capped = Math.min(100, Math.max(0, Number(model.percent || 0)));
    return '<article class="academy-subject-card" data-academy-subject-card="' + academyEscape(model.optionId || '') + '">' +
      '<div class="academy-pill-row">' +
        '<span class="academy-canvas-status academy-canvas-status-' + academyEscape(academyStatusClass(model.status)) + '">' + academyEscape(model.statusLabel || 'Ready') + '</span>' +
        '<span class="academy-pill">' + academyEscape(model.units || 0) + ' units</span>' +
        '<span class="academy-pill">' + academyEscape(model.lessons || 0) + ' lessons</span>' +
        (model.n31Available ? '<span class="academy-pill academy-pill-strong">Study ready</span>' : '') +
      '</div>' +
      '<h4>' + academyEscape(model.label || 'Subject') + '</h4>' +
      '<div class="academy-subject-meter">' +
        '<div class="academy-subject-percent">' + academyEscape(capped) + '%</div>' +
        '<div class="academy-progress-bar" aria-hidden="true"><div class="academy-progress-fill" style="width:' + academyEscape(capped) + '%"></div></div>' +
      '</div>' +
      '<div class="academy-subject-targets">' +
        '<span><strong>Last</strong>' + academyEscape(model.lastTarget || 'No activity yet') + '</span>' +
        '<span><strong>Resume</strong>' + academyEscape(model.resumeTarget || 'Next lesson') + '</span>' +
      '</div>' +
      '<div class="academy-subject-card-footer">' +
        '<span class="academy-pill">' + academyEscape(model.source === 'n32_subject_progress' ? 'N32 progress' : 'Local progress') + '</span>' +
        '<button type="button" class="academy-primary-action" data-academy-open-subject="1" data-academy-subject-option-id="' + academyEscape(model.optionId || '') + '" data-academy-subject-action="' + academyEscape(model.action || 'start') + '">' + academyEscape(model.actionLabel || 'Start') + '</button>' +
      '</div>' +
    '</article>';
  }

  function academySubjectDashboardHtml(ctx, displayName, subjectModels, subjectProgressUnavailable) {
    var rows = academyArray(subjectModels);
    var cardHtml = rows.map(academySubjectCardHtml).join('');
    return '<section class="academy-subject-dashboard" aria-label="Academy subject dashboard">' +
      '<div class="academy-subject-dashboard-header">' +
        '<div>' +
          '<div class="academy-pill-row">' +
            '<span class="academy-pill academy-pill-strong">Grades 3-8 Dashboard</span>' +
            '<span class="academy-pill">Grade ' + academyEscape(ctx.grade) + '</span>' +
            '<span class="academy-pill">' + academyEscape(rows.length) + ' subjects</span>' +
            '<span class="academy-pill">' + academyEscape(subjectProgressUnavailable ? 'Progress fallback' : 'Subject progress live') + '</span>' +
          '</div>' +
          '<h3>Choose a subject to study, practice, or submit work.</h3>' +
          '<p>' + academyEscape(displayName) + ' sees one subject at a time after opening a card.</p>' +
        '</div>' +
      '</div>' +
      '<section class="academy-subject-grid" aria-label="Grade ' + academyEscape(ctx.grade) + ' subjects">' +
        (cardHtml || '<section class="academy-canvas-empty" role="status"><strong>No subjects returned.</strong><span>Academy subjects will appear here when the grade package is available.</span></section>') +
      '</section>' +
      '<div class="academy-subject-event-status" data-academy-subject-event-status="1" aria-live="polite"></div>' +
    '</section>';
  }

  function academyRecordSubjectEvent(payload, ctx, option, model, action) {
    option = option || {};
    model = model || {};
    var studentId = getAcademyStudentId(payload);
    var eventAction = action === 'start' ? 'start_subject' : 'open_subject';
    return academyApiPostOptional('/api/academy/students/' + encodeURIComponent(studentId) + '/progress/subject-events', {
      student_id: studentId,
      grade: ctx && ctx.grade,
      subject_id: option.subject_id || model.subjectId || option.n31_subject_id || option.package_subject_id || option.option_id,
      package_subject_id: option.package_subject_id || '',
      subject_label: option.label || model.label || 'Subject',
      event_type: eventAction,
      action: eventAction,
      ui_action: action || 'start',
      resume_target: model.resumeTarget || '',
      last_target: model.lastTarget || '',
      progress_percent: model.percent || 0,
      source: 'academy_my_class_subject_dashboard',
      actor_role: 'student',
      actor_id: 'academy-demo-lucia',
      recorded_at: new Date().toISOString()
    }, payload);
  }

	  function academyGeneratedRuntimeCard(card) {
	    card = card || {};
	    var preview = card.student_preview || {};
	    return {
	      lesson_card_id: card.lesson_card_id || card.card_id || '',
	      card_id: card.lesson_card_id || card.card_id || '',
	      module_id: card.unit_id || 'generated_unit',
	      title: card.lesson_title || card.title || 'Lesson shell',
	      student_summary: preview.focus || academyArray(card.student_outcomes).join(' ') || 'Study the lesson, practice the idea, and prepare your review artifact.',
	      teacher_summary: card.unit_title || '',
	      estimated_minutes: card.estimated_minutes || 45,
	      activity_type: 'generated_core_lesson',
	      deliverable: 'Prepared student artifact',
	      generated_shell: true,
	      raw_generated_card: card
	    };
	  }

	  function academyCanvasLessonId(canvasCard) {
	    canvasCard = canvasCard || {};
	    return String(canvasCard.lesson_card_id || canvasCard.source_existing_lesson_card_id || canvasCard.card_id || canvasCard.canvas_card_id || '');
	  }

	  function academyPublishedCanvasIsStudentVisible(canvasCard) {
	    canvasCard = canvasCard || {};
	    if (canvasCard.student_visible === false) return false;
	    var status = String(canvasCard.publication_status || canvasCard.review_status || '').toLowerCase();
	    return !status || status === 'published' || status === 'published_n36c_controlled';
	  }

	  function academyPublishedCanvasCount(canvasCards) {
	    return academyArray(canvasCards).filter(academyPublishedCanvasIsStudentVisible).length;
	  }

	  function academySubjectSelectionKeys(selectedOption, selectedSubjectUnits, ctx) {
	    var keys = [];
	    var seen = {};
	    function addKey(value) {
	      var key = academyComparableSubjectId(value, ctx && ctx.grade);
	      if (!key || seen[key]) return;
	      seen[key] = true;
	      keys.push(key);
	    }
	    selectedOption = selectedOption || {};
	    addKey(selectedOption.subject_id);
	    addKey(selectedOption.package_subject_id);
	    addKey(selectedOption.n31_subject_id);
	    addKey(selectedOption.label);
	    addKey(selectedOption.subject && selectedOption.subject.subject_id);
	    addKey(selectedOption.generated_subject && selectedOption.generated_subject.subject_id);
	    academyArray(selectedSubjectUnits).forEach(function (unit) {
	      addKey(unit && unit.package_subject_id);
	      addKey(unit && unit.subject_id);
	      addKey(unit && unit.subject_label);
	      addKey(unit && unit.source_package_path);
	    });
	    return keys;
	  }

	  function academyCanvasMatchesSelectedSubject(canvasCard, selectedOption, selectedSubjectUnits, ctx) {
	    var keys = academySubjectSelectionKeys(selectedOption, selectedSubjectUnits, ctx);
	    if (!keys.length) return false;
	    var parts = [
	      canvasCard && canvasCard.lesson_card_id,
	      canvasCard && canvasCard.canvas_card_id,
	      canvasCard && canvasCard.module_id,
	      canvasCard && canvasCard.activity_pack_id,
	      canvasCard && canvasCard.title,
	      canvasCard && canvasCard.student_intro
	    ].map(function (value) {
	      return academyComparableSubjectId(value, ctx && ctx.grade);
	    }).filter(Boolean);
	    var haystack = '_' + parts.join('_') + '_';
	    for (var i = 0; i < keys.length; i += 1) {
	      var key = keys[i];
	      if (!key || key.length < 3) continue;
	      if (haystack.indexOf('_' + key + '_') !== -1) return true;
	      if (haystack.indexOf('_' + key) !== -1 && haystack.indexOf(key + '_') !== -1) return true;
	    }
	    return false;
	  }

	  function academyPublishedCanvasCardsForSelection(canvasCards, gradeCardMap, selectedOption, selectedSubjectUnits, ctx) {
	    var rows = [];
	    var seen = {};
	    academyArray(canvasCards).forEach(function (canvasCard) {
	      var lessonId = academyCanvasLessonId(canvasCard);
	      if (!lessonId || seen[lessonId] || (gradeCardMap && gradeCardMap[lessonId])) return;
	      if (!academyPublishedCanvasIsStudentVisible(canvasCard)) return;
	      if (!academyCanvasMatchesSelectedSubject(canvasCard, selectedOption, selectedSubjectUnits, ctx)) return;
	      seen[lessonId] = true;
	      rows.push(canvasCard);
	    });
	    return rows;
	  }

	  function academyPublishedCanvasRuntimeCard(canvasCard, selectedOption) {
	    canvasCard = canvasCard || {};
	    var lessonId = academyCanvasLessonId(canvasCard);
	    return {
	      lesson_card_id: lessonId,
	      card_id: lessonId,
	      module_id: canvasCard.module_id || 'published_canvas_cards',
	      title: canvasCard.title || 'Published Canvas card',
	      student_summary: canvasCard.student_intro || canvasCard.learning_goal || 'Study this published Canvas card, practice the idea, and prepare evidence for review.',
	      teacher_summary: selectedOption && selectedOption.label ? selectedOption.label + ' published Canvas card' : 'Published Canvas card',
	      estimated_minutes: canvasCard.estimated_minutes || 25,
	      activity_type: canvasCard.render_type || 'study_card',
	      deliverable: canvasCard.student_instruction || 'Prepared learning artifact',
	      published_canvas_card: true,
	      canvas_card: canvasCard
	    };
	  }

	  function academyPublishedCanvasModules(canvasCards, selectedOption) {
	    var modules = [];
	    var byId = {};
	    academyArray(canvasCards).forEach(function (canvasCard) {
	      var moduleId = String((canvasCard && canvasCard.module_id) || 'published_canvas_cards');
	      if (!byId[moduleId]) {
	        byId[moduleId] = {
	          module_id: moduleId,
	          title: canvasCard && canvasCard.module_title || academyTitleCase(moduleId.replace(/^n31_g[0-9]+_/, '')),
	          essential_question: 'Study the published Canvas card, practice in your own words, then prepare evidence for human review.',
	          lesson_card_count: 0,
	          published_canvas_module: true,
	          subject_label: selectedOption && selectedOption.label || ''
	        };
	        modules.push(byId[moduleId]);
	      }
	      byId[moduleId].lesson_card_count += 1;
	    });
	    return modules;
	  }

	  function academyPublishedCanvasLessonActions(cardId) {
	    return '<div class="academy-period-lesson-actions">' +
	      '<span class="academy-canvas-status academy-canvas-status-published_canvas_ready">Published</span>' +
	      '<span class="academy-pill">Read-only</span>' +
	      '<button type="button" class="academy-secondary-action" data-academy-open-published-canvas="1" data-academy-card-id="' + academyEscape(cardId) + '">Study / Practice / Submit</button>' +
	    '</div>';
	  }

  function academyGeneratedModulesFromCards(cards) {
    var modules = [];
    var byId = {};
    academyArray(cards).forEach(function (card) {
      var moduleId = String(card && card.unit_id || 'generated_unit');
	      if (!byId[moduleId]) {
	        byId[moduleId] = {
	          module_id: moduleId,
	          title: card.unit_title || academyTitleCase(moduleId),
	          essential_question: 'Open one lesson at a time. Study, practice, then prepare the classroom artifact for human review.',
	          lesson_card_count: 0,
	          generated_shell: true
	        };
        modules.push(byId[moduleId]);
      }
      byId[moduleId].lesson_card_count += 1;
    });
    return modules;
  }

  function academyListHtml(items, className) {
    var rows = academyArray(items).filter(function (item) {
      return item !== null && item !== undefined && String(item).trim() !== '';
    });
    if (!rows.length) return '<p>Not configured yet.</p>';
    return '<ul class="' + academyEscape(className || 'academy-activity-list') + '">' + rows.map(function (item) {
      if (typeof item === 'object') {
        var parts = [];
        Object.keys(item).forEach(function (key) {
          var value = item[key];
          if (value === null || value === undefined || String(value).trim() === '') return;
          parts.push('<strong>' + academyEscape(academyTitleCase(key)) + ':</strong> ' + academyEscape(value));
        });
        return '<li>' + (parts.join(' · ') || academyEscape(JSON.stringify(item))) + '</li>';
      }
      return '<li>' + academyEscape(item) + '</li>';
    }).join('') + '</ul>';
  }

  function academyTextHtml(value) {
    return academyEscape(value || '').replace(/\n/g, '<br>');
  }

	  function academyStudyMaterialHtml(material) {
	    material = material || {};
	    var blocks = academyArray(material.content_blocks);
	    var blockHtml = blocks.map(function (block) {
	      block = block || {};
	      var terms = academyArray(block.terms);
	      var body = block.body || block.student_text || block.instructions || block.text || '';
	      return '<article class="academy-study-block">' +
	        '<div class="academy-pill-row">' +
	          '<span class="academy-pill">' + academyEscape(academyTitleCase(block.block_type || block.section || 'study')) + '</span>' +
	        '</div>' +
	        '<strong>' + academyEscape(block.title || block.heading || 'Study block') + '</strong>' +
	        '<p class="academy-study-body">' + academyTextHtml(body) + '</p>' +
	        (terms.length ? '<div class="academy-pill-row">' + terms.map(function (term) { return '<span class="academy-pill">' + academyEscape(term) + '</span>'; }).join('') + '</div>' : '') +
	      '</article>';
	    }).join('');
	    var takeaways = academyListHtml(material.student_takeaways, 'academy-activity-list');
	    var practice = academyListHtml(academyArray(material.practice_prompts).length ? material.practice_prompts : material.practice_prompts_preview, 'academy-activity-list');
	    return '<article class="academy-activity-block academy-study-page">' +
	      '<div class="academy-pill-row">' +
	        '<span class="academy-pill academy-pill-strong">Study First</span>' +
        '<span class="academy-pill">' + academyEscape(material.estimated_minutes || 12) + ' min</span>' +
        '<span class="academy-pill">Student material</span>' +
      '</div>' +
      '<strong>' + academyEscape(material.title || 'Student study pack') + '</strong>' +
      '<p>' + academyEscape(material.reading_level || 'Student reading level') + '</p>' +
      '<div class="academy-study-stack">' + (blockHtml || '<p>Study material is pending review.</p>') + '</div>' +
      '<strong>Remember</strong>' +
      takeaways +
      '<strong>Practice Before The Questions</strong>' +
      practice +
    '</article>';
  }

  function academyN31SelectedMaterial(rootPayload, detailPayload, selectedOption, ctx) {
    var detailRows = academyN31MaterialRows(detailPayload);
    if (detailRows.length) return detailRows[0];
    if (detailPayload && detailPayload.material && typeof detailPayload.material === 'object') return detailPayload.material;
    if (detailPayload && detailPayload.subject && typeof detailPayload.subject === 'object' && detailPayload.subject.material) return detailPayload.subject.material;
    if (selectedOption && selectedOption.n31_material) return selectedOption.n31_material;
    if (selectedOption) {
      return academyN31FindMaterialForSubject(rootPayload, selectedOption.n31_subject_id || selectedOption.subject_id, selectedOption.label, ctx && ctx.grade);
    }
    return null;
  }

  function academyN31Chunks(payload) {
    payload = payload || {};
    var rows = [];
    function addRows(value) {
      academyArray(value).forEach(function (row) {
        if (row && typeof row === 'object') rows.push(row);
      });
    }
    addRows(payload.chunks);
    addRows(payload.rag_chunks);
    addRows(payload.review_chunks);
    addRows(payload.items);
    addRows(payload.rows);
    if (!rows.length && payload.chunk && typeof payload.chunk === 'object') rows.push(payload.chunk);
    return rows.map(function (row, index) {
      return { row: row, index: index };
    }).sort(function (a, b) {
      var aSeq = Number(a.row.sequence || a.row.chunk_sequence || a.row.sort_order);
      var bSeq = Number(b.row.sequence || b.row.chunk_sequence || b.row.sort_order);
      aSeq = Number.isFinite(aSeq) ? aSeq : a.index + 1000;
      bSeq = Number.isFinite(bSeq) ? bSeq : b.index + 1000;
      if (aSeq !== bSeq) return aSeq - bSeq;
      return String(a.row.chunk_id || '').localeCompare(String(b.row.chunk_id || ''));
    }).map(function (item) {
      return item.row;
    });
  }

  function academyN31ChunksForMaterial(chunksPayload, material, ctx) {
    var rows = academyN31Chunks(chunksPayload);
    if (!rows.length) return rows;
    material = material || {};
    var materialId = String(material.material_id || material.id || '');
    var subjectId = academyComparableSubjectId(academyN31SubjectId(material), ctx && ctx.grade);
    var filtered = rows.filter(function (chunk) {
      var chunkMaterialId = String(chunk.material_id || chunk.source_material_id || '');
      var chunkSubjectId = academyComparableSubjectId(chunk.subject_id || chunk.subject && chunk.subject.subject_id, ctx && ctx.grade);
      if (materialId && chunkMaterialId && chunkMaterialId !== materialId) return false;
      if (subjectId && chunkSubjectId && chunkSubjectId !== subjectId) return false;
      return true;
    });
    return filtered.length ? filtered : rows;
  }

  function academyN31Excerpt(value, maxLength) {
    var text = String(value || '').replace(/\s+/g, ' ').trim();
    var max = Number(maxLength || 360);
    if (text.length <= max) return text;
    return text.slice(0, Math.max(0, max - 3)) + '...';
  }

  function academyN31GenerationGateHtml() {
    return '<article class="academy-generation-gate" aria-label="Card generation disabled">' +
      '<div class="academy-pill-row">' +
        '<span class="academy-pill academy-pill-strong">Card Generation Mode</span>' +
        '<span class="academy-pill">Disabled</span>' +
        '<span class="academy-pill">No POST</span>' +
      '</div>' +
      '<strong>Generate Cards from Study Material</strong>' +
      '<p>This gate is visible for N32B readiness only. Card generation remains locked until a later approved runtime phase.</p>' +
      '<div class="academy-action-row">' +
        '<button type="button" class="academy-secondary-action" disabled>Generate Cards from Study Material</button>' +
        '<span class="academy-submission-status" data-state="error">Runtime writes disabled.</span>' +
      '</div>' +
    '</article>';
  }

  function academyN31StudyLayerHtml(ctx, selectedOption, material, chunksPayload, summaryPayload, unavailable) {
    selectedOption = selectedOption || {};
    summaryPayload = summaryPayload || {};
    var subjectLabel = selectedOption.label || 'Selected subject';
    if (!material) {
      return '<section class="academy-n31-study-layer" data-state="pending" aria-label="N31 Study Material layer">' +
        '<div class="academy-n31-header">' +
          '<div>' +
            '<div class="academy-pill-row">' +
              '<span class="academy-pill academy-pill-strong">Study Material</span>' +
              '<span class="academy-pill">N31 read-only</span>' +
              '<span class="academy-pill">Grade ' + academyEscape(ctx.grade) + '</span>' +
              '<span class="academy-pill">Writes disabled</span>' +
            '</div>' +
            '<h3>' + academyEscape(subjectLabel) + '</h3>' +
            '<p>' + academyEscape(unavailable ? 'N31 live-material endpoints are not available yet from N32A, so this subject is showing the existing Academy package only.' : 'N31 Study Material is pending for this subject.') + '</p>' +
          '</div>' +
        '</div>' +
        academyN31GenerationGateHtml() +
      '</section>';
    }
    var student = academyN31MaterialStudent(material);
    var units = academyN31MaterialUnits(material);
    var chunks = academyN31ChunksForMaterial(chunksPayload, material, ctx);
    var blocks = academyArray(student.study_blocks);
    if (!blocks.length) blocks = academyArray(student.content_blocks);
    if (!blocks.length) blocks = academyArray(material.content_blocks);
    var outcomes = academyArray(student.student_outcomes || material.student_outcomes);
    var practice = academyArray(student.guided_practice || student.practice_prompts || material.guided_practice || material.practice_prompts);
    var portfolio = student.portfolio_artifact || material.portfolio_artifact || material.artifact_prompt || '';
    var safety = student.safety_note || material.safety_note || '';
    var focus = student.focus || material.focus || material.summary || material.description || 'Study the material, practice in your own words, then prepare a classroom-safe artifact.';
    var materialSubjectLabel = academyN31SubjectLabel(material) || subjectLabel;
    var unitHtml = units.map(function (unit, index) {
      unit = unit || {};
      var seq = unit.sequence || unit.unit_sequence || index + 1;
      return '<article class="academy-n31-unit">' +
        '<div class="academy-pill-row">' +
          '<span class="academy-pill academy-pill-strong">Unit ' + academyEscape(seq) + '</span>' +
          (unit.unit_id ? '<span class="academy-pill">' + academyEscape(unit.unit_id) + '</span>' : '') +
        '</div>' +
        '<strong>' + academyEscape(unit.title || unit.unit_title || 'Study unit') + '</strong>' +
        (unit.summary || unit.description ? '<p>' + academyEscape(unit.summary || unit.description) + '</p>' : '') +
      '</article>';
    }).join('');
    var chunkHtml = chunks.map(function (chunk, index) {
      chunk = chunk || {};
      var label = chunk.chunk_kind || chunk.kind || 'study_chunk';
      var text = chunk.title || chunk.heading || academyN31Excerpt(chunk.text || chunk.body || chunk.student_text || '', 420);
      return '<article class="academy-n31-chunk">' +
        '<span>Chunk ' + academyEscape(index + 1) + ' · ' + academyEscape(academyTitleCase(label)) + '</span>' +
        '<strong>' + academyEscape(chunk.title || chunk.heading || chunk.chunk_id || 'N31 study chunk') + '</strong>' +
        (text ? '<p>' + academyEscape(text) + '</p>' : '') +
      '</article>';
    }).join('');
    var blockHtml = blocks.map(function (block) {
      block = block || {};
      return '<article class="academy-study-block">' +
        '<strong>' + academyEscape(block.title || block.heading || 'Study block') + '</strong>' +
        '<p class="academy-study-body">' + academyTextHtml(block.body || block.student_text || block.text || block.instructions || '') + '</p>' +
      '</article>';
    }).join('');
    return '<section class="academy-n31-study-layer" aria-label="N31 Study Material layer">' +
      '<div class="academy-n31-header">' +
        '<div>' +
          '<div class="academy-pill-row">' +
            '<span class="academy-pill academy-pill-strong">Study Material</span>' +
            '<span class="academy-pill">N31 read-only</span>' +
            '<span class="academy-pill">Grade ' + academyEscape(ctx.grade) + '</span>' +
            '<span class="academy-pill">' + academyEscape(materialSubjectLabel) + '</span>' +
            '<span class="academy-pill">' + academyEscape(units.length) + ' units</span>' +
            '<span class="academy-pill">' + academyEscape(chunks.length) + ' chunks</span>' +
            '<span class="academy-pill">RAG off</span>' +
            '<span class="academy-pill">Writes disabled</span>' +
          '</div>' +
          '<h3>' + academyEscape(material.title || summaryPayload.title || materialSubjectLabel + ' Study Material') + '</h3>' +
          '<p>' + academyEscape(focus) + '</p>' +
        '</div>' +
      '</div>' +
      '<nav class="academy-workspace-nav" aria-label="N31 Study Material workspace">' +
        '<button type="button" data-academy-n31-tab="study" data-active="true">Study</button>' +
        '<button type="button" data-academy-n31-tab="practice">Practice</button>' +
        '<button type="button" data-academy-n31-tab="submit">Submit</button>' +
      '</nav>' +
      '<section class="academy-workspace-pane" data-academy-n31-pane="study">' +
        '<section class="academy-n31-ordered-map" aria-label="Ordered N31 material">' +
          '<article class="academy-activity-block">' +
            '<strong>Grade ' + academyEscape(ctx.grade) + ' -> ' + academyEscape(materialSubjectLabel) + ' -> Units</strong>' +
            '<p>Study material is grouped by subject and ordered by unit before chunks.</p>' +
            '<div class="academy-n31-unit-list">' + (unitHtml || '<p>No N31 units returned for this subject yet.</p>') + '</div>' +
          '</article>' +
          '<article class="academy-activity-block">' +
            '<strong>Subject Chunks</strong>' +
            '<p>Chunks are read-only review material. They are not embedded or written to a runtime index in this view.</p>' +
            '<div class="academy-n31-chunk-list">' + (chunkHtml || '<p>No N31 chunks returned for this subject yet.</p>') + '</div>' +
          '</article>' +
        '</section>' +
        (outcomes.length ? '<article class="academy-activity-block"><strong>Student Outcomes</strong>' + academyListHtml(outcomes, 'academy-activity-list') + '</article>' : '') +
        (blockHtml ? '<article class="academy-activity-block"><strong>Mini Lesson</strong><div class="academy-study-stack">' + blockHtml + '</div></article>' : '') +
      '</section>' +
      '<section class="academy-workspace-pane" data-academy-n31-pane="practice" hidden>' +
        '<article class="academy-activity-block">' +
          '<div class="academy-pill-row">' +
            '<span class="academy-pill academy-pill-strong">Practice</span>' +
            '<span class="academy-pill">Self-check</span>' +
            '<span class="academy-pill">Guided practice</span>' +
          '</div>' +
          '<strong>Practice In Your Own Words</strong>' +
          academyListHtml(practice, 'academy-step-list') +
        '</article>' +
      '</section>' +
      '<section class="academy-workspace-pane" data-academy-n31-pane="submit" hidden>' +
        '<section class="academy-activity-grid" aria-label="N31 submit preparation">' +
          '<article class="academy-activity-block">' +
            '<div class="academy-pill-row">' +
              '<span class="academy-pill academy-pill-strong">Submit Preparation</span>' +
              '<span class="academy-pill">Human review</span>' +
              '<span class="academy-pill">Writes disabled</span>' +
            '</div>' +
            '<strong>Portfolio Artifact</strong>' +
            '<p>' + academyEscape(portfolio || 'Prepare a classroom-safe artifact from the study material and practice prompts.') + '</p>' +
            (safety ? '<strong>Safety Note</strong><p>' + academyEscape(safety) + '</p>' : '') +
          '</article>' +
          academyN31GenerationGateHtml() +
        '</section>' +
      '</section>' +
    '</section>';
  }

  function academyChoicesHtml(choices) {
    var rows = academyArray(choices).filter(function (choice) { return choice && String(choice.text || '').trim(); });
    if (!rows.length) return '<p>Write your answer in your own words.</p>';
    return '<ol class="academy-choice-list">' + rows.map(function (choice) {
      return '<li><strong>' + academyEscape(choice.choice_id || '') + '</strong> ' + academyEscape(choice.text || '') + '</li>';
    }).join('') + '</ol>';
  }

  function academyAssessmentItemHtml(item, index) {
    item = item || {};
    var itemId = String(item.assessment_item_id || '');
    var questionType = String(item.question_type || 'practice').toLowerCase();
    var choices = academyArray(item.choices).filter(function (choice) { return choice && String(choice.text || '').trim(); });
    var responseHtml = '';
    if (choices.length && questionType !== 'ordering') {
      responseHtml = '<div class="academy-choice-list" role="group" aria-label="Answer choices">' + choices.map(function (choice) {
        return '<button type="button" class="academy-choice-button" data-academy-practice-choice="1" data-assessment-item-id="' + academyEscape(itemId) + '" data-choice-id="' + academyEscape(choice.choice_id || '') + '">' +
          '<strong>' + academyEscape(choice.choice_id || '') + '</strong> ' + academyEscape(choice.text || '') +
        '</button>';
      }).join('') + '</div>';
    } else if (choices.length && questionType === 'ordering') {
      responseHtml = '<ol class="academy-choice-list">' + choices.map(function (choice) {
        return '<li><strong>' + academyEscape(choice.choice_id || '') + '</strong> ' + academyEscape(choice.text || '') + '</li>';
      }).join('') + '</ol>' +
      '<button type="button" class="academy-order-check-button" data-academy-order-check="1" data-assessment-item-id="' + academyEscape(itemId) + '" data-sequence="' + academyEscape(choices.map(function (choice) { return choice.choice_id || ''; }).join('|')) + '">Check this order</button>';
    } else {
      responseHtml = '<textarea class="academy-short-response-box" data-academy-short-response="1" data-assessment-item-id="' + academyEscape(itemId) + '" maxlength="2000" placeholder="Write your answer in your own words."></textarea>' +
      '<button type="button" class="academy-secondary-action" data-academy-short-response-check="1" data-assessment-item-id="' + academyEscape(itemId) + '">Send for review</button>';
    }
    return '<article class="academy-question-card">' +
      '<div class="academy-pill-row">' +
        '<span class="academy-pill academy-pill-strong">Question ' + academyEscape(index + 1) + '</span>' +
        '<span class="academy-pill">' + academyEscape(academyTitleCase(item.question_type || 'practice')) + '</span>' +
        '<span class="academy-pill">' + academyEscape(academyTitleCase(item.difficulty || 'practice')) + '</span>' +
        '<span class="academy-pill">Answer key hidden</span>' +
      '</div>' +
      '<strong>' + academyEscape(item.student_prompt || 'Practice question') + '</strong>' +
      responseHtml +
      '<div class="academy-practice-feedback" data-academy-practice-feedback="' + academyEscape(itemId) + '"></div>' +
      (item.evidence_skill ? '<p>Skill: ' + academyEscape(item.evidence_skill) + '</p>' : '') +
    '</article>';
  }

  function bindAcademyPracticeActions(panel, lesson, payload, ctx) {
    if (!panel || !lesson) return;
    var lessonCardId = String((lesson.canvas_card && lesson.canvas_card.lesson_card_id) || (lesson.activity_pack && (lesson.activity_pack.card_id || lesson.activity_pack.lesson_card_id)) || '');
    function feedbackEl(itemId) {
      return panel.querySelector('[data-academy-practice-feedback="' + String(itemId).replace(/"/g, '\\"') + '"]');
    }
    function setFeedback(itemId, message, state) {
      var el = feedbackEl(itemId);
      if (!el) return;
      el.textContent = message || '';
      if (state) el.setAttribute('data-state', state);
      else el.removeAttribute('data-state');
    }
    if (!academyRuntimeWritesEnabled()) {
      panel.querySelectorAll('[data-academy-practice-choice]').forEach(function (button) {
        button.onclick = function () {
          var itemId = String(button.getAttribute('data-assessment-item-id') || '');
          setFeedback(itemId, 'Practice checking is read-only in this pilot. Explain your reasoning, then ask for human review.', 'review');
        };
      });
      panel.querySelectorAll('[data-academy-order-check]').forEach(function (button) {
        button.onclick = function () {
          var itemId = String(button.getAttribute('data-assessment-item-id') || '');
          setFeedback(itemId, 'Order checking is read-only in this pilot. Use the prompt to rehearse your reasoning.', 'review');
        };
      });
      panel.querySelectorAll('[data-academy-short-response-check]').forEach(function (button) {
        button.onclick = function () {
          var itemId = String(button.getAttribute('data-assessment-item-id') || '');
          var input = panel.querySelector('[data-academy-short-response][data-assessment-item-id="' + itemId + '"]');
          var responseText = String((input && input.value) || '').trim();
          setFeedback(itemId, responseText ? 'Response held locally for practice. Runtime review writes are disabled.' : 'Write your answer first.', responseText ? 'review' : 'incorrect');
        };
      });
      return;
    }
    function attemptNumber(button) {
      var current = Number(button.getAttribute('data-attempt-number') || '0');
      var next = current + 1;
      button.setAttribute('data-attempt-number', String(next));
      return next;
    }
    function postCheck(button, body, itemId) {
      var itemButtons = panel.querySelectorAll('[data-assessment-item-id="' + itemId + '"]');
      itemButtons.forEach(function (itemButton) {
        if (itemButton.tagName === 'BUTTON') itemButton.disabled = true;
      });
      setFeedback(itemId, 'Checking...', '');
      var studentId = getAcademyStudentId(payload);
      academyApiPost('/api/academy/students/' + encodeURIComponent(studentId) + '/practice/check-answer', body, payload).then(function (response) {
        var result = response && response.result ? response.result : {};
        var correct = result.correct;
        if (correct === true) {
          button.classList.add('is-correct');
          button.classList.remove('is-incorrect');
          setFeedback(itemId, result.feedback || 'Correct.', 'correct');
          itemButtons.forEach(function (itemButton) {
            if (itemButton.tagName === 'BUTTON') itemButton.disabled = true;
          });
          return;
        }
        if (correct === false) {
          button.classList.add('is-incorrect');
          button.classList.remove('is-correct');
          setFeedback(itemId, result.feedback || 'Try again.', 'incorrect');
          itemButtons.forEach(function (itemButton) {
            if (itemButton.tagName === 'BUTTON' && !itemButton.classList.contains('is-correct')) itemButton.disabled = false;
          });
          return;
        }
        setFeedback(itemId, result.feedback || 'Saved for review.', 'review');
        itemButtons.forEach(function (itemButton) {
          if (itemButton.tagName === 'BUTTON') itemButton.disabled = false;
        });
      }).catch(function (err) {
        setFeedback(itemId, (err && err.code) ? err.code : 'Could not check this answer.', 'incorrect');
        itemButtons.forEach(function (itemButton) {
          if (itemButton.tagName === 'BUTTON') itemButton.disabled = false;
        });
      });
    }
    panel.querySelectorAll('[data-academy-practice-choice]').forEach(function (button) {
      button.onclick = function () {
        var itemId = String(button.getAttribute('data-assessment-item-id') || '');
        var choiceId = String(button.getAttribute('data-choice-id') || '');
        postCheck(button, {
          grade: ctx.grade,
          lesson_card_id: lessonCardId,
          assessment_item_id: itemId,
          selected_choice_id: choiceId,
          attempt_number: attemptNumber(button),
          actor_role: 'student',
          actor_id: 'academy-demo-lucia',
          reason: 'academy_my_class_interactive_practice'
        }, itemId);
      };
    });
    panel.querySelectorAll('[data-academy-order-check]').forEach(function (button) {
      button.onclick = function () {
        var itemId = String(button.getAttribute('data-assessment-item-id') || '');
        var sequence = String(button.getAttribute('data-sequence') || '').split('|').filter(Boolean);
        postCheck(button, {
          grade: ctx.grade,
          lesson_card_id: lessonCardId,
          assessment_item_id: itemId,
          selected_sequence: sequence,
          attempt_number: attemptNumber(button),
          actor_role: 'student',
          actor_id: 'academy-demo-lucia',
          reason: 'academy_my_class_interactive_ordering_practice'
        }, itemId);
      };
    });
    panel.querySelectorAll('[data-academy-short-response-check]').forEach(function (button) {
      button.onclick = function () {
        var itemId = String(button.getAttribute('data-assessment-item-id') || '');
        var input = panel.querySelector('[data-academy-short-response][data-assessment-item-id="' + itemId + '"]');
        var responseText = String((input && input.value) || '').trim();
        if (!responseText) {
          setFeedback(itemId, 'Write your answer first.', 'incorrect');
          return;
        }
        postCheck(button, {
          grade: ctx.grade,
          lesson_card_id: lessonCardId,
          assessment_item_id: itemId,
          short_response: responseText,
          attempt_number: attemptNumber(button),
          actor_role: 'student',
          actor_id: 'academy-demo-lucia',
          reason: 'academy_my_class_short_response_practice'
        }, itemId);
      };
    });
  }

  function academyReferencesHtml(references) {
    var rows = academyArray(references).filter(function (ref) { return ref && (ref.title || ref.url); });
    if (!rows.length) return '<p>No external reference links for this lesson.</p>';
    return '<div class="academy-reference-list">' + rows.map(function (ref) {
      var url = String(ref.url || '').trim();
      var label = ref.title || ref.source_id || url;
      var provider = ref.provider ? ' · ' + ref.provider : '';
      var posture = ref.reference_only ? 'Reference only' : 'Source';
      var link = url ? '<a href="' + academyEscape(url) + '" target="_blank" rel="noopener noreferrer">' + academyEscape(label) + '</a>' : academyEscape(label);
      return '<article class="academy-activity-content-item">' +
        '<strong>' + link + '</strong>' +
        '<div>' + academyEscape(posture + provider) + '</div>' +
        '<div>RAG: ' + academyEscape(ref.rag_allowed ? 'allowed' : 'off') + '</div>' +
      '</article>';
    }).join('') + '</div>';
  }

  function academyPdfReadyHtml(pdfRows) {
    var rows = academyArray(pdfRows);
    if (!rows.length) return '<p>No student packet linked yet.</p>';
    return '<div class="academy-reference-list">' + rows.map(function (row) {
      return '<article class="academy-activity-content-item">' +
        '<strong>' + academyEscape(row.title || 'Student packet') + '</strong>' +
        '<div>Markdown packet: ' + academyEscape(row.markdown_packet_path || 'pending') + '</div>' +
        '<div>PDF: ' + academyEscape(row.pdf_generated ? 'available' : 'pending generation') + '</div>' +
      '</article>';
    }).join('') + '</div>';
  }

  function academyContentBlockHtml(block) {
    block = block || {};
    var items = academyArray(block.items);
    var itemHtml = '';
    if (items.length) {
      itemHtml = '<div class="academy-activity-content-items">' + items.map(function (item) {
        if (typeof item === 'object') {
          return '<div class="academy-activity-content-item">' + Object.keys(item).map(function (key) {
            return '<div><strong>' + academyEscape(academyTitleCase(key)) + ':</strong> ' + academyEscape(item[key]) + '</div>';
          }).join('') + '</div>';
        }
        return '<div class="academy-activity-content-item">' + academyEscape(item) + '</div>';
      }).join('') + '</div>';
    }
    return '<article class="academy-activity-block">' +
      '<div class="academy-pill-row">' +
        '<span class="academy-pill academy-pill-strong">' + academyEscape(academyTitleCase(block.block_type || 'activity')) + '</span>' +
      '</div>' +
      '<strong>' + academyEscape(block.title || 'Activity block') + '</strong>' +
      '<p>' + academyEscape(block.instructions || '') + '</p>' +
      itemHtml +
    '</article>';
  }

  function renderAcademyCanvasLessonPanel(panel, lesson, payload, ctx, body, displayName) {
    if (!panel) return;
    lesson = lesson || {};
    var canvas = lesson.canvas_card || {};
    var pack = lesson.activity_pack || {};
    var student = pack.student_experience || {};
    var submission = pack.submission || {};
    var review = pack.review || {};
    var safety = pack.safety || {};
    var studyHtml = academyArray(lesson.study_materials).map(academyStudyMaterialHtml).join('');
    var questionHtml = academyArray(lesson.assessment_items).map(academyAssessmentItemHtml).join('');
    var referenceHtml = academyReferencesHtml(lesson.external_references || canvas.external_reference_refs);
    var pdfHtml = academyPdfReadyHtml(lesson.pdf_ready);
    var activityConfigured = !!pack.activity_pack_id;
    var overview = body && body.querySelector('#academy-classroom-overview');
    if (overview) overview.hidden = true;
    panel.hidden = false;
    panel.classList.add('academy-lesson-workspace');
    panel.innerHTML =
      '<div class="academy-activity-header">' +
        '<div>' +
          '<div class="academy-pill-row">' +
            '<span class="academy-pill academy-pill-strong">Lesson Canvas</span>' +
            '<span class="academy-pill">' + academyEscape(academyTitleCase(canvas.render_type || 'quest')) + '</span>' +
            '<span class="academy-pill">' + academyEscape(lesson.study_material_count || 0) + ' study pack</span>' +
            '<span class="academy-pill">' + academyEscape(lesson.assessment_item_count || 0) + ' questions</span>' +
            '<span class="academy-pill">Student-ready practice</span>' +
          '</div>' +
          '<h3>' + academyEscape(canvas.title || pack.title || 'Academy lesson') + '</h3>' +
          '<p>' + academyEscape(canvas.student_intro || student.mission_hook || 'Study first, answer the practice questions, then submit your evidence.') + '</p>' +
        '</div>' +
        '<button type="button" class="academy-secondary-action" data-academy-close-activity="1">Back to Subject</button>' +
      '</div>' +
      '<nav class="academy-workspace-nav" aria-label="Lesson workspace">' +
        '<button type="button" data-academy-workspace-tab="study" data-active="true">Study</button>' +
        '<button type="button" data-academy-workspace-tab="practice">Practice</button>' +
        '<button type="button" data-academy-workspace-tab="submit">Submit</button>' +
      '</nav>' +
      '<section class="academy-workspace-pane" data-academy-workspace-pane="study" aria-label="Study first">' +
        '<article class="academy-activity-block">' +
          '<strong>How This Lesson Works</strong>' +
          '<p>Read the study card first. Then use the practice questions to check your understanding. After that, submit your classroom-safe evidence.</p>' +
          academyListHtml(canvas.completion_criteria, 'academy-activity-list') +
        '</article>' +
        (studyHtml || '<article class="academy-activity-block"><strong>Study material pending</strong><p>No student study pack is attached to this card yet.</p></article>') +
        '<section class="academy-activity-grid" aria-label="Sources and packets">' +
          '<article class="academy-activity-block">' +
            '<strong>Reference Links</strong>' +
            '<p>These are reference-only sources and standards metadata. They are not copied into the lesson.</p>' +
            referenceHtml +
          '</article>' +
          '<article class="academy-activity-block">' +
            '<strong>Student Packet</strong>' +
            '<p>PDF generation is staged; the markdown packet is the current source for printable student packets.</p>' +
            pdfHtml +
          '</article>' +
        '</section>' +
      '</section>' +
      '<section class="academy-workspace-pane" data-academy-workspace-pane="practice" aria-label="Practice questions" hidden>' +
        '<article class="academy-activity-block">' +
          '<div class="academy-pill-row">' +
            '<span class="academy-pill academy-pill-strong">Practice Questions</span>' +
            '<span class="academy-pill">' + academyEscape(academyRuntimeWritesEnabled() ? 'Server checked' : 'Read-only practice') + '</span>' +
            '<span class="academy-pill">Not high-stakes</span>' +
            '<span class="academy-pill">Answer key hidden</span>' +
          '</div>' +
          '<div class="academy-question-list">' + (questionHtml || '<p>No practice questions are attached yet.</p>') + '</div>' +
        '</article>' +
      '</section>' +
      '<section class="academy-workspace-pane" data-academy-workspace-pane="submit" aria-label="Submit evidence" hidden>' +
      (activityConfigured ? (
        '<section class="academy-activity-grid" aria-label="Academy evidence">' +
          '<article class="academy-activity-block">' +
            '<strong>Mission Evidence</strong>' +
            '<p>' + academyEscape(student.student_task || submission.artifact_title || 'Complete the lesson evidence.') + '</p>' +
            academyListHtml(student.step_by_step, 'academy-step-list') +
            '<strong>Ask Eugenie</strong>' +
            academyListHtml(canvas.eugenie_prompt_suggestions || student.eugenie_help_prompts, 'academy-activity-list') +
          '</article>' +
          '<article class="academy-activity-block">' +
            '<strong>Turn In</strong>' +
            '<p>' + academyEscape(submission.artifact_title || submission.portfolio_artifact || 'Learning artifact') + '</p>' +
            '<div class="academy-pill-row">' +
              '<span class="academy-pill">' + academyEscape(review.human_review_required ? 'Human review' : 'Low-stakes check') + '</span>' +
              '<span class="academy-pill">Submission RAG: ' + academyEscape(submission.rag_allowed ? 'allowed' : 'off') + '</span>' +
              '<span class="academy-pill">Private data blocked</span>' +
            '</div>' +
            '<form class="academy-submission-form" data-academy-submission-form="1">' +
              '<label>My evidence<textarea data-academy-artifact-response="1" maxlength="12000" placeholder="Write the answer, table, plan, or evidence you made for this lesson."></textarea></label>' +
              '<label>Reflection<textarea data-academy-student-reflection="1" maxlength="3000" placeholder="What did you decide, learn, or improve?"></textarea></label>' +
              '<label>Source note<textarea data-academy-source-note="1" maxlength="2000" placeholder="Optional: name the class source or approved evidence you used."></textarea></label>' +
              '<label>AI help note<textarea data-academy-ai-use-declaration="1" maxlength="2000" placeholder="Optional: describe how Eugenie helped without giving the final answer."></textarea></label>' +
              '<label class="academy-submission-check"><input type="checkbox" data-academy-checklist-ack="1"> I used approved sources only and did not include private information.</label>' +
              '<div class="academy-action-row">' +
                '<button type="button" class="academy-primary-action" data-academy-submit-artifact="1"' + (academyRuntimeWritesEnabled() ? '' : ' disabled') + '>' + academyEscape(submission.student_visible_submit_label || 'Submit artifact') + '</button>' +
                '<span class="academy-submission-status" data-academy-submission-status="1"></span>' +
              '</div>' +
            '</form>' +
          '</article>' +
        '</section>' +
        '<article class="academy-activity-block">' +
          '<strong>Safety</strong>' +
          '<div class="academy-pill-row">' +
            '<span class="academy-pill">Sources: ' + academyEscape(academyArray(safety.approved_source_ids).join(', ') || 'Nexus approved') + '</span>' +
            '<span class="academy-pill">Citation: ' + academyEscape(safety.citation_required ? 'yes' : 'not required') + '</span>' +
            '<span class="academy-pill">Student RAG: off</span>' +
          '</div>' +
        '</article>'
      ) : '<article class="academy-activity-block"><strong>Evidence submission pending</strong><p>No activity pack is attached to this lesson yet.</p></article>') +
      '</section>';
    panel.querySelectorAll('[data-academy-workspace-tab]').forEach(function (button) {
      button.onclick = function () {
        var target = String(button.getAttribute('data-academy-workspace-tab') || 'study');
        panel.querySelectorAll('[data-academy-workspace-tab]').forEach(function (tab) {
          tab.setAttribute('data-active', String(tab === button));
        });
        panel.querySelectorAll('[data-academy-workspace-pane]').forEach(function (pane) {
          pane.hidden = String(pane.getAttribute('data-academy-workspace-pane') || '') !== target;
        });
      };
    });
    panel.querySelectorAll('[data-academy-close-activity]').forEach(function (button) {
      button.onclick = function () {
        panel.hidden = true;
        panel.innerHTML = '';
        panel.classList.remove('academy-lesson-workspace');
        if (overview) overview.hidden = false;
      };
    });
    bindAcademyPracticeActions(panel, lesson, payload || {}, ctx || {});
    if (activityConfigured) {
      bindAcademySubmissionActions(panel, pack, payload || {}, ctx || {}, body || null, displayName || 'Lucía Hernández');
    }
    try {
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      panel.scrollIntoView();
    }
  }

  function renderAcademyActivityPack(panel, pack, payload, ctx, body, displayName) {
    if (!panel) return;
    var student = pack.student_experience || {};
    var submission = pack.submission || {};
    var review = pack.review || {};
    var safety = pack.safety || {};
    var blocks = academyArray(pack.content_blocks).slice(0, 3).map(academyContentBlockHtml).join('');
    panel.hidden = false;
    panel.innerHTML =
      '<div class="academy-activity-header">' +
        '<div>' +
          '<div class="academy-pill-row">' +
            '<span class="academy-pill academy-pill-strong">Activity Pack</span>' +
            '<span class="academy-pill">' + academyEscape(academyTitleCase(pack.activity_type || 'lesson')) + '</span>' +
            '<span class="academy-pill">' + academyEscape(pack.estimated_minutes || 25) + ' min</span>' +
            '<span class="academy-pill">' + academyEscape(academyRuntimeWritesEnabled() ? 'Evidence enabled' : 'Evidence read-only') + '</span>' +
          '</div>' +
          '<h3>' + academyEscape(pack.title || 'Academy activity') + '</h3>' +
          '<p>' + academyEscape(student.mission_hook || student.student_task || 'Open the activity steps and complete the lesson evidence.') + '</p>' +
        '</div>' +
        '<button type="button" class="academy-secondary-action" data-academy-close-activity="1">Close</button>' +
      '</div>' +
      '<section class="academy-activity-grid" aria-label="Academy activity details">' +
        '<article class="academy-activity-block">' +
          '<strong>What To Do</strong>' +
          '<p>' + academyEscape(student.student_task || submission.artifact_title || 'Complete the activity evidence.') + '</p>' +
          academyListHtml(student.step_by_step, 'academy-step-list') +
        '</article>' +
        '<article class="academy-activity-block">' +
          '<strong>Materials</strong>' +
          academyListHtml(student.materials, 'academy-activity-list') +
          '<strong>Ask Eugenie</strong>' +
          academyListHtml(student.eugenie_help_prompts, 'academy-activity-list') +
        '</article>' +
      '</section>' +
      '<section class="academy-activity-grid" aria-label="Academy checks and review">' +
        '<article class="academy-activity-block">' +
          '<strong>Before Complete</strong>' +
          academyListHtml(review.checklist, 'academy-activity-list') +
        '</article>' +
        '<article class="academy-activity-block">' +
          '<strong>Artifact</strong>' +
          '<p>' + academyEscape(submission.artifact_title || submission.portfolio_artifact || 'Learning artifact') + '</p>' +
          '<div class="academy-pill-row">' +
            '<span class="academy-pill">Submission: student evidence</span>' +
            '<span class="academy-pill">' + academyEscape(review.human_review_required ? 'Human review' : 'Low-stakes check') + '</span>' +
            '<span class="academy-pill">RAG: ' + academyEscape(submission.rag_allowed ? 'allowed' : 'off') + '</span>' +
          '</div>' +
          '<form class="academy-submission-form" data-academy-submission-form="1">' +
            '<label>My evidence<textarea data-academy-artifact-response="1" maxlength="12000" placeholder="Write the answer, table, plan, or evidence you made for this activity."></textarea></label>' +
            '<label>Reflection<textarea data-academy-student-reflection="1" maxlength="3000" placeholder="What did you decide, learn, or improve?"></textarea></label>' +
            '<label>Source note<textarea data-academy-source-note="1" maxlength="2000" placeholder="Optional: name the class source or approved evidence you used."></textarea></label>' +
            '<label>AI help note<textarea data-academy-ai-use-declaration="1" maxlength="2000" placeholder="Optional: describe how Eugenie helped without giving the final answer."></textarea></label>' +
            '<label class="academy-submission-check"><input type="checkbox" data-academy-checklist-ack="1"> I used approved sources only and did not include private information.</label>' +
            '<div class="academy-action-row">' +
              '<button type="button" class="academy-primary-action" data-academy-submit-artifact="1"' + (academyRuntimeWritesEnabled() ? '' : ' disabled') + '>' + academyEscape(submission.student_visible_submit_label || 'Submit artifact') + '</button>' +
              '<span class="academy-submission-status" data-academy-submission-status="1"></span>' +
            '</div>' +
          '</form>' +
        '</article>' +
      '</section>' +
      (blocks ? '<section class="academy-activity-grid" aria-label="Academy content blocks">' + blocks + '</section>' : '') +
      '<article class="academy-activity-block">' +
        '<strong>Safety</strong>' +
        '<div class="academy-pill-row">' +
          '<span class="academy-pill">Sources: ' + academyEscape(academyArray(safety.approved_source_ids).join(', ') || 'Nexus approved') + '</span>' +
          '<span class="academy-pill">Citation: ' + academyEscape(safety.citation_required ? 'yes' : 'not required') + '</span>' +
          '<span class="academy-pill">Private data: blocked</span>' +
        '</div>' +
      '</article>';
    panel.querySelectorAll('[data-academy-close-activity]').forEach(function (button) {
      button.onclick = function () {
        panel.hidden = true;
        panel.innerHTML = '';
      };
    });
    bindAcademySubmissionActions(panel, pack, payload || {}, ctx || {}, body || null, displayName || 'Lucía Hernández');
    try {
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (e) {
      panel.scrollIntoView();
    }
  }

  function bindAcademySubmissionActions(panel, pack, payload, ctx, body, displayName) {
    if (!panel || !pack) return;
    var submitButton = panel.querySelector('[data-academy-submit-artifact]');
    if (!submitButton) return;
    if (!academyRuntimeWritesEnabled()) {
      var disabledStatus = panel.querySelector('[data-academy-submission-status]');
      submitButton.disabled = true;
      if (disabledStatus) {
        disabledStatus.textContent = 'Runtime writes disabled for this read-only pilot.';
        disabledStatus.setAttribute('data-state', 'error');
      }
      return;
    }
    submitButton.onclick = function () {
      var responseEl = panel.querySelector('[data-academy-artifact-response]');
      var reflectionEl = panel.querySelector('[data-academy-student-reflection]');
      var sourceNoteEl = panel.querySelector('[data-academy-source-note]');
      var aiUseEl = panel.querySelector('[data-academy-ai-use-declaration]');
      var ackEl = panel.querySelector('[data-academy-checklist-ack]');
      var statusEl = panel.querySelector('[data-academy-submission-status]');
      var artifactResponse = String((responseEl && responseEl.value) || '').trim();
      var reflection = String((reflectionEl && reflectionEl.value) || '').trim();
      var sourceNote = String((sourceNoteEl && sourceNoteEl.value) || '').trim();
      var aiUse = String((aiUseEl && aiUseEl.value) || '').trim();
      function setStatus(message, state) {
        if (!statusEl) return;
        statusEl.textContent = message;
        if (state) statusEl.setAttribute('data-state', state);
        else statusEl.removeAttribute('data-state');
      }
      if (!artifactResponse) {
        setStatus('Write your evidence first.', 'error');
        return;
      }
      if (!reflection) {
        setStatus('Add a short reflection before submitting.', 'error');
        return;
      }
      if (!ackEl || !ackEl.checked) {
        setStatus('Confirm the safety checklist first.', 'error');
        return;
      }
      submitButton.disabled = true;
      setStatus('Submitting evidence...', '');
      var studentId = getAcademyStudentId(payload);
      academyApiPost('/api/academy/students/' + encodeURIComponent(studentId) + '/submissions', {
        grade: ctx.grade,
        lesson_card_id: pack.card_id || pack.lesson_card_id,
        activity_pack_id: pack.activity_pack_id,
        artifact_response: artifactResponse,
        student_reflection: reflection,
        source_note: sourceNote,
        ai_use_declaration: aiUse,
        checklist_acknowledged: true,
        actor_role: 'student',
        actor_id: 'academy-demo-lucia',
        reason: 'academy_my_class_phase_4d_submission'
      }, payload).then(function (result) {
        var submission = result && result.submission ? result.submission : {};
        var needsReview = submission.status === 'needs_human_review';
        setStatus(needsReview ? 'Submitted for Academy review.' : 'Evidence submitted.', 'success');
        window.setTimeout(function () {
          if (body && ctx && displayName) loadAcademyClassroomPackage(payload, ctx, displayName, body);
        }, 900);
      }).catch(function (err) {
        submitButton.disabled = false;
        setStatus((err && err.code) ? err.code : 'Submission failed.', 'error');
      });
    };
  }

  function renderAcademyActivityError(panel, err) {
    if (!panel) return;
    var code = err && err.code ? String(err.code) : 'academy.activity_pack_unavailable';
    panel.hidden = false;
    panel.innerHTML =
      '<article class="academy-activity-panel academy-activity-error" role="alert">' +
        '<div class="academy-activity-header">' +
          '<div><h3>Activity unavailable</h3><p>' + academyEscape(code) + '</p></div>' +
          '<button type="button" class="academy-secondary-action" data-academy-close-activity="1">Close</button>' +
        '</div>' +
      '</article>';
    panel.querySelectorAll('[data-academy-close-activity]').forEach(function (button) {
      button.onclick = function () {
        panel.hidden = true;
        panel.innerHTML = '';
      };
    });
  }

	  function academyGeneratedItemsByLesson(items, lessonCardId) {
	    return academyArray(items).filter(function (item) {
	      return academyGeneratedLessonId(item) === String(lessonCardId || '');
	    });
	  }

	  function academyGeneratedItemByLesson(items, lessonCardId) {
	    var rows = academyGeneratedItemsByLesson(items, lessonCardId);
	    return rows.length ? rows[0] : null;
	  }

	  function academyGeneratedAssessmentItemHtml(item, index) {
	    item = item || {};
	    var choices = academyArray(item.choices).filter(function (choice) { return choice && String(choice.text || '').trim(); });
	    var responseMode = item.student_response_mode || item.question_type || item.item_type || 'practice';
	    var responseHtml = choices.length ?
	      '<ol class="academy-choice-list">' + choices.map(function (choice) {
	        return '<li><strong>' + academyEscape(choice.choice_id || '') + '</strong> ' + academyEscape(choice.text || '') + '</li>';
	      }).join('') + '</ol>' :
	      '<textarea class="academy-short-response-box" maxlength="2000" placeholder="Practice your response here."></textarea>';
	    return '<article class="academy-question-card">' +
	      '<div class="academy-pill-row">' +
	        '<span class="academy-pill academy-pill-strong">Practice ' + academyEscape(index + 1) + '</span>' +
	        '<span class="academy-pill">' + academyEscape(academyTitleCase(responseMode)) + '</span>' +
	        '<span class="academy-pill">Answer key hidden</span>' +
	      '</div>' +
	      '<strong>' + academyEscape(item.student_prompt || 'Practice prompt') + '</strong>' +
	      responseHtml +
	      (item.skill_focus ? '<p>Skill: ' + academyEscape(item.skill_focus) + '</p>' : '') +
	    '</article>';
	  }

	  function academyGeneratedSubmissionHtml(activity, card, canvas) {
	    activity = activity || {};
	    card = card || {};
	    canvas = canvas || {};
	    var submission = activity.submission || {};
	    var review = activity.review || {};
	    var preview = card.student_preview || {};
	    var tabs = canvas.workspace_tabs_ready || {};
	    var runtimeWritesEnabled = academyRuntimeWritesEnabled() && (submission.runtime_submission_writes_enabled === true || preview.runtime_submission_writes_enabled === true || tabs.runtime_submit_writes_enabled === true);
	    var submissionEnabled = submission.submission_enabled === true || preview.submission_enabled === true || tabs.submission_enabled === true;
	    var canSubmit = runtimeWritesEnabled && submissionEnabled;
	    var instructions = academyArray(activity.artifact_instructions);
	    var criteria = academyArray(activity.success_criteria);
	    var rubric = academyArray(review.rubric_student_visible);
	    return '<section class="academy-activity-grid" aria-label="Generated core submission preparation">' +
	      '<article class="academy-activity-block">' +
	        '<div class="academy-pill-row">' +
	          '<span class="academy-pill academy-pill-strong">Prepare Artifact</span>' +
	          '<span class="academy-pill">' + academyEscape(review.human_review_required === false ? 'Classroom review' : 'Human review') + '</span>' +
	          '<span class="academy-pill">Writes: ' + academyEscape(canSubmit ? 'package enabled' : 'disabled') + '</span>' +
	        '</div>' +
	        '<strong>' + academyEscape(activity.title || 'Activity preparation') + '</strong>' +
	        '<p>' + academyEscape(activity.student_prompt || submission.submit_prompt || 'Use this space to prepare your work for review.') + '</p>' +
	        '<strong>Artifact Instructions</strong>' +
	        academyListHtml(instructions, 'academy-step-list') +
	        (activity.reflection_prompt ? '<strong>Reflection Prompt</strong><p>' + academyEscape(activity.reflection_prompt) + '</p>' : '') +
	      '</article>' +
	      '<article class="academy-activity-block">' +
	        '<strong>Preparation Workspace</strong>' +
	        '<p>' + academyEscape(submission.submit_prompt || (canSubmit ? 'Prepare your artifact and wait for the classroom submission runtime.' : 'Live submission writes stay disabled for this pilot package.')) + '</p>' +
	        '<form class="academy-submission-form">' +
	          '<label>Prepared artifact<textarea maxlength="12000" placeholder="Draft the answer, plan, chart, prompt, or evidence you will show for human review."></textarea></label>' +
	          '<label>Reflection<textarea maxlength="3000" placeholder="Explain what you decided, checked, or improved."></textarea></label>' +
	          '<label>Source or AI help note<textarea maxlength="2000" placeholder="Optional: name approved class sources or how Eugenie helped your thinking."></textarea></label>' +
	          '<div class="academy-action-row">' +
	            '<button type="button" class="academy-secondary-action" disabled>' + academyEscape(submission.student_visible_submit_label || 'Prepare work for human review') + '</button>' +
	            '<span class="academy-submission-status" data-state="' + academyEscape(canSubmit ? 'review' : 'error') + '">' + academyEscape(canSubmit ? 'Runtime submit handler is not part of this read-only UI pass.' : 'Submit disabled by package runtime flags.') + '</span>' +
	          '</div>' +
	        '</form>' +
	      '</article>' +
	      ((criteria.length || rubric.length) ? '<article class="academy-activity-block"><strong>Success Criteria</strong>' + academyListHtml(criteria.length ? criteria : rubric, 'academy-activity-list') + '</article>' : '') +
	    '</section>';
	  }

	  function renderAcademyGeneratedLessonPanel(panel, card, packageData) {
	    if (!panel || !card) return;
	    packageData = packageData || {};
	    var lessonId = String(card.lesson_card_id || card.card_id || '');
	    var canvas = academyGeneratedItemByLesson(packageData.canvasCards, lessonId) || {};
	    var study = academyGeneratedItemByLesson(packageData.learningMaterials, lessonId) || {};
	    var assessments = academyGeneratedItemsByLesson(packageData.assessments, lessonId);
	    var activity = academyGeneratedItemByLesson(packageData.activities, lessonId) || {};
	    var readyAssessments = assessments.filter(function (item) {
	      return academyGeneratedStudentReady(item) && String(item && item.student_prompt || '').trim();
	    });
	    var studyReady = academyGeneratedStudentReady(study) && academyArray(study.content_blocks).length;
	    var activityReady = academyGeneratedStudentReady(activity) && (String(activity.student_prompt || '').trim() || academyArray(activity.artifact_instructions).length);
	    var hasStudentContent = studyReady || readyAssessments.length || activityReady;
	    var preview = card.student_preview || {};
	    var subjectLabel = card.subject_label || packageData.subjectLabel || 'Subject';
	    panel.hidden = false;
	    panel.classList.add('academy-lesson-workspace');
	    if (!hasStudentContent) {
	      panel.innerHTML =
	        '<article class="academy-activity-panel academy-lesson-workspace" aria-label="Generated core package review shell">' +
	          '<div class="academy-activity-header">' +
	            '<div>' +
	              '<div class="academy-pill-row">' +
	                '<span class="academy-pill academy-pill-strong">Generated Core Package</span>' +
	                '<span class="academy-pill">Review shell</span>' +
	                '<span class="academy-pill">' + academyEscape(subjectLabel) + '</span>' +
	              '</div>' +
	              '<h3>' + academyEscape(card.lesson_title || card.title || 'Lesson shell') + '</h3>' +
	              '<p>Student-facing package data is pending review for this lesson.</p>' +
	            '</div>' +
	            '<button type="button" class="academy-secondary-action" data-academy-close-activity="1">Back to Subject</button>' +
	          '</div>' +
	          '<section class="academy-canvas-empty" role="status">' +
	            '<strong>Review shell only.</strong>' +
	            '<span>Real study, practice, and artifact prompts will appear here when the generated core package returns student-visible rows.</span>' +
	          '</section>' +
	        '</article>';
	      panel.querySelectorAll('[data-academy-close-activity]').forEach(function (button) {
	        button.onclick = function () {
	          panel.hidden = true;
	          panel.innerHTML = '';
	          panel.classList.remove('academy-lesson-workspace');
	        };
	      });
	      try {
	        panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
	      } catch (e) {
	        panel.scrollIntoView();
	      }
	      return;
	    }
	    var studyHtml = studyReady ? academyStudyMaterialHtml(study) : '<article class="academy-activity-block"><strong>Study material pending review.</strong><p>This lesson has generated package data, but no student study material row is ready yet.</p></article>';
	    var assessmentRows = readyAssessments.length ? readyAssessments.map(academyGeneratedAssessmentItemHtml).join('') : '<article class="academy-question-card"><strong>Practice prompts pending review.</strong><p>No student-facing practice prompts are attached to this lesson yet.</p></article>';
	    var submissionHtml = activityReady ? academyGeneratedSubmissionHtml(activity, card, canvas) : '<article class="academy-activity-block"><strong>Artifact prompt pending review.</strong><p>No student-facing preparation prompt is attached to this lesson yet.</p></article>';
	    panel.innerHTML =
	      '<article class="academy-activity-panel academy-lesson-workspace" aria-label="Generated core package lesson">' +
	        '<div class="academy-activity-header">' +
	          '<div>' +
	            '<div class="academy-pill-row">' +
	              '<span class="academy-pill academy-pill-strong">Generated Core Package</span>' +
	              '<span class="academy-pill">Student pilot</span>' +
	              '<span class="academy-pill">' + academyEscape(subjectLabel) + '</span>' +
	              '<span class="academy-pill">' + academyEscape(card.estimated_minutes || 45) + ' min</span>' +
	              '<span class="academy-pill">Guided practice</span>' +
	            '</div>' +
	            '<h3>' + academyEscape(card.lesson_title || card.title || 'Academy lesson') + '</h3>' +
	            '<p>' + academyEscape(canvas.student_intro || preview.focus || academyArray(card.student_outcomes).join(' ') || 'Study first, practice the idea, then prepare your classroom artifact.') + '</p>' +
	          '</div>' +
	          '<button type="button" class="academy-secondary-action" data-academy-close-activity="1">Back to Subject</button>' +
	        '</div>' +
        '<nav class="academy-workspace-nav" aria-label="Lesson workspace tabs">' +
          '<button type="button" data-academy-shell-tab="study" data-active="true">Study</button>' +
          '<button type="button" data-academy-shell-tab="practice">Practice</button>' +
          '<button type="button" data-academy-shell-tab="submit">Submit</button>' +
	        '</nav>' +
	        '<section class="academy-workspace-pane" data-academy-shell-pane="study">' +
	          studyHtml +
	        '</section>' +
	        '<section class="academy-workspace-pane" data-academy-shell-pane="practice" hidden>' +
	          '<article class="academy-activity-block">' +
	            '<strong>Practice Shells</strong>' +
	            '<p>Use these prompts to practice in your own words before preparing evidence for review.</p>' +
	          '</article>' +
	          assessmentRows +
	        '</section>' +
	        '<section class="academy-workspace-pane" data-academy-shell-pane="submit" hidden>' +
	          submissionHtml +
	        '</section>' +
	      '</article>';
    panel.querySelectorAll('[data-academy-shell-tab]').forEach(function (button) {
      button.onclick = function () {
        var tab = String(button.getAttribute('data-academy-shell-tab') || 'study');
        panel.querySelectorAll('[data-academy-shell-tab]').forEach(function (item) {
          item.setAttribute('data-active', item === button ? 'true' : 'false');
        });
        panel.querySelectorAll('[data-academy-shell-pane]').forEach(function (pane) {
          pane.hidden = String(pane.getAttribute('data-academy-shell-pane') || '') !== tab;
        });
      };
    });
    panel.querySelectorAll('[data-academy-close-activity]').forEach(function (button) {
	      button.onclick = function () {
	        panel.hidden = true;
	        panel.innerHTML = '';
	        panel.classList.remove('academy-lesson-workspace');
	      };
	    });
    try {
      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
	    } catch (e) {
	      panel.scrollIntoView();
	    }
	  }

	  function renderAcademyPublishedCanvasCardPanel(panel, canvasCard) {
	    if (!panel || !canvasCard) return;
	    var lessonId = academyCanvasLessonId(canvasCard);
	    var title = canvasCard.title || 'Published Canvas card';
	    var renderType = academyTitleCase(canvasCard.render_type || 'study_card');
	    var intro = canvasCard.student_intro || canvasCard.learning_goal || 'Study this published Canvas card, practice the idea, and prepare evidence for review.';
	    var studyPrompts = academyArray(canvasCard.study_prompts);
	    if (!studyPrompts.length) {
	      studyPrompts = [
	        'What do you already understand about this card?',
	        'What model, example, table, or note could help you check your thinking?',
	        'What question would help you improve the work before review?'
	      ];
	    }
	    var studentTasks = academyArray(canvasCard.student_tasks);
	    if (!studentTasks.length) {
	      studentTasks = [
	        'Write a one-sentence explanation in your own words.',
	        'Create an example, model, outline, table, or plan that makes the idea clear.',
	        'Add one question or reasonableness check before turning in the work.'
	      ];
	    }
	    var completionCriteria = academyArray(canvasCard.completion_criteria);
	    if (!completionCriteria.length) {
	      completionCriteria = [
	        'The work is written in your own words.',
	        'The work includes only student-facing reasoning, evidence, and reflection.',
	        'A human reviewer can understand what you studied, practiced, and prepared.'
	      ];
	    }
	    var promptSuggestions = academyArray(canvasCard.eugenie_prompt_suggestions);
	    panel.hidden = false;
	    panel.classList.add('academy-lesson-workspace');
	    panel.innerHTML =
	      '<article class="academy-activity-panel academy-lesson-workspace" aria-label="Published Canvas card lesson">' +
	        '<div class="academy-activity-header">' +
	          '<div>' +
	            '<div class="academy-pill-row">' +
	              '<span class="academy-pill academy-pill-strong">Published Canvas Card</span>' +
	              '<span class="academy-pill">' + academyEscape(renderType) + '</span>' +
	              '<span class="academy-pill">Read-only</span>' +
	              '<span class="academy-pill">Writes disabled</span>' +
	              '<span class="academy-pill">RAG off</span>' +
	            '</div>' +
	            '<h3>' + academyEscape(title) + '</h3>' +
	            '<p>' + academyEscape(intro) + '</p>' +
	          '</div>' +
	          '<button type="button" class="academy-secondary-action" data-academy-close-activity="1">Back to Subject</button>' +
	        '</div>' +
	        '<nav class="academy-workspace-nav" aria-label="Published Canvas card workspace">' +
	          '<button type="button" data-academy-published-tab="study" data-active="true">Study</button>' +
	          '<button type="button" data-academy-published-tab="practice">Practice</button>' +
	          '<button type="button" data-academy-published-tab="submit">Submit</button>' +
	        '</nav>' +
	        '<section class="academy-workspace-pane" data-academy-published-pane="study">' +
	          '<article class="academy-activity-block">' +
	            '<div class="academy-pill-row">' +
	              '<span class="academy-pill academy-pill-strong">Study First</span>' +
	              '<span class="academy-pill">' + academyEscape(lessonId || 'published_canvas') + '</span>' +
	            '</div>' +
	            '<strong>Study Prompts</strong>' +
	            academyListHtml(studyPrompts, 'academy-step-list') +
	            (promptSuggestions.length ? '<strong>Eugenie Prompt Ideas</strong>' + academyListHtml(promptSuggestions, 'academy-activity-list') : '') +
	          '</article>' +
	        '</section>' +
	        '<section class="academy-workspace-pane" data-academy-published-pane="practice" hidden>' +
	          '<article class="academy-activity-block">' +
	            '<div class="academy-pill-row">' +
	              '<span class="academy-pill academy-pill-strong">Practice</span>' +
	              '<span class="academy-pill">Guided practice</span>' +
	            '</div>' +
	            '<strong>Practice In Your Own Words</strong>' +
	            academyListHtml(studentTasks, 'academy-step-list') +
	          '</article>' +
	        '</section>' +
	        '<section class="academy-workspace-pane" data-academy-published-pane="submit" hidden>' +
	          '<section class="academy-activity-grid" aria-label="Published Canvas submit preparation">' +
	            '<article class="academy-activity-block">' +
	              '<div class="academy-pill-row">' +
	                '<span class="academy-pill academy-pill-strong">Submit Preparation</span>' +
	                '<span class="academy-pill">Human review</span>' +
	                '<span class="academy-pill">Runtime writes disabled</span>' +
	              '</div>' +
	              '<strong>Completion Criteria</strong>' +
	              academyListHtml(completionCriteria, 'academy-activity-list') +
	            '</article>' +
	            '<article class="academy-activity-block">' +
	              '<strong>Preparation Workspace</strong>' +
	              '<p>Prepare your artifact here, then share it through the classroom review path when runtime submissions are enabled.</p>' +
	              '<form class="academy-submission-form">' +
	                '<label>Prepared artifact<textarea maxlength="12000" placeholder="Draft the explanation, model, plan, table, or check you will show for review."></textarea></label>' +
	                '<label>Reflection<textarea maxlength="3000" placeholder="Explain what you checked or improved."></textarea></label>' +
	                '<div class="academy-action-row">' +
	                  '<button type="button" class="academy-secondary-action" disabled>Prepare work for human review</button>' +
	                  '<span class="academy-submission-status" data-state="error">Submit disabled by package runtime flags.</span>' +
	                '</div>' +
	              '</form>' +
	            '</article>' +
	          '</section>' +
	        '</section>' +
	      '</article>';
	    panel.querySelectorAll('[data-academy-published-tab]').forEach(function (button) {
	      button.onclick = function () {
	        var tab = String(button.getAttribute('data-academy-published-tab') || 'study');
	        panel.querySelectorAll('[data-academy-published-tab]').forEach(function (item) {
	          item.setAttribute('data-active', item === button ? 'true' : 'false');
	        });
	        panel.querySelectorAll('[data-academy-published-pane]').forEach(function (pane) {
	          pane.hidden = String(pane.getAttribute('data-academy-published-pane') || '') !== tab;
	        });
	      };
	    });
	    panel.querySelectorAll('[data-academy-close-activity]').forEach(function (button) {
	      button.onclick = function () {
	        panel.hidden = true;
	        panel.innerHTML = '';
	        panel.classList.remove('academy-lesson-workspace');
	      };
	    });
	    try {
	      panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
	    } catch (e) {
	      panel.scrollIntoView();
	    }
	  }

	  function academyModuleProgress(module, ledger) {
    var rows = ledger && Array.isArray(ledger.module_progress) ? ledger.module_progress : [];
    for (var i = 0; i < rows.length; i += 1) {
      if (String(rows[i].module_id || '') === String(module.module_id || '')) return rows[i];
    }
    return {};
  }

  function academyCardRecord(card, progressData, grade, moduleMap) {
    var lessonProgress = academyLessonProgressForCard(progressData || {}, grade, card);
    var status = String(lessonProgress.status || 'not_started');
    var module = moduleMap[String(card.module_id || '')] || {};
    return {
      card: card,
      module: module,
      progress: lessonProgress,
      status: status,
      cardId: String(card.lesson_card_id || card.card_id || card.id || ''),
      tone: academyCardTone(card)
    };
  }

  function academyUpdateLessonCard(payload, ctx, displayName, body, action, cardId) {
    if (!body || !cardId) return;
    var buttons = body.querySelectorAll('[data-academy-progress-action]');
    buttons.forEach(function (button) {
      button.disabled = true;
    });
    var studentId = getAcademyStudentId(payload);
    academyApiPost('/api/academy/students/' + encodeURIComponent(studentId) + '/progress/lesson-cards', {
      grade: ctx.grade,
      lesson_card_id: cardId,
      action: action,
      actor_role: 'student',
      actor_id: 'academy-demo-lucia',
      reason: 'academy_my_class_phase_4a'
    }, payload).then(function () {
      loadAcademyClassroomPackage(payload, ctx, displayName, body);
    }).catch(function (err) {
      renderAcademyClassroomError(body, ctx, err);
    });
  }

  function bindAcademyProgressActions(body, payload, ctx, displayName) {
    if (!body) return;
    var buttons = body.querySelectorAll('[data-academy-progress-action]');
    if (!academyRuntimeWritesEnabled()) {
      buttons.forEach(function (button) {
        button.disabled = true;
      });
      return;
    }
    buttons.forEach(function (button) {
      button.onclick = function () {
        var action = String(button.getAttribute('data-academy-progress-action') || '');
        var cardId = String(button.getAttribute('data-academy-card-id') || '');
        academyUpdateLessonCard(payload, ctx, displayName, body, action, cardId);
      };
    });
  }

  function bindAcademyActivityActions(body, payload, ctx, displayName) {
    if (!body) return;
    var panel = body.querySelector('#academy-activity-panel');
    var buttons = body.querySelectorAll('[data-academy-open-activity]');
    buttons.forEach(function (button) {
      button.onclick = function () {
        var cardId = String(button.getAttribute('data-academy-card-id') || '');
        if (!cardId || !panel) return;
        panel.hidden = false;
        panel.innerHTML =
          '<article class="academy-activity-block" role="status">' +
            '<strong>Loading lesson canvas...</strong>' +
            '<p>Reading the study material, practice questions, packet metadata, and activity evidence for this lesson.</p>' +
          '</article>';
        academyApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/lesson-cards/' + encodeURIComponent(cardId) + '/canvas-card', payload)
          .then(function (lesson) {
            renderAcademyCanvasLessonPanel(panel, lesson || {}, payload || {}, ctx || {}, body, displayName || 'Lucía Hernández');
          })
          .catch(function (err) {
            academyApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/lesson-cards/' + encodeURIComponent(cardId) + '/activity-pack', payload)
              .then(function (pack) {
                renderAcademyActivityPack(panel, pack || {}, payload || {}, ctx || {}, body, displayName || 'Lucía Hernández');
              })
              .catch(function () {
                renderAcademyActivityError(panel, err);
              });
        });
      };
    });
  }

  function bindAcademySubjectActions(body, payload, ctx, displayName) {
    if (!body) return;
    var select = body.querySelector('[data-academy-subject-select]');
    if (!select) return;
    select.onchange = function () {
      var optionId = String(select.value || '');
      try {
        localStorage.setItem(academySubjectStorageKey(ctx), optionId);
      } catch (e) {}
      loadAcademyClassroomPackage(payload, ctx, displayName, body, optionId, true);
    };
  }

  function bindAcademySubjectDashboardActions(body, payload, ctx, displayName, subjectOptions, subjectModels) {
    if (!body) return;
    var optionMap = {};
    var modelMap = {};
    academyArray(subjectOptions).forEach(function (option) {
      if (option && option.option_id) optionMap[String(option.option_id)] = option;
    });
    academyArray(subjectModels).forEach(function (model) {
      if (model && model.optionId) modelMap[String(model.optionId)] = model;
    });
    function openSubject(optionId, action) {
      optionId = String(optionId || '');
      action = String(action || 'start');
      var option = optionMap[optionId] || null;
      var model = modelMap[optionId] || null;
      var status = body.querySelector('[data-academy-subject-event-status]');
      if (!optionId || !option) return;
      if (status) {
        status.textContent = 'Opening ' + (model && model.label ? model.label : option.label || 'subject') + '...';
        status.removeAttribute('data-state');
      }
      academyRecordSubjectEvent(payload || {}, ctx || {}, option, model || {}, action).then(function (result) {
        if (!status) return;
        if (result && result.ok) {
          status.textContent = 'Progress event saved.';
          status.setAttribute('data-state', 'success');
        } else {
          status.textContent = 'Opened subject. Progress event will sync when the endpoint is available.';
          status.setAttribute('data-state', 'error');
        }
      });
      loadAcademyClassroomPackage(payload, ctx, displayName, body, optionId, true);
    }
    body.querySelectorAll('[data-academy-subject-card]').forEach(function (card) {
      card.onclick = function (event) {
        if (event && event.target && event.target.closest && event.target.closest('button')) return;
        var optionId = String(card.getAttribute('data-academy-subject-card') || '');
        var model = modelMap[optionId] || {};
        openSubject(optionId, model.action || 'start');
      };
    });
    body.querySelectorAll('[data-academy-open-subject]').forEach(function (button) {
      button.onclick = function (event) {
        if (event && event.stopPropagation) event.stopPropagation();
        var optionId = String(button.getAttribute('data-academy-subject-option-id') || '');
        var action = String(button.getAttribute('data-academy-subject-action') || 'start');
        openSubject(optionId, action);
      };
    });
  }

  function bindAcademyBackToSubjectsAction(body, payload, ctx, displayName) {
    if (!body) return;
    body.querySelectorAll('[data-academy-back-to-subjects]').forEach(function (button) {
      button.onclick = function () {
        loadAcademyClassroomPackage(payload, ctx, displayName, body, '', false);
      };
    });
  }

  function bindAcademySubjectWorkspaceTabs(body) {
    if (!body) return;
    body.querySelectorAll('[data-academy-subject-tab]').forEach(function (button) {
      button.onclick = function () {
        var tab = String(button.getAttribute('data-academy-subject-tab') || 'study');
        body.querySelectorAll('[data-academy-subject-tab]').forEach(function (item) {
          item.setAttribute('data-active', item === button ? 'true' : 'false');
        });
        body.querySelectorAll('[data-academy-subject-pane]').forEach(function (pane) {
          pane.hidden = String(pane.getAttribute('data-academy-subject-pane') || '') !== tab;
        });
      };
    });
  }

	  function bindAcademyGeneratedLessonActions(body, packageData) {
	    if (!body) return;
	    var panel = body.querySelector('#academy-activity-panel');
    var lessonCards = academyArray(packageData && packageData.lessonCards);
    var cardMap = {};
    lessonCards.forEach(function (card) {
      var id = String(card && (card.lesson_card_id || card.card_id) || '');
      if (id) cardMap[id] = card;
    });
    body.querySelectorAll('[data-academy-open-generated-lesson]').forEach(function (button) {
      button.onclick = function () {
        var cardId = String(button.getAttribute('data-academy-card-id') || '');
        if (!cardId || !panel) return;
        var card = cardMap[cardId] || { lesson_card_id: cardId, lesson_title: 'Lesson shell' };
	        renderAcademyGeneratedLessonPanel(panel, card, packageData || {});
	      };
	    });
	  }

	  function bindAcademyPublishedCanvasActions(body, canvasMap) {
	    if (!body) return;
	    var panel = body.querySelector('#academy-activity-panel');
	    body.querySelectorAll('[data-academy-open-published-canvas]').forEach(function (button) {
	      button.onclick = function () {
	        var cardId = String(button.getAttribute('data-academy-card-id') || '');
	        if (!cardId || !panel) return;
	        renderAcademyPublishedCanvasCardPanel(panel, (canvasMap && canvasMap[cardId]) || { lesson_card_id: cardId });
	      };
	    });
	  }

  function bindAcademyN31StudyLayerActions(body) {
    if (!body) return;
    body.querySelectorAll('[data-academy-n31-tab]').forEach(function (button) {
      button.onclick = function () {
        var tab = String(button.getAttribute('data-academy-n31-tab') || 'study');
        body.querySelectorAll('[data-academy-n31-tab]').forEach(function (item) {
          item.setAttribute('data-active', item === button ? 'true' : 'false');
        });
        body.querySelectorAll('[data-academy-n31-pane]').forEach(function (pane) {
          pane.hidden = String(pane.getAttribute('data-academy-n31-pane') || '') !== tab;
        });
      };
    });
  }

  function academyProgressForGrade(progressData, grade) {
    var rows = (progressData && progressData.grade_progress) || [];
    for (var i = 0; i < rows.length; i += 1) {
      if (String(rows[i].grade) === String(grade)) return rows[i];
    }
    return null;
  }

  function renderAcademyClassroomLoading(body, ctx) {
    if (!body) return;
    body.innerHTML =
      '<section class="academy-canvas-home" data-nexus-academy-classroom="1">' +
        '<div class="academy-canvas-hero">' +
          '<span class="academy-canvas-kicker">Nexus Academy</span>' +
          '<h2>Academy My Class - Grade ' + academyEscape(ctx.grade) + '</h2>' +
          '<p>Loading Academy package for Grade ' + academyEscape(ctx.grade) + '.</p>' +
        '</div>' +
        '<section class="academy-canvas-empty" role="status">' +
          '<strong>Loading Academy package for selected grade.</strong>' +
          '<span>Reading modules, lesson cards, progress, certificates and RAG metadata from the read-only Academy API.</span>' +
        '</section>' +
      '</section>';
  }

  function renderAcademyClassroomError(body, ctx, err) {
    if (!body) return;
    var code = err && err.code ? String(err.code) : '';
    body.innerHTML =
      '<section class="academy-canvas-home" data-nexus-academy-classroom="1">' +
        '<div class="academy-canvas-hero">' +
          '<span class="academy-canvas-kicker">Nexus Academy</span>' +
          '<h2>Academy My Class - Grade ' + academyEscape(ctx.grade) + '</h2>' +
          '<p>Academy package unavailable for Grade ' + academyEscape(ctx.grade) + '.</p>' +
        '</div>' +
        '<section class="academy-canvas-empty" role="alert">' +
          '<strong>Academy package unavailable for Grade ' + academyEscape(ctx.grade) + '.</strong>' +
          '<span>' + (code ? 'Error: ' + academyEscape(code) : 'Check that the read-only Academy API is running on 127.0.0.1:8090.') + '</span>' +
        '</section>' +
      '</section>';
  }

  function renderAcademyClassroomLoaded(body, ctx, displayName, data, payload) {
    if (!body) return;
    var summary = data.summary || {};
	    var modules = (data.modules && data.modules.modules) || [];
	    var gradeCards = (data.cards && data.cards.cards) || [];
	    var cards = gradeCards.slice();
	    var gradeCardMap = {};
	    gradeCards.forEach(function (card) {
	      var id = String(card && (card.lesson_card_id || card.card_id || card.id) || '');
	      if (id) gradeCardMap[id] = card;
	    });
	    var certificate = data.certificate || {};
	    var certificateReadiness = data.certificateReadiness || {};
	    var gradeCanvasCards = (data.canvasCards && data.canvasCards.canvas_cards) || [];
	    var canvasCards = gradeCanvasCards.slice();
    var subjectOptions = academyArray(data.subjectOptions);
    var openSubjectWorkspace = data.openSubjectWorkspace === true;
	    var selectedSubjectOption = data.selectedSubjectOption || academyChooseSubjectOption(ctx, subjectOptions);
	    var selectedSubjectDetail = data.selectedSubjectDetail || {};
	    var selectedSubject = (selectedSubjectDetail && selectedSubjectDetail.subject) || (selectedSubjectOption && selectedSubjectOption.subject) || null;
	    var selectedSubjectUnits = academyArray((selectedSubjectDetail && selectedSubjectDetail.units) || (selectedSubject && selectedSubject.units));
	    var selectedN31Material = academyN31SelectedMaterial(data.n31LiveMaterials || {}, data.selectedN31SubjectDetail || {}, selectedSubjectOption, ctx);
	    var selectedN31Chunks = data.selectedN31Chunks || {};
	    var n31Unavailable = !!(data.n31LiveMaterialsUnavailable || data.n31SummaryUnavailable || data.selectedN31SubjectUnavailable || data.selectedN31ChunksUnavailable);
	    var generatedPackage = data.generatedPackage || {};
	    var selectedSubjectCardSet = selectedSubjectUnits.length ? academySubjectSetFromUnits(selectedSubjectUnits, 'lesson_card_ids') : {};
	    if (Object.keys(selectedSubjectCardSet).length) {
	      generatedPackage = academyFilterGeneratedPackageToLessonIds(generatedPackage, selectedSubjectCardSet);
	    }
	    var generatedLessonCards = academyArray(generatedPackage.lessonCards);
	    var selectedUsesGeneratedPackage = !!(generatedLessonCards.length && (selectedSubjectOption && selectedSubjectOption.mode === 'generated_shell' || academyArray(generatedPackage.subjectIds).length));
	    var selectedGeneratedReady = selectedUsesGeneratedPackage && academyGeneratedPackageHasReadyContent(generatedPackage);
	    var selectedIsGenerated = selectedUsesGeneratedPackage && selectedGeneratedReady;
	    var selectedGeneratedPending = selectedUsesGeneratedPackage && !selectedGeneratedReady;
	    var n3Unavailable = !!(data.subjectsUnavailable || data.generatedSubjectsUnavailable);
	    if (selectedIsGenerated) {
	      modules = academyGeneratedModulesFromCards(generatedLessonCards);
	      cards = generatedLessonCards.map(academyGeneratedRuntimeCard);
	      canvasCards = academyArray(generatedPackage.canvasCards).map(function (canvasCard) {
        return {
          lesson_card_id: canvasCard.lesson_card_id,
          study_material_ref_count: canvasCard.study_material_shell_id ? 1 : 0,
	          assessment_item_ref_count: academyArray(canvasCard.assessment_item_shell_ids).length
	        };
	      });
	    } else if (selectedGeneratedPending) {
	      modules = [];
	      cards = [];
	      canvasCards = [];
	    } else if (selectedSubjectUnits.length) {
	      var subjectModuleSet = academySubjectSetFromUnits(selectedSubjectUnits, 'module_ids');
	      var subjectCardSet = selectedSubjectCardSet;
      if (Object.keys(subjectModuleSet).length) {
        modules = modules.filter(function (module) {
          return !!subjectModuleSet[String(module.module_id || '')];
        });
      }
      if (Object.keys(subjectCardSet).length) {
        cards = cards.filter(function (card) {
          return !!subjectCardSet[String(card.lesson_card_id || card.card_id || '')];
        });
        canvasCards = canvasCards.filter(function (canvasCard) {
          return !!subjectCardSet[String(canvasCard.lesson_card_id || '')];
	        });
	      }
	    }
	    var publishedCanvasCards = academyPublishedCanvasCardsForSelection(gradeCanvasCards, gradeCardMap, selectedSubjectOption, selectedSubjectUnits, ctx);
	    if (publishedCanvasCards.length) {
	      var existingCardSet = {};
	      cards.forEach(function (card) {
	        var cardId = String(card && (card.lesson_card_id || card.card_id || card.id) || '');
	        if (cardId) existingCardSet[cardId] = true;
	      });
	      publishedCanvasCards.forEach(function (canvasCard) {
	        var lessonId = academyCanvasLessonId(canvasCard);
	        if (!lessonId || existingCardSet[lessonId]) return;
	        existingCardSet[lessonId] = true;
	        cards.push(academyPublishedCanvasRuntimeCard(canvasCard, selectedSubjectOption));
	      });
	      var existingModuleSet = {};
	      modules.forEach(function (module) {
	        var moduleId = String(module && module.module_id || '');
	        if (moduleId) existingModuleSet[moduleId] = true;
	      });
	      academyPublishedCanvasModules(publishedCanvasCards, selectedSubjectOption).forEach(function (module) {
	        if (!module || !module.module_id || existingModuleSet[String(module.module_id)]) return;
	        existingModuleSet[String(module.module_id)] = true;
	        modules.push(module);
	      });
	      var existingCanvasSet = {};
	      canvasCards.forEach(function (canvasCard) {
	        var lessonId = academyCanvasLessonId(canvasCard);
	        if (lessonId) existingCanvasSet[lessonId] = true;
	      });
	      publishedCanvasCards.forEach(function (canvasCard) {
	        var lessonId = academyCanvasLessonId(canvasCard);
	        if (!lessonId || existingCanvasSet[lessonId]) return;
	        existingCanvasSet[lessonId] = true;
	        canvasCards.push(canvasCard);
	      });
	    }
	    var progress = academyProgressForGrade(data.progress || {}, ctx.grade) || {};
	    var ledger = academyLedgerForGrade(data.progress || {}, ctx.grade);
	    var moduleMap = {};
	    var canvasMap = {};
	    modules.forEach(function (module) {
	      moduleMap[String(module.module_id || '')] = module;
	    });
    canvasCards.forEach(function (canvasCard) {
      canvasMap[String(canvasCard.lesson_card_id || '')] = canvasCard;
    });
    var progressPercent = Number(progress.progress_percent || 0);
    var subjectModels = subjectOptions.map(function (option) {
      return academySubjectProgressModel(option, data.subjectProgress || {}, data.progress || {}, ctx, gradeCardMap);
    });
    var publishedCanvasTotal = academyPublishedCanvasCount(gradeCanvasCards);
    var dashboardLessonTotal = Math.max(
      Number(progress.lesson_cards_total || 0),
      Number(gradeCards.length || 0),
      Number(publishedCanvasTotal || 0)
    );
    var certificateStatus = certificateReadiness.status || progress.certificate_status || certificate.status || 'not_started';
    var certificateCardsTotal = Number(certificateReadiness.cards_total || progress.lesson_cards_total || cards.length || 0);
    var certificateCardsReady = Number(certificateReadiness.cards_ready || 0);
    var certificatePercent = certificateCardsTotal ? Math.min(100, Math.round((certificateCardsReady / certificateCardsTotal) * 100)) : 0;
    var certificateReviewsText = (certificateReadiness.reviews_approved || 0) + '/' + (certificateReadiness.reviews_required || 0) + ' reviews approved';
    var certificateRequirementText = academyCertificateRequirementText(certificateReadiness);
	    var records = cards.map(function (card) {
	      if (card && card.published_canvas_card) {
	        var publishedModule = moduleMap[String(card.module_id || '')] || {};
	        return {
	          card: card,
	          module: publishedModule,
	          progress: { status: 'published_canvas_ready' },
	          status: 'published_canvas_ready',
	          cardId: String(card.lesson_card_id || card.card_id || card.id || ''),
	          tone: academyCardTone(card)
	        };
	      }
	      if (selectedIsGenerated) {
	        var module = moduleMap[String(card.module_id || '')] || {};
	        return {
	          card: card,
	          module: module,
	          progress: { status: 'generated_pilot_ready' },
	          status: 'generated_pilot_ready',
	          cardId: String(card.lesson_card_id || card.card_id || card.id || ''),
	          tone: academyCardTone(card)
	        };
      }
      return academyCardRecord(card, data.progress || {}, ctx.grade, moduleMap);
    });
    var currentRecord = null;
    for (var i = 0; i < records.length; i += 1) {
      if (records[i].status === 'in_progress') {
        currentRecord = records[i];
        break;
      }
    }
    if (!currentRecord) {
      for (var j = 0; j < records.length; j += 1) {
        if (records[j].status !== 'completed') {
          currentRecord = records[j];
          break;
        }
      }
    }
    if (!currentRecord && records.length) currentRecord = records[0];

    var certificateJourneyHtml =
      '<section class="academy-certificate-journey" aria-label="Academy certificate journey">' +
        '<div>' +
          '<div class="academy-pill-row">' +
            '<span class="academy-pill academy-pill-strong">Certificate Journey</span>' +
            '<span class="academy-pill">' + academyEscape(academyCertificateLabel(certificateStatus)) + '</span>' +
            '<span class="academy-pill">Issue disabled until Phase 4G</span>' +
          '</div>' +
          '<h3>' + academyEscape(certificateReadiness.certificate_title || certificate.title || 'Grade certificate') + '</h3>' +
          '<p>' + academyEscape(certificateReadiness.ready_for_issue ? 'Your grade work is ready for admin issue review. No certificate has been issued yet.' : 'Keep moving through lessons, evidence and reviews to unlock certificate readiness.') + '</p>' +
          '<p><strong>Next requirement:</strong> ' + academyEscape(certificateRequirementText) + '</p>' +
        '</div>' +
        '<aside class="academy-certificate-meter">' +
          '<strong>' + academyEscape(certificatePercent) + '%</strong>' +
          '<div class="academy-progress-bar" aria-hidden="true"><div class="academy-progress-fill" style="width:' + academyEscape(certificatePercent) + '%"></div></div>' +
          '<div class="academy-stats-row">' +
            '<div class="academy-stat-tile"><span>Cards Ready</span><strong>' + academyEscape(certificateCardsReady) + '/' + academyEscape(certificateCardsTotal) + '</strong></div>' +
            '<div class="academy-stat-tile"><span>Reviews</span><strong>' + academyEscape(certificateReviewsText) + '</strong></div>' +
          '</div>' +
        '</aside>' +
      '</section>';

	    var currentHtml = '';
	    if (currentRecord) {
	      var currentCard = currentRecord.card;
	      var currentType = academyTitleCase(currentCard.activity_type || 'lesson');
	      var currentActionHtml = currentCard.published_canvas_card ?
	        academyPublishedCanvasLessonActions(currentRecord.cardId) :
	        (selectedIsGenerated ?
	          academyGeneratedLessonActions(currentRecord.cardId, selectedSubjectOption && selectedSubjectOption.package_subject_id, true) :
	          academyActionButtons(currentRecord.cardId, currentRecord.status, true, canvasMap[currentRecord.cardId]));
      currentHtml =
        '<section class="academy-mission-card" data-tone="' + academyEscape(currentRecord.tone) + '" aria-label="Current Academy mission">' +
          '<div class="academy-mission-icon academy-lesson-icon" data-tone="' + academyEscape(currentRecord.tone) + '">' + academyEscape(academyCardIcon(currentCard)) + '</div>' +
          '<div class="academy-mission-body">' +
            '<div class="academy-pill-row">' +
              '<span class="academy-pill academy-pill-strong">Current Mission</span>' +
              '<span class="academy-pill">' + academyEscape(currentType) + '</span>' +
              '<span class="academy-pill">' + academyEscape(currentCard.estimated_minutes || 25) + ' min</span>' +
              '<span class="academy-pill">' + academyEscape(currentRecord.module.title || 'Academy module') + '</span>' +
            '</div>' +
            '<h3 class="academy-mission-title">' + academyEscape(currentCard.title || 'Academy lesson') + '</h3>' +
            '<p class="academy-mission-summary">' + academyEscape(currentCard.student_summary || currentCard.teacher_summary || 'Complete this Academy activity and keep your learning path moving.') + '</p>' +
            '<div class="academy-mission-meta">' +
              '<span class="academy-pill">Deliverable: ' + academyEscape(currentCard.deliverable || 'Learning artifact') + '</span>' +
              '<span class="academy-pill">Tutor ready</span>' +
            '</div>' +
            '<div class="academy-mission-meta">' + academyCanvasInfoPills(canvasMap[currentRecord.cardId]) + '</div>' +
            currentActionHtml +
          '</div>' +
	        '</section>';
	    } else {
	      currentHtml = selectedGeneratedPending ?
	        '<section class="academy-canvas-empty" role="status">' +
	          '<strong>Generated package review shell.</strong>' +
	          '<span>The selected subject has generated package records, but student-ready study, practice, and artifact rows are still pending review.</span>' +
	        '</section>' :
	        '<section class="academy-canvas-empty" role="status">' +
	          '<strong>No Academy lessons published for Grade ' + academyEscape(ctx.grade) + ' yet.</strong>' +
	          '<span>Academy lessons will appear here when this grade package has student-visible cards.</span>' +
	        '</section>';
	    }

    var periodHtml = modules.map(function (module, index) {
      var moduleProgress = academyModuleProgress(module, ledger);
      var completed = Number(moduleProgress.lesson_cards_completed || 0);
      var total = Number(moduleProgress.lesson_cards_total || module.lesson_card_count || 0);
      var pathPercent = total ? Math.min(100, Math.round((completed / total) * 100)) : 0;
      var moduleRecords = records.filter(function (record) {
        return String(record.card.module_id || '') === String(module.module_id || '');
      });
      var lessonRows = moduleRecords.map(function (record, lessonIndex) {
        var card = record.card;
        return '<article class="academy-period-lesson" data-tone="' + academyEscape(record.tone) + '">' +
          '<div class="academy-period-lesson-main">' +
            '<div class="academy-pill-row">' +
              '<span class="academy-pill">Lesson ' + academyEscape(lessonIndex + 1) + '</span>' +
              '<span class="academy-pill">' + academyEscape(card.estimated_minutes || 25) + ' min</span>' +
              academyCanvasInfoPills(canvasMap[record.cardId]) +
            '</div>' +
            '<div class="academy-period-lesson-title">' + academyEscape(card.title || 'Academy lesson') + '</div>' +
            '<div class="academy-period-lesson-summary">' + academyEscape(card.student_summary || card.teacher_summary || '') + '</div>' +
          '</div>' +
		          (card.published_canvas_card ? academyPublishedCanvasLessonActions(record.cardId) : (selectedIsGenerated ? academyGeneratedLessonActions(record.cardId, selectedSubjectOption && selectedSubjectOption.package_subject_id, true) : academyCompactLessonActions(record.cardId, record.status, canvasMap[record.cardId]))) +
		        '</article>';
      }).join('');
      return '<article class="academy-period-card">' +
        '<div class="academy-pill-row">' +
          '<span class="academy-pill academy-pill-strong">Period ' + academyEscape(index + 1) + '</span>' +
          '<span class="academy-pill">' + academyEscape(completed) + '/' + academyEscape(total) + ' done</span>' +
        '</div>' +
        '<h3>' + academyEscape(module.title || 'Academy module') + '</h3>' +
        '<p>' + academyEscape(module.essential_question || module.program_summary || module.summary || '') + '</p>' +
        '<div class="academy-progress-bar" aria-hidden="true"><div class="academy-progress-fill" style="width:' + academyEscape(pathPercent) + '%"></div></div>' +
        '<div class="academy-period-lessons">' + (lessonRows || '<p>No lessons linked to this period yet.</p>') + '</div>' +
      '</article>';
    }).join('');

    var completedRows = records.filter(function (record) {
      return record.status === 'completed';
	    }).slice(0, 3).map(function (record) {
	      return '<article class="academy-completed-row">' +
	        '<div><strong>' + academyEscape(record.card.title || 'Completed lesson') + '</strong><br><span>' + academyEscape(record.module.title || 'Academy module') + '</span></div>' +
	        '<span class="academy-canvas-status academy-canvas-status-completed">Done</span>' +
	      '</article>';
	    }).join('');
	    var n31StudyLayerHtml = academyN31StudyLayerHtml(ctx, selectedSubjectOption, selectedN31Material, selectedN31Chunks, data.n31Summary || {}, n31Unavailable);
	    var generatedPackageNoteHtml = selectedIsGenerated ?
	      '<div class="academy-shell-note">Generated core package pilot content is loaded for this subject. Study and practice are student-facing; Submit remains preparation-only while runtime writes are disabled.</div>' :
	      (selectedGeneratedPending ? '<div class="academy-shell-note">Generated package records are available, but student-facing content is still pending review for this selection.</div>' : '');
	    var heroHtml =
	      '<section class="academy-student-hero" aria-label="Academy learning overview">' +
	        '<div class="academy-student-hero-copy">' +
	          '<div class="academy-pill-row">' +
	            '<span class="academy-pill academy-pill-strong">Nexus Academy</span>' +
	            '<span class="academy-pill">Grade ' + academyEscape(ctx.grade) + '</span>' +
	          '</div>' +
	          '<h2>Academy My Class</h2>' +
	          '<p>' + academyEscape(displayName) + ' · ' + academyEscape(summary.title || ctx.title) + '</p>' +
	          '<div class="academy-pill-row">' +
	            '<span class="academy-pill">Subject dashboard</span>' +
	            '<span class="academy-pill">Human review for certificates</span>' +
	            '<span class="academy-pill">Eugenie tutor active</span>' +
	          '</div>' +
	        '</div>' +
	        '<aside class="academy-progress-panel" aria-label="Academy progress">' +
	          '<div><div class="academy-progress-number">' + academyEscape(progressPercent) + '%</div><div class="academy-progress-label">Grade path progress</div></div>' +
	          '<div class="academy-progress-bar" aria-hidden="true"><div class="academy-progress-fill" style="width:' + academyEscape(Math.min(100, Math.max(0, progressPercent))) + '%"></div></div>' +
	          '<div class="academy-stats-row">' +
	            '<div class="academy-stat-tile"><span>Lessons</span><strong>' + academyEscape(progress.lesson_cards_completed || 0) + '/' + academyEscape(dashboardLessonTotal || 0) + '</strong></div>' +
	            '<div class="academy-stat-tile"><span>Published</span><strong>' + academyEscape(publishedCanvasTotal || 0) + '</strong></div>' +
	            '<div class="academy-stat-tile"><span>Subjects</span><strong>' + academyEscape(subjectModels.length || subjectOptions.length || 0) + '</strong></div>' +
	          '</div>' +
	        '</aside>' +
	      '</section>';
	    if (!openSubjectWorkspace) {
	      body.innerHTML =
	        '<section class="academy-student-shell" data-nexus-academy-classroom="1">' +
	          heroHtml +
	          '<section id="academy-activity-panel" class="academy-activity-panel" hidden aria-live="polite"></section>' +
	          '<section id="academy-classroom-overview" class="academy-classroom-overview">' +
	            academySubjectDashboardHtml(ctx, displayName, subjectModels, !!data.subjectProgressUnavailable) +
	          '</section>' +
	        '</section>';
	      bindAcademySubjectDashboardActions(body, payload || {}, ctx, displayName, subjectOptions, subjectModels);
	      return;
	    }

	    var selectedSubjectModel = null;
	    for (var sm = 0; sm < subjectModels.length; sm += 1) {
	      if (selectedSubjectOption && subjectModels[sm].optionId === selectedSubjectOption.option_id) {
	        selectedSubjectModel = subjectModels[sm];
	        break;
	      }
	    }
	    if (!selectedSubjectModel) {
	      selectedSubjectModel = academySubjectProgressModel(selectedSubjectOption || {}, data.subjectProgress || {}, data.progress || {}, ctx, gradeCardMap);
	    }
	    var subjectWorkspaceHeaderHtml =
	      '<div class="academy-subject-workspace-header">' +
	        '<div>' +
	          '<div class="academy-pill-row">' +
	            '<span class="academy-pill academy-pill-strong">Grade ' + academyEscape(ctx.grade) + '</span>' +
	            '<span class="academy-canvas-status academy-canvas-status-' + academyEscape(academyStatusClass(selectedSubjectModel.status)) + '">' + academyEscape(selectedSubjectModel.statusLabel || 'Ready') + '</span>' +
	            '<span class="academy-pill">' + academyEscape(selectedSubjectModel.percent || 0) + '% complete</span>' +
	            '<span class="academy-pill">' + academyEscape(selectedSubjectModel.n31Available ? 'N31 study ready' : 'Study package') + '</span>' +
	          '</div>' +
	          '<h3>' + academyEscape((selectedSubjectOption && selectedSubjectOption.label) || selectedSubjectModel.label || 'Subject') + '</h3>' +
	          '<p>Resume: ' + academyEscape(selectedSubjectModel.resumeTarget || 'Next lesson') + '</p>' +
	        '</div>' +
	        '<div class="academy-subject-workspace-actions">' +
	          '<button type="button" class="academy-secondary-action" data-academy-back-to-subjects="1">Back to Subjects</button>' +
	        '</div>' +
	      '</div>';
	    body.innerHTML =
	      '<section class="academy-student-shell" data-nexus-academy-classroom="1">' +
	        heroHtml +
	        '<section id="academy-activity-panel" class="academy-activity-panel" hidden aria-live="polite"></section>' +
	        '<section id="academy-classroom-overview" class="academy-classroom-overview">' +
	          '<section class="academy-subject-workspace" aria-label="Selected subject workspace">' +
	            subjectWorkspaceHeaderHtml +
	            '<nav class="academy-workspace-nav" aria-label="Subject workspace tabs">' +
	              '<button type="button" data-academy-subject-tab="study" data-active="true">Study</button>' +
	              '<button type="button" data-academy-subject-tab="practice">Practice</button>' +
	              '<button type="button" data-academy-subject-tab="submit">Submit</button>' +
	            '</nav>' +
	            '<section class="academy-workspace-pane" data-academy-subject-pane="study">' +
	              n31StudyLayerHtml +
	              generatedPackageNoteHtml +
	              currentHtml +
	            '</section>' +
	            '<section class="academy-workspace-pane" data-academy-subject-pane="practice" hidden>' +
	              '<div class="academy-section-header"><div><h3>' + academyEscape((selectedSubjectOption && selectedSubjectOption.label) || ('Grade ' + ctx.grade)) + ' Practice</h3><span>Open one lesson at a time. Each unit keeps its work together.</span></div></div>' +
	              '<section class="academy-period-grid" aria-label="Academy subject periods">' + (periodHtml || '<section class="academy-canvas-empty" role="status"><strong>No periods returned.</strong></section>') + '</section>' +
	            '</section>' +
	            '<section class="academy-workspace-pane" data-academy-subject-pane="submit" hidden>' +
	              '<article class="academy-activity-block">' +
	                '<div class="academy-pill-row">' +
	                  '<span class="academy-pill academy-pill-strong">Subject Submit</span>' +
	                  '<span class="academy-pill">Human review</span>' +
	                  '<span class="academy-pill">Runtime writes disabled</span>' +
	                '</div>' +
	                '<strong>Prepare Evidence From This Subject</strong>' +
	                '<p>Open a lesson, use its Submit tab, and keep work source-safe for review. If the subject event endpoint is offline, this workspace still opens normally.</p>' +
	              '</article>' +
	              certificateJourneyHtml +
	              (completedRows ? '<div class="academy-section-header"><div><h3>Completed Recently</h3><span>Your progress ledger is tracking this subject separately when subject-progress is available.</span></div></div><section class="academy-completed-strip" aria-label="Academy completed lessons">' + completedRows + '</section>' : '') +
	            '</section>' +
	          '</section>' +
	        '</section>' +
	      '</section>';
    bindAcademyProgressActions(body, payload || {}, ctx, displayName);
    bindAcademyActivityActions(body, payload || {}, ctx, displayName);
    bindAcademySubjectActions(body, payload || {}, ctx, displayName);
    bindAcademyBackToSubjectsAction(body, payload || {}, ctx, displayName);
    bindAcademySubjectWorkspaceTabs(body);
    bindAcademyGeneratedLessonActions(body, generatedPackage);
    bindAcademyPublishedCanvasActions(body, canvasMap);
    bindAcademyN31StudyLayerActions(body);
  }

  function loadAcademyClassroomPackage(payload, ctx, displayName, body, selectedSubjectOptionId, openSubjectWorkspace) {
    renderAcademyClassroomLoading(body, ctx);
    var studentId = getAcademyStudentId(payload);
    Promise.all([
      academyApiGet('/api/academy/students/' + encodeURIComponent(studentId) + '/context', payload),
      academyApiGet('/api/academy/students/' + encodeURIComponent(studentId) + '/progress', payload),
      academyApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/summary', payload),
      academyApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/modules', payload),
      academyApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/lesson-cards', payload),
      academyApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/certificate', payload),
      academyApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/rag', payload),
      academyApiGet('/api/academy/students/' + encodeURIComponent(studentId) + '/grades/' + encodeURIComponent(ctx.grade) + '/certificate/readiness', payload),
      academyApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/canvas-cards', payload),
      academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/subjects', payload, { subjects: [] }),
      academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/generated-subjects', payload, { subjects: [] }),
      academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/n31/live-materials', payload, { student_material_expansions: [] }),
      academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/n31/live-materials/summary', payload, {}),
      academyOptionalApiGet('/api/academy/students/' + encodeURIComponent(studentId) + '/grades/' + encodeURIComponent(ctx.grade) + '/subject-progress', payload, { subjects: [] })
    ]).then(function (results) {
      if (ctx.grade !== gradeContext(lastAcademyPayload && lastAcademyPayload.active_grade).grade) return;
      var subjectsPayload = results[9] || { subjects: [] };
      var generatedSubjectsPayload = results[10] || { subjects: [] };
      var n31LiveMaterialsPayload = results[11] || { student_material_expansions: [] };
      var n31SummaryPayload = results[12] || {};
      var subjectProgressPayload = results[13] || { subjects: [] };
      var subjectOptions = academyBuildSubjectOptions(subjectsPayload, generatedSubjectsPayload, ctx, n31LiveMaterialsPayload);
      var selectedSubjectOption = academyChooseSubjectOption(ctx, subjectOptions, selectedSubjectOptionId);
	      var selectedSubjectPath = selectedSubjectOption && selectedSubjectOption.mode === 'subject_map'
	        ? '/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/subjects/' + encodeURIComponent(selectedSubjectOption.subject_id)
	        : '';
	      var selectedN31SubjectId = selectedSubjectOption && selectedSubjectOption.n31_available
	        ? String(selectedSubjectOption.n31_subject_id || selectedSubjectOption.subject_id || '')
	        : '';
	      var selectedN31SubjectPath = selectedN31SubjectId
	        ? '/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/n31/live-materials/subjects/' + encodeURIComponent(selectedN31SubjectId)
	        : '';
	      return Promise.all([
	        selectedSubjectPath ? academyOptionalApiGet(selectedSubjectPath, payload, {}) : Promise.resolve({}),
	        selectedN31SubjectPath ? academyOptionalApiGet(selectedN31SubjectPath, payload, {}) : Promise.resolve({}),
	        selectedN31SubjectPath ? academyOptionalApiGet(selectedN31SubjectPath + '/chunks', payload, { chunks: [] }) : Promise.resolve({ chunks: [] })
	      ]).then(function (selectedReads) {
	        var selectedSubjectDetail = selectedReads[0] || {};
	        var selectedN31SubjectDetail = selectedReads[1] || {};
	        var selectedN31Chunks = selectedReads[2] || { chunks: [] };
	        var packageSubjectIds = academyGeneratedPackageIdsForSelection(selectedSubjectOption, selectedSubjectDetail, generatedSubjectsPayload, ctx);
	        return Promise.all(packageSubjectIds.map(function (packageSubjectId) {
	          return Promise.all([
	            academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/generated-subjects/' + encodeURIComponent(packageSubjectId) + '/lesson-cards', payload, { lesson_cards: [] }),
	            academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/generated-subjects/' + encodeURIComponent(packageSubjectId) + '/canvas-cards', payload, { canvas_card_shells: [] }),
	            academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/generated-subjects/' + encodeURIComponent(packageSubjectId) + '/learning-material-shells', payload, { learning_material_shells: [] }),
	            academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/generated-subjects/' + encodeURIComponent(packageSubjectId) + '/assessment-item-shells', payload, { assessment_item_shells: [] }),
	            academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/generated-subjects/' + encodeURIComponent(packageSubjectId) + '/activity-pack-shells', payload, { activity_pack_shells: [] }),
	            academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/generated-subjects/' + encodeURIComponent(packageSubjectId) + '/pdf-ready', payload, { pdf_manifests: [] }),
	            academyOptionalApiGet('/api/academy/grades/' + encodeURIComponent(ctx.grade) + '/generated-subjects/' + encodeURIComponent(packageSubjectId) + '/parent-summary', payload, {})
	          ]).then(function (packageResponses) {
	            return academyGeneratedPackageFromResponses(packageSubjectId, selectedSubjectOption && selectedSubjectOption.label, packageResponses);
	          });
	        })).then(function (packageRows) {
	          var generatedPackage = academyMergeGeneratedPackages(packageRows, selectedSubjectOption && selectedSubjectOption.label);
	        renderAcademyClassroomLoaded(body, ctx, displayName, {
	        context: results[0],
	        progress: results[1],
	        summary: results[2],
	        modules: results[3],
        cards: results[4],
        certificate: results[5],
        rag: results[6],
        certificateReadiness: results[7],
          canvasCards: results[8],
          subjects: subjectsPayload,
	          generatedSubjects: generatedSubjectsPayload,
	          subjectOptions: subjectOptions,
	          selectedSubjectOption: selectedSubjectOption,
	          selectedSubjectDetail: selectedSubjectDetail,
	          n31LiveMaterials: n31LiveMaterialsPayload,
	          n31Summary: n31SummaryPayload,
	          selectedN31SubjectDetail: selectedN31SubjectDetail,
	          selectedN31Chunks: selectedN31Chunks,
	          subjectsUnavailable: !!subjectsPayload.unavailable,
	          generatedSubjectsUnavailable: !!generatedSubjectsPayload.unavailable,
	          n31LiveMaterialsUnavailable: !!n31LiveMaterialsPayload.unavailable,
	          n31SummaryUnavailable: !!n31SummaryPayload.unavailable,
	          selectedN31SubjectUnavailable: !!selectedN31SubjectDetail.unavailable,
	          selectedN31ChunksUnavailable: !!selectedN31Chunks.unavailable,
	          generatedPackage: generatedPackage,
	          subjectProgress: subjectProgressPayload,
	          subjectProgressUnavailable: !!subjectProgressPayload.unavailable,
	          openSubjectWorkspace: openSubjectWorkspace === true
	        }, payload);
	        });
	      });
	    }).catch(function (err) {
	      renderAcademyClassroomError(body, ctx, err);
	    });
  }

  function renderAcademyHome(payload) {
    if (!isExplicitAcademyPayload(payload)) return;
    lastAcademyPayload = payload || {};
    activeAcademyView = 'home';
    var ctx = gradeContext(payload && payload.active_grade);
    var displayName = String((payload && (payload.display_name || payload.name)) || 'Lucía Hernández').trim() || 'Lucía Hernández';
    publishAcademyScope(ctx, displayName);
    updateChatContext(ctx, displayName);

    var mirrorHome = document.getElementById('mirror-home');
    var mirrorContent = document.getElementById('mirror-content');
    var title = document.querySelector('.mirror-title');
    var subtitle = document.querySelector('.mirror-subtitle');
    var docTitle = document.getElementById('mirror-doc-title');
    var docMeta = document.getElementById('mirror-doc-meta');
    var toolbar = document.querySelector('.mirror-doc-toolbar');
    var body = document.getElementById('mirror-doc-body');
    var editor = document.getElementById('mirror-doc-editor');

    if (mirrorHome) mirrorHome.style.setProperty('display', 'none', 'important');
    if (mirrorContent) {
      mirrorContent.style.setProperty('display', 'block', 'important');
      mirrorContent.classList.remove('embed-frame-active', 'context-teacher-dashboard');
    }
    if (title) title.textContent = 'Work Mirror · Academy Home';
    if (subtitle) subtitle.textContent = displayName + ' · Grade ' + ctx.grade + ' · Nexus Academy';
    if (docTitle) docTitle.textContent = 'Academy Home';
    if (docMeta) docMeta.textContent = 'Grade ' + ctx.grade + ' · Student Academy Workspace';
    if (editor) editor.style.display = 'none';

    var selectorHtml = '<div class="academy-canvas-toolbar"><label><span>Grade</span><select id="academy-canvas-grade-select" aria-label="Academy demo grade">';
    allowedGrades.forEach(function (grade) {
      selectorHtml += '<option value="' + grade + '"' + (grade === ctx.grade ? ' selected' : '') + '>Grade ' + grade + '</option>';
    });
    selectorHtml += '</select></label></div>';
    if (toolbar) toolbar.innerHTML = selectorHtml;

    if (body) {
      var debug = '';
      try {
        if (canShowAcademyDebug()) {
          debug = '<section class="academy-canvas-debug" aria-label="Academy Debug"><strong>Academy Debug</strong>' +
            '<code>active_overlay=academy</code>' +
            '<code>tenant_type=academy</code>' +
            '<code>base_role=student</code>' +
            '<code>rag_namespace=' + ctx.ragNamespace + '</code>' +
            '<code>' + ctx.curriculumManifestPath + '</code>' +
            '</section>';
        }
      } catch (e) {}

      body.innerHTML =
        '<section class="academy-canvas-home" data-nexus-academy-home="1">' +
          '<div class="academy-canvas-hero">' +
            '<span class="academy-canvas-kicker">Nexus Academy</span>' +
            '<h2>Academy Home · Grade ' + ctx.grade + '</h2>' +
            '<p>Welcome back, ' + displayName + '. Eugenie is ready to guide your Grade ' + ctx.grade + ' learning path.</p>' +
            '<button type="button" class="academy-primary-action academy-open-classroom" data-academy-open-classroom="1">Open My Class</button>' +
          '</div>' +
          '<section class="academy-canvas-empty" role="status">' +
            '<strong>Grade ' + ctx.grade + ' Academy path is available in My Class.</strong>' +
            '<span>Open one subject at a time to study, practice, and prepare evidence for review.</span>' +
          '</section>' +
          '<section class="academy-canvas-grid" aria-label="Academy areas">' +
            '<article class="academy-canvas-card"><small>Ready</small><strong>Subjects</strong><span>Choose Math, ELAR, Science, Civics, AI, Coding, Projects, or Certifications.</span></article>' +
            '<article class="academy-canvas-card"><small>Ready</small><strong>Study</strong><span>Read the student material before practicing.</span></article>' +
            '<article class="academy-canvas-card"><small>Ready</small><strong>Practice</strong><span>Work through prompts in your own words.</span></article>' +
            '<article class="academy-canvas-card"><small>Ready</small><strong>Submit</strong><span>Prepare classroom-safe evidence for review.</span></article>' +
            '<article class="academy-canvas-card"><small>Tracked</small><strong>Progress</strong><span>Progress stays separate by grade.</span></article>' +
            '<article class="academy-canvas-card"><small>Gated</small><strong>Certificates</strong><span>Readiness is checked before any credential can be issued.</span></article>' +
          '</section>' +
          '<section class="academy-canvas-progress" aria-label="Academy progress">' +
            '<div><span>Progress</span><strong>0%</strong></div>' +
            '<div><span>Certificates</span><strong>Pending</strong></div>' +
          '</section>' +
          debug +
        '</section>';
    }

    var select = document.getElementById('academy-canvas-grade-select');
    if (select) {
      select.onchange = function () {
        var nextGrade = normalizeGrade(select.value);
        var nextCtx = gradeContext(nextGrade);
        postAcademyGradeChange(nextCtx);
        var nextPayload = Object.assign({}, lastAcademyPayload || {}, {
          type: 'nexus-open-academy-home',
          view: 'academy_home',
          active_grade: nextGrade,
          display_name: displayName
        });
        if (activeAcademyView === 'classroom') renderAcademyClassroom(nextPayload);
        else renderAcademyHome(nextPayload);
      };
    }
  }

  function renderAcademyClassroom(payload) {
    if (!isExplicitAcademyPayload(payload)) return;
    lastAcademyPayload = payload || {};
    activeAcademyView = 'classroom';
    var ctx = gradeContext(payload && payload.active_grade);
    var displayName = String((payload && (payload.display_name || payload.name)) || 'Lucía Hernández').trim() || 'Lucía Hernández';
    publishAcademyScope(ctx, displayName);
    updateChatContext(ctx, displayName);

    var mirrorHome = document.getElementById('mirror-home');
    var mirrorContent = document.getElementById('mirror-content');
    var title = document.querySelector('.mirror-title');
    var subtitle = document.querySelector('.mirror-subtitle');
    var docTitle = document.getElementById('mirror-doc-title');
    var docMeta = document.getElementById('mirror-doc-meta');
    var toolbar = document.querySelector('.mirror-doc-toolbar');
    var body = document.getElementById('mirror-doc-body');
    var editor = document.getElementById('mirror-doc-editor');

    if (mirrorHome) mirrorHome.style.setProperty('display', 'none', 'important');
    if (mirrorContent) {
      mirrorContent.style.setProperty('display', 'block', 'important');
      mirrorContent.classList.remove('embed-frame-active', 'context-teacher-dashboard');
    }
    if (title) title.textContent = 'Work Mirror · Academy My Class';
    if (subtitle) subtitle.textContent = displayName + ' · Grade ' + ctx.grade + ' · Nexus Academy';
    if (docTitle) docTitle.textContent = 'Academy My Class';
    if (docMeta) docMeta.textContent = 'Grade ' + ctx.grade + ' · Academy learning path';
    if (editor) editor.style.display = 'none';

    var selectorHtml = '<div class="academy-canvas-toolbar"><label><span>Grade</span><select id="academy-canvas-grade-select" aria-label="Academy demo grade">';
    allowedGrades.forEach(function (grade) {
      selectorHtml += '<option value="' + grade + '"' + (grade === ctx.grade ? ' selected' : '') + '>Grade ' + grade + '</option>';
    });
    selectorHtml += '</select></label></div>';
    if (toolbar) toolbar.innerHTML = selectorHtml;

    if (body) loadAcademyClassroomPackage(payload, ctx, displayName, body);

    var select = document.getElementById('academy-canvas-grade-select');
    if (select) {
      select.onchange = function () {
        var nextGrade = normalizeGrade(select.value);
        var nextCtx = gradeContext(nextGrade);
        postAcademyGradeChange(nextCtx);
        renderAcademyClassroom(Object.assign({}, lastAcademyPayload || {}, {
          type: 'nexus-open-academy-home',
          view: 'academy_home',
          active_grade: nextGrade,
          display_name: displayName
        }));
      };
    }
  }

  window.nexusRenderAcademyHome = renderAcademyHome;
  window.nexusRenderAcademyClassroom = renderAcademyClassroom;
  document.addEventListener('click', function (event) {
    var academyPayload = lastAcademyPayload || readStoredAcademyPayload();
    if (!academyPayload) return;
    var target = event.target && event.target.closest ? event.target.closest('#btn-mirror-classroom, [data-academy-open-classroom="1"]') : null;
    if (!target) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    renderAcademyClassroom(academyPayload);
  }, true);
  window.addEventListener('message', function (event) {
    if (event.source !== window.parent || event.origin !== window.location.origin) return;
    var data = event.data || {};
    if (data && data.__nexusBrowser && data.type === 'nexus-open-academy-home') {
      if (String(data.view || data.route || data.canvas_view || '').trim().toLowerCase() === 'academy_classroom') {
        renderAcademyClassroom(data);
        return;
      }
      renderAcademyHome(data);
      return;
    }
    if (data && data.__nexusBrowser && data.type === 'nexus-session-sync' && isExplicitAcademyPayload(data)) {
      if (String(data.view || data.route || data.canvas_view || '').trim().toLowerCase() === 'academy_classroom') {
        renderAcademyClassroom(data);
        return;
      }
      renderAcademyHome(data);
    }
  });
})();
`
  doc.head.appendChild(script)
}
/* @nexus-profile academy:end */


function getOrCreateTopBar(doc: Document): HTMLElement {
  let bar = doc.getElementById(TOP_BAR_ID) as HTMLElement | null
  if (!bar) {
    bar = doc.createElement('div')
    bar.id = TOP_BAR_ID
    doc.body.appendChild(bar)
  }
  return bar
}

function ensureStyle(doc: Document) {
  let style = doc.getElementById(STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = doc.createElement('style')
    style.id = STYLE_ID
    doc.head.appendChild(style)
  }

  style.textContent = `
    body {
      padding-top: 96px !important;
    }

    #${TOP_BAR_ID} {
      --nexus-topbar-left: clamp(300px, 22vw, 350px);
      --nexus-topbar-right: clamp(112px, 10vw, 190px);
      position: fixed;
      top: 16px;
      left: var(--nexus-topbar-left);
      width: min(720px, calc(100vw - var(--nexus-topbar-left) - var(--nexus-topbar-right)));
      z-index: 9999;
      pointer-events: none;
      padding: 0;
    }

    #${TOP_BAR_ID} .search-bubble {
      margin: 0 !important;
      width: 100% !important;
      box-shadow: 0 18px 48px rgba(34, 211, 238, 0.22);
      border: 1px solid rgba(34, 211, 238, 0.35);
      pointer-events: auto;
    }

    #${TOP_BAR_ID} .search-line {
      gap: 14px;
      min-width: 0;
    }

    #${TOP_BAR_ID} .search-tag {
      max-width: 150px;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    #${TOP_BAR_ID} #offline-search-input {
      flex: 1;
      min-width: 0;
      font-size: 0.95rem;
      padding: 11px 14px;
    }

    body .chat-input-area .search-bubble {
      display: none !important;
    }

    #mirror-presence-indicator {
      display: none !important;
    }

    @media (max-width: 1220px) {
      #${TOP_BAR_ID} {
        --nexus-topbar-left: 300px;
        --nexus-topbar-right: 96px;
        width: min(640px, calc(100vw - var(--nexus-topbar-left) - var(--nexus-topbar-right)));
      }

      #${TOP_BAR_ID} .search-tag {
        max-width: 118px;
      }

      #${TOP_BAR_ID} #offline-search-input {
        font-size: 0.9rem;
      }
    }

    @media (max-width: 980px) {
      body {
        padding-top: 146px !important;
      }

      #${TOP_BAR_ID} {
        top: 86px;
        left: 18px;
        right: 92px;
        width: auto;
      }

      #${TOP_BAR_ID} .search-tag {
        display: none;
      }
    }
  `
}

function installThemeBridge(doc: Document) {
  if (doc.getElementById(THEME_BRIDGE_SCRIPT_ID)) return
  const script = doc.createElement('script')
  script.id = THEME_BRIDGE_SCRIPT_ID
  script.type = 'text/javascript'
  script.text = `
    (function () {
      if (window.__nexusBrowserThemeBridge) return;
      window.__nexusBrowserThemeBridge = true;
      function postTheme() {
        try {
          var theme = String(document.body && document.body.dataset ? document.body.dataset.themeEffective || '' : '').toLowerCase();
          if (theme !== 'light' && theme !== 'dark') theme = 'dark';
          window.parent.postMessage({ __nexusBrowser: true, type: 'nexus-theme-effective', theme: theme }, '*');
        } catch (e) {}
      }
      postTheme();
      try {
        var observer = new MutationObserver(postTheme);
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme-effective'] });
      } catch (e) {}
      window.addEventListener('storage', postTheme);
      window.setTimeout(postTheme, 250);
      window.setTimeout(postTheme, 1200);
    })();
  `
  doc.head.appendChild(script)
}

function enforceOllamaOnlyMode(doc: Document) {
  // We do NOT modify institutional GenUI files. This is a safe overlay that:
  // - hides OpenCode-specific controls
  // - forces UI selectors to local_ollama when present
  // - keeps the Eugene streaming payload on local Ollama

  // Style overlay
  let style = doc.getElementById(OLLAMA_ONLY_STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = doc.createElement('style')
    style.id = OLLAMA_ONLY_STYLE_ID
    doc.head.appendChild(style)
  }

  style.textContent = `
    /* Hide OpenCode embedded controls (Ollama-only mode) */
    #btn-agents-launch-opencode,
    #btn-agents-stop-opencode,
    #agents-opencode-agent-select,
    #agents-opencode-frame,
    #agents-iframe-shell {
      display: none !important;
    }

    /* Hide provider/model selectors to avoid confusion */
	    #agents-provider-select,
	    #agents-model-select {
	      display: none !important;
	    }

	    #nexus-eugene-model-switch {
	      border: 1px solid rgba(56, 189, 248, 0.18);
	      background: rgba(9, 21, 46, 0.72);
	      border-radius: 14px;
	      padding: 14px;
	      display: grid;
	      gap: 10px;
	    }

	    #nexus-eugene-model-switch .nexus-model-title {
	      font-size: 0.9rem;
	      font-weight: 700;
	    }

	    #nexus-eugene-model-switch .nexus-model-row {
	      display: flex;
	      flex-wrap: wrap;
	      gap: 8px;
	      align-items: center;
	    }

	    #nexus-eugene-model-switch select {
	      min-height: 40px;
	      flex: 1;
	      min-width: min(100%, 240px);
	      border-radius: 12px;
	      border: 1px solid rgba(56, 189, 248, 0.24);
	      background: rgba(3, 8, 20, 0.84);
	      color: var(--text-main);
	      padding: 0 12px;
	    }

	    #nexus-eugene-model-switch .nexus-model-status {
	      white-space: pre-wrap;
	      color: var(--text-soft);
	      font-size: 0.84rem;
	      line-height: 1.45;
	    }
	  `

  // Script overlay
  let script = doc.getElementById(OLLAMA_ONLY_SCRIPT_ID) as HTMLScriptElement | null
  const shouldInjectScript = !script
  if (!script) {
    script = doc.createElement('script')
    script.id = OLLAMA_ONLY_SCRIPT_ID
    script.type = 'text/javascript'
  }

  script.text = String.raw`
(function () {
	  var MODEL_LABEL = 'qwen3.5:cloud';
	  var MODEL_STATUS = null;

	  function authHeaders() {
	    try {
	      if (typeof window.buildAuthHeaders === 'function') return window.buildAuthHeaders() || {};
	    } catch (e) {}
	    return {};
	  }

	  function getAcademyScope() {
	    try {
	      var scope = window.__nexusAcademyScope || null;
	      return scope && scope.active_overlay === 'academy' ? scope : null;
	    } catch (e) {
	      return null;
	    }
	  }

	  function setIndicator() {
	    try {
	      var el = document.getElementById('agent-route-indicator');
	      if (el) {
	        var academyScope = getAcademyScope();
	        if (academyScope) {
	          var displayName = String(academyScope.display_name || 'Lucía Hernández').trim() || 'Lucía Hernández';
	          var grade = String(academyScope.active_grade || '5').trim() || '5';
	          el.textContent = 'Academy context: ' + displayName + ' · Grade ' + grade;
	        } else {
	          el.textContent = 'Chat activo: Eugene (' + MODEL_LABEL + ')';
	        }
	      }
	      var homeModel = document.getElementById('home-model-status');
	      if (homeModel) homeModel.textContent = MODEL_LABEL;
    } catch (e) {}
  }
	  window.nexusRefreshModelIndicator = setIndicator;

	  function refreshModelLabel() {
	    try {
	      fetch('/api/agents/model/status?session_id=nexus_browser', { cache: 'no-store', headers: authHeaders() })
	        .then(function (r) { return r.ok ? r.json() : null; })
	        .then(function (data) {
	          if (!data) return;
	          MODEL_STATUS = data;
	          var sm = data.active_model || data.chat_model || '';
	          if (typeof sm === 'string' && sm.trim()) {
	            MODEL_LABEL = sm.trim();
	            setIndicator();
	          }
	          renderModelSwitch(data);
	        })
	        .catch(function () {
	          fetch('/api/agents/capabilities?session_id=nexus_browser', { cache: 'no-store', headers: authHeaders() })
	            .then(function (r) { return r.ok ? r.json() : null; })
	            .then(function (data) {
	              if (!data) return;
	              var sm = data.single_model || '';
	              if (typeof sm === 'string' && sm.trim()) {
	                MODEL_LABEL = sm.trim();
	                setIndicator();
	              }
	            })
	            .catch(function () {});
	        });
	    } catch (e) {}
	  }

  function forceLocalProvider() {
    try {
      var sel = document.getElementById('agents-provider-select');
      if (!sel) return;
      // If the option exists, select it.
      var hasLocal = false;
      for (var i = 0; i < sel.options.length; i++) {
        if (sel.options[i].value === 'local_ollama') { hasLocal = true; break; }
      }
      if (hasLocal) {
        sel.value = 'local_ollama';
        try { sel.dispatchEvent(new Event('change', { bubbles: true })); } catch (e) {}
      }
	    } catch (e) {}
	  }

	  function ensureModelSwitchPanel() {
	    try {
	      if (document.getElementById('nexus-eugene-model-switch')) return;
	      var host = document.querySelector('.agents-launcher-col');
	      if (!host) return;
	      var panel = document.createElement('div');
	      panel.id = 'nexus-eugene-model-switch';
	      panel.innerHTML =
	        '<div class="nexus-model-title">Eugene chat provider/model</div>' +
	        '<div class="nexus-model-row">' +
	          '<select id="nexus-eugene-model-select"></select>' +
	          '<button class="pill-btn primary" id="btn-nexus-eugene-model-apply">Use for chat</button>' +
	          '<button class="pill-btn" id="btn-nexus-eugene-model-load">Load model</button>' +
	          '<button class="pill-btn" id="btn-nexus-eugene-model-health">Health</button>' +
	          '<button class="pill-btn" id="btn-nexus-eugene-model-test">Test prompt</button>' +
	        '</div>' +
	        '<div class="nexus-model-status" id="nexus-eugene-model-status">Loading model status...</div>';
	      host.insertBefore(panel, host.firstChild);
	      var modelSelect = document.getElementById('nexus-eugene-model-select');
	      var apply = document.getElementById('btn-nexus-eugene-model-apply');
	      var load = document.getElementById('btn-nexus-eugene-model-load');
	      var health = document.getElementById('btn-nexus-eugene-model-health');
	      var test = document.getElementById('btn-nexus-eugene-model-test');
	      if (modelSelect) {
	        modelSelect.addEventListener('change', function () {
	          var model = selectedModel();
	          setModelSwitchStatus(
	            'Selected: ' + model + '\n' +
	            'Click Use for chat to switch Eugene.\n' +
	            'Click Load/Test to warm local models or verify OpenAI on demand.'
	          );
	        });
	      }
	      if (apply) apply.addEventListener('click', selectEugeneModel);
	      if (load) load.addEventListener('click', loadEugeneModel);
	      if (health) health.addEventListener('click', refreshModelLabel);
	      if (test) test.addEventListener('click', testEugeneModel);
	      if (MODEL_STATUS) renderModelSwitch(MODEL_STATUS);
	    } catch (e) {}
	  }

	  function setModelSwitchStatus(text) {
	    try {
	      var out = document.getElementById('nexus-eugene-model-status');
	      if (out) out.textContent = text || '';
	    } catch (e) {}
	  }

	  function renderModelSwitch(data) {
	    try {
	      ensureModelSwitchPanel();
	      var sel = document.getElementById('nexus-eugene-model-select');
	      if (!sel || !data) return;
	      var active = data.active_model || data.chat_model || '';
	      var options = Array.isArray(data.options) ? data.options : [];
	      options = options.filter(function (item) { return item && item.model !== 'qwen3.5:9b'; });
	      sel.innerHTML = '';
	      options.forEach(function (item) {
	        var opt = document.createElement('option');
	        opt.value = item.model || '';
	        var suffix = '';
	        if (!item.installed) suffix = item.provider === 'openai' ? ' (API key missing)' : ' (not installed)';
	        opt.textContent = (item.label || item.model || 'model') + suffix;
	        opt.disabled = !item.installed;
	        if (item.model === active) opt.selected = true;
	        sel.appendChild(opt);
	      });
	      var installed = (data.installed_qwen35_models || []).join(', ') || 'none';
	      var loaded = (data.loaded_models || []).join(', ') || 'none';
	      var openai = data.openai || {};
	      setModelSwitchStatus(
	        'Active: ' + (active || '-') + '\n' +
	        'Provider: ' + (data.active_provider || '-') + '\n' +
	        'Vision: ' + (data.vision_model || '-') + '\n' +
	        'Installed Qwen/QN 3.5: ' + installed + '\n' +
	        'Loaded: ' + loaded + '\n' +
	        'OpenAI API: ' + (openai.configured ? 'configured' : 'missing')
	      );
	    } catch (e) {}
	  }

	  function selectedModel() {
	    var sel = document.getElementById('nexus-eugene-model-select');
	    return sel && sel.value ? sel.value : MODEL_LABEL;
	  }

	  function selectEugeneModel() {
	    try {
	      var model = selectedModel();
	      if (!model) return;
	      setModelSwitchStatus('Switching Eugene to ' + model + '...');
	      fetch('/api/agents/model/select', {
	        method: 'POST',
	        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
	        body: JSON.stringify({ session_id: 'nexus_browser', model: model })
	      })
	        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); })
	        .then(function (res) {
	          if (!res.ok || !res.data || !res.data.ok) throw new Error((res.data && (res.data.detail || res.data.error)) || ('HTTP ' + res.status));
	          MODEL_STATUS = res.data;
	          MODEL_LABEL = res.data.active_model || model;
	          renderModelSwitch(res.data);
	          setIndicator();
	          setModelSwitchStatus('Active for chat: ' + MODEL_LABEL + '\nProvider: ' + (res.data.active_provider || '-'));
	        })
	        .catch(function (err) { setModelSwitchStatus('Model switch failed: ' + (err.message || err)); });
	    } catch (e) {}
	  }

	  function loadEugeneModel() {
	    try {
	      var model = selectedModel();
	      if (!model) return;
	      setModelSwitchStatus('Switching Eugene Chat to ' + model + '...');
	      fetch('/api/agents/model/select', {
	        method: 'POST',
	        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
	        body: JSON.stringify({ session_id: 'nexus_browser', model: model })
	      })
	        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); })
	        .then(function (res) {
	          if (!res.ok || !res.data || !res.data.ok) throw new Error((res.data && (res.data.detail || res.data.error)) || ('HTTP ' + res.status));
	          MODEL_STATUS = res.data;
	          MODEL_LABEL = res.data.active_model || model;
	          renderModelSwitch(res.data);
	          setIndicator();
	          if (String(model).indexOf('openai:') === 0) {
	            setModelSwitchStatus('Active for chat: ' + MODEL_LABEL + '\nTesting OpenAI on demand...');
	            return fetch('/api/agents/model/test', {
	              method: 'POST',
	              headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
	              body: JSON.stringify({
	                session_id: 'nexus_browser',
	                model: model,
	                prompt: 'Responde solo: OK_OPENAI'
	              })
	            }).then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); });
	          }
	          if (String(model).indexOf(':cloud') >= 0) {
	            setModelSwitchStatus('Active for chat: ' + MODEL_LABEL + '\nCloud model does not need local loading.');
	            return null;
	          }
	          setModelSwitchStatus('Loading ' + model + ' into Ollama...\nFirst local load can take a while.');
	          return fetch('/api/agents/model/test', {
	            method: 'POST',
	            headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
	            body: JSON.stringify({
	              session_id: 'nexus_browser',
	              model: model,
	              prompt: 'Responde solo: OK_QWEN35'
	            })
	          }).then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); });
	        })
	        .then(function (res) {
	          if (!res) return;
	          if (!res.ok || !res.data || !res.data.ok) throw new Error((res.data && (res.data.detail || res.data.error)) || ('HTTP ' + res.status));
	          MODEL_STATUS = res.data.status || MODEL_STATUS;
	          if (MODEL_STATUS) renderModelSwitch(MODEL_STATUS);
	          setIndicator();
	          setModelSwitchStatus(
	            'Active for chat: ' + model + '\n' +
	            (String(model).indexOf('openai:') === 0 ? 'OpenAI API test: PASS\n' : 'Loaded in Ollama: yes\n') +
	            'First token: ' + (res.data.first_token_ms == null ? '-' : res.data.first_token_ms + ' ms') + '\n' +
	            'Total: ' + res.data.total_ms + ' ms'
	          );
	        })
	        .catch(function (err) { setModelSwitchStatus('Model load failed: ' + (err.message || err)); });
	    } catch (e) {}
	  }

	  function testEugeneModel() {
	    try {
	      var model = selectedModel();
	      setModelSwitchStatus('Testing ' + model + '...');
	      fetch('/api/agents/model/test', {
	        method: 'POST',
	        headers: Object.assign({ 'Content-Type': 'application/json' }, authHeaders()),
	        body: JSON.stringify({
	          session_id: 'nexus_browser',
	          model: model,
	          prompt: String(model).indexOf('openai:') === 0 ? 'Responde solo: OK_OPENAI' : 'Responde solo: OK_QWEN35'
	        })
	      })
	        .then(function (r) { return r.json().then(function (data) { return { ok: r.ok, status: r.status, data: data }; }); })
	        .then(function (res) {
	          if (!res.ok || !res.data || !res.data.ok) throw new Error((res.data && (res.data.detail || res.data.error)) || ('HTTP ' + res.status));
	          var data = res.data;
	          MODEL_STATUS = data.status || MODEL_STATUS;
	          if (MODEL_STATUS) renderModelSwitch(MODEL_STATUS);
	          setModelSwitchStatus(
	            'Test: PASS\n' +
	            'Model: ' + data.model + '\n' +
	            'First token: ' + (data.first_token_ms == null ? '-' : data.first_token_ms + ' ms') + '\n' +
	            'Total: ' + data.total_ms + ' ms\n' +
	            'Reply: ' + (data.reply || '(empty)')
	          );
	        })
	        .catch(function (err) { setModelSwitchStatus('Test failed: ' + (err.message || err)); });
	    } catch (e) {}
	  }

  function overrideChatPayload() {
    try {
      var f = window.currentChatPayloadConfig;
      if (typeof f !== 'function') return;
      if (f.__nexusOllamaOnlyPatched) return;

      function wrapped() {
        var p = f.apply(this, arguments) || {};
        try {
          p.agent_provider = String(MODEL_LABEL).indexOf('openai:') === 0 ? 'openai' : 'local_ollama';
          p.agent_model = MODEL_LABEL || '';
          p.agent_name = 'chat';
        } catch (e) {}
        return p;
      }
      wrapped.__nexusOllamaOnlyPatched = true;
      window.currentChatPayloadConfig = wrapped;
    } catch (e) {}
  }

	  // Run once, then keep it stable.
	  ensureModelSwitchPanel();
	  setIndicator();
	  refreshModelLabel();
	  forceLocalProvider();
	  overrideChatPayload();

  // Some parts of Jenny UI re-render labels; keep the indicator consistent.
	  try {
	    var mo = new MutationObserver(function () {
	      ensureModelSwitchPanel();
	      setIndicator();
	    });
	    mo.observe(document.body, { childList: true, subtree: true, characterData: true });
	  } catch (e) {}

  // Retry provider forcing shortly after load.
	  setTimeout(forceLocalProvider, 400);
	  setTimeout(forceLocalProvider, 1200);
	  setTimeout(overrideChatPayload, 400);
	  setTimeout(overrideChatPayload, 1200);
	  setTimeout(ensureModelSwitchPanel, 500);
	  setTimeout(refreshModelLabel, 800);
	  setTimeout(refreshModelLabel, 1800);
		})();
		`
  if (shouldInjectScript) doc.head.appendChild(script)
}

function injectDigitalMirrorLauncher(doc: Document) {
  try {
    const win = doc.defaultView as any
    const legacyIds = ['face-recognition-fab', 'face-recognition-modal', 'face-float-btn']
    legacyIds.forEach((id) => doc.getElementById(id)?.remove())

    win.showFaceRecognitionModal = () => {
      win.parent?.postMessage({ __nexusBrowser: true, type: 'nexus-open-digital-mirror' }, win.location.origin)
    }
    win.openFaceEnrollment = win.showFaceRecognitionModal
    win.enrollDeveloperFace = win.showFaceRecognitionModal
    win.toggleFaceMenu = win.showFaceRecognitionModal
    win.checkFaceStatus = () => undefined

    if (!doc.getElementById('nexus-digital-mirror-style')) {
      const style = doc.createElement('style')
      style.id = 'nexus-digital-mirror-style'
      style.textContent = `
        #nexus-digital-mirror-fab {
          position: fixed;
          right: max(18px, env(safe-area-inset-right));
          bottom: max(18px, env(safe-area-inset-bottom));
          z-index: 999999;
          display: inline-grid;
          place-items: center;
          width: 54px;
          height: 54px;
          border: 1px solid rgba(34, 211, 238, 0.42);
          border-radius: 999px;
          background: linear-gradient(135deg, #0891b2, #2563eb);
          color: #f8fafc;
          box-shadow: 0 16px 40px rgba(2, 6, 23, 0.44), 0 0 0 1px rgba(255,255,255,0.12) inset;
          cursor: pointer;
          font-size: 22px;
          -webkit-tap-highlight-color: transparent;
        }
        #nexus-digital-mirror-fab:hover {
          transform: translateY(-1px);
          box-shadow: 0 20px 48px rgba(2, 6, 23, 0.5), 0 0 22px rgba(34, 211, 238, 0.28);
        }
        #nexus-digital-mirror-fab svg {
          width: 26px;
          height: 26px;
        }
      `
      doc.head.appendChild(style)
    }

    if (!doc.getElementById('nexus-digital-mirror-fab')) {
      const button = doc.createElement('button')
      button.id = 'nexus-digital-mirror-fab'
      button.type = 'button'
      button.title = 'Digital Mirror'
      button.setAttribute('aria-label', 'Open Digital Mirror')
      button.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke-linecap="round" stroke-linejoin="round">
          <defs>
            <linearGradient id="nexusMirrorIconStrokeFallback" x1="4" y1="4" x2="20" y2="22" gradientUnits="userSpaceOnUse">
              <stop offset="0" stop-color="#67e8f9"></stop>
              <stop offset="0.52" stop-color="#c4b5fd"></stop>
              <stop offset="1" stop-color="#f0abfc"></stop>
            </linearGradient>
            <radialGradient id="nexusMirrorIconGlassFallback" cx="42%" cy="36%" r="62%">
              <stop offset="0" stop-color="#f8fbff" stop-opacity="0.9"></stop>
              <stop offset="0.42" stop-color="#a5f3fc" stop-opacity="0.3"></stop>
              <stop offset="1" stop-color="#312e81" stop-opacity="0.06"></stop>
            </radialGradient>
          </defs>
          <circle cx="11" cy="10" r="5.2" fill="url(#nexusMirrorIconGlassFallback)" stroke="url(#nexusMirrorIconStrokeFallback)" stroke-width="1.8"></circle>
          <path d="M14.6 13.8 18.2 18" stroke="url(#nexusMirrorIconStrokeFallback)" stroke-width="2.2"></path>
          <path d="M17.3 18.9 19.1 17.2" stroke="url(#nexusMirrorIconStrokeFallback)" stroke-width="2.2"></path>
          <path d="M17.8 4.2v2.2M16.7 5.3h2.2M19.9 8.1l.8 1.5 1.5.8-1.5.8-.8 1.5-.8-1.5-1.5-.8 1.5-.8.8-1.5Z" stroke="#f0abfc" stroke-width="1.25"></path>
          <path d="M8.3 7.8c.75-.9 1.7-1.35 2.85-1.35" stroke="#ffffff" stroke-opacity="0.65" stroke-width="1.2"></path>
        </svg>
      `
      button.addEventListener('click', () => {
        win.parent?.postMessage({ __nexusBrowser: true, type: 'nexus-open-digital-mirror' }, win.location.origin)
      })
      doc.body.appendChild(button)
    }
  } catch {
    // Best-effort UI cleanup only. The canonical launcher is the floating Hub bubble.
  }
}

function injectRoleOverlay(doc: Document) {
  const ROOT_OWNER_EMAIL = 'olaldemarsaul@gmail.com'
  const activeDemoPersona = String(window.localStorage.getItem(ACTIVE_DEMO_PERSONA_KEY) || '').trim().toLowerCase()
  const preserveDemoPersonaRole =
    NEXUS_PRODUCT_PROFILE === 'dual_demo' &&
    ['developer', 'admin', 'teacher', 'student', 'guest', 'academy'].includes(activeDemoPersona)

  const readEmail = () => {
    const email = String(
      window.localStorage.getItem('NEXUS_SESSION_EMAIL') ||
        window.localStorage.getItem('NEXUS_LAST_EMAIL') ||
        window.localStorage.getItem('NEXUS_EMAIL') ||
        window.localStorage.getItem('NEXUS_CLOUD_OWNER_EMAIL') ||
        ''
    )
      .trim()
      .toLowerCase()
    if (email) return email
    return ''
  }

  const readRole = () =>
    String(
      window.localStorage.getItem('NEXUS_SESSION_ROLE') ||
        window.localStorage.getItem('NEXUS_LAST_ROLE') ||
        ''
    )
      .trim()
      .toLowerCase() || 'external_guest'

  const applyModulesToDoc = (mods: Record<string, boolean> | null, role: string) => {
    const fallback: Record<string, Record<string, boolean>> = {
      owner: { files: false, shared_with_me: false, shared_by_me: false, classroom: false, teacher: false, admin: false, agents: true, institutional_index: true },
      root: { files: false, shared_with_me: false, shared_by_me: false, classroom: false, teacher: false, admin: false, agents: true, institutional_index: true },
      developer: { files: false, shared_with_me: false, shared_by_me: false, classroom: false, teacher: false, admin: false, agents: true, institutional_index: true },
      admin: { files: true, shared_with_me: true, shared_by_me: true, classroom: false, teacher: true, admin: true, agents: false, institutional_index: false },
      subprincipal_admin: { files: true, shared_with_me: true, shared_by_me: true, classroom: false, teacher: true, admin: true, agents: false, institutional_index: false },
      principal_admin: { files: true, shared_with_me: true, shared_by_me: true, classroom: false, teacher: true, admin: true, agents: false, institutional_index: false },
      principal: { files: true, shared_with_me: true, shared_by_me: true, classroom: false, teacher: true, admin: true, agents: false, institutional_index: false },
      manager: { files: true, shared_with_me: true, shared_by_me: true, classroom: true, teacher: true, admin: true, agents: false, institutional_index: false },
      supervisor: { files: true, shared_with_me: true, shared_by_me: true, classroom: true, teacher: true, admin: false, agents: false, institutional_index: false },
      teacher: { files: true, shared_with_me: true, shared_by_me: true, classroom: true, teacher: true, admin: false, agents: false, institutional_index: false },
      academy_student: { files: false, shared_with_me: true, shared_by_me: false, classroom: true, teacher: false, admin: false, agents: false, institutional_index: false },
      student: { files: false, shared_with_me: true, shared_by_me: false, classroom: true, teacher: false, admin: false, agents: false, institutional_index: false },
      parent_guest: { files: false, shared_with_me: true, shared_by_me: false, classroom: false, teacher: false, admin: false, agents: false, institutional_index: false },
      trusted_guest: { files: false, shared_with_me: true, shared_by_me: false, classroom: false, teacher: false, admin: false, agents: false, institutional_index: false },
      invited: { files: false, shared_with_me: true, shared_by_me: true, classroom: false, teacher: false, admin: false, agents: false, institutional_index: false },
      external_guest: { files: false, shared_with_me: true, shared_by_me: true, classroom: false, teacher: false, admin: false, agents: false, institutional_index: false },
      guest: { files: false, shared_with_me: true, shared_by_me: true, classroom: false, teacher: false, admin: false, agents: false, institutional_index: false }
    }

    const roleBase = fallback[role] || fallback.external_guest || {}
    const policyBase = mods || roleBase
    // In dual-demo mode the authenticated owner remains the authority, but the
    // selected persona is the UI ceiling. Owner policy must never widen a
    // Student/Teacher/Admin/Guest demonstration surface.
    const effectiveBase = preserveDemoPersonaRole
      ? Object.fromEntries(
          Object.keys(roleBase).map((key) => [key, !!roleBase[key] && policyBase[key] !== false])
        )
      : policyBase
    const effective =
      role === 'guest' || role === 'invited' || role === 'external_guest'
        ? { ...effectiveBase, shared_with_me: true, shared_by_me: true }
        : role === 'parent_guest' || role === 'trusted_guest'
          ? { ...effectiveBase, shared_with_me: true, shared_by_me: false }
        : effectiveBase
    const map: Record<string, string[]> = {
      files: ['btn-mirror-files'],
      shared_with_me: ['btn-mirror-shared'],
      shared_by_me: ['btn-mirror-shared-by'],
      classroom: ['btn-mirror-classroom'],
      teacher: ['btn-mirror-teacher'],
      admin: ['btn-mirror-admin'],
      agents: ['btn-mirror-agents'],
      institutional_index: ['btn-mirror-ii']
    }

    Object.entries(map).forEach(([key, ids]) => {
      const visible = !!effective[key]
      ids.forEach((id) => {
        const el = doc.getElementById(id) as HTMLElement | null
        if (!el) return
        el.hidden = !visible
        el.removeAttribute('data-role-hidden')
        el.classList.remove('is-hidden')
        el.style.setProperty('display', visible ? 'inline-flex' : 'none', 'important')
        el.style.setProperty('visibility', visible ? 'visible' : 'hidden', 'important')
        el.style.setProperty('opacity', visible ? '1' : '0', 'important')
      })
    })
  }

  const forceDeveloperSupportButtons = () => {
    const allowedIds = ['btn-mirror-ii', 'btn-mirror-agents', 'btn-mirror-expand']
    const blockedIds = [
      'btn-mirror-files',
      'btn-mirror-shared',
      'btn-mirror-shared-by',
      'btn-mirror-classroom',
      'btn-mirror-teacher',
      'btn-mirror-admin'
    ]
    allowedIds.forEach((id) => {
      const el = doc.getElementById(id) as HTMLElement | null
      if (!el) return
      el.hidden = false
      el.removeAttribute('data-role-hidden')
      el.classList.remove('is-hidden')
      el.style.setProperty('display', 'inline-flex', 'important')
      el.style.setProperty('visibility', 'visible', 'important')
      el.style.setProperty('opacity', '1', 'important')
    })
    blockedIds.forEach((id) => {
      const el = doc.getElementById(id) as HTMLElement | null
      if (!el) return
      el.hidden = true
      el.setAttribute('data-role-hidden', '1')
      el.classList.add('is-hidden')
      el.style.setProperty('display', 'none', 'important')
      el.style.setProperty('visibility', 'hidden', 'important')
      el.style.setProperty('opacity', '0', 'important')
    })
  }

  const forceGuestStableButtons = () => {
    const role = readRole()
    const isParentGuest = role === 'parent_guest' || role === 'trusted_guest'
    if (doc.body) {
      doc.body.dataset.roleAccess = isParentGuest ? 'parent_guest' : (role || 'guest')
    }
    const showIds = isParentGuest ? ['btn-mirror-shared'] : ['btn-mirror-shared', 'btn-mirror-shared-by']
    const hideIds = [
      ...(isParentGuest ? ['btn-mirror-shared-by'] : []),
      'btn-mirror-my-work',
      'btn-doc-new-sheet',
      'btn-mirror-image-studio',
      ...(!isParentGuest ? ['btn-mirror-vbox', 'btn-institutional-messages'] : []),
      'btn-mirror-files',
      'btn-mirror-classroom',
      'btn-mirror-teacher',
      'btn-mirror-admin',
      'btn-mirror-agents',
      'btn-mirror-ii'
    ]
    showIds.forEach((id) => {
      const el = doc.getElementById(id) as HTMLElement | null
      if (!el) return
      el.hidden = false
      el.removeAttribute('data-role-hidden')
      el.classList.remove('is-hidden')
      el.style.setProperty('display', 'inline-flex', 'important')
      el.style.setProperty('visibility', 'visible', 'important')
      el.style.setProperty('opacity', '1', 'important')
    })
    hideIds.forEach((id) => {
      const el = doc.getElementById(id) as HTMLElement | null
      if (!el) return
      el.hidden = true
      el.setAttribute('data-role-hidden', '1')
      el.classList.add('is-hidden')
      el.style.setProperty('display', 'none', 'important')
      el.style.setProperty('visibility', 'hidden', 'important')
      el.style.setProperty('opacity', '0', 'important')
    })
  }

  const refreshRoleUI = async () => {
    const email = readEmail()
    const role = readRole()
    if (!email) {
      applyModulesToDoc(null, role)
      return
    }
    if (role === 'guest' || role === 'invited' || role === 'external_guest' || role === 'parent_guest' || role === 'trusted_guest') {
      forceGuestStableButtons()
      return
    }
    if (
      email === ROOT_OWNER_EMAIL &&
      (role === 'owner' || role === 'root' || role === 'developer')
    ) {
      forceDeveloperSupportButtons()
    }
    try {
      const qs = new URLSearchParams({
        email,
        role,
        developer_mode: '0',
        device_trusted: '1',
        stepup_token: ''
      })
      const base = window.location.protocol === 'file:' ? 'http://127.0.0.1:2727' : ''
      const resp = await fetch(base + '/qbridge/policy/me?' + qs.toString())
      const data = await resp.json().catch(() => ({}))
      if (data && data.ok) {
        const authorityRole = String(data.role || role).trim().toLowerCase()
        if (data.role) {
          try {
            window.localStorage.setItem('NEXUS_AUTHORITY_ROLE', authorityRole)
            if (!preserveDemoPersonaRole) {
              window.localStorage.setItem('NEXUS_SESSION_ROLE', authorityRole)
            }
          } catch {}
        }
        const interfaceRole = preserveDemoPersonaRole ? role : authorityRole
        applyModulesToDoc((data.ui_modules || null) as Record<string, boolean> | null, interfaceRole)
        if (interfaceRole === 'owner' || interfaceRole === 'root' || interfaceRole === 'developer') {
          forceDeveloperSupportButtons()
        }
        return
      }
    } catch {}
    applyModulesToDoc(null, role)
    if (role === 'owner' || role === 'root' || role === 'developer') {
      forceDeveloperSupportButtons()
    }
  }

  refreshRoleUI()
  window.setTimeout(refreshRoleUI, 500)
  window.setTimeout(refreshRoleUI, 1500)
  window.setTimeout(refreshRoleUI, 3000)
  let ownerPulse = 0
  const ownerTimer = window.setInterval(function () {
    ownerPulse += 1
    const role = readRole()
    if (role === 'owner' || role === 'root' || role === 'developer') {
      forceDeveloperSupportButtons()
    }
    if (ownerPulse >= 20) {
      window.clearInterval(ownerTimer)
    }
  }, 500)
  let guestPulse = 0
  const guestTimer = window.setInterval(function () {
    guestPulse += 1
    const role = readRole()
    if (role === 'guest' || role === 'invited' || role === 'external_guest' || role === 'parent_guest' || role === 'trusted_guest') {
      forceGuestStableButtons()
    }
    if (guestPulse >= 12) {
      window.clearInterval(guestTimer)
    }
  }, 350)

  const existingObserver = (doc.defaultView as any)?.__nexusRoleUiObserver
  if (!existingObserver && doc.body) {
    let roleRefreshQueued = false
    const obs = new MutationObserver(() => {
      if (roleRefreshQueued) return
      roleRefreshQueued = true
      window.setTimeout(() => {
        roleRefreshQueued = false
        const role = readRole()
        if (role === 'guest' || role === 'invited' || role === 'external_guest' || role === 'parent_guest' || role === 'trusted_guest') {
          return
        }
        refreshRoleUI()
      }, 120)
    })
    obs.observe(doc.body, { childList: true, subtree: true })
    ;(doc.defaultView as any).__nexusRoleUiObserver = obs
  }

  let style = doc.getElementById(ROLE_OVERLAY_STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = doc.createElement('style')
    style.id = ROLE_OVERLAY_STYLE_ID
    doc.head.appendChild(style)
  }

  style.textContent = `
    [data-role-hidden="1"] {
      display: none !important;
    }
    body[data-role-access="guest"] #btn-mirror-shared,
    body[data-role-access="guest"] #btn-mirror-shared-by,
    body[data-role-access="invited"] #btn-mirror-shared,
    body[data-role-access="invited"] #btn-mirror-shared-by,
    body[data-role-access="external_guest"] #btn-mirror-shared,
    body[data-role-access="external_guest"] #btn-mirror-shared-by,
    body[data-role-access="trusted_guest"] #btn-mirror-shared,
    body[data-role-access="parent_guest"] #btn-mirror-shared {
      display: inline-flex !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
    body[data-role-access="elder_limmerloop"] #btn-mirror-shared,
    body[data-role-access="elder_limmerloop"] #btn-mirror-shared-by {
      display: inline-flex !important;
      visibility: visible !important;
      opacity: 1 !important;
    }
    body[data-role-access="guest"] #btn-mirror-files,
    body[data-role-access="guest"] #btn-mirror-classroom,
    body[data-role-access="guest"] #btn-mirror-teacher,
    body[data-role-access="guest"] #btn-mirror-admin,
    body[data-role-access="guest"] #btn-mirror-agents,
    body[data-role-access="guest"] #btn-mirror-ii,
    body[data-role-access="invited"] #btn-mirror-files,
    body[data-role-access="invited"] #btn-mirror-classroom,
    body[data-role-access="invited"] #btn-mirror-teacher,
    body[data-role-access="invited"] #btn-mirror-admin,
    body[data-role-access="invited"] #btn-mirror-agents,
    body[data-role-access="invited"] #btn-mirror-ii,
    body[data-role-access="external_guest"] #btn-mirror-files,
    body[data-role-access="external_guest"] #btn-mirror-classroom,
    body[data-role-access="external_guest"] #btn-mirror-teacher,
    body[data-role-access="external_guest"] #btn-mirror-admin,
    body[data-role-access="external_guest"] #btn-mirror-agents,
    body[data-role-access="external_guest"] #btn-mirror-ii,
    body[data-role-access="trusted_guest"] #btn-mirror-shared-by,
    body[data-role-access="trusted_guest"] #btn-mirror-files,
    body[data-role-access="trusted_guest"] #btn-mirror-classroom,
    body[data-role-access="trusted_guest"] #btn-mirror-teacher,
    body[data-role-access="trusted_guest"] #btn-mirror-admin,
    body[data-role-access="trusted_guest"] #btn-mirror-agents,
    body[data-role-access="trusted_guest"] #btn-mirror-ii,
    body[data-role-access="parent_guest"] #btn-mirror-shared-by,
    body[data-role-access="parent_guest"] #btn-mirror-files,
    body[data-role-access="parent_guest"] #btn-mirror-classroom,
    body[data-role-access="parent_guest"] #btn-mirror-teacher,
    body[data-role-access="parent_guest"] #btn-mirror-admin,
    body[data-role-access="parent_guest"] #btn-mirror-agents,
    body[data-role-access="parent_guest"] #btn-mirror-ii,
    body[data-role-access="elder_limmerloop"] #btn-mirror-files,
    body[data-role-access="elder_limmerloop"] #btn-mirror-classroom,
    body[data-role-access="elder_limmerloop"] #btn-mirror-teacher,
    body[data-role-access="elder_limmerloop"] #btn-mirror-admin,
    body[data-role-access="elder_limmerloop"] #btn-mirror-agents,
    body[data-role-access="elder_limmerloop"] #btn-mirror-ii {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
    }
  `

  let script = doc.getElementById(ROLE_OVERLAY_SCRIPT_ID) as HTMLScriptElement | null
  const shouldInjectScript = !script
  if (!script) {
    script = doc.createElement('script')
    script.id = ROLE_OVERLAY_SCRIPT_ID
    script.type = 'text/javascript'
  }

  script.text = String.raw`
  (function () {
    if (window.__nexusRoleOverlay) return;
    window.__nexusRoleOverlay = true;

    const PRESERVE_DEMO_PERSONA_ROLE = ${preserveDemoPersonaRole ? 'true' : 'false'};

    const BTN_MAP = {
      files: ['btn-mirror-files'],
      shared_with_me: ['btn-mirror-shared'],
      shared_by_me: ['btn-mirror-shared-by'],
      classroom: ['btn-mirror-classroom'],
      teacher: ['btn-mirror-teacher'],
      admin: ['btn-mirror-admin'],
      agents: ['btn-mirror-agents'],
      institutional_index: ['btn-mirror-ii']
    };

	    const ROLE_FALLBACK = {
	      owner: { files:true, shared_with_me:true, shared_by_me:true, classroom:true, teacher:true, admin:true, agents:true, institutional_index:true },
	      admin: { files:true, shared_with_me:true, shared_by_me:true, classroom:true, teacher:true, admin:true, agents:true, institutional_index:true },
      manager: { files:true, shared_with_me:true, shared_by_me:true, classroom:true, teacher:true, admin:true, agents:false, institutional_index:false },
      supervisor: { files:true, shared_with_me:true, shared_by_me:true, classroom:true, teacher:true, admin:false, agents:false, institutional_index:false },
      teacher: { files:true, shared_with_me:true, shared_by_me:true, classroom:true, teacher:true, admin:false, agents:false, institutional_index:false },
	      staff: { files:true, shared_with_me:true, shared_by_me:false, classroom:false, teacher:false, admin:false, agents:false, institutional_index:false },
	      member: { files:true, shared_with_me:true, shared_by_me:false, classroom:false, teacher:false, admin:false, agents:false, institutional_index:false },
	      academy_student: { files:false, shared_with_me:true, shared_by_me:false, classroom:true, teacher:false, admin:false, agents:false, institutional_index:false },
	      student: { files:false, shared_with_me:true, shared_by_me:false, classroom:true, teacher:false, admin:false, agents:false, institutional_index:false },
      parent_guest: { files:false, shared_with_me:true, shared_by_me:false, classroom:false, teacher:false, admin:false, agents:false, institutional_index:false },
      elder_limmerloop: { files:false, shared_with_me:true, shared_by_me:true, classroom:false, teacher:false, admin:false, agents:false, institutional_index:false },
      trusted_guest: { files:false, shared_with_me:true, shared_by_me:false, classroom:false, teacher:false, admin:false, agents:false, institutional_index:false },
      invited: { files:false, shared_with_me:true, shared_by_me:true, classroom:false, teacher:false, admin:false, agents:false, institutional_index:false },
      external_guest: { files:false, shared_with_me:true, shared_by_me:true, classroom:false, teacher:false, admin:false, agents:false, institutional_index:false },
      guest: { files:false, shared_with_me:true, shared_by_me:true, classroom:false, teacher:false, admin:false, agents:false, institutional_index:false }
    };

	    function getSessionRole() {
	      const role = String(localStorage.getItem('NEXUS_SESSION_ROLE') || localStorage.getItem('NEXUS_LAST_ROLE') || '').trim().toLowerCase();
	      return role || 'external_guest';
	    }

    const ROOT_OWNER_EMAIL = 'olaldemarsaul@gmail.com';

    function getSessionEmail() {
      let email = String(
        localStorage.getItem('NEXUS_SESSION_EMAIL') ||
          localStorage.getItem('NEXUS_LAST_EMAIL') ||
          localStorage.getItem('NEXUS_EMAIL') ||
          localStorage.getItem('NEXUS_CLOUD_OWNER_EMAIL') ||
          ''
      ).trim().toLowerCase();

      if (!email) {
        email = '';
      }

      return email;
    }

    function getSessionId() {
      let sid = String(localStorage.getItem('NEXUS_SESSION_ID') || '').trim();
      if (!sid) {
        sid = 'sess_' + Math.random().toString(36).slice(2) + Date.now().toString(36);
        try { localStorage.setItem('NEXUS_SESSION_ID', sid); } catch (e) {}
      }
      return sid;
    }

    function buildHeaders() {
      const h = {};
      const email = getSessionEmail();
      const role = getSessionRole();
      if (email) h['x-user-email'] = email;
      if (role) h['x-user-role'] = role;
      h['x-developer-mode'] = '0';
      h['x-device-trusted'] = '1';
      h['x-session-id'] = getSessionId();
      return h;
    }

	    function applyModules(mods, role) {
	      const normalizedRole = String(role || getSessionRole() || 'external_guest').trim().toLowerCase();
      const fallback = ROLE_FALLBACK[normalizedRole] || ROLE_FALLBACK.external_guest;
      const policyBase = mods || fallback;
      const effectiveBase = PRESERVE_DEMO_PERSONA_ROLE
        ? Object.keys(fallback).reduce(function (acc, key) {
            acc[key] = !!fallback[key] && policyBase[key] !== false;
            return acc;
          }, {})
        : policyBase;
      const isGuestLikeRole = normalizedRole === 'guest' || normalizedRole === 'invited' || normalizedRole === 'external_guest' || normalizedRole === 'parent_guest' || normalizedRole === 'trusted_guest';
      document.body.dataset.roleAccess = normalizedRole || 'external_guest';
      const stabilized = (normalizedRole === 'guest' || normalizedRole === 'invited' || normalizedRole === 'external_guest')
        ? { ...effectiveBase, shared_with_me: true, shared_by_me: true }
        : (normalizedRole === 'parent_guest' || normalizedRole === 'trusted_guest')
          ? { ...effectiveBase, shared_with_me: true, shared_by_me: false }
        : effectiveBase;
      Object.keys(BTN_MAP).forEach((key) => {
        const visible = key in stabilized ? !!stabilized[key] : false;
        BTN_MAP[key].forEach((id) => {
          const el = document.getElementById(id);
          if (!el) return;
          if (!visible) {
            el.setAttribute('data-role-hidden', '1');
          } else {
            el.removeAttribute('data-role-hidden');
          }
        });
      });
      if (isGuestLikeRole) {
        forceGuestStableButtons();
      }
    }

    function forceGuestStableButtons() {
      const role = getSessionRole();
      const isParentGuest = role === 'parent_guest' || role === 'trusted_guest';
      document.body.dataset.roleAccess = isParentGuest ? 'parent_guest' : (role || 'guest');
      (isParentGuest ? ['btn-mirror-shared'] : ['btn-mirror-shared', 'btn-mirror-shared-by']).forEach(function(id) {
        const el = document.getElementById(id);
        if (!el) return;
        el.removeAttribute('data-role-hidden');
        el.hidden = false;
        el.style.setProperty('display', 'inline-flex', 'important');
        el.style.setProperty('visibility', 'visible', 'important');
        el.style.setProperty('opacity', '1', 'important');
      });
      (isParentGuest
        ? ['btn-mirror-shared-by', 'btn-mirror-my-work', 'btn-doc-new-sheet', 'btn-mirror-image-studio', 'btn-mirror-files', 'btn-mirror-classroom', 'btn-mirror-teacher', 'btn-mirror-admin', 'btn-mirror-agents', 'btn-mirror-ii']
        : ['btn-mirror-my-work', 'btn-doc-new-sheet', 'btn-mirror-image-studio', 'btn-mirror-vbox', 'btn-institutional-messages', 'btn-mirror-files', 'btn-mirror-classroom', 'btn-mirror-teacher', 'btn-mirror-admin', 'btn-mirror-agents', 'btn-mirror-ii']
      ).forEach(function(id) {
        const el = document.getElementById(id);
        if (!el) return;
        el.setAttribute('data-role-hidden', '1');
        el.hidden = true;
        el.style.setProperty('display', 'none', 'important');
        el.style.setProperty('visibility', 'hidden', 'important');
        el.style.setProperty('opacity', '0', 'important');
      });
    }

    function forceGuestStableButtonsIfNeeded() {
      const role = getSessionRole();
      if (role === 'guest' || role === 'invited' || role === 'external_guest' || role === 'parent_guest' || role === 'trusted_guest') {
        forceGuestStableButtons();
      }
    }

	    async function fetchPolicy() {
	      const email = getSessionEmail();
	      if (!email) {
        applyModules(null, getSessionRole());
        return;
      }
      const role = getSessionRole();
      if (role === 'guest' || role === 'invited' || role === 'external_guest' || role === 'parent_guest' || role === 'trusted_guest') {
        applyModules(null, role);
        forceGuestStableButtons();
        return;
      }
      const qs = new URLSearchParams({
        email: email,
        role: role || 'external_guest',
        developer_mode: '0',
        device_trusted: '1',
        stepup_token: ''
      });
      try {
        const base = window.location.protocol === 'file:' ? 'http://127.0.0.1:2727' : '';
        const url = base + '/qbridge/policy/me?' + qs.toString();
        const resp = await fetch(url, { headers: buildHeaders() });
        const data = await resp.json().catch(() => ({}));
        if (data && data.ok) {
          if (data.role) {
            try {
              localStorage.setItem('NEXUS_AUTHORITY_ROLE', String(data.role));
              if (!PRESERVE_DEMO_PERSONA_ROLE) localStorage.setItem('NEXUS_SESSION_ROLE', String(data.role));
            } catch (e) {}
          }
          applyModules(data.ui_modules || null, PRESERVE_DEMO_PERSONA_ROLE ? role : (data.role || role));
          return;
        }
      } catch (e) {}
      applyModules(null, role);
    }

	    async function adminCreateOverride(ev) {
	      const target = ev && ev.target ? ev.target.closest('[data-admin-create]') : null;
      if (!target) return;
      ev.preventDefault();
      ev.stopPropagation();
      if (target.__nexusRoleOverride) return;
      target.__nexusRoleOverride = true;

      const name = window.prompt('Nombre del perfil:', 'Nuevo Usuario');
      if (!name) return;
      const email = window.prompt('Correo del perfil:', '');
      if (!email || !email.includes('@')) {
        alert('Correo invalido.');
        return;
      }
      const role = (window.prompt('Rol (admin/teacher/student/invited/manager/staff/member):', 'invited') || 'invited').trim().toLowerCase();

      const headers = Object.assign({ 'Content-Type': 'application/json' }, buildHeaders());
      const base = '/api/qbridge';

      try {
        if (role === 'invited' || role === 'guest' || role === 'external_guest') {
          const resp = await fetch(base + '/auth/create_invite', {
            method: 'POST',
            headers,
            body: JSON.stringify({ email: email.trim().toLowerCase(), name: name.trim(), role: 'invited' })
          });
          const data = await resp.json().catch(() => ({}));
          if (!resp.ok || !data.ok) throw new Error(data.error || ('HTTP ' + resp.status));
          alert('Invitacion creada. Token:\n' + (data.invite_token || ''));
          return;
        }

        const resp = await fetch(base + '/admin/users/create', {
          method: 'POST',
          headers,
          body: JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase(), role })
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || !data.ok) throw new Error(data.error || ('HTTP ' + resp.status));
        alert('Perfil creado: ' + email.trim().toLowerCase() + ' (' + role + ')');
      } catch (e) {
        alert('No se pudo crear perfil: ' + (e && e.message ? e.message : e));
      } finally {
        target.__nexusRoleOverride = false;
      }
    }

    function openGuestSafeDefault() {
      const role = getSessionRole();
      if (!(role === 'guest' || role === 'invited' || role === 'external_guest' || role === 'parent_guest' || role === 'trusted_guest')) return;
      const home = document.getElementById('btn-mirror-home');
      const mirrorHome = document.getElementById('mirror-home');
      const mirrorContent = document.getElementById('mirror-content');
      const mirrorFiles = document.getElementById('mirror-files');
      forceGuestStableButtons();
      try {
        if (mirrorHome && mirrorContent) {
          mirrorHome.style.setProperty('display', 'block', 'important');
          mirrorContent.style.setProperty('display', 'none', 'important');
          if (mirrorFiles) mirrorFiles.style.setProperty('display', 'none', 'important');
          return;
        }
        if (home && typeof home.click === 'function') home.click();
      } catch (e) {}
    }

	    function applySessionSync(payload) {
	      if (!payload) return;
	      try {
	        var productMode = String(payload.product_mode || '').trim().toLowerCase();
	        if (productMode === 'school' || productMode === 'academy') {
	          localStorage.setItem('NEXUS_ACTIVE_PRODUCT_MODE', productMode);
	        }
	        if (productMode === 'school') {
	          if (String(localStorage.getItem('NEXUS_TENANT_TYPE') || '').trim().toLowerCase() === 'academy') localStorage.removeItem('NEXUS_TENANT_TYPE');
	          if (String(localStorage.getItem('NEXUS_OVERLAY') || '').trim().toLowerCase().indexOf('academy') === 0) localStorage.removeItem('NEXUS_OVERLAY');
	          if (String(localStorage.getItem('NEXUS_ACTIVE_OVERLAY') || '').trim().toLowerCase().indexOf('academy') === 0) localStorage.removeItem('NEXUS_ACTIVE_OVERLAY');
	          localStorage.removeItem('NEXUS_ACADEMY_DEMO_PROFILE');
	          localStorage.removeItem('NEXUS_ACADEMY_MULTIGRADE_ENABLED');
	          localStorage.removeItem('NEXUS_ACADEMY_PROFILE');
	        }
	        if (payload.tenant_type === 'academy') {
	          localStorage.setItem('NEXUS_TENANT_TYPE', 'academy');
	          localStorage.setItem('NEXUS_DELIVERY_MODEL', String(payload.delivery_model || 'virtual_tutor_only'));
	          localStorage.setItem('NEXUS_STUDENT_PORTAL_ONLY', '1');
	          localStorage.setItem('NEXUS_HUMAN_TEACHER_ACCESS', '0');
	          localStorage.setItem('NEXUS_VIRTUAL_TEACHERS_ENABLED', '1');
	          localStorage.setItem('NEXUS_OVERLAY', 'academy');
	          localStorage.setItem('NEXUS_ACTIVE_OVERLAY', 'academy');
	          localStorage.setItem('NEXUS_KIOSK_POLICY', String(payload.kiosk_policy || 'academy_student_kiosk'));
	          localStorage.removeItem('NEXUS_DEMO_CONTROL');
	        }
	        if (payload.email) {
          localStorage.setItem('NEXUS_SESSION_EMAIL', String(payload.email));
          localStorage.setItem('NEXUS_LAST_EMAIL', String(payload.email));
        }
        if (payload.name) {
          localStorage.setItem('NEXUS_SESSION_NAME', String(payload.name));
          localStorage.setItem('NEXUS_USER_NAME', String(payload.name));
        }
	        if (payload.role || payload.tenant_type === 'academy') {
	          const nextRole = payload.tenant_type === 'academy' ? 'student' : String(payload.role);
	          localStorage.setItem('NEXUS_SESSION_ROLE', nextRole);
	          localStorage.setItem('NEXUS_LAST_ROLE', nextRole);
        }
        if (payload.session_lease) {
          localStorage.setItem('NEXUS_SESSION_LEASE', String(payload.session_lease));
        }
        const email = String(localStorage.getItem('NEXUS_SESSION_EMAIL') || localStorage.getItem('NEXUS_LAST_EMAIL') || '').trim().toLowerCase();
        const role = String(localStorage.getItem('NEXUS_SESSION_ROLE') || localStorage.getItem('NEXUS_LAST_ROLE') || '').trim().toLowerCase();
        const name = String(localStorage.getItem('NEXUS_SESSION_NAME') || localStorage.getItem('NEXUS_USER_NAME') || '').trim().toLowerCase();
        const nextIdentityKey = (email || 'unknown') + '|' + (role || 'guest') + '|' + (name || 'user');
        const prevIdentityKey = localStorage.getItem('eugene_session_identity_key') || '';
        if (prevIdentityKey && prevIdentityKey !== nextIdentityKey) {
          localStorage.removeItem('eugene_session_id');
        }
      } catch (e) {}
      fetchPolicy();
      try {
        if (typeof window.refreshHomeDashboard === 'function') window.refreshHomeDashboard();
      } catch (e) {}
      const role = getSessionRole();
      if (role === 'guest' || role === 'invited' || role === 'external_guest' || role === 'parent_guest' || role === 'trusted_guest') {
        setTimeout(openGuestSafeDefault, 120);
        setTimeout(openGuestSafeDefault, 650);
      }
    }

    window.addEventListener('message', function (event) {
      var data = event && event.data ? event.data : null;
      if (!data || !data.__nexusBrowser || data.type !== 'nexus-session-sync') return;
      if (event.source !== window.parent || event.origin !== window.location.origin) return;
      applySessionSync(data);
    });

    let policyRefreshQueued = false;
    const observer = new MutationObserver(() => {
      if (document.getElementById('btn-mirror-files')) {
        const role = getSessionRole();
        if (role === 'guest' || role === 'invited' || role === 'external_guest' || role === 'parent_guest' || role === 'trusted_guest') {
          return;
        }
        if (policyRefreshQueued) return;
        policyRefreshQueued = true;
        setTimeout(function () {
          policyRefreshQueued = false;
          fetchPolicy();
        }, 120);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    document.addEventListener('click', adminCreateOverride, true);

    setTimeout(fetchPolicy, 800);
    setTimeout(forceGuestStableButtonsIfNeeded, 350);
    setTimeout(forceGuestStableButtonsIfNeeded, 900);
    setTimeout(forceGuestStableButtonsIfNeeded, 1800);
    setTimeout(openGuestSafeDefault, 1100);
    let guestStablePulse = 0;
    const guestStableTimer = setInterval(function () {
      guestStablePulse += 1;
      forceGuestStableButtonsIfNeeded();
      if (guestStablePulse >= 12) clearInterval(guestStableTimer);
    }, 350);
	  })();
	  `
  if (shouldInjectScript) doc.head.appendChild(script)
}

/* @nexus-profile demo:start */
function injectDemoPresenterBridge(doc: Document) {
  const w = doc.defaultView as any
  if (!w || w.__nexusDemoPresenterInjected) return
  w.__nexusDemoPresenterInjected = true

  let style = doc.getElementById(DEMO_PRESENTER_STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = doc.createElement('style')
    style.id = DEMO_PRESENTER_STYLE_ID
    doc.head.appendChild(style)
  }

  style.textContent = `
    #nexus-demo-highlight {
      position: fixed;
      z-index: 2147483000;
      border: 2px solid rgba(250, 204, 21, 0.92);
      border-radius: 10px;
      background: rgba(250, 204, 21, 0.055);
      box-shadow: 0 0 0 3px rgba(250, 204, 21, 0.18), 0 10px 34px rgba(250, 204, 21, 0.34);
      pointer-events: none;
      transition: top 0.18s ease, left 0.18s ease, width 0.18s ease, height 0.18s ease, opacity 0.18s ease;
      animation: nexusPresenterFocusPulse 1.4s ease-in-out infinite;
    }

    #nexus-demo-highlight-label {
      position: fixed;
      z-index: 2147483001;
      max-width: min(420px, calc(100vw - 32px));
      padding: 7px 10px;
      border-radius: 8px;
      background: rgba(15, 23, 42, 0.82);
      border: 1px solid rgba(250, 204, 21, 0.46);
      color: #f8fafc;
      font: 600 12px/1.25 Inter, system-ui, sans-serif;
      box-shadow: 0 10px 26px rgba(0, 0, 0, 0.24);
      pointer-events: none;
    }

    @keyframes nexusPresenterFocusPulse {
      0%, 100% { box-shadow: 0 0 0 3px rgba(250, 204, 21, 0.16), 0 10px 34px rgba(250, 204, 21, 0.28); }
      50% { box-shadow: 0 0 0 6px rgba(34, 211, 238, 0.16), 0 10px 38px rgba(34, 211, 238, 0.24); }
    }

    body.nexus-demo-paused #nexus-demo-highlight,
    body.nexus-demo-paused #nexus-demo-highlight-label {
      opacity: 0.42;
    }
  `

  let script = doc.getElementById(DEMO_PRESENTER_SCRIPT_ID) as HTMLScriptElement | null
  const shouldInjectScript = !script
  if (!script) {
    script = doc.createElement('script')
    script.id = DEMO_PRESENTER_SCRIPT_ID
    script.type = 'text/javascript'
  }

  script.text = String.raw`
(function () {
  if (window.__nexusDemoPresenterBridge) return;
  window.__nexusDemoPresenterBridge = true;

  var VIEW_SELECTORS = {
    home: '#btn-mirror-home',
    my_work: '#btn-mirror-my-work',
    files: '#btn-mirror-files',
    shared: '#btn-mirror-shared',
    shared_with_me: '#btn-mirror-shared',
    shared_by: '#btn-mirror-shared-by',
    shared_by_me: '#btn-mirror-shared-by',
    classroom: '#btn-mirror-classroom',
    teacher: '#btn-mirror-teacher',
    admin: '#btn-mirror-admin',
    institutional_index: '#btn-mirror-ii',
    ii: '#btn-mirror-ii',
    agents: '#btn-mirror-agents'
  };

  function selectOne(selector) {
    try { return selector ? document.querySelector(selector) : null; } catch (e) { return null; }
  }

  function compactElementText(el, limit) {
    try {
      return String((el && (el.innerText || el.textContent)) || '').replace(/\s+/g, ' ').trim().slice(0, limit || 140);
    } catch (e) {
      return '';
    }
  }

  function presenterElementSelector(el) {
    if (!el || !el.tagName) return '';
    try {
      if (el.id) return '#' + String(el.id).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
      var testId = el.getAttribute && (el.getAttribute('data-testid') || el.getAttribute('data-module') || el.getAttribute('data-action'));
      if (testId) return el.tagName.toLowerCase() + '[data-testid="' + String(testId).replace(/"/g, '\\"') + '"]';
      var aria = el.getAttribute && el.getAttribute('aria-label');
      if (aria) return el.tagName.toLowerCase() + '[aria-label="' + String(aria).replace(/"/g, '\\"') + '"]';
      var cls = String(el.className || '').split(/\s+/).filter(Boolean).slice(0, 2).join('.');
      return el.tagName.toLowerCase() + (cls ? '.' + cls : '');
    } catch (e) {
      return '';
    }
  }

  function describePresenterElement(el) {
    if (!el || !el.tagName || el === document.body || el === document.documentElement) return null;
    try {
      var rect = el.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return null;
      return {
        tag: String(el.tagName || '').toLowerCase(),
        id: el.id || '',
        classes: String(el.className || '').split(/\s+/).filter(Boolean).slice(0, 5),
        selector: presenterElementSelector(el),
        role: (el.getAttribute && (el.getAttribute('role') || el.getAttribute('aria-role'))) || '',
        ariaLabel: (el.getAttribute && el.getAttribute('aria-label')) || '',
        title: (el.getAttribute && el.getAttribute('title')) || '',
        text: compactElementText(el, 180),
        disabled: !!(el.disabled || (el.getAttribute && el.getAttribute('aria-disabled') === 'true')),
        rect: {
          x: Math.round(rect.left),
          y: Math.round(rect.top),
          width: Math.round(rect.width),
          height: Math.round(rect.height)
        }
      };
    } catch (e) {
      return null;
    }
  }

  function rememberPresenterElement(kind, target) {
    try {
      var el = target && target.closest
        ? target.closest('button, [role="button"], a, input, textarea, select, [data-module], [data-action], .control-btn, .mirror-card, .message, .tool-button')
        : null;
      var described = describePresenterElement(el || target);
      if (!described) return;
      window.__nexusPresenterElementContext = window.__nexusPresenterElementContext || {};
      window.__nexusPresenterElementContext[kind] = described;
      window.__nexusPresenterElementContext.updatedAt = new Date().toISOString();
    } catch (e) {}
  }

  document.addEventListener('pointerover', function (event) {
    rememberPresenterElement('hovered', event.target);
  }, true);
  document.addEventListener('click', function (event) {
    rememberPresenterElement('clicked', event.target);
  }, true);
  document.addEventListener('focusin', function (event) {
    rememberPresenterElement('focused', event.target);
  }, true);

  function fallbackNarration(step, repeat) {
    var fallback = repeat && step && step.repeatNarrative ? step.repeatNarrative : '';
    return String(
      fallback ||
      (step && step.fallbackNarration) ||
      (step && step.narrationFallback) ||
      (step && step.narrative) ||
      (step && step.businessMessage) ||
      (step && step.narration && step.narration.business) ||
      ''
    ).trim();
  }

  function createNarrationBubble(repeat) {
    try {
      if (typeof window.createMessageRow === 'function') {
        return window.createMessageRow('', 'eugene', { metaText: repeat ? 'Presenter Mode · replay' : 'Presenter Mode' });
      }
    } catch (e) {}

    var chatWindow = document.getElementById('chat-window');
    if (!chatWindow) return null;
    var row = document.createElement('div');
    row.className = 'message-row eugene';
    var avatar = document.createElement('div');
    avatar.className = 'msg-avatar eugene';
    avatar.textContent = 'E';
    var wrap = document.createElement('div');
    wrap.className = 'message-bubble-wrap';
    var bubble = document.createElement('div');
    bubble.className = 'message eugene';
    var meta = document.createElement('div');
    meta.className = 'msg-meta';
    meta.textContent = repeat ? 'Presenter Mode · replay' : 'Presenter Mode';
    wrap.appendChild(bubble);
    wrap.appendChild(meta);
    row.appendChild(avatar);
    row.appendChild(wrap);
    chatWindow.appendChild(row);
    chatWindow.scrollTop = chatWindow.scrollHeight;
    return { row: row, bubble: bubble, meta: meta };
  }

  function appendEugeneNarrative(text, repeat) {
    var msg = String(text || '').trim();
    if (!msg) return;
    var rec = createNarrationBubble(repeat);
    if (!rec || !rec.bubble) return;
    rec.bubble.textContent = msg;
    if (rec.meta) rec.meta.textContent = repeat ? 'Presenter Mode · replay' : 'Presenter Mode';
    try {
      var chatWindow = document.getElementById('chat-window');
      if (chatWindow) chatWindow.scrollTop = chatWindow.scrollHeight;
    } catch (e) {}
  }

  function clampVoiceNumber(value, fallback, min, max) {
    var n = Number(value);
    if (!Number.isFinite(n)) n = fallback;
    return Math.max(min, Math.min(max, n));
  }

  function presenterVoiceHints(voiceConfig, key, fallback) {
    var raw = voiceConfig && voiceConfig[key];
    var values = Array.isArray(raw) ? raw : String(raw || '').split(',');
    if (!values.length && Array.isArray(fallback)) values = fallback;
    return values.map(function (item) {
      return String(item || '').trim().toLowerCase();
    }).filter(Boolean);
  }

  function waitForPresenterVoices(timeoutMs) {
    return new Promise(function (resolve) {
      if (!window.speechSynthesis) return resolve([]);
      var voices = window.speechSynthesis.getVoices();
      if (voices && voices.length) return resolve(voices);
      var done = false;
      var timer = window.setTimeout(finish, timeoutMs || 1400);
      function finish() {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        try {
          if (window.speechSynthesis.removeEventListener) {
            window.speechSynthesis.removeEventListener('voiceschanged', finish);
          }
          if (window.speechSynthesis.onvoiceschanged === finish) {
            window.speechSynthesis.onvoiceschanged = null;
          }
        } catch (e) {}
        resolve(window.speechSynthesis.getVoices() || []);
      }
      try {
        if (window.speechSynthesis.addEventListener) {
          window.speechSynthesis.addEventListener('voiceschanged', finish, { once: true });
        } else {
          window.speechSynthesis.onvoiceschanged = finish;
        }
      } catch (e) {
        window.speechSynthesis.onvoiceschanged = finish;
      }
    });
  }

  function isNoveltyVoiceName(name) {
    return /bad news|bahh|bells|boing|bubbles|cellos|fred|good news|hysterical|jester|junior|organ|ralph|superstar|trinoids|whisper|wobble|zarvox/i.test(name);
  }

  function scorePresenterVoice(voice, voiceConfig) {
    var name = String(voice && voice.name || '');
    var lowerName = name.toLowerCase();
    var lang = String(voice && voice.lang || '').toLowerCase();
    var requestedLang = String((voiceConfig && voiceConfig.lang) || 'en-US').toLowerCase();
    var preferred = presenterVoiceHints(voiceConfig, 'preferredVoices', []);
    var avoid = presenterVoiceHints(voiceConfig, 'avoidVoices', []);
    var hint = String((voiceConfig && voiceConfig.voiceHint) || '').trim().toLowerCase();
    var score = 0;

    if (lang === requestedLang) score += 45;
    else if (lang.split('-')[0] && lang.split('-')[0] === requestedLang.split('-')[0]) score += 20;
    if (voice && voice.localService) score += 8;
    if (voice && voice.default) score += 8;
    if (/premium|enhanced|natural|neural|siri/i.test(name)) score += 28;
    if (/^(samantha|ava|allison|nicky|flo|shelley|sandy|reed|eddy|daniel|karen|catherine|moira|tessa|arthur|martha)/i.test(name)) score += 18;
    if (/grandma|grandpa/i.test(name)) score -= 24;
    if (isNoveltyVoiceName(name)) score -= 500;

    if (hint) {
      if (lowerName === hint) score += 140;
      else if (lowerName.includes(hint) || lang.includes(hint)) score += 90;
    }
    preferred.forEach(function (item, index) {
      if (!item) return;
      if (lowerName === item) score += 130 - index;
      else if (lowerName.includes(item) || item.includes(lowerName)) score += 95 - index;
    });
    avoid.forEach(function (item) {
      if (item && lowerName.includes(item)) score -= 520;
    });

    return score;
  }

  function selectPresenterVoice(voices, voiceConfig) {
    var usable = (voices || []).filter(function (voice) {
      return voice && voice.name && !isNoveltyVoiceName(voice.name);
    });
    if (!usable.length) usable = voices || [];
    usable.sort(function (a, b) {
      return scorePresenterVoice(b, voiceConfig) - scorePresenterVoice(a, voiceConfig);
    });
    return usable[0] || null;
  }

  function presenterNativeBridge() {
    try {
      if (window.nexusBridge && typeof window.nexusBridge.speakPresenterText === 'function') return window.nexusBridge;
    } catch (e) {}
    try {
      if (window.parent && window.parent.nexusBridge && typeof window.parent.nexusBridge.speakPresenterText === 'function') return window.parent.nexusBridge;
    } catch (e) {}
    return null;
  }

  function presenterAudioBridge() {
    try {
      if (window.nexusBridge && typeof window.nexusBridge.playPresenterAudio === 'function') return window.nexusBridge;
    } catch (e) {}
    try {
      if (window.parent && window.parent.nexusBridge && typeof window.parent.nexusBridge.playPresenterAudio === 'function') return window.parent.nexusBridge;
    } catch (e) {}
    return null;
  }

  function nativePresenterSpeechPayload(msg, voiceConfig) {
    return {
      text: msg,
      engine: (voiceConfig && voiceConfig.engine) || 'neural-first',
      neural: !(voiceConfig && voiceConfig.neural === false),
      neuralProvider: (voiceConfig && voiceConfig.neuralProvider) || 'openai',
      neuralModel: (voiceConfig && voiceConfig.neuralModel) || 'gpt-4o-mini-tts',
      neuralVoice: (voiceConfig && voiceConfig.neuralVoice) || 'ash',
      neuralInstructions: (voiceConfig && voiceConfig.neuralInstructions) || '',
      lang: (voiceConfig && voiceConfig.lang) || 'en-US',
      rate: Number((voiceConfig && voiceConfig.rate) || 0.88),
      pitch: Number((voiceConfig && voiceConfig.pitch) || 0.98),
      volume: Number((voiceConfig && voiceConfig.volume) || 1),
      voiceHint: (voiceConfig && voiceConfig.voiceHint) || 'Reed (English (US))',
      preferredVoices: (voiceConfig && voiceConfig.preferredVoices) || []
    };
  }

  function speakWithNativePresenterVoice(msg, voiceConfig) {
    var payload = nativePresenterSpeechPayload(msg, voiceConfig || {});
    var bridge = presenterNativeBridge();
    if (bridge) {
      return bridge.speakPresenterText(payload).then(function (result) {
        if (result && result.ok) {
          window.__nexusPresenterVoiceStatus = {
            selectedVoice: result.voice || payload.neuralVoice || payload.voiceHint || 'native presenter',
            selectedLang: payload.lang,
            engine: result.engine || 'native',
            naturalVoiceMode: true
          };
          return true;
        }
        return false;
      }).catch(function () { return false; });
    }
    if (!window.parent || window.parent === window) return Promise.resolve(false);
    return new Promise(function (resolve) {
      var id = 'presenter_speech_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      var done = false;
      var timer = window.setTimeout(finish, 12000, false);
      function finish(ok, result) {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        window.removeEventListener('message', onMessage);
        if (ok) {
          window.__nexusPresenterVoiceStatus = {
            selectedVoice: (result && result.voice) || payload.neuralVoice || payload.voiceHint || 'native presenter',
            selectedLang: payload.lang,
            engine: (result && result.engine) || 'native',
            naturalVoiceMode: true
          };
        }
        resolve(!!ok);
      }
      function onMessage(event) {
        var data = event && event.data;
        if (!data || data.__nexusBrowser !== true || data.type !== 'nexus-presenter-native-speak-response') return;
        if (data.id !== id) return;
        finish(!!(data.result && data.result.ok), data.result || {});
      }
      window.addEventListener('message', onMessage);
      try {
        window.parent.postMessage({
          __nexusBrowser: true,
          type: 'nexus-presenter-native-speak',
          id: id,
          payload: payload
        }, '*');
      } catch (e) {
        finish(false);
      }
    });
  }

  function stopPresenterSpeech() {
    try {
      if (window.__nexusPresenterAudio) {
        window.__nexusPresenterAudio.pause();
        window.__nexusPresenterAudio.currentTime = 0;
        window.__nexusPresenterAudio = null;
      }
    } catch (e) {}
    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch (e) {}
    try {
      var bridge = presenterNativeBridge();
      if (bridge && typeof bridge.stopPresenterSpeech === 'function') bridge.stopPresenterSpeech();
    } catch (e) {}
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ __nexusBrowser: true, type: 'nexus-presenter-native-stop' }, '*');
      }
    } catch (e) {}
  }

  function shouldUseRobotFallback(voiceConfig) {
    return !!(voiceConfig && voiceConfig.allowRobotFallback === true);
  }

  function postPresenterVoiceStatus(status) {
    try {
      if (window.parent && window.parent !== window) {
        window.parent.postMessage(Object.assign({ type: 'nexus-demo-presenter-voice-status' }, status || {}), '*');
      }
    } catch (e) {}
  }

  function playOpenAiAudioWithShell(voice) {
    if (!voice || !voice.audio_base64) return Promise.resolve(false);
    var payload = {
      audio_base64: voice.audio_base64,
      mime: voice.mime || 'audio/mpeg',
      provider: voice.provider || 'openai_tts',
      model: voice.model || 'gpt-4o-mini-tts',
      voice: voice.voice || 'ash'
    };
    function markPlayed(result) {
      window.__nexusPresenterVoiceStatus = {
        selectedVoice: (result && result.voice) || payload.voice,
        selectedLang: 'multi',
        engine: (result && result.engine) || 'openai-backend-audio',
        model: (result && result.model) || payload.model,
        naturalVoiceMode: true
      };
      postPresenterVoiceStatus({
        phase: 'playing',
        voiceName: window.__nexusPresenterVoiceStatus.selectedVoice,
        voiceLang: window.__nexusPresenterVoiceStatus.selectedLang,
        engine: window.__nexusPresenterVoiceStatus.engine,
        model: window.__nexusPresenterVoiceStatus.model
      });
      return true;
    }
    function playViaParentMessage(previousError) {
      if (!window.parent || window.parent === window) {
        postPresenterVoiceStatus({ phase: 'error', error: previousError || 'AUDIO_PARENT_BRIDGE_UNAVAILABLE' });
        return Promise.resolve(false);
      }
      return new Promise(function (resolve) {
      var id = 'presenter_openai_audio_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      var done = false;
      var timer = window.setTimeout(finish, 12000, false);
      function finish(ok, result) {
        if (done) return;
        done = true;
        window.clearTimeout(timer);
        window.removeEventListener('message', onMessage);
        if (ok) {
          markPlayed(result || {});
        } else {
          postPresenterVoiceStatus({ phase: 'error', error: (result && result.error) || previousError || 'AUDIO_BRIDGE_TIMEOUT' });
        }
        resolve(!!ok);
      }
      function onMessage(event) {
        var data = event && event.data;
        if (!data || data.__nexusBrowser !== true || data.type !== 'nexus-presenter-openai-audio-response') return;
        if (data.id !== id) return;
        finish(!!(data.result && data.result.ok), data.result || {});
      }
      window.addEventListener('message', onMessage);
      try {
        window.parent.postMessage({
          __nexusBrowser: true,
          type: 'nexus-presenter-openai-audio',
          id: id,
          payload: payload
        }, '*');
      } catch (e) {
        finish(false);
      }
      });
    }
    var bridge = presenterAudioBridge();
    if (bridge) {
      return bridge.playPresenterAudio(payload).then(function (result) {
        if (result && result.ok) return markPlayed(result);
        return playViaParentMessage((result && result.error) || 'AUDIO_DIRECT_BRIDGE_NOT_PLAYING');
      }).catch(function (e) {
        return playViaParentMessage(String(e && e.message ? e.message : e || 'AUDIO_DIRECT_BRIDGE_ERROR'));
      });
    }
    return playViaParentMessage('AUDIO_DIRECT_BRIDGE_UNAVAILABLE');
  }

  async function playPresenterVoiceAudio(voice) {
    if (!voice || !voice.audio_base64) return false;
    try {
      stopPresenterSpeech();
      var shellPlayed = await playOpenAiAudioWithShell(voice);
      if (shellPlayed) return true;
      postPresenterVoiceStatus({ phase: 'error', error: 'SHELL_AUDIO_NOT_PLAYING' });
      return false;
    } catch (e) {
      postPresenterVoiceStatus({ phase: 'error', error: String(e && e.message ? e.message : e || 'SHELL_AUDIO_ERROR') });
      return false;
    }
  }

  async function speakNarration(text, voiceConfig, enabled) {
    if (!enabled) return;
    var msg = String(text || '').trim();
    if (!msg) return;
    try {
      var nativeSpoke = await speakWithNativePresenterVoice(msg, voiceConfig || {});
      if (nativeSpoke) return;
      if (!window.speechSynthesis || typeof window.SpeechSynthesisUtterance === 'undefined') return;
      var voices = await waitForPresenterVoices(1400);
      var selectedVoice = selectPresenterVoice(voices, voiceConfig || {});
      window.speechSynthesis.cancel();
      var utterance = new window.SpeechSynthesisUtterance(msg);
      utterance.lang = (voiceConfig && voiceConfig.lang) || (selectedVoice && selectedVoice.lang) || 'en-US';
      utterance.rate = clampVoiceNumber(voiceConfig && voiceConfig.rate, 0.88, 0.72, 1.04);
      utterance.pitch = clampVoiceNumber(voiceConfig && voiceConfig.pitch, 0.98, 0.82, 1.08);
      utterance.volume = clampVoiceNumber(voiceConfig && voiceConfig.volume, 1, 0, 1);
      if (selectedVoice) utterance.voice = selectedVoice;
      window.__nexusPresenterVoiceStatus = {
        selectedVoice: selectedVoice ? selectedVoice.name : 'browser default',
        selectedLang: selectedVoice ? selectedVoice.lang : utterance.lang,
        rate: utterance.rate,
        pitch: utterance.pitch,
        naturalVoiceMode: true
      };
      try {
        window.parent.postMessage({
          type: 'nexus-demo-presenter-voice-status',
          voiceName: window.__nexusPresenterVoiceStatus.selectedVoice,
          voiceLang: window.__nexusPresenterVoiceStatus.selectedLang
        }, '*');
      } catch (e) {}
      window.speechSynthesis.speak(utterance);
    } catch (e) {}
  }

  function visibleText(selector, limit) {
    var el = selectOne(selector);
    if (!el) return '';
    return String(el.innerText || el.textContent || '').replace(/\s+/g, ' ').trim().slice(0, limit || 1200);
  }

  function captureVisibleState(step) {
    var highlighted = step && step.highlightSelector ? visibleText(step.highlightSelector, 700) : '';
    var controls = [];
    var buttons = [];
    try {
      controls = Array.prototype.slice.call(document.querySelectorAll('.mirror-controls button:not([hidden])'))
        .filter(function (btn) {
          var rect = btn.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        })
        .map(function (btn) { return String(btn.innerText || btn.textContent || '').trim(); })
        .filter(Boolean)
        .slice(0, 18);
    } catch (e) {}
    try {
      buttons = Array.prototype.slice.call(document.querySelectorAll('button:not([hidden]), [role="button"]:not([hidden])'))
        .filter(function (btn) {
          var rect = btn.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0;
        })
        .map(function (btn) {
          return {
            text: compactElementText(btn, 90),
            ariaLabel: (btn.getAttribute && btn.getAttribute('aria-label')) || '',
            title: (btn.getAttribute && btn.getAttribute('title')) || '',
            selector: presenterElementSelector(btn)
          };
        })
        .filter(function (item) { return item.text || item.ariaLabel || item.title; })
        .slice(0, 28);
    } catch (e) {}
    var elementContext = {};
    try { elementContext = window.__nexusPresenterElementContext || {}; } catch (e) {}
    return {
      role: (function () { try { return localStorage.getItem('NEXUS_SESSION_ROLE') || ''; } catch (e) { return ''; } })(),
      email: (function () { try { return localStorage.getItem('NEXUS_SESSION_EMAIL') || localStorage.getItem('NEXUS_EMAIL') || ''; } catch (e) { return ''; } })(),
      user: visibleText('#home-user-status', 220),
      mirrorTitle: visibleText('.mirror-title', 220),
      mirrorSubtitle: visibleText('.mirror-subtitle', 360),
      mirrorDocumentTitle: visibleText('#mirror-doc-title', 220),
      mirrorDocumentMeta: visibleText('#mirror-doc-meta', 320),
      highlightedText: highlighted,
      visibleControls: controls,
      visibleButtons: buttons,
      focusedElement: (elementContext && elementContext.focused) || describePresenterElement(document.activeElement),
      clickedElement: elementContext && elementContext.clicked,
      hoveredElement: elementContext && elementContext.hovered,
      mirrorExcerpt: visibleText('.mirror-body', 900),
      chatRoute: visibleText('#agent-route-indicator', 220)
    };
  }

  function buildDemoContextPacket(step, index, total, repeat) {
    var visibleState = captureVisibleState(step || {});
    return {
      packetType: 'nexus.demo.presenter.step.v1',
      generatedAt: new Date().toISOString(),
      stepIndex: Number(index || 0),
      totalSteps: Number(total || 0),
      repeat: !!repeat,
      role: step.role || visibleState.role || 'current',
      view: step.view || 'current',
      module: step.module || step.title || '',
      action: step.action || step.title || '',
      highlightSelector: step.highlightSelector || '',
      highlightLabel: step.highlightLabel || '',
      visibleContext: step.visibleContext || '',
      visibleState: visibleState,
      narration: step.narration || {
        business: step.businessMessage || '',
        pedagogical: step.pedagogicalMessage || '',
        technical: step.technicalMessage || ''
      },
      businessMessage: step.businessMessage || (step.narration && step.narration.business) || '',
      pedagogicalMessage: step.pedagogicalMessage || (step.narration && step.narration.pedagogical) || '',
      technicalMessage: step.technicalMessage || (step.narration && step.narration.technical) || '',
      desiredTone: step.desiredTone || 'natural, confident, institutional',
      maxNarrationSeconds: Number(step.maxNarrationSeconds || 35),
      fallbackNarration: fallbackNarration(step, repeat),
      narrationFallback: fallbackNarration(step, repeat),
      expectedEvidence: Array.isArray(step.expectedEvidence) ? step.expectedEvidence : [],
      qaChecks: Array.isArray(step.qaChecks) ? step.qaChecks : [],
      relatedDocs: Array.isArray(step.relatedDocs) ? step.relatedDocs : [],
      questionsItCanAnswer: Array.isArray(step.questionsItCanAnswer) ? step.questionsItCanAnswer : []
    };
  }

  function presenterRuntimeBase() {
    var configured = '';
    try {
      configured = (typeof RUNTIME_BASE !== 'undefined' && RUNTIME_BASE) ? String(RUNTIME_BASE) : '';
    } catch (e) {}
    if (!configured) {
      try { configured = window.EUGENE_RUNTIME_BASE ? String(window.EUGENE_RUNTIME_BASE) : ''; } catch (e) {}
    }
    configured = configured.replace(/\/+$/, '');
    if (configured) {
      if (/\/api$/i.test(configured)) return configured.replace(/\/api$/i, '');
      return configured;
    }
    try {
      if (window.location && window.location.origin && window.location.origin !== 'null') return window.location.origin;
    } catch (e) {}
    return 'http://127.0.0.1:2801';
  }

  function presenterSessionId() {
    try {
      if (typeof getOrCreateSessionId === 'function') return getOrCreateSessionId();
    } catch (e) {}
    try {
      return localStorage.getItem('NEXUS_SESSION_LEASE') || localStorage.getItem('eugene_session_id') || localStorage.getItem('nexus_session_id') || 'nexus_presenter_demo_session';
    } catch (e) {
      return 'nexus_presenter_demo_session';
    }
  }

  function normalizePresenterRole(value, fallback) {
    var role = String(value || fallback || 'developer').trim().toLowerCase();
    if (role === 'parent') role = 'guest';
    if (role === 'student' || role === 'teacher' || role === 'admin' || role === 'developer' || role === 'guest') return role;
    return 'developer';
  }

  function presenterAllowedScope(role) {
    if (role === 'developer') return ['explain_architecture', 'switch_demo_overlay', 'inspect_health', 'describe_agents', 'answer_product_questions'];
    if (role === 'admin') return ['explain_admin_panel', 'describe_users_roles_permissions', 'describe_curriculum_reports', 'answer_admin_questions'];
    if (role === 'teacher') return ['explain_teacher_dashboard', 'describe_assignments_submissions_reports', 'answer_teacher_questions'];
    if (role === 'student') return ['explain_student_interface', 'describe_assignments_files_tutor', 'answer_student_questions'];
    return ['explain_guest_portal', 'describe_shared_documents', 'answer_guest_questions'];
  }

  function presenterBlockedScope(role) {
    var blocked = ['auth_mutation', 'device_binding_mutation', 'vault_write', 'qbridge_core_change', 'caddy_change', 'validator_runtime_change', 'normal_eugene_prompt_change'];
    if (role === 'student') blocked = blocked.concat(['developer_tools', 'system_scripts', 'role_switcher', 'admin_dashboard']);
    if (role === 'guest') blocked = blocked.concat(['qbox_global', 'institutional_dashboard', 'role_switcher', 'developer_tools']);
    if (role === 'teacher') blocked = blocked.concat(['device_binding', 'global_user_permissions']);
    if (role === 'admin') blocked = blocked.concat(['runtime_core_edits']);
    return blocked;
  }

  function buildPresenterSessionContract(packet, visibleState) {
    var rawRole = (packet && packet.role) || (visibleState && visibleState.role) || ((typeof getSessionRole === 'function') ? getSessionRole() : '');
    var role = normalizePresenterRole(rawRole, 'developer');
    var name = (typeof getCurrentUserName === 'function') ? getCurrentUserName() : '';
    var email = (typeof getCurrentUserEmail === 'function') ? getCurrentUserEmail() : '';
    return {
      actor_name: name || (visibleState && visibleState.userName) || ('Demo ' + role),
      actor_email: email || (visibleState && visibleState.email) || '',
      actor_role: role,
      expected_overlay: (packet && (packet.view || packet.role)) || role,
      session_scope: 'demo_presenter',
      allowed_scope: presenterAllowedScope(role),
      blocked_scope: presenterBlockedScope(role),
      identity_priority: 'this_contract_over_chat_history'
    };
  }

  function buildPresenterScreenContext(packet, visibleState) {
    var state = visibleState || (packet && packet.visibleState) || {};
    var contract = buildPresenterSessionContract(packet || {}, state);
    return {
      actor_name: contract.actor_name,
      actor_email: contract.actor_email,
      actor_role: contract.actor_role,
      expected_overlay: contract.expected_overlay,
      session_scope: contract.session_scope,
      allowed_scope: contract.allowed_scope,
      blocked_scope: contract.blocked_scope,
      visible_modules: Array.isArray(state.visibleButtons) && state.visibleButtons.length
        ? state.visibleButtons
        : (Array.isArray(state.visibleControls) ? state.visibleControls : []),
      current_screen: (packet && packet.view) || state.activeView || '',
      selected_module: (packet && packet.module) || '',
      focused_button: (state.focusedElement && (state.focusedElement.text || state.focusedElement.ariaLabel || state.focusedElement.title)) ||
        (state.clickedElement && (state.clickedElement.text || state.clickedElement.ariaLabel || state.clickedElement.title)) ||
        (packet && packet.highlightLabel) ||
        '',
      focused_element: state.focusedElement || null,
      clicked_element: state.clickedElement || null,
      hovered_element: state.hoveredElement || null,
      visible_text: JSON.stringify(state || {}).slice(0, 6000),
      overlay_contract: contract
    };
  }

  function normalizeRagText(value) {
    return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  function ragHaystack(chunk) {
    return normalizeRagText([
      chunk && chunk.id,
      chunk && chunk.title,
      chunk && chunk.source,
      chunk && chunk.summary,
      chunk && chunk.answerGuidance,
      chunk && chunk.returnBridge,
      Array.isArray(chunk && chunk.roles) ? chunk.roles.join(' ') : '',
      Array.isArray(chunk && chunk.modules) ? chunk.modules.join(' ') : '',
      Array.isArray(chunk && chunk.keywords) ? chunk.keywords.join(' ') : ''
    ].join(' '));
  }

  function selectPresenterRagChunks(ragPack, query, packet, limit) {
    var chunks = ragPack && Array.isArray(ragPack.chunks) ? ragPack.chunks : [];
    if (!chunks.length) return [];
    var text = normalizeRagText([
      query || '',
      packet && packet.role,
      packet && packet.view,
      packet && packet.module,
      packet && packet.action,
      packet && packet.visibleContext
    ].join(' '));
    var tokens = text.split(/[^a-z0-9]+/).filter(function (token) { return token.length > 2; });
    var scored = chunks.map(function (chunk) {
      var hay = ragHaystack(chunk);
      var score = 0;
      tokens.forEach(function (token) {
        if (hay.indexOf(token) !== -1) score += token.length > 6 ? 3 : 1;
      });
      if (packet && packet.role && Array.isArray(chunk.roles) && chunk.roles.map(normalizeRagText).indexOf(normalizeRagText(packet.role)) !== -1) score += 6;
      if (packet && packet.module && hay.indexOf(normalizeRagText(packet.module)) !== -1) score += 8;
      return { chunk: chunk, score: score };
    }).filter(function (entry) { return entry.score > 0; }).sort(function (a, b) { return b.score - a.score; });
    if (!scored.length) scored = chunks.slice(0, 3).map(function (chunk) { return { chunk: chunk, score: 1 }; });
    return scored.slice(0, limit || 4).map(function (entry) { return entry.chunk; });
  }

  function buildNarrationPrompt(packet, ragPack) {
    var seconds = Math.max(10, Math.min(90, Number(packet.maxNarrationSeconds || 35)));
    var wordBudget = Math.max(30, Math.min(210, Math.round(seconds * 2.35)));
    var ragChunks = selectPresenterRagChunks(ragPack, packet.module + ' ' + packet.action, packet, 4);
    return [
      'You are Eugenie, presenting Nexus QCloud from inside the sovereign local Nexus Browser interface.',
      'Generate one natural spoken narration for the current demo step using the Presenter RAG context.',
      'Use English. Do not mention that you received JSON. Do not output markdown, bullets, labels, or code.',
      'Sound like a calm institutional product guide: concrete, credible, and useful.',
      'Keep it under about ' + seconds + ' seconds, roughly ' + wordBudget + ' words or fewer.',
      'Blend the business, pedagogical, and technical messages only when relevant to the visible screen.',
      'If the visible screen is missing a module, use the safe failsafe framing instead of pretending it is visible.',
      'Presenter RAG context:',
      JSON.stringify({
        title: ragPack && ragPack.title,
        runtimeRules: ragPack && ragPack.runtimeRules,
        voiceGuidance: ragPack && ragPack.voiceGuidance,
        chunks: ragChunks
      }, null, 2),
      'Context packet:',
      JSON.stringify({
        role: packet.role,
        view: packet.view,
        module: packet.module,
        action: packet.action,
        visibleContext: packet.visibleContext,
        visibleState: packet.visibleState,
        narration: packet.narration,
        expectedEvidence: packet.expectedEvidence,
        qaChecks: packet.qaChecks,
        relatedDocs: packet.relatedDocs,
        questionsItCanAnswer: packet.questionsItCanAnswer,
        desiredTone: packet.desiredTone,
        fallbackNarration: packet.fallbackNarration
      }, null, 2)
    ].join('\n');
  }

  async function generatePresenterNarration(packet, repeat, voiceConfig, voiceEnabled, ragPack) {
    var fallback = String(packet.fallbackNarration || packet.narrationFallback || '').trim();
    var rec = createNarrationBubble(repeat);
    if (!rec || !rec.bubble) {
      appendEugeneNarrative(fallback, repeat);
      if (shouldUseRobotFallback(voiceConfig)) await speakNarration(fallback, voiceConfig, voiceEnabled);
      return;
    }

    rec.bubble.textContent = fallback || 'Presenter Premium is preparing narration...';
    if (rec.meta) rec.meta.textContent = fallback ? 'Presenter Premium · fallback ready' : 'Presenter Premium · OpenAI';
    postPresenterVoiceStatus({ phase: 'generating' });

    if (window.__nexusDemoNarrationAbort) {
      try { window.__nexusDemoNarrationAbort.abort(); } catch (e) {}
    }
    var ac = new AbortController();
    window.__nexusDemoNarrationAbort = ac;
    var generationToken = 'narration_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    window.__nexusDemoNarrationToken = generationToken;
    var hardTimeout = window.setTimeout(function () {
      try { ac.abort(); } catch (e) {}
    }, Math.max(18000, Math.min(80000, Number(packet.maxNarrationSeconds || 35) * 1200)));

    try {
      var runtimeBase = presenterRuntimeBase();
      var screenContext = buildPresenterScreenContext(packet, packet.visibleState || {});
      var contract = screenContext.overlay_contract || {};
      var body = {
        session_id: presenterSessionId(),
        presenter_id: 'nexus_browser_presenter_premium_v1',
        model: (voiceConfig && voiceConfig.presenterModel) || '',
        contract: contract,
        screen_context: screenContext,
        current_step: packet,
        module_map: window.__NEXUS_DEMO_MODULE_MAP || {},
        rag_pack: ragPack || {},
        instruction: repeat ? 'Repeat this step with fresh phrasing.' : 'Narrate the current presenter step.'
      };

      var res = await fetch(runtimeBase.replace(/\/+$/, '') + '/api/presenter/narrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ac.signal
      });
      if (!res.ok) throw new Error('HTTP_' + res.status);
      var data = await res.json();
      if (window.__nexusDemoNarrationToken !== generationToken) return;
      var finalAcc = String((data && data.text) || fallback).trim();
      rec.bubble.textContent = finalAcc;
      if (rec.meta) {
        var provider = data && data.provider ? String(data.provider) : 'openai';
        var model = data && data.model ? String(data.model) : '';
        rec.meta.textContent = (data && data.fallback_active)
          ? 'Presenter Premium · fallback qwen3.5:4b'
          : ('Presenter Premium · ' + provider + (model ? ' · ' + model : ''));
      }
      var audioPlayed = voiceEnabled !== false ? await playPresenterVoiceAudio(data && data.voice) : false;
      if (!audioPlayed && shouldUseRobotFallback(voiceConfig)) {
        await speakNarration(finalAcc, voiceConfig, voiceEnabled);
      } else if (!audioPlayed && voiceEnabled !== false && rec.meta) {
        rec.meta.textContent += ' · OpenAI voice unavailable';
      }
    } catch (e) {
      rec.bubble.textContent = fallback || 'Nexus QCloud is ready for the next guided step.';
      if (rec.meta) rec.meta.textContent = 'Presenter Premium · local fallback';
      if (shouldUseRobotFallback(voiceConfig)) await speakNarration(rec.bubble.textContent, voiceConfig, voiceEnabled);
    } finally {
      window.clearTimeout(hardTimeout);
      if (window.__nexusDemoNarrationToken === generationToken) window.__nexusDemoNarrationToken = '';
    }
  }

  function normalizeQuestionText(value) {
    try {
      return String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    } catch (e) {
      return String(value || '').toLowerCase();
    }
  }

  function localAudienceAnswer(question, matches, currentStep, ragPack) {
    var primary = Array.isArray(matches) && matches.length ? matches[0] : null;
    var packet = buildDemoContextPacket(currentStep || {}, 0, 0, false);
    var ragChunks = selectPresenterRagChunks(ragPack, question, packet, 2);
    var ragFallback = ragChunks.length
      ? ' Presenter memory: ' + ragChunks.map(function (chunk) { return chunk.summary || chunk.answerGuidance || chunk.title; }).filter(Boolean).join(' ')
      : '';
    if (!primary) {
      return 'The Presenter module map does not have an exact match for that question yet. The safe answer is to keep the demo on the current module, use the local fallback narration, and treat this as a mapping item for the next presenter pack update.' + ragFallback;
    }
    var docs = Array.isArray(primary.relatedDocs) && primary.relatedDocs.length
      ? ' Related docs: ' + primary.relatedDocs.slice(0, 3).join(', ') + '.'
      : '';
    var safe = Array.isArray(primary.safePresenterActions) && primary.safePresenterActions.length
      ? ' Safe demo action: ' + primary.safePresenterActions[0] + '.'
      : '';
    var caveat = '';
    var q = normalizeQuestionText(question);
    if (q.includes('onlyoffice')) {
      caveat = ' Only mention live editing if health is green; otherwise use the document fallback.';
    }
    if (q.includes('device') || q.includes('binding') || q.includes('otp') || q.includes('auth')) {
      caveat = ' Presenter Mode is read-only for auth, OTP, devices, sessions, and binding.';
    }
    return String(primary.title || primary.id || 'Mapped module') + ': ' +
      String(primary.fallbackAnswer || primary.summary || 'This module is mapped for Presenter Mode.') +
      ragFallback + safe + docs + caveat +
      (currentStep && currentStep.title ? ' The live demo can continue from "' + currentStep.title + '".' : '');
  }

  function buildAudienceQuestionPrompt(question, matches, currentStep, visibleState, moduleMapTitle, ragPack) {
    var packet = buildDemoContextPacket(currentStep || {}, 0, 0, false);
    var ragChunks = selectPresenterRagChunks(ragPack, question, packet, 5);
    return [
      'You are Eugenie, answering an audience question during Nexus QCloud Presenter Mode.',
      'Use Qwen 3.5 Cloud as the narrative mind when available, but stay grounded in the Presenter RAG pack and module map.',
      'Answer in the same language as the audience question when it is clear; otherwise use English.',
      'Do not mention raw JSON, internal ports, or file paths unless the audience specifically asks for technical implementation.',
      'Do not propose edits to auth, Vault, device binding, QBridge core, Caddy, Validator, or runtime-critical services.',
      'If the RAG pack and map do not answer the question, say that the Presenter Pack does not map that detail yet and give the safest related answer.',
      'If the audience asks about "this button", "este boton", "aqui", or "what happens if I click", use focusedElement, clickedElement, hoveredElement, and visibleButtons from visible state before giving a generic module answer.',
      'Keep the answer under 70 seconds, then bridge back to the current presentation step in one sentence.',
      'Audience question: ' + String(question || ''),
      'Presenter RAG pack: ' + JSON.stringify({
        title: ragPack && ragPack.title,
        runtimeRules: ragPack && ragPack.runtimeRules,
        voiceGuidance: ragPack && ragPack.voiceGuidance,
        chunks: ragChunks
      }),
      'Module map: ' + String(moduleMapTitle || 'Presenter module map'),
      'Current step: ' + JSON.stringify(currentStep || {}),
      'Visible state: ' + JSON.stringify(visibleState || {}),
      'Best module matches: ' + JSON.stringify(matches || [])
    ].join('\n');
  }

  async function generateAudienceAnswer(question, matches, currentStep, voiceConfig, voiceEnabled, moduleMapTitle, ragPack) {
    var visibleState = captureVisibleState(currentStep || {});
    var fallback = localAudienceAnswer(question, matches, currentStep, ragPack);
    var rec = createNarrationBubble(false);
    if (!rec || !rec.bubble) {
      appendEugeneNarrative(fallback, false);
      if (shouldUseRobotFallback(voiceConfig)) await speakNarration(fallback, voiceConfig, voiceEnabled);
      return;
    }

    rec.bubble.textContent = fallback;
    if (rec.meta) rec.meta.textContent = 'Presenter Premium · audience fallback ready';
    postPresenterVoiceStatus({ phase: 'generating' });

    if (window.__nexusDemoNarrationAbort) {
      try { window.__nexusDemoNarrationAbort.abort(); } catch (e) {}
    }
    var ac = new AbortController();
    window.__nexusDemoNarrationAbort = ac;
    var generationToken = 'audience_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    window.__nexusDemoNarrationToken = generationToken;
    var hardTimeout = window.setTimeout(function () {
      try { ac.abort(); } catch (e) {}
    }, 65000);

    try {
      var runtimeBase = presenterRuntimeBase();
      var packet = buildDemoContextPacket(currentStep || {}, 0, 0, false);
      var screenContext = buildPresenterScreenContext(packet, visibleState || {});
      var contract = screenContext.overlay_contract || {};
      var body = {
        session_id: presenterSessionId(),
        presenter_id: 'nexus_browser_presenter_premium_v1',
        model: (voiceConfig && voiceConfig.presenterModel) || '',
        contract: contract,
        screen_context: screenContext,
        question: question,
        current_step: currentStep || {},
        module_map: {
          title: moduleMapTitle || 'Presenter module map',
          matches: matches || [],
          modules: matches || []
        },
        rag_pack: ragPack || {}
      };

      var res = await fetch(runtimeBase.replace(/\/+$/, '') + '/api/presenter/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: ac.signal
      });
      if (!res.ok) throw new Error('HTTP_' + res.status);
      var data = await res.json();
      if (window.__nexusDemoNarrationToken !== generationToken) return;
      var finalAcc = String((data && data.text) || fallback).trim();
      rec.bubble.textContent = finalAcc;
      if (rec.meta) {
        var provider = data && data.provider ? String(data.provider) : 'openai';
        var model = data && data.model ? String(data.model) : '';
        rec.meta.textContent = (data && data.fallback_active)
          ? 'Presenter Premium · audience fallback qwen3.5:4b'
          : ('Presenter Premium · audience Q · ' + provider + (model ? ' · ' + model : ''));
      }
      var audioPlayed = voiceEnabled !== false ? await playPresenterVoiceAudio(data && data.voice) : false;
      if (!audioPlayed && shouldUseRobotFallback(voiceConfig)) {
        await speakNarration(finalAcc, voiceConfig, voiceEnabled);
      } else if (!audioPlayed && voiceEnabled !== false && rec.meta) {
        rec.meta.textContent += ' · OpenAI voice unavailable';
      }
    } catch (e) {
      rec.bubble.textContent = fallback;
      if (rec.meta) rec.meta.textContent = 'Presenter Premium · audience local fallback';
      if (shouldUseRobotFallback(voiceConfig)) await speakNarration(fallback, voiceConfig, voiceEnabled);
    } finally {
      window.clearTimeout(hardTimeout);
      if (window.__nexusDemoNarrationToken === generationToken) window.__nexusDemoNarrationToken = '';
    }
  }

  function openView(view) {
    var key = String(view || '').trim().toLowerCase();
    if (!key) return;
    var selector = key.charAt(0) === '#' || key.charAt(0) === '.' || key.charAt(0) === '[' ? key : VIEW_SELECTORS[key] || '';
    var el = selectOne(selector);
    if (!el) return;
    try {
      el.hidden = false;
      el.removeAttribute('data-role-hidden');
      el.style.setProperty('display', 'inline-flex', 'important');
      el.style.setProperty('visibility', 'visible', 'important');
      el.style.setProperty('opacity', '1', 'important');
      el.click();
    } catch (e) {}
  }

  function ensureHighlightEls() {
    var box = document.getElementById('nexus-demo-highlight');
    if (!box) {
      box = document.createElement('div');
      box.id = 'nexus-demo-highlight';
      document.body.appendChild(box);
    }
    var label = document.getElementById('nexus-demo-highlight-label');
    if (!label) {
      label = document.createElement('div');
      label.id = 'nexus-demo-highlight-label';
      document.body.appendChild(label);
    }
    return { box: box, label: label };
  }

  function clearHighlight() {
    var box = document.getElementById('nexus-demo-highlight');
    var label = document.getElementById('nexus-demo-highlight-label');
    if (box) box.remove();
    if (label) label.remove();
  }

  function highlight(selector, labelText) {
    clearHighlight();
    var el = selectOne(selector);
    if (!el) return;
    try { el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' }); } catch (e) {}
    window.setTimeout(function () {
      var target = selectOne(selector);
      if (!target) return;
      var rect = target.getBoundingClientRect();
      if (!rect || rect.width <= 0 || rect.height <= 0) return;
      var els = ensureHighlightEls();
      var pad = 8;
      var top = Math.max(10, rect.top - pad);
      var left = Math.max(10, rect.left - pad);
      var width = Math.min(window.innerWidth - left - 10, rect.width + pad * 2);
      var height = Math.min(window.innerHeight - top - 10, rect.height + pad * 2);
      els.box.style.top = top + 'px';
      els.box.style.left = left + 'px';
      els.box.style.width = Math.max(24, width) + 'px';
      els.box.style.height = Math.max(24, height) + 'px';
    els.label.textContent = labelText || 'Eugenie is focusing this area';
      var labelTop = top + height + 8;
      if (labelTop > window.innerHeight - 70) labelTop = Math.max(10, top - 46);
      els.label.style.top = labelTop + 'px';
      els.label.style.left = Math.min(left, window.innerWidth - 440) + 'px';
    }, 260);
  }

  window.addEventListener('message', function (event) {
    var data = event && event.data;
    if (!data || data.__nexusBrowser !== true) return;
    if (event.source !== window.parent) return;

    if (data.type === 'nexus-demo-presenter-pause') {
      document.body.classList.add('nexus-demo-paused');
      stopPresenterSpeech();
      return;
    }
    if (data.type === 'nexus-demo-presenter-resume') {
      document.body.classList.remove('nexus-demo-paused');
      return;
    }
    if (data.type === 'nexus-demo-presenter-exit') {
      document.body.classList.remove('nexus-demo-paused');
      try {
        if (window.__nexusDemoNarrationAbort) window.__nexusDemoNarrationAbort.abort();
      } catch (e) {}
      stopPresenterSpeech();
      clearHighlight();
      return;
    }
    if (data.type === 'nexus-demo-presenter-question') {
      var matches = Array.isArray(data.matches) ? data.matches : [];
      var primary = matches.length ? matches[0] : null;
      var currentStep = data.currentStep || {};
      if (primary && primary.view) openView(primary.view);
      window.setTimeout(function () {
        if (primary && primary.highlightSelector) {
          highlight(primary.highlightSelector, primary.title || primary.module || 'Presenter module');
        }
      }, 220);
      generateAudienceAnswer(
        data.question || '',
        matches,
        currentStep,
        data.voice || {},
        data.voiceEnabled !== false,
        data.moduleMapTitle || '',
        data.ragPack || {}
      );
      return;
    }
    if (data.type !== 'nexus-demo-presenter-step') return;

    document.body.classList.remove('nexus-demo-paused');
    var step = data.step || {};
    if (data.moduleMap) window.__NEXUS_DEMO_MODULE_MAP = data.moduleMap;
    if (data.ragPack) window.__NEXUS_DEMO_RAG_PACK = data.ragPack;
    openView(step.view);
    window.setTimeout(function () {
      if (step.highlightSelector) highlight(step.highlightSelector, step.highlightLabel || step.title || '');
    }, 180);
    window.setTimeout(function () {
      var packet = buildDemoContextPacket(step, data.index || 0, data.total || 0, !!data.repeat);
      window.__NEXUS_LAST_DEMO_CONTEXT_PACKET = packet;
      generatePresenterNarration(packet, !!data.repeat, data.voice || {}, data.voiceEnabled !== false, data.ragPack || {});
    }, 60);
  });
})();
`
  if (shouldInjectScript) doc.head.appendChild(script)
}
/* @nexus-profile demo:end */

function injectDistrictMirrorStateBridge(doc: Document) {
  const view = doc.defaultView as (Window & { __districtMirrorStateBridgeInjected?: boolean; applyMirrorStateFromServer?: (state: unknown) => void }) | null
  if (!view || view.__districtMirrorStateBridgeInjected) return
  view.__districtMirrorStateBridgeInjected = true
  view.addEventListener('message', (event: MessageEvent) => {
    if (event.source !== view.parent || event.origin !== view.location.origin) return
    const message = event.data as Record<string, unknown> | null
    if (!message || message.__nexusBrowser !== true || message.type !== 'nexus-district-mirror-state') return
    // The Browser validates again before handing the server response to Jenny.
    // No arbitrary postMessage, selector, localStorage value, role, or email can
    // produce the District frame.
    if (!isDistrictMirrorState(message.state)) return
    try {
      view.applyMirrorStateFromServer?.(message.state)
    } catch (error) {
      console.warn('[NexusBrowser] District Mirror state rejected by Jenny bridge', error)
    }
  })
}

function injectMirrorWebSocket(doc: Document) {
  const w = doc.defaultView as any
  if (!w || w.__mirrorWebSocketInjected) return
  w.__mirrorWebSocketInjected = true

  const script = doc.createElement('script')
  script.type = 'text/javascript'
  script.text = String.raw`
(function () {
  if (window.mirrorWebSocket) return;

  const GATEWAY_BASE = window.location.origin || 'http://127.0.0.1:2801';
  let ws = null;
  let reconnectAttempts = 0;
  let reconnectTimer = null;
  let sessionId = null;
  let userEmail = null;
  let userRole = null;
  let clientId = null;
  let ignoreInitialMirrorState = true;

  // Get session info from localStorage
  function getSessionInfo() {
    try {
      sessionId =
        localStorage.getItem('NEXUS_SESSION_LEASE') ||
        localStorage.getItem('eugene_session_id') ||
        localStorage.getItem('nexus_session_id') ||
        'browser-' + Date.now();
      userEmail = localStorage.getItem('NEXUS_SESSION_EMAIL') || localStorage.getItem('NEXUS_EMAIL') || '';
      userRole = localStorage.getItem('NEXUS_SESSION_ROLE') || localStorage.getItem('NEXUS_LAST_ROLE') || 'guest';
    } catch (e) {
      sessionId = 'browser-' + Date.now();
      userEmail = '';
      userRole = 'guest';
    }
    try {
      localStorage.setItem('NEXUS_SESSION_LEASE', sessionId)
      localStorage.setItem('eugene_session_id', sessionId)
      localStorage.setItem('nexus_session_id', sessionId)
    } catch (e) {}
    clientId = 'client-' + Math.random().toString(36).substring(2, 10);
  }

  // Generate unique client ID
  function getClientId() {
    if (!clientId) {
      clientId = 'client-' + Math.random().toString(36).substring(2, 10);
    }
    return clientId;
  }

  // Connect to mirror WebSocket
  function connect() {
    if (ws && ws.readyState === WebSocket.OPEN) return;

    getSessionInfo();
    const host = window.location.hostname || '127.0.0.1';
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    const wsBase = isLocal ? 'ws://127.0.0.1:8700' : GATEWAY_BASE.replace(/^http/, 'ws');
    const wsUrl = wsBase + '/api/mirror/ws?session_id=' + encodeURIComponent(sessionId);

    try {
      ws = new WebSocket(wsUrl);

      ws.onopen = function() {
        console.log('[MirrorWS] Connected to mirror WebSocket');
        reconnectAttempts = 0;
        ignoreInitialMirrorState = true;
        // Keep mirror state passive on Browser startup/reconnect.
        // Explicit syncs should happen only on demand so the last mirror
        // document does not steal focus from views like JW Search.
      };

      ws.onmessage = function(event) {
        try {
          const data = JSON.parse(event.data);
          handleMirrorEvent(data);
        } catch (e) {
          console.warn('[MirrorWS] Failed to parse message:', e);
        }
      };

      ws.onclose = function(event) {
        console.log('[MirrorWS] Disconnected:', event.code, event.reason);
        ws = null;
        scheduleReconnect();
      };

      ws.onerror = function(error) {
        console.error('[MirrorWS] Error:', error);
      };

    } catch (e) {
      console.error('[MirrorWS] Failed to connect:', e);
      scheduleReconnect();
    }
  }

  // Reconnect with exponential backoff
  function scheduleReconnect() {
    if (reconnectTimer) return;

    const delay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
    reconnectAttempts++;

    console.log('[MirrorWS] Reconnecting in', delay, 'ms');
    reconnectTimer = setTimeout(function() {
      reconnectTimer = null;
      connect();
    }, delay);
  }

  // Handle events from mirror WebSocket
  function handleMirrorEvent(data) {
    if (!data || !data.type) {
      console.warn('[MirrorWS] Invalid event received:', data);
      return;
    }
    
    try {
      switch (data.type) {
        case 'mirror_hello':
          console.log('[MirrorWS] Server hello:', data.client_id || 'unknown');
          updatePresenceIndicator(1);
          break;

        case 'mirror_state':
          console.log('[MirrorWS] Mirror state update:', data.state?.revision || 0);
          window.mirrorRevision = data.state?.revision || 0;
          // Actualizar presencia
          if (data.state && data.state.presence) {
            updatePresenceIndicator(
              data.state.presence.count || 0,
              data.state.presence.clients || []
            );
          }
          if (ignoreInitialMirrorState) {
            ignoreInitialMirrorState = false;
            console.log('[MirrorWS] Initial mirror_state ignored to preserve Inicio offline on boot');
            break;
          }
          // Aplicar el estado canonico completo del espejo, incluso si el contenido visible esta vacio.
          if (data.state && typeof window.applyMirrorStateFromServer === 'function') {
            try {
              window.applyMirrorStateFromServer(data.state);
              console.log('[MirrorWS] Mirror state applied via applyMirrorStateFromServer');
            } catch (e) {
              console.warn('[MirrorWS] applyMirrorStateFromServer error:', e);
            }
          } else if (data.state && data.state.view) {
            var view = data.state.view;
            var mirrorObj = {
              type: view.kind || 'doc',
              title: view.title || 'Espejo de trabajo',
              meta: view.meta || '',
              body: typeof view.content === 'string' ? view.content : '',
              path: view.path || null,
              source: view.source || 'mirror_api'
            };
            if (typeof window.handleMirrorUpdate === 'function') {
              try {
                window.handleMirrorUpdate(mirrorObj);
                console.log('[MirrorWS] Mirror state applied via fallback handleMirrorUpdate');
              } catch (e) {
                console.warn('[MirrorWS] handleMirrorUpdate error:', e);
              }
            }
          }
          break;

        case 'open':
          console.log('[MirrorWS] Open document:', data.payload?.path || 'unknown');
          if (data.payload && typeof window.handleMirrorUpdate === 'function') {
            try {
              window.handleMirrorUpdate(data.payload);
            } catch (e) {
              console.warn('[MirrorWS] handleMirrorUpdate error:', e);
            }
          }
          break;

        case 'render':
          console.log('[MirrorWS] Render:', data.payload?.title || 'untitled');
          if (data.payload && typeof window.handleMirrorUpdate === 'function') {
            try {
              window.handleMirrorUpdate(data.payload);
            } catch (e) {
              console.warn('[MirrorWS] handleMirrorUpdate error:', e);
            }
          }
          break;

        case 'canvas_command':
        case 'document_sheet_command':
        case 'canvas_ops':
          console.log('[MirrorWS] Canvas command:', data.type);
          if (typeof window.handleMirrorUpdate === 'function') {
            try {
              window.handleMirrorUpdate(data.payload || data);
            } catch (e) {
              console.warn('[MirrorWS] handleMirrorUpdate error:', e);
            }
          }
          break;

        case 'panel':
          console.log('[MirrorWS] Panel:', data.payload?.panel || 'unknown');
          if (typeof window.activatePanel === 'function' && data.payload && data.payload.panel) {
            try {
              window.activatePanel(String(data.payload.panel));
            } catch (e) {
              console.warn('[MirrorWS] activatePanel error:', e);
            }
          }
          break;

        case 'document':
          console.log('[MirrorWS] Document update:', data.payload?.revision || 0);
          if (data.payload) {
            window.mirrorRevision = data.payload.revision || window.mirrorRevision || 0;
            // Jenny_UI already applies the canonical mirror_state payload.
            // Rewriting the DOM again from the wrapper causes interactive
            // views like JW search cards to flicker and get replaced by a
            // residual document body a moment later.
          }
          break;

        case 'presence':
          console.log('[MirrorWS] Presence:', (data.payload && data.payload.count) || 0, 'clients');
          if (data.payload) {
            updatePresenceIndicator(
              data.payload.count || 0,
              data.payload.clients || []
            );
          }
          break;

        case 'mirror_conflict':
          console.warn('[MirrorWS] Conflict detected');
          showConflictModal(data.local_content || '', data.remote_content || '');
          break;

        default:
          console.log('[MirrorWS] Unknown event:', data.type);
      }
    } catch (e) {
      console.error('[MirrorWS] Error handling event:', e);
    }
  }

  // Apply mirror state to UI
  function applyMirrorState(state) {
    if (!state) return;

    // Update document if present
    if (state.current_document?.path) {
      console.log('[MirrorWS] Current document:', state.current_document.path);
    }
    window.mirrorRevision = state.revision || window.mirrorRevision || 0;

    // Update panel if present
    if (state.active_panel && typeof window.activatePanel === 'function') {
      window.activatePanel(state.active_panel);
    }
  }

  // Update presence indicator
  function updatePresenceIndicator(count, clients) {
    try {
      var staleIndicator = document.getElementById('mirror-presence-indicator');
      if (staleIndicator && staleIndicator.parentNode) staleIndicator.parentNode.removeChild(staleIndicator);
    } catch (_) {}

    var uniqueClients = [];
    var seen = new Set();
    if (clients && Array.isArray(clients)) {
      clients.forEach(function(c) {
        if (!c) return;
        var rawKey = String(c.email || c.user_id || c.role || c.client_id || '').trim().toLowerCase();
        if (!rawKey) return;
        if (seen.has(rawKey)) return;
        seen.add(rawKey);
        uniqueClients.push(c);
      });
    }
    var effectiveCount = uniqueClients.length > 0 ? uniqueClients.length : Math.max(1, Number(count || 1) || 1);
    var currentName = '';
    var currentEmail = '';
    try {
      currentName = String(localStorage.getItem('NEXUS_SESSION_NAME') || localStorage.getItem('NEXUS_USER_NAME') || '').trim();
      currentEmail = String(localStorage.getItem('NEXUS_SESSION_EMAIL') || localStorage.getItem('NEXUS_EMAIL') || '').trim();
    } catch (_) {}
    var singleClient = uniqueClients.length === 1 ? uniqueClients[0] : null;
    var singleLabel = currentName || (singleClient && String(singleClient.name || singleClient.email || '').trim()) || currentEmail || '1 usuario';
    var text = effectiveCount > 1 ? (effectiveCount + ' usuarios') : singleLabel;
    var tooltip = '';
    if (uniqueClients.length > 0) {
      tooltip = uniqueClients.map(function(c) {
        if (!c) return '';
        return String(c.name || c.email || c.client_id || 'unknown');
      }).filter(Boolean).join(', ');
    } else {
      tooltip = currentEmail || currentName || text;
    }
    try {
      window.parent && window.parent.postMessage({
        __nexusBrowser: true,
        type: 'nexus-mirror-presence',
        count: effectiveCount,
        label: text,
        tooltip: tooltip
      }, '*');
    } catch (_) {}
  }

  // Show conflict modal
  function showConflictModal(localContent, remoteContent) {
    var modal = document.getElementById('mirror-conflict-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'mirror-conflict-modal';
      modal.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.7);display:flex;align-items:center;justify-content:center;z-index:99999;';
      document.body.appendChild(modal);
    }

    var localText = String(localContent || '').substring(0, 500);
    var remoteText = String(remoteContent || '').substring(0, 500);
    
    modal.innerHTML = '<div style="background:#1e293b;border-radius:16px;padding:24px;max-width:800px;width:90%;color:#e2e8f0;">' +
      '<h3 style="margin:0 0 16px;color:#f87171;">⚠️ Conflicto de edición</h3>' +
      '<p style="margin-bottom:16px;">El documento fue modificado por otro usuario. Elige qué versión conservar:</p>' +
      '<div style="display:flex;gap:12px;margin-bottom:16px;">' +
        '<div style="flex:1;padding:12px;background:#0f172a;border-radius:8px;">' +
          '<div style="font-size:12px;color:#64748b;margin-bottom:8px;">Tu versión</div>' +
          '<div style="max-height:200px;overflow:auto;font-size:13px;">' + localText + '</div>' +
        '</div>' +
        '<div style="flex:1;padding:12px;background:#0f172a;border-radius:8px;">' +
          '<div style="font-size:12px;color:#64748b;margin-bottom:8px;">Versión del servidor</div>' +
          '<div style="max-height:200px;overflow:auto;font-size:13px;">' + remoteText + '</div>' +
        '</div>' +
      '</div>' +
      '<div style="display:flex;gap:8px;justify-content:flex-end;">' +
        '<button onclick="window.resolveMirrorConflict(\'local\')" style="padding:8px 16px;background:#3b82f6;border:none;border-radius:8px;color:#fff;cursor:pointer;">Conservar mi versión</button>' +
        '<button onclick="window.resolveMirrorConflict(\'remote\')" style="padding:8px 16px;background:#10b981;border:none;border-radius:8px;color:#fff;cursor:pointer;">Usar versión del servidor</button>' +
        '<button onclick="document.getElementById(\'mirror-conflict-modal\').style.display=\'none\'" style="padding:8px 16px;background:#475569;border:none;border-radius:8px;color:#fff;cursor:pointer;">Cancelar</button>' +
      '</div>' +
    '</div>';
    modal.style.display = 'flex';
  }

  // Resolve conflict
  window.resolveMirrorConflict = function(choice) {
    const modal = document.getElementById('mirror-conflict-modal');
    if (modal) modal.style.display = 'none';

    if (choice === 'local' && ws && ws.readyState === WebSocket.OPEN) {
      // Send local content to server
      const mirrorBody = document.getElementById('mirror-doc-body');
      if (mirrorBody) {
        ws.send(JSON.stringify({
          type: 'document',
          client_id: getClientId(),
          content: mirrorBody.innerHTML,
          base_revision: window.mirrorRevision || 0
        }));
      }
    } else if (choice === 'remote') {
      // Request resync from server
      ws.send(JSON.stringify({ type: 'get_state' }));
    }
  };

  // Send presence update
  function sendPresence() {
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    const payload = {
      focus: document.activeElement?.id || null,
      selection: window.getSelection()?.toString() || ''
    };

    ws.send(JSON.stringify({
      type: 'presence',
      client_id: getClientId(),
      focus: payload.focus,
      selection: payload.selection
    }));
  }

  // Send document update (debounced)
  let documentTimer = null;
  function sendDocumentUpdate(content) {
    if (documentTimer) clearTimeout(documentTimer);
    documentTimer = setTimeout(function() {
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      ws.send(JSON.stringify({
        type: 'document',
        client_id: getClientId(),
        content: content,
        base_revision: window.mirrorRevision || 0
      }));
      documentTimer = null;
    }, 300);
  }

  // Watch for mirror changes
  function setupMirrorObserver() {
    // Jenny UI uses mirror-doc-body (display) and mirror-doc-editor (textarea for editing)
    var mirrorBody = document.getElementById('mirror-doc-body');
    var mirrorEditor = document.getElementById('mirror-doc-editor');
    
    if (!mirrorBody && !mirrorEditor) {
      // Retry after DOM is ready
      setTimeout(setupMirrorObserver, 1000);
      return;
    }

    // Observe mirror-doc-editor (textarea) for input
    if (mirrorEditor) {
      mirrorEditor.addEventListener('input', function() {
        if (mirrorEditor.value) {
          sendDocumentUpdate(mirrorEditor.value);
        }
      });
      console.log('[MirrorWS] Observing mirror-doc-editor');
    }
  }

  // Public API
  window.mirrorWebSocket = {
    connect: connect,
    disconnect: function() {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      reconnectTimer = null;
      if (ws) ws.close();
      ws = null;
    },
    getStatus: function() {
      return ws ? ws.readyState : null; // 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED
    },
    getClientId: getClientId,
    send: function(type, payload) {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        console.warn('[MirrorWS] Not connected');
        return false;
      }
      ws.send(JSON.stringify({
        type: type,
        client_id: getClientId(),
        ...(payload || {})
      }));
      return true;
    },
    sendDocument: sendDocumentUpdate,
    sendPresence: sendPresence
  };

  // Auto-connect on load
  getSessionInfo();
  connect();

  // Setup presence interval (1 second)
  setInterval(sendPresence, 1000);

  // Setup mirror observer after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupMirrorObserver);
  } else {
    setTimeout(setupMirrorObserver, 500);
  }

  console.log('[MirrorWS] Client initialized, session:', sessionId);
})();
  `
  doc.head.appendChild(script)
}
